import { DayOfWeek, IDatePickerStrings, MessageBarType } from '@fluentui/react';
import { Sorting } from 'src/Models/Sorting';
import { DisplayColumn } from '../../Models/DisplayColumns';
import { getURLParameter } from 'src/utils/utils';
import translationENUS from 'src/Locales/en-US.json';
import translationENUK from 'src/Locales/en-UK.json';
import translationJA from 'src/Locales/ja.json';
import moment from 'moment';
import UATUSLogo from 'src/navbar/images/LocalizationIcons/icon _United States.png';
import { MACCConstants } from './MaccConst';
import { Record } from 'immutable';
import { MANAGE_ACTION_FIELDS } from 'src/manageAction/utils/manageActionConstants';
import { ITimelineItem } from 'src/Components/CaseTracking/CaseTracking.types';
import { WizardActionBar } from '@coherence-design-system/controls';
import type { UserContext } from 'src/Models/UserProfile';

type AreaPathCustomQuery = {
  queryGroups?: Array<{
    clause?: {
      column?: { internalName?: string };
      values?: string[];
    };
  }>;
};

export enum Settings {
  LIMIT_ADO_TEXT = 250,
  PREFETCH_TOKEN_TIME = 180000,
  CHECK_TOKEN_INTERVAL = 300000,
  ZOOM_PERCENTAGE_LIMIT = 200,
  LIMIT_SUPPORT_CASE_lINK = 255,
  IDB_CACHE_DURATION = 86400000,
}

export enum BrowserEvents {
  Resume = 'resume',
  BeforeUnload = 'beforeunload',
  PageHide = 'pagehide',
  VisibilityChange = 'visibilitychange',
  Resize = 'resize',
}

export enum HttpVerbs {
  get = 'get',
  post = 'post',
  delete = 'delete',
  put = 'put',
  patch = 'patch',
}

export enum AnalyticsFilter {
  lastWeek = 'lastWeek',
  lastMonth = 'lastMonth',
  All = 'All',
}
export enum ChartStates {
  InProgress = 'In Progress',
  Done = 'Done',
  OnHold = 'On Hold',
  Removed = 'Removed',
  Rejected = 'Rejected',
  Cancelled = 'Cancelled',
}
export enum ChartSubStates {
  New = 'New',
  Dispatched = 'Dispatched',
  NA = 'N/A',
  Duplicate = 'Duplicate',
  NoActionRequired = 'No Action Required',
  Resolved = 'Resolved',
  Rejected = 'Rejected',
  RequestOutOfScope = 'Request Out of Scope',
}
export enum HttpStatusCode {
  ok = 200,
  internalServerError = 500,
  conflict = 409,
}

export const CurrencySymbol: Record<string, string> = {
  INR: '₹',
  USD: '$',
  JPY: '¥',
  EUR: '€',
  GBP: '£',
};

export interface KeyNamePair {
  key: string;
  name: string;
}

export interface DropDown {
  key: string;
  text: string;
}

export interface KeyValuePair {
  key: string;
  text: string;
}

export interface ComboBox {
  key: string;
  text: string;
}

export interface DropDownPair {
  [x: string]: any;
  key: number;
  text: string;
}

export interface ILayoutData {
  layout: {
    row: {
      element: string;
    }[];
  }[];
}

export enum RecordType {
  Add = 'Add',
  Update = 'Update',
  Delete = 'Delete',
}

export enum AuthorizationScopes {
  Actions,
}

export enum States {
  New = 'New',
  ActivatingOffer = 'Activating Offer',
  OfferActivated = 'Offer Activated',
  ProjectComplete = 'Project Complete',
  InProgress = 'In Progress',
  Closed = 'Closed',
  Draft = 'Draft',
}
export enum SubStates {
  AwaitingAssignment = 'Awaiting Assignment',
  AwaitingOfferSubmission = 'Awaiting Offer Submission',
  AwaitingResponse = 'Awaiting Response',
  AwaitingSellerResponse = 'Awaiting Seller Response',
  CancelledBySeller = 'Cancelled by Seller',
  CollectingRejectionReason = 'Collecting Rejection Reason',
  ConfirmOfferUsage = 'Confirm Offer Usage',
  Disqualified = 'Disqualified',
  DraftInProgress = 'Draft in Progress',
  FollowUpToOfferEngineSent = 'Follow Up to Offer Engine Sent',
  ImplementingOffer = 'Implementing Offer',
  MeetingComplete = 'Meeting Complete',
  MeetingScheduled = 'Meeting Scheduled',
  NextBestActionAccepted = 'Next Best Action Accepted',
  NextBestActionRecommended = 'Next Best Action Recommended',
  NextBestActionRejected = 'Next Best Action Rejected',
  NextBestActionReviewInProgress = 'Next Best Action Review In Progress',
  NoResponse = 'No Response',
  OfferApproved = 'Offer Approved',
  OfferRecommended = 'Offer Recommended',
  OfferRequestSubmitted = 'Offer Request Submitted',
  OfferSelected = 'Offer Selected',
  OfferSubmitted = 'Offer Submitted',
  PendingScheduledMeeting = 'Pending Scheduled Meeting',
  PendingTriageResponse = 'Pending Triage Response',
  ProjectCancelled = 'Project Cancelled',
  ProjectComplete = 'Project Complete',
  ProjectDelayed = 'Project Delayed',
  ProjectStartPending = 'Project Start Pending',
  ProjectStarted = 'Project Started',
  RejectedByApprover = 'Rejected by Approver',
  RejectionReasonCollected = 'Rejection Reason Collected',
  RejectionReasonNotCollected = 'Rejection Reason Not Collected',
  SubmittedPendingApproval = 'Submitted - Pending Approval',
  UnderReview = 'Under Review',
  Resolved = 'Resolved',
  Closed = 'Closed',
  New = 'New',
  Dispatched = 'Dispatched',
  InTechnialFeedback = 'In Technical Feedback Review',
  Approved = 'Approved',
}

export enum URLParams {
  tab = 'tab',
}

export const removeItemInArray = <T>(array: T[] = [], item: T): T[] => {
  const index = array.indexOf(item);
  if (index !== -1) {
    array.splice(index, 1);
  }
  return array;
};

export const PilotUsersFlights: string[] = ['PilotUsers'];
export const FieldLandingUsersFlights: string[] = ['FieldLandingUsers'];
export const CorpLandingUsersFlights: string[] = ['CorpLandingUsers'];
export const UMCEM_ACCESS_FLIGHT = 'UAT-ManagedCapacityCreate'; //'RAIDAccess' ;

export enum Flights {
  ArtemisGA = 'ArtemisGA',
  PilotUsers = 'PilotUsers',
  FieldLandingUsers = 'FieldLandingUsers',
  CorpLandingUsers = 'CorpLandingUsers',
  RelatedOppV2 = 'RelatedOppV2',
  CSTCopilot = 'CSTCopilot',
  MEABedrockAccess = 'MEABedrockAccess',
}

export enum ViewTypes {
  user = 'user',
  system = 'system',
  shared = 'shared',
  sharedWithMe = 'sharedwithMe',
}
export enum ReloadType {
  reloadOnlyViews = 'reloadOnlyViews',
  reloadOnlyFolderViews = 'reloadOnlyFolderViews',
  reloadViewsAndFolderViews = 'reloadViewsAndFolderViews',
  reloadFvrtsViews = 'reloadFvrtsViews',
}
export enum CustomTextForCompare {
  myViews = 'My Views',
  sharedViews = 'Shared Views',
  systemViews = 'System Views',
  userViews = 'User Views',
  dateType = 'dateType',
  additionalTheme = 'Additional Theme',
  Id = 'ID',
  sharedWithMeViews = 'Shared With Me',
}

export enum DataTypes {
  String = 'string',
}

export enum PickListFollowsFields {
  Follows = '@Follows',
}

export enum WorkItemTypes {
  epic = 'Epic',
  feature = 'Feature',
  pbi = 'Product Backlog Item',
  task = 'Task',
  customerProject = 'Customer Project',
  feedback = 'Feedback',
  action = 'Action',
  actions = 'Actions',
  bug = 'Bug',
  issue = 'Issue',
  userStory = 'User Story',
}

export enum WorkItemTypeIcons {
  actions = 'TaskSolid',
  epic = 'CrownSolid',
  feature = 'Trophy2Solid',
  pbi = 'PageListMirroredSolid',
  task = 'TaskSolid',
  customerProject = 'FinancialSolid',
  feedback = 'FlameSolid',
  default = 'AsteriskSolid',
  bug = 'LadybugSolid',
  issue = 'ConstructionConeSolid',
  userStory = 'ReadingModeSolid',
}

export enum MeetingTypes {
  D365FieldScrum = 'D365 Field Scrum',
  MWHQScrum = 'MW HQ Scrum',
  AzureHQScrum = 'Azure HQ Scrum',
  AzureFieldScrum = 'Azure Field Scrum',
  MWFieldScrum = 'MW Field Scrum',
  ABSFieldScrum = 'ABS Field Scrum',
  SecurityFieldScrum = 'Security Field Scrum',
  SupportDeliveryReview = 'Support Delivery Scrum',
  DealClinic = 'Deal Clinic',
  PartnerSharing = 'Partner Sharing',
  SupportDealAcceleration = 'Support Deal Acceleration',
  IATeam = 'IATeam',
  NONE = 'None',
}

export enum RefID {
  BASR = 'ba_sr',
  MWSR = 'mw_sr',
  MSXEM = 'MSXEM',
  IAR = 'IAR',
  SupportURL = 'UATPORTAL-SUPPORTDELV-URL',
  FieldURL = 'UATPORTAL-FIELD-URL',
  CorpURL = 'UATPORTAL-CORP-URL',
  SupportFlyout = 'UATPORTAL-SUPPORTDELV',
  FieldFlyout = 'UATPORTAL-FIELD',
  CorpFlyout = 'UATPORTAL-CORP',
  AHPMFlyout = 'UATPORTAL-AHPM',
  AHPMURL = 'UATPORTAL-AHPM-URL',
  APEXFlyout = 'UATPORTAL-APEX',
  APEXURL = 'UATPORTAL-APEX-URL',
  MWHUBFlyout = 'UATPORTAL-MWHUB',
  MWHUBURL = 'UATPORTAL-MWHUB-URL',
  Feedback = 'FB360PORTAL-FEEDBACK',
  MACCURL = 'UATPORTAL-MACC-URL',
  NonAICapacityForecasting = 'NONAI-CAP-FOR',
  NonAICapacityEscalation = 'NONAI-CAP-ESC',
  AOAI3PGPUCapacityRequest = 'AOAI-3PGPUCAP',
  AOAINon3PGPUCapacityRequest = 'AOAI-CAP',
  MSecR = 'UATPORTAL-MSECR-URL',
}

export enum Operators {
  Equal = '=',
  Notequal = '!=',
  In = 'In',
  NotIn = 'Not In',
  GreaterThan = '>',
  Lessthan = '<',
  GreaterThanOrEqual = '>=',
  LessThanOrEqual = '<=',
}

export enum NotificationsDisable {
  'Manageaction',
}

export enum ExportAction {
  'DownloadActions',
  'EmailActions',
}

export enum TrueFalse {
  true = 'true',
  false = 'false',
}

export const IgnoredColumnsExport = [
  'RowStatus',
  'RelatedLinks',
  'ChildActions',
  'TechnicalFeedbackStatus',
];

export const NoHistory = 'No history available';

export const descriptionHistoryText =
  'This request hasn’t been updated yet. Once changes are made, they’ll appear here.';

export enum SearchText {
  InProgress = 'Search in progress, please wait..',
  NoActions = 'There are no actions available',
  Filtering = 'Filter in progress, please wait..',
  NoRequests = 'There are no requests submitted under this category.',
  NoFiltersApplied = 'There are no filter rules applied to this query. Please add one or more filter rules before running the query.',
  Loading = 'Loading...',
  NoActionsText = "You can add actions by clicking 'Create' and selecting the specific action you want to create.",
}

export enum ManageViewText {
  NoViewsHeading = "Click 'Run Query' to preview data", //'There are no queries to preview data', //commented for now, it may required later, so keeping it
  NoViewsDescription = "You can build your query and preview it by clicking 'Run Query'.",
  NoDataHeading = 'No data was found',
  NoDataDescription = "You can recheck the query and rerun it by clicking 'Run Query'.",
}

export enum ColumnOptionsUseCase {
  ManageView = 'ManageView',
  ArtemisDashboard = 'ArtemisDashboard',
}

export enum EmailActionsUseCase {
  ManageView = 'ManageView',
}

export enum RoBDefaultTagsOrder {
  'Title' = 1,
  'Assigned Team' = 2,
  'Action Owner' = 3,
  'Requestors' = 4,
  'Meeting Type' = 5,
  'Meeting SubType' = 6,
  'Region(s)' = 7,
  'Sub Region' = 8,
  'Area(s)' = 9,
  'Problem Details' = 10,
  'Priority' = 11,
  'Due Date' = 12,
  'Contributor 1' = 13,
  'Contributor 2' = 14,
}

export class UATConstants {
  public static Routes = {
    Dashboard: '/dashboard',
    CreateActionPage: '/createaction',
    UserRatingPage: '/userrating',
    CreateActionPageForMacc: '/createaction/macc',
    CreateActionPageForCorp: '/createaction/corp',
    CreateActionPageForAHPM: '/createaction/ahpm',
    CreateActionAPEX: '/createaction/apex',
    CreateActionPageForMWHubCustomer: '/createaction/customerhub',
    CreateReport: '/reports',
    CreateActionPageForField: '/createaction/field',
    CreateActionPageForIat: '/createaction/iat',
    CreateActionPageForSupport: '/createaction/supportdelivery',
    CreateActionPageForSupportScrum: '/createaction/supportdeliveryscrum',
    ManageView: '/manageview',
    ManageActionPage: '/action',
    SharedQueriesPage: '/sharedqueries',
    SharedQueriesWithParam: '/sharedqueries/:token',
    LandingPage: '/',
    IndexHtml: '/index.html',
    Index: '/index',
    Home: '/home',
    DashboardSummary: '/dashboard-summary',
    Overview: '/overview',
    ManageAction: '/manageaction',
    TenantOverviewHub: '/hub',
    TenantOverviewHubWithTenantId: '/hub?tenantId=',
  };

  public static Scopes = {
    All: '/',
    Dashboard: '/dashboard',
    Actions: '/actions',
  };

  public static Keys = {
    BackSpace: 8,
    Enter: 13,
    Delete: 46,
    LeftArrow: 37,
    RightArrow: 39,
    TabKey: 9,
  };

  public static IconColors = {
    verifiedBrand: '#4B4A4A',
    error: '#ff0000',
    info: '#0BA8E6',
    warning: '#FF6600',
  };

  public static NotificationTypes = {
    error: 'Error',
    warning: 'Warning',
    critical: 'Critical',
    info: 'Info',
  };
  public static multiSortColumns = ['Custom.CoSell_RequestCategory', 'Custom.Contributor1'];
  public static ApiPaths = {
    Actions: '/api/v3/Actions',
    UserContext: '/api/Service/usercontext',
    MSXData: '/api/msxdata',
    PersonDetails: '/api/Service/getPersonDetails',
    GetAlias: '/api/GetAlias',
    Users: '/api/users',
    UsersTable: '/api/usersTable',
    UsersAndGroups: '/api/usersAndGroups',
    SearchGroups: '/api/searchGroups',
    MentionUsersTable: '/api/mentionUsersTable',
    CreateAction: '/api/Actions/PostAction',
    CreateAHPMAction: '/api/Actions/PostAHPMAction',
    GetGroupMembers: '/api/groupMembersForAHPM',
    GetGroupMembersforMaaS: '/api/groupMembersForMaaS',
    GetGroupMembersforAMM: '/api/groupMembersForAMM',
    GetGroupMembersForMDC: '/api/groupMembersForMDC',
    CreateAPEXAction: '/api/Actions/PostAPEXAction',
    BulkCreateAction: '/api/Actions/PostBulkAction',
    BulkCreateActionsUsingCommonSvcs:
      '/api/v1/raid/transactional/raiditems/upload?validateFile=false&createActions=true&tenantId=',
    BulkUploadFileValidateUsingCommonSvcs:
      '/api/v1/raid/transactional/raiditems/uploadvalidate?validateFile=true&createActions=false&tenantId=',
    GetActions: '/api/Actions/Get',
    CheckFeedbackItem: '/api/getFeedbackItem',
    GetDashboardActions: '/api/V2/Actions/dashboard',
    GetWaveReportActions: '/api/V2/Actions/MWHubWaveReport',
    GetActionsCount: '/api/V2/Actions/actionCount',
    GetChartInfo: '/api/V2/Actions/chartInfo',
    UpdateAction: '/api/updateAction',
    GetAction: '/api/getAction',
    GetAreaPaths: '/api/areaPaths',
    GetWaveDetails: '/api/V2/Actions/waveDetails',
    GetActionOwnerForAreaEOU: '/api/V2/Actions/getActionOwnerForAreaEOU',
    GetGroupMembersWithAreaEou: '/api/V2/Actions/getGroupMembersWithAreaEou',
    GetGroupMembersForMaas: '/api/groupMembersForMaaS',
    GetWorkItemComments: '/api/getWorkItemComments',
    PostWorkItemComment: '/api/postWorkItemComment',
    DeleteWorkItemComment: '/api/deleteWorkItemComment',
    UpdateWorkItemComment: '/api/updateWorkItemComment',
    GetWorkItemHistory: '/api/getWorkItemHistory',
    SaveCustomQuery: '/api/saveQuery',
    UpdateCustomQuery: '/api/updateQuery',
    GetADOQueriesByUser: '/api/GetADOQueriesByUser',
    GetViews: '/api/getViews',
    GetAllADOQueries: '/api/getAllADOQueries',
    UpdateADOQueries: '/api/updateQuery',
    GetADOTags: '/api/getADOTags',
    GetToolsName: '/api/getToolsName',
    UpdateUserMetrics: '/api/User/UpdateUserMetrics',
    UpdateDisplayColumnsByUser: '/api/updateDisplayColumnsByUser',
    AdminUsersAreaPaths: '/api/adminUsersAreaPaths',
    GetViewsWithFolders: '/api/getViewsWithFolders',
    PostFolder: '/api/createFolder',
    DeleteUserView: '/api/deleteADOQueryById',
    DeleteFolder: '/api/deleteFolderById',
    MarkViewAsDefault: '/api/markAsDefault',
    MarkTenantAsDefault: '/api/Service/markTenantAsDefault',
    AddViewToFavorites: '/api/addToFavoriteViews',
    RemoveViewFromFavorites: '/api/removeFromFavoriteViews',
    SearchRelatedActionItem: '/api/V2/Actions/searchRelatedActionItems',
    AddRelatedActionItemsList: '/api/Actions/getRelatedWorkItems',
    SearchRemoteWorkItemsList: '/api/Actions/getRemoteLinks',
    SearchRemoteWorkItem: '/api/Actions/getRemoteLink',
    GetADOSharedQueries: '/api/getADOSharedQueries',
    MigrateUserViews: '/api/migrateUserViews',
    MigrateSharedViews: '/api/migrateSharedViews',
    GenerateVSTSToken: '/api/generateToken',
    GetActionsToExport: '/api/V2/Actions/getActionsToExport',
    //GetDropdownData: '/api/getDropdownData',
    GetDropdownData: '/api/getCosmosDropdownData',
    SendWorkItemsEmail: '/api/sendWorkItemsEmail',
    PostUserRating: '/api/postUserRating',
    GetUserRating: '/api/getUserRatings',
    GetRatingConfig: '/api/getRatingConfig',
    GetAreaPath: '/api/areaPaths#areaPath',
    GetContactDetails: '/api/V2/MSX/GetcontactsByOpportunity',
    followActionRoot: 'api/v1/raid/transactional/raiditem',
    GetFollowing: 'followactions',
    AddFollowing: `follow`,
    DeleteFollowing: 'unfollow',
    GetUserLevelNotifications: '/api/getUserLevelNotifications',
    PostDisableUserLevelNotifications: '/api/postDisableUserLevelNotifications',
    GetFeedbackItems: '/api/getRelatedFeedbackItems',
    GetFeedbackComments: '/api/getRelatedFeedbackComments',
    GetFeedbackItemDetails: '/api/getFeedbackItemDetails',
    CreateTemporaryView: '/api/createTemporaryView',
    CheckAndAddUserToView: '/api/checkAndAddUserToView',
    ProvisionUser: '/api/User/ProvisionUser',
    GetMilestoneData: '/api/V2/MSX/GetByMilestone',
    GetAMMMilestoneSnapshot: '/api/V2/MSX/GetAMMMilestoneSnapshot',
    GetOpportunityData: '/api/V2/MSX/GetByOpportunity',
    GetOpportunityInfoByTPID: '/api/v1/MSXData/GetOpportunitiesByTpId',
    SearchOpportunitiesByTpId: '/api/v1/MSXData/SearchOpportunitiesByTpId',
    GetMilestonesByOpportunities: '/api/v1/MSXData/GetMultipleMilestonesByOppIds',
    GetByOpportunityWithEcifAcrRoiCalculations:
      '/api/V2/MSX/GetByOpportunityWithEcifAcrRoiCalculations',
    GetByOpportunityWithMilestones: '/api/V2/MSX/GetByOpportunityWithMilestones',
    GetCustomerData: '/api/V2/MSX/GetByCustomer',
    GetCapacityIntakeByTPID: '/api/V2/MSX/GetCapacityIntakeByTPID',
    GetCustomerBySubscriptionId: '/api/V2/MSX/GetCustomerBySubscriptionId',
    SetColumnOptions: '/api/saveDisplayColumnsByUser',
    GetEngagementItems: '/api/getRelatedEngagementItems',
    AddAnalyticsView: '/api/addAnalyticsView',
    UpdateAnalyticsView: '/api/updateAnalyticsView',
    DeleteAnalyticsView: '/api/deleteAnalyticsView',
    GetAnalyticsViewFields: '/api/GetAnalyticsViewFields',
    GetAnalyticsViewFieldValues: '/api/GetAnalyticsViewFieldValues',
    GetAnalyticsViewAreaPath: '/api/GetAnalyticsViewAreaPath',
    GetAnalyticsViewTags: '/api/GetAnalyticsViewTags',
    GetAnalyticsViewList: '/api/GetAnalyticsViewList',
    GetAnalyticsViewById: '/api/GetAnalyticsViewById',
    SendWorkItemsEmail1: '/api/sendWorkItemsEmail1',
    SendEmai: '/api/sendEmail',
    SendDraft: '/api/draftEmail',
    DeleteDraft: '/api/deletedraftEmail',
    getEmail: '/api/GetEmails',
    downloadAttachmentFileAsync: '/api/downloadAttachmentFileAsync',
    getEmailMetaData: '/api/GetEmailMetaData',
    getEmailTemplateDetails: '/api/GetEmailTemplateDetails',
    getEmailTemplateByFileName: '/api/GetEmailTemplateByFileName',
    getEmailDetail: '/api/GetEmail',
    linkEmailToAction: '/api/LinkEmailToAction',
    delinkEmailFromAction: '/api/DeLinkEmailFromAction',
    GetEmai: '/api/getEmail',
    getPartnerArea: '/api/partnerArea',
    getPartnerCountry: '/api/partnerRegionAreaFromCountry',
    QuickSave: '/api/Actions/PostBulkEditAction',
    PostBulkRelatedAction: '/api/Actions/PostBulkRelatedAction',
    partnerRegionAreaFromCountry: '/api/partnerRegionAreaFromCountry',
    CreateMaaSAction: '/api/Actions/PostMaaSAction',
    GetCRMIDSData: '/api/V2/MSX/GetCRMAccountIDsFromTPId',
    SetUserLanguage: '/api/setUserLanguage',
    GetOpportunityListData: '/api/V2/MSX/GetOppIDsFromCRMAccountId',
    CoSellRequestCategoryFromTopic: '/api/CoSellRequestCategoryFromTopic',
    GetBusinessRulesHelperData: '/api/GetBusinessRulesHelperData',
    GetPartnerInfo: '/api/V2/Actions/getPartnerList',
    SavePartnerList: '/api/V2/Actions/saveUpdatePartnerList',
    GetControlInfo: '/api/V2/Actions/getControlList',
    SaveControlList: '/api/V2/Actions/saveUpdateControlList',
    GetTaskInfo: '/api/V2/Actions/getTaskList',
    SaveTaskList: '/api/V2/Actions/saveUpdateTaskList',
    GetPartnerOneIDDetails: '/api/V2/Actions/getpartnerOneinfo',
    GetPartnerEngagementByReferralID: '/api/V2/MSX/GetPartnerEngagementByReferralID',
    RaidItemsAPIPath: '/api/v1/raid/transactional/search',
    BulkEditAPIPath: '/api/v1/raid/transactional/raiditems/bulkedit',
    BulkDeleteAPIPath: '/api/v1/raid/transactional/raiditems/bulkdelete',
    GetViewsList: '/api/v1/raid/transactional/staticviews',
    GetViewsArtemisLegacy: '/api/v1/GetViews',
    GetViewsArtemis: '/api/v1/raid/transactional/staticviews',
    RaidItemsAPISearchPath: '/api/v1/raid/transactional/search',
    RaidItemCreate: '/api/v1/raid/transactional/raiditems/create?tenantid=',
    ValidateRedButtonCustomerTPID: '/api/v1/raid/transactional/raiditems/validate-red-button-tpid',
    GetWorkscopeItemList: '/api/ECIFWorkscope/GetWorkscopes',
    GetWorkscopeItemDetails: '/api/ECIFWorkscope/GetWorkscopedetails/',
    AllTenant: '/api/v1/raid/transactional/tenants',
    UploadArtemisAttachments: '/api/v1/raid/transactional/UploadFile',
    RaidItemUpdate: 'api/v1/raid/transactional/raiditems/update?tenantId=',
    FolderView: '/api/v1/raid/transactional/folderviews',
    View: '/api/v1/raid/transactional/views',
    UserMetadata: '/api/v1/raid/transactional/usermetadata',
    ArtemisCreate: '/api/v1/raid/transactional/raiditems/create?tenantid=',
    postWorkscopeCreation: '/api/ECIFWorkscope/PostWorkscopeCaseDetails',
    GetAMMDashboardTasks: '/api/v2/Actions/AMMTasks',
    GetAMMProjectTracking: '/api/v1/raid/transactional/ammprojecttracking/',
  };

  public static OlfApiPaths = {
    /**Source: https://customersuccessrepoapi.azurefd.net/swagger/index.html*/
    GetTPIDData: '/api/customer',
    GetMilestoneData: '/api/milestone',
    GetOpportunityData: '/api/opportunity',
  };

  public static Links = {
    cookies:
      'https://privacy.microsoft.com/en-US/privacystatement#maincookiessimilartechnologiesmodule',
    termsOfUse: 'https://www.microsoft.com/en-us/legal/intellectualproperty/copyright/default.aspx',
    privacy: 'https://aka.ms/UA-Tracker-PrivStmt',
    privacyNotice: 'https://privacy.microsoft.com/en-US/data-privacy-notice',
    fieldActions: 'https://aka.ms/fieldActions',
    corpActions: `https://aka.ms/corpActions`,
    triageActions: 'https://aka.ms/uat/devops/requestedactions',
    acrWeeklyScrum: 'https://aka.ms/ACRWeeklyScrum',
    // isCorpACRWeeklyScrum: 'https://aka.ms/acrengagementshq',
    corpAzureReport: 'https://aka.ms/corpAzureReport',
    mwWeeklyScrum: 'https://aka.ms/MWscrum-UAT',
    fieldMWReport: 'https://aka.ms/FieldMWScrum',
    baWeeklyScrum: 'https://aka.ms/UATBAWeeklyScrum',
    FieldBAReport: 'https://aka.ms/FieldBAScrum',
    corpViewActions: 'https://aka.ms/uat/assignedactions',
    corpRequestedActions: 'https://aka.ms/uat/devops/requestedactions',
    userFeedbackLink: 'https://aka.ms/ideas_UNIFIEDACTIONTRACKER_DEALACCELERATIONUNBLOCKING',
    ReportsLink: 'https://aka.ms/uadashboard',
    isAmendmentLink: 'https://empguide.trafficmanager.net/concession/3143',
    userGuideLink: 'https://aka.ms/uat/userguide',
    uatSupportLink: 'https://aka.ms/uatsupport',
    uatAccessLink:
      'https://forms.office.com/Pages/ResponsePage.aspx?id=v4j5cvGGr0GRqy180BHbR0Jkf-iJYKdGvmf2q4LrBaNURVlQQ085WERSWEs0QzlUQklYUklYUFFSSS4u',
    documentationLink: 'https://microsoft.sharepoint.com/teams/UnifiedActionTracker',
    techRobProcessDocumentationLink: 'https://aka.ms/UATPlaybook',
    MsxLink:
      'https://microsoftsales.crm.dynamics.com/main.aspx?appid=fe0c3504-3700-e911-a849-000d3a10b7cc&forceUCI=1&pagetype=entityrecord&etn=opportunity&id=',
    MsxPartnerLink:
      'https://microsoftsales.crm.dynamics.com/main.aspx?appid=fe0c3504-3700-e911-a849-000d3a10b7cc&forceUCI=1&pagetype=entityrecord&etn=msp_partnerengagement&id=',
    meetingTypeLink: 'https://aka.ms/uatsmcguide',
    aoaiCreateActionUserGuideLink: 'https://aka.ms/aicapacityhub',
  };

  public Scopes = {
    All: '/',
  };

  public static ProviderNames = {
    ARTEMIS: 'ARTEMIS',
    ADO: 'ADO',
  };

  public static GPS_CONSTANT_VALUES = {
    Activities: 'Activities',
    Other: 'Other',
  };

  public static IndexedDBKeyNames = {
    UserContext: 'UserContext',
    CosmosDropdownData: 'CosmosDropdownData',
    Tenants: 'Tenants',
    Taxonomy: 'Taxonomy',
  };

  public static All: number = -1;
  public static jwtTokenForGraph: string = 'jwtTokenForGraph';
  public static jwtTokenForApi: string = 'jwtTokenForApi';
  public static jwtTokenForCoreApi: string = 'jwtTokenForCoreApi';
  public static jwtTokenForADOApi: string = 'jwtTokenForAdoApi';
  public static devopsTokenForADOApi: string = 'devopsTokenForADOApi';
  public static jwtUserAccountName: string = 'jwtUserAccountName';
  public static jwtUserAccountEmail: string = 'jwtUserAccountEmail';
  public static subscriptionid: string = 'subscriptionId';
  public static coreTokenExpiryTime: string = 'coreTokenExpiryTime';
  public static loginTokenExpiryTime: string = 'logintokenexpirytime';
  public static graphTokenExpiryTime: string = 'graphTokenExpiryTime';
  public static devopsTokenExpiryTime: string = 'devopstokenexpirytime';
  public static showNotifications: string = 'showNotifications';
  public static notificationCount: string = 'notificationCount';
  public static getBroadcastData: string = 'getBroadcastData';
  public static hideCopilotFeatureBubble: string = 'hideCopilotFeatureBubble';
  public static showCopilotWindow: string = 'showCopilotWindow';
  public static copilotPinnedState: string = 'copilotPinnedState';
  public static CopilotDisclaimerText: string =
    'Copilot will be enabled for Microsoft Federal actions in the future.';
  public static SelectedView: string = 'selectedView';
  public static CopilotPinnedState: string = 'copilotPinnedState';
  public static ApplyFilterViewId: string = 'applyViewId';

  public static apiRetries: number = 3;
  public static apiRetryDelay: number = 500;

  public static notificationConfiguration?: string[] =
    import.meta.env.VITE_NOTIFICATIONCONFIGURATION?.split(',');
  public static CopilotPromptMinimumCharacters: number =
    Number(import.meta.env.VITE_COPILOTPROMPTMINIMUMCHARACTERS) || 10;
  public static CopilotPromptMaximumCharacters: number = 1500;
  public static IsNotDev = () => {
    return import.meta.env.VITE_CUSTOM_NODE_ENV !== 'DEV';
  };
  public static ClientId: string = UATConstants.IsNotDev()
    ? 'ad34d210-a191-454f-96eb-fc33b8bb6f43'
    : 'ad34d210-a191-454f-96eb-fc33b8bb6f43';
  public static WebApiUrl: string = import.meta.env.VITE_WEBAPIURL || 'https://localhost:44381';
  public static AOAIDWRPORTALURL: string =
    import.meta.env.VITE_AOAIDWRPORTALURL || 'https://localhost:44381';
  public static WebUiAkaUrl: string =
    import.meta.env.VITE_WEBUIAKAURL || 'http://localhost:3000/action';
  public static CoreWebApiUrl: string =
    import.meta.env.VITE_COREWEBAPIURL || 'https://localhost:44334';
  public static WebUiUrl: string = import.meta.env.VITE_WEBUIURL || 'http://localhost:3000';
  public static OLFWebApiUrl: string =
    import.meta.env.VITE_MSX_URL || 'https://customersuccessmsxapitest.azurewebsites.net';
  public static MSXDynamicsURL: string =
    import.meta.env.VITE_MSX_DYNAMICS ||
    'https://msxuat.crm.dynamics.com/main.aspx?appid=fe0c3504-3700-e911-a849-000d3a10b7cc&forceUCI=1&pagetype=entityrecord';
  public static CapeWebUiUrl: string =
    import.meta.env.VITE_CAPE_URL || 'https://cape-test-v2.azurewebsites.net/';
  public static CommonServicesWebApiUrl: string =
    import.meta.env.VITE_COMMONWEBAPIURL ||
    'https://commonservice-api-test-geghbnbwe9hbh5b0.z01.azurefd.net';
  /**
   * Base URL for the NEW Common.FormEngine v1 endpoints (server-driven create forms: blank view /
   * compute view / create / capacity-intake route). These endpoints are served by the SAME host as
   * every other API, so this defaults to `CommonServicesWebApiUrl` — which makes it correct for every
   * deployed environment (perf / test / int / prod) with no extra configuration. Only set
   * `VITE_FORMENGINEAPIURL` to override it for LOCAL engine development against a locally-running
   * Common.API dev server, e.g. `VITE_FORMENGINEAPIURL="https://localhost:62283"` in `.env.dev` (its
   * `launchSettings` `applicationUrl`; use `http://localhost:62284` to skip the dev cert).
   */
  public static FormEngineApiUrl: string =
    import.meta.env.VITE_FORMENGINEAPIURL || UATConstants.CommonServicesWebApiUrl;
  public static FeedbackProductId: string = 'aabbccdd-0000-4001-9999-000000000001';
  public static MaasMembersWebUrl: string = 'https://uamsvcs.microsoft.com';
  public static AMMPBILink: string =
    import.meta.env.VITE_AMMPBILINK ||
    'https://msit.powerbi.com/groups/me/apps/09f06f7f-137d-4c2a-beeb-74249ffeba8a/reports/5431f013-a6f7-46df-beea-f87d69b4a12b/dd2b71f78ceaa376bc53?ctid=72f988bf-86f1-41af-91ab-2d7cd011db47&experience=power-bi';
  public static GetRaidEntityQueryId: string =
    import.meta.env.VITE_GetRaidEntityQueryId || 'e07db7d1-0e25-4c9d-9c73-4e12c8b2fc7w';
  public static RaidQueryId: string = 'e07db7d1-0e25-4c9d-9c73-4e12c8b2fc7w';
  public static AvsSharePointLink: string = import.meta.env.VITE_AMMAVSSharePointUrl;
  public static AMMMAAPLINK: string = 'https://aka.ms/MAAPACR';

  public static MAX_OPPORTUNITY_SELECT_LIMIT: number =
    Number(import.meta.env.VITE_MAX_OPPORTUNITY_SELECT_LIMIT) || 10;
  public static MAX_MILESTONE_SELECT_LIMIT: number =
    Number(import.meta.env.VITE_MAX_MILESTONE_SELECT_LIMIT) || 5;

  public static TypeSeparator = '&type=';
  public static AOAITabPath = '&tab=Azure AI Capacity';
  public static ApextabPath = '&tab=MW Programs';
  public static CapsltabPath = '&tab=CAPSL';
  public static CustomerHubtabPath = '&tab=Customer Hub';
  public static OLFApiLogMessage: string = 'MSXDataMissing';
  public static AssignedTo: string = 'Action Owner';
  public static CustomView: string = 'View';
  public static CrateActionDefaultValue_Category: string = 'None'; // id/key for None
  public static CoSelltabPath = '&tab=CoSell';
  public static PMYTTabPath = '&tab=PMYT';
  public static GPSInvestmentstabPath = '&tab=Partner Investments';
  public static McapsHelpPmTabPath = '&tab=MCAPS Help PM';
  public static PMYTTabNodePath = '&tab=Partner Marketing Yield Tracker';
  public static RootAreaPath = String(
    import.meta.env.VITE_ROOTAREAPATH || 'Unified Action Tracker Test\\'
  )
    .trim()
    .replace(/[\\/]+$/, '\\');
  public static Project = import.meta.env.VITE_PROJECT || 'Unified Action Tracker Test';
  public static CompeteAreaPath = [
    UATConstants.Project + '\\WCB\\@ABS Triage',
    UATConstants.Project + '\\WCB\\Customer Success\\AI Business Solutions',
    UATConstants.Project + '\\WCB\\Microsoft Solutions\\AI Business Solutions',
  ];
  public static CreateActionDefaultValue_AreaPath =
    UATConstants.RootAreaPath + 'WCB\\@Azure Triage';
  public static AreaPath_In_TechROB = [
    UATConstants.Project + '\\WCB\\@Azure Triage',
    UATConstants.Project + '\\WCB\\@Security Triage',
    UATConstants.Project + '\\WCB\\Customer Success\\Infrastructure',
    UATConstants.Project + '\\WCB\\Customer Success\\Apps and Infra',
    UATConstants.Project + '\\WCB\\Customer Success\\Data and AI',
    UATConstants.Project + '\\WCB\\Customer Success\\Digital App Innovation',
    UATConstants.Project + '\\WCB\\Customer Success\\Security',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\Cloud',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\Entra',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\Purview',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\Sentinel',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\Scrum',
    UATConstants.Project + '\\WCB\\Customer Success\\Security\\XDR',
    UATConstants.Project + '\\WCB\\@Security Triage\\STU',
    UATConstants.Project + '\\WCB\\STU\\Security',
    UATConstants.Project + '\\WCB\\STU',
    UATConstants.Project + '\\MCAPS\\CE-S\\IAT',
    UATConstants.Project + '\\MCAPS\\GCS Partner Success',
    UATConstants.Project + '\\WCB\\@ABS Triage',
    UATConstants.Project + '\\WCB\\Customer Success\\AI Business Solutions',
    UATConstants.Project + '\\WCB\\Microsoft Solutions\\AI Business Solutions',
  ];
  public static AssignedTeam_In_TechROB_For_ManageAction = [
    'WCB Azure Triage',
    'WCB Security Triage',
    'Microsoft Federal',
    'WCB Customer Success Infrastructure',
    'WCB Customer Success Apps and Infra',
    'WCB Customer Success Data and AI',
    'WCB Customer Success Digital App Innovation',
    'WCB Customer Success Security',
    'WCB Customer Success Security Cloud',
    'WCB Customer Success Security Entra',
    'WCB Customer Success Security Purview',
    'WCB Customer Success Security Sentinel',
    'WCB Customer Success Security Scrum',
    'WCB Security Triage STU',
    'WCB STU Security',
    'WCB STU',
    'MCAPS CE-S IAT',
    'WCB STU',
    'GCS Partner Success',
    'WCB Customer Success Security XD',
    'MCAPS ABS Triage',
    'MCAPS Customer Success ABS Scrum',
    'MCAPS Microsoft Solutions ABS Scrum',
  ];
  public static IAT_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'MCAPS\\CE-S\\IAT';
  public static AMMAreaPath = UATConstants.RootAreaPath + 'AMM Offer Desk';
  public static MWHub_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'WCB\\@MW Triage';
  public static MCAPSHelpPM_DefaultValue_AreaPath =
    UATConstants.RootAreaPath + 'MCAPS\\MCAPS Help PM';
  public static APEX_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'MCAPS\\EO\\CS-BI\\APEX';
  public static MW_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'WCB\\@MW Triage';
  public static BA_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'WCB\\@BA Triage';
  public static ABS_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'WCB\\@ABS Triage';
  public static Security_DefaultValue_AreaPath =
    UATConstants.RootAreaPath + 'WCB\\@Security Triage';
  public static QBCValidationAreaPath = UATConstants.RootAreaPath + 'WCB\\STU\\QBC-VSU';
  public static GSMOValidationAreaPath = UATConstants.RootAreaPath + 'GSMO\\United States\\MO';
  public static SupportActionDefaultValue_AreaPath =
    UATConstants.RootAreaPath +
    'WCB\\Customer Success\\Consumption and Support Office\\Delivery and Consumption Excellence';
  public static FedType = 'Fed';
  public static WWSupportSalesCommunity_AreaPath =
    UATConstants.RootAreaPath + 'WCB\\Microsoft Solutions\\Support Services';
  public static GSMOAreaPath = 'GSMO United States MO';
  public static AOAI_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'MCAPS\\AOAI Capacity';
  public static MEA_DefaultValue_AreaPath = UATConstants.RootAreaPath + 'MEA Bedrock';
  // public static MEA_DefaultActionOwner = import.meta.env.VITE_MEADefaultActionOwner || 'meabedrocktriage-test@microsoft.com'; // Commented out for now as the default action owner is not part of MEA Bedrock team, it may required later, so keeping it here for reference.
  public static MEA_DefaultActionOwner =
    import.meta.env.VITE_MEADefaultActionOwner || 'meabedrocktriage-test@microsoft.onmicrosoft.com';
  public static MEA_DefaultActionOwnerName =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'UAT MEA Bedrock Triage - TEST'
      : 'UAT MEA Bedrock Triage';
  public static MSecR_DefaultActionOwner =
    import.meta.env.VITE_MSecRAssignedTo || 'msecrcustomerengagementteamtest@microsoft.com';
  public static MSecR_DefaultActionOwnerName =
    import.meta.env.VITE_MSecRAssignedToName || 'MSecR Customer Engagement Team Test';

  public static NonAICapacityAreaPath = UATConstants.RootAreaPath + 'MCAPS\\Capacity (Non-AI)';
  public static MicrosoftFederalAreaPath = 'Microsoft Federal';
  public static MicrosoftFederalRequestDetails = 'MS Federal Request Details';
  public static MicrosoftFederalRootAreaPath = UATConstants.RootAreaPath + 'Microsoft Federal';
  public static TenantIdGuidRegex: RegExp =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  public static TenantIdMandatoryWorkloadPrefixes: string[] = ['ABS', 'MW', 'Power Platform'];
  public static TechROBAreaPathTest = UATConstants.RootAreaPath + 'WCB\\@Azure Triage';
  public static McapsCapsl = 'MCAPS CAPSL';
  public static McapsGsi = 'MCAPS GSI ACO PIR';
  public static McapsGcsPartnerSuccess = 'MCAPS GCS Partner Success';
  public static McapsCapslAreaPath = UATConstants.RootAreaPath + 'MCAPS\\CAPSL';
  public static USMaaSAreaPath = UATConstants.RootAreaPath + 'MCAPS\\MaaS';
  public static RedButtonSMC = 'MCAPS SMC Red Button';
  public static RedButtonSMCAssignedTeam = 'Red Button SME&C';
  public static McapsMacc = 'MCAPSHelp MACC Exception';
  public static RedButtonTSI = 'MCAPS Red Button TSI';
  public static RedButtonTSIAssignedTeam = 'Red Button Elevate Non-profit';
  public static MCAPSLeaderRoBAreaPath = 'MCAPS Leader RoB';
  public static ROBDisabledFiledsDefaultValue = 'NA';
  public static MCAPSLeaderRoBGlobalRegion = 'Global';
  public static MCAPSLeaderRoBGlobalRegionTooltip =
    'Global cannot be selected alongside other Regions';
  public static SGS = 'Strategic Growth Shaping';
  public static PS = 'Regional Skilling';
  public static RSD = 'Regional Skilling Desk';
  public static AzureISV = 'Azure Sponsorship for ISV';
  public static SGSSubState = 'N/A';
  public static SGSState = 'Not Started';
  public static SGSCustomerBriefingStatus = 'Not Started';
  public static SGSAccountTeamBriefingStatus = 'Not Started';
  public static SGSAreaPath = UATConstants.RootAreaPath + 'Strategic Growth Shaping';
  public static AHPMAreaPath = 'MCAPS Help Problem Management';
  public static RedButtonSMCAreaPath = UATConstants.RootAreaPath + 'MCAPS\\SMC Red Button';
  public static McapsMaccAreaPath = UATConstants.RootAreaPath + 'MCAPSHelp\\MACC Exception';
  public static DefenderForCloudAreaPath = UATConstants.RootAreaPath + 'MCAPS\\Defender For Cloud';
  public static DefenderForCloudAreaPathName = 'Defender for Cloud Exception Team';
  public static RedButtonTSIAreaPath = UATConstants.RootAreaPath + 'MCAPS\\Red Button TSI';
  public static MCAPSPMYT = 'MCAPS Partner Marketing Yield Tracker';
  public static PMYTAreaPath = UATConstants.RootAreaPath + 'MCAPS\\Partner Marketing Yield Tracker';
  public static MSecRAreaPath = UATConstants.RootAreaPath + 'Microsoft Security Research';
  public static getAreaPathWithoutRoot(areaPath: string): string {
    const rootAreaPath = UATConstants.RootAreaPath || '';
    const normalizedAreaPath = String(areaPath || '');
    const areaPathWithoutRoot = normalizedAreaPath.startsWith(rootAreaPath)
      ? normalizedAreaPath.slice(rootAreaPath.length)
      : normalizedAreaPath;
    const assignedTeam = areaPathWithoutRoot.replace(/\\/g, ' ').replace(/@/g, '').trim();

    if (
      assignedTeam === 'MCAPS Red Button TSI' ||
      assignedTeam === 'MCAPS Red Button Elevate Non-profit'
    ) {
      return UATConstants.RedButtonTSIAssignedTeam;
    }

    if (
      assignedTeam === 'MCAPS SMC Red Button' ||
      assignedTeam === 'MCAPS SME&C Red Button' ||
      assignedTeam === 'MCAPS Red Button SME&C'
    ) {
      return UATConstants.RedButtonSMCAssignedTeam;
    }

    return assignedTeam;
  }
  public static Redbuttontab = '&tab=Red Button';
  public static RedButtonSMCAssignedTo =
    import.meta.env.VITE_RedButtonSMCAssignedTo || 'uat-redbuttonpre@microsoft.com';
  public static RedButtonTSIAssignedTo =
    import.meta.env.VITE_RedButtonTSIAssignedTo || 'uat-redbuttonmgmt@microsoft.com';
  public static RedButtonSMCAssignedToDisplayName =
    import.meta.env.VITE_RedButtonSMCDisplayName ||
    'Red Button Pre - Unified Action Tracker Test Account';
  public static RedButtonTSIAssignedToDisplayName =
    import.meta.env.VITE_RedButtonTSIDisplayName ||
    'Red Button Elevate Non-profit - Unified Action Tracker Test';
  public static McapsMaccPurpleSupportTo =
    import.meta.env.VITE_McapsMaccPurpleHelp || 'MACCPurpleSupport@microsoft.com';
  public static McapsMaccPurpleSupportName =
    import.meta.env.VITE_McapsPurpleHelpDisplayName || 'Macc Purple Exception Support';
  public static DefenderForCloudSupportTo =
    import.meta.env.VITE_DefenderForCloudHelp ||
    'DefenderForCloudSupportTest@microsoft.onmicrosoft.com';
  public static DefenderForCloudSupportName =
    import.meta.env.VITE_DefenderForCloudHelpDisplayName ||
    'Defender for Cloud Exception Support Test';
  public static AzureSponsorshipISVName =
    import.meta?.env?.VITE_AzureSponsorshipforISV || 'Azure Sponsorship for ISV';

  public static McapsGPSInvestmentSupportTo =
    UATConstants.Project === 'Unified Action Tracker'
      ? 'askpir@microsoft.com'
      : 'gpsinvestments-test@microsoft.com';
  public static McapsGPSInvestmentSupportName =
    UATConstants.Project === 'Unified Action Tracker'
      ? 'Partner Investment Ask'
      : 'GPS Investments Test​';

  public static DownloadAttribute = '&download=1';
  public static SpendPlanTemplatePath =
    import.meta.env.VITE_SpendPlanTemplate + UATConstants.DownloadAttribute ||
    'https://microsoft.sharepoint.com/teams/UnifiedActionTracker/_layouts/15/download.aspx?UniqueId=a48c21f7%2Dd7c0%2D4827%2Dad86%2De64ad463e2d9' +
      UATConstants.DownloadAttribute;
  public static InvestmentRevenueTemplatePath =
    import.meta.env.VITE_InvestmentRevenueTemplate + UATConstants.DownloadAttribute ||
    'https://microsoft.sharepoint.com/teams/UnifiedActionTracker/_layouts/15/download.aspx?UniqueId=48095b9a%2D91d2%2D4b81%2Dad68%2Dcc6c3e128172' +
      UATConstants.DownloadAttribute;
  public static ROITemplatePath =
    import.meta.env.VITE_ROITemplate + UATConstants.DownloadAttribute ||
    'https://microsoft.sharepoint.com/teams/UnifiedActionTracker/_layouts/15/download.aspx?UniqueId=4bdfafd9%2D097a%2D4b85%2D88f7%2De2ddd314e0d5' +
      UATConstants.DownloadAttribute;
  public static NonAICapacityTemplatePath =
    import.meta.env.VITE_NonAICapacityTemplate || 'https://aka.ms/A360CapacityForecastTemplate';
  public static Ecifbuttontab = '&tab=ECIF';
  public static ECIF = 'MCAPS ECIF Pre-Approval';
  public static ECIFAreaPath = UATConstants.RootAreaPath + 'MCAPS\\ECIF Pre-Approval';
  public static ECIFAssignedToDisplayName =
    UATConstants.Project === 'Unified Action Tracker DEV'
      ? 'ECIF Test'
      : 'ECIF Pre-approval Generic SPOC';
  public static ECIFAssignedTo =
    UATConstants.Project === 'Unified Action Tracker DEV'
      ? 'ecif_test@microsoft.com'
      : 'ecifgenericmb@microsoft.com';
  public static ECIFWorkscopeCreateUrl =
    import.meta.env.VITE_ECIFWorkscopeCreateUrl ||
    'https://ecif-webapp-dev.azurewebsites.net/ecifWorkscope';
  public static MaaS = 'MCAPS MaaS';
  public static AmericasOCTOAssignedTeam = 'MCAPS HQ Americas OCTO';
  public static MaaSAreaPath = UATConstants.RootAreaPath + 'MCAPS\\MaaS';
  public static RegionalSkillingAreaPath =
    UATConstants.RootAreaPath + 'MCAPS\\Regional Skilling Desk';
  public static RegionalSkillingEmailMail =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? import.meta.env.VITE_RegionalSkillingEmailMail || 'regionalskilling-test'
      : import.meta.env.VITE_RegionalSkillingEmailMail || 'regionalskilling';
  public static MaaSRefId = 'USMaaS';
  public static GPSRefId = 'PartnerInvestments';
  public static PMYT = 'MCAPS Partner Marketing Yield Tracker';
  public static PMYTRefId = 'PMYT';
  public static UnmanagedRefId = 'umcem';
  public static MaaSAssignedToMail =
    import.meta.env.VITE_McapsMaaSAssignedToMail || 'usmaas@microsoft.com';
  public static MaaSAssignedToName =
    import.meta.env.VITE_McapsMaasAssignedToName || 'Marketing as a Service (MaaSAmericas)';
  public static McapsCoSell = 'MCAPS CoSell';
  public static McapsCoSellAreaPath = UATConstants.RootAreaPath + 'MCAPS\\CoSell';
  public static MSAmericasSEandOAreaPath =
    UATConstants.RootAreaPath + 'MCAPS\\MS Americas SE and O';
  public static MSAmericasSEandO = 'MCAPS MS Americas SE and O';
  public static MSAmericasSEandOPrivateAreaPath =
    UATConstants.RootAreaPath + 'MCAPS\\MS Americas SE and O (Private)';
  public static MSAmericasSEandOPrivate = 'MCAPS MS Americas SE and O (Private)';
  public static CoSellAssignedTo =
    UATConstants.Project === 'Unified Action Tracker'
      ? 'coselloperations@microsoft.com'
      : 'cosell-test1@microsoft.com';
  public static CoSellAssignedToDisplayName =
    UATConstants.Project === 'Unified Action Tracker'
      ? 'Co-Sell Operations Service'
      : 'cosell-test1';
  public static GPSInvestments = 'MCAPS Partner Investments';
  public static RoBRequests = 'MCAPS Leader RoB Requests';
  public static GPSInvestmentsAreaPath = UATConstants.RootAreaPath + 'MCAPS\\Partner Investments';
  public static GSIAreaPath = UATConstants.RootAreaPath + 'MCAPS GSI ACO PIR';

  public static GPSInvBusinessTemplatePath =
    'https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F05%2E%20PIR%2FPIR%20Business%20Case%20Templates&p=true&ga=1&OR=Teams%2DHL&CT=1706828814014&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiIyNy8yNDAxMDQxNzUwMyIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D';
  // import.meta.env.VITE_BusinessTemplate + UATConstants.DownloadAttribute ||
  // 'https://microsoft.sharepoint.com/teams/UnifiedActionTracker/_layouts/15/download.aspx?UniqueId=4bdfafd9%2D097a%2D4b85%2D88f7%2De2ddd314e0d5' +
  //   UATConstants.DownloadAttribute;
  public static GPSInvFinancialTemplatePath =
    'https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F05%2E%20PIR%2FPIR%20Financial%20Templates&p=true&ga=1&OR=Teams%2DHL&CT=1706828852352&clickparams=eyJBcHBOYW1lIjoiVGVhbXMtRGVza3RvcCIsIkFwcFZlcnNpb24iOiIyNy8yNDAxMDQxNzUwMyIsIkhhc0ZlZGVyYXRlZFVzZXIiOmZhbHNlfQ%3D%3D';
  // import.meta.env.VITE_FinancialTemplate + UATConstants.DownloadAttribute ||
  // 'https://microsoft.sharepoint.com/teams/UnifiedActionTracker/_layouts/15/download.aspx?UniqueId=4bdfafd9%2D097a%2D4b85%2D88f7%2De2ddd314e0d5' +
  //   UATConstants.DownloadAttribute;

  public static GPSInvCommercialTemplatePath =
    'https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F16%2E%20Commercial%20Governance%2FCommercial%20Governance%20Framework%2Epdf&parent=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F16%2E%20Commercial%20Governance&p=true&ct=1715964020898&or=Teams%2DHL&ga=1https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F16%2E%20Commercial%20Governance%2FCommercial%20Governance%20Framework%2Epdf&parent=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F16%2E%20Commercial%20Governance&p=true&ct=1715964020898&or=Teams%2DHL&ga=1';
  // import.meta.env.VITE_CommercialTemplate + UATConstants.DownloadAttribute ||
  // 'https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/_layouts/15/download.aspx?SourceUrl=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F14%2E%20DPS%20Governance%2FDPS%20Governance%20Framework%2Epdf' +
  //   UATConstants.DownloadAttribute;
  public static GPSInvDPSTemplatePath =
    'https://microsoft.sharepoint.com/teams/PartnerInvestmentFramework/PartnerInvestmentcouncil/Shared%20Documents/Forms/AllItems.aspx?id=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F14%2E%20DPS%20Governance%2FDPS%20Governance%20Framework%2Epdf&parent=%2Fteams%2FPartnerInvestmentFramework%2FPartnerInvestmentcouncil%2FShared%20Documents%2F14%2E%20DPS%20Governance&p=true&ga=1';
  public static UnitesStatesAreaName = 'United States';
  public static AHPM_Default_Substate = 'Under Review';
  public static BAHelp = 'bahelp@microsoft.com';
  public static BAHelpName = 'AI Business Process Blockers Help';
  public static MWHelp = 'mwhelp@microsoft.com';
  public static MWHelpName = 'AI Workforce Blockers Help';
  public static ABSHelp =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'absblockershelptest@microsoft.onmicrosoft.com'
      : 'absblockershelp@microsoft.onmicrosoft.com';
  public static ABSHelpName =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'AI Business Solutions Blockers Help Test'
      : 'AI Business Solutions Blockers Help';
  public static ABSAssignedTeam = 'MCAPS ABS Triage';
  public static AIHelp = 'aihelp@microsoft.com';
  public static SecurityHelp = 'sechelp@microsoft.com';
  public static SecurityHelpName = 'Security Blockers Help';
  public static SupportDeliveryHelp = 'SDeliver@microsoft.com';
  public static WWSupportSalesCommunity = 'wwsupsc@microsoft.com';
  public static MSFederalUATTriage = 'MSFederalUATTriage@microsoft.com';
  public static MSFederalUATTriageName = 'MS Federal UAT Triage';
  public static McapsCapslmail = 'csat@microsoft.com';
  public static McapsCapslName = 'CPE Relationship Measurement';
  public static GSImail = 'ACOnotifications@microsoft.com';
  public static GSIName = 'Azure Credit Offer Program';
  public static McapsCapslGcsPartnerEmail = 'partnersuccesstriage@microsoft.com';
  public static McapsCapslGcsPartnerName = 'PartnerSuccessROB';
  public static SupportDeliveryHelpName = 'Support Delivery Review Team';
  public static WWSupportSalesCommunityName = 'WW Support Sales Community';
  public static AOAIHelp = 'aoaitriage@microsoft.com';
  public static AOAIHelpName = 'Azure AI Capacity Triage';
  public static MCAPSAOAICapacity = 'MCAPS AOAI Capacity';
  public static MEAHelp = 'mea-uat@microsoft.com';
  public static MEAHelpName = 'MEA Triage';
  public static MCAPSMEA = 'MEA Bedrock';
  public static MCAPSNonAICapacity = 'MCAPS Capacity (Non-AI)';
  public static AzureMeetingType = MeetingTypes.AzureFieldScrum;
  public static BAMeetingType = MeetingTypes.D365FieldScrum;
  public static MWMeetingType = MeetingTypes.MWFieldScrum;
  public static ABSMeetingType = MeetingTypes.ABSFieldScrum;
  public static SecurityMeetingType = MeetingTypes.SecurityFieldScrum;
  public static CreateActionDefaultValue_MeetingType = 'None';
  public static SupportActionDefaultValue_MeetingType = MeetingTypes.SupportDeliveryReview;
  public static WWSupportSalesCommunityName_MeetingType = MeetingTypes.SupportDealAcceleration;
  public static NONE_MeetingType = MeetingTypes.NONE;
  public static ShowCovidRecordsLabel = 'Show COVID-19 Actions';
  public static ActionOwner: string = '';
  public static DefaultDateFormat: string = 'MM/DD/YYYY hh:mm A';
  public static ExcelDateFormat: string = 'YYYY-MM-DD hh:mm:ss';
  public static SimpleDateFormat: string = 'MM/DD/YYYY';
  public static InsightsInstrumentationKey: string =
    import.meta.env.VITE_INSTRUMENTATIONKEY || '9fcf9918-dd23-45bd-8d26-a7a1695ebe76';
  public static DefaultAssignedTeam =
    import.meta.env.VITE_DEFAULTTEAM || 'Unified Action Tracker Test';
  public static Organization = import.meta.env.VITE_ORG || 'unifiedactiontrackertest';
  public static ProjectName = 'Unified Action Tracker';
  public static IsProduction = UATConstants.Project === 'Unified Action Tracker';
  public static UnmanagedCapacityActionOwner = UATConstants.IsProduction
    ? {
        email: 'UnmanagedCapacity@microsoft.com',
        displayName: 'Unmanaged Capacity Queue',
      }
    : {
        email: 'UnmanagedCapacityTest@microsoft.com',
        displayName: 'Unmanaged Capacity Queue Test',
      };
  public static ADOBaseUrl = 'https://dev.azure.com';
  public static SelectAllText = 'Select All';
  public static AllowedStates = ['Done', 'In Progress', 'Rejected'];
  public static AllowedRegionalStates = ['Done', 'In Progress', 'Cancelled', 'New', 'On Hold'];
  public static ACREmail =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'acrtest@microsoft.com'
      : 'acrhelp@microsoft.com';
  public static ACRName =
    UATConstants.Project === 'Unified Action Tracker Test' ? 'ACR HELP Test' : 'ACR HELP';
  public static CustomFieldArgument =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'Custom.c1f2fddd-c94e-4ad7-a789-972c51dc3c7c'
      : 'Custom.36520105-2048-4366-8c5e-7ef1c19cb0e3';
  public static OL360IntegrationURL =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'https://ol360-int-webui.azurewebsites.net/issue?id='
      : 'https://ol360-prod-webui.azurewebsites.net/issue?id=';
  public static IAT_Default_MeetingSubType: string = 'IARoom';
  public static IAT_Default_External_SystemName: string = 'Innovation Acceleration Report ID';
  public static PRODEnv = 'PROD';

  /* Use Default ViewID (Assigned To Me) when User has no specific Default View set
      Int: 85ca4119-5de8-46d2-a977-face24cd2264
      Test: ff7a2e1e-9944-4c63-a7c2-197f18071054
      Prod: 91c525c8-270a-4419-a7b8-207f686b2b34
  */
  public static DefaultViewID =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? import.meta.env.VITE_DEFAULT_VIEW_ID || 'af24b5a1-d558-48e4-a9e8-bbec8453b61a'
      : import.meta.env.VITE_DEFAULT_VIEW_ID || '91c525c8-270a-4419-a7b8-207f686b2b34';

  public static AllActionsViewID =
    import.meta.env.VITE_ALLACTIONS_VIEW_ID || 'c3b15069-6ae3-4e57-a907-7924737e2c06';
  public static EmptyGuid = '00000000-0000-0000-0000-000000000000';

  public static IsCorpAreaPath = UATConstants.Project + '\\WCB\\@Azure Triage';
  public static IsNotCorpAreaPath = UATConstants.Project + '\\GSMO\\';
  public static SharedQueriesFlightName = 'SharedQueries';
  public static MyViewsGuid = '0c11f207-7240-4e3c-9d72-4cbe0edf289c';
  public static QueriesMigrationMaxLimit: string =
    import.meta.env.VITE_MIGRATEQUERIESMAXLIMIT || '10';
  public static ErrorMsgMaxLimit =
    'Sorry you can migrate max ' + UATConstants.QueriesMigrationMaxLimit + ' queries at a time';
  public static AssignToCorp = UATConstants.IsProduction
    ? 'Custom.AssignToCorp'
    : 'Custom.AssigntoCorp';
  public static IsAutoClosed: string = 'Custom.IsAutoClosed';
  public static AssignToCorpProd: string = 'Custom.AssignToCorp';
  public static deleteViewTitle: string = 'Delete View';
  public static deleteViewSubText: string = 'will be deleted permanently. Please confirm.';
  public static deleteFolderTitle: string = 'Delete Folder';

  public static ContinueButton: string = 'Continue';
  public static CancelButton: string = 'Cancel';
  public static selectAllFilterTitle: string = 'Filter by';
  public static selectAllFilterText: string =
    "Please select 'All' filter to view a combined list of fields. Changing applied 'Filter by' value will reset the selection of fields to blank.";
  public static deleteFolderSubText: string =
    'and all items (if any) under this folder will be deleted permanently. Please confirm.';

  public static State: string = 'State';
  public static OfferingName: string = 'Offering Name';
  public static ScoreByOffering: string = 'Score By Offering';
  public static SubState: string = 'Sub State';
  public static ServiceName: string = 'Service Name';
  public static DescriptionText: string = 'Description';
  public static PlannedSemester: string = 'Planned Semester';
  public static Targetdate: string = 'Target Date';
  public static ProjectedPublicPreviewDate: string = 'Projected Public Preview Date';

  public static WorkItemType = UATConstants.IsProduction ? 'Actions' : 'Action';

  // public static WebsiteRegex = '@((http|https):\/\/)?(www\.)?[a-zA-Z0-9:%._\+~#?&//=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9:%._\+~#?&//=]*)';
  //public static WebsiteRegex = '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?';
  public static DraftGPSLink =
    /^(https?:\/\/)?([A-Za-z0-9\-]+\.){1,}([A-Za-z]{2,})(\/[^\/]*)*(\?.*)?$/;
  public static WebsiteRegexDocumentLink =
    /^(https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/;
  public static WebsiteRegex =
    ///^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*(\.[a-zA-Z]{2,})([a-zA-Z0-9_\-./?%&=]*)?$/;
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*(\.[a-zA-Z]{2,})([a-zA-Z0-9_\-./?%&=]*)?$/;
  // public static WebsiteRegex =/(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?\/[a-zA-Z0-9]{2,}|((https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z]{2,}(\.[a-zA-Z]{2,})(\.[a-zA-Z]{2,})?)|(https:\/\/www\.|http:\/\/www\.|https:\/\/|http:\/\/)?[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}\.[a-zA-Z0-9]{2,}(\.[a-zA-Z0-9]{2,})?/g;
  public static GPSWebsiteRegex =
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*(\.[a-zA-Z]{2,})([a-zA-Z0-9_\-./?%&=]*)?$/;
  public static MSXIDRegex = '^$|([0-9]{1}-[A-Za-z0-9]{9,12}$)';
  public static EmailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
  public static validEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]{2,}){1,}$/;
  public static EmailRegexOrAsterisk = /^([\w-\.]+@([\w-]+\.)+[\w-]{2,4}|\*+)$/;
  public static validFiveEmailRegex =
    /^(([a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]{2,}){1,};\s*)?){4}[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]{2,}){1,}$/;
  public static NewEmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z]+\.[a-zA-Z]{2,}$/;
  public static CharacterRegex = /^[a-zA-Z ]*$/;
  public static NumericRegex = '^[0-9]+$';
  public static DecimalRegex = /^(\d+)?(?:\.\d+)?$/;
  public static PhoneNumberRegex = /^\+?[\d-+().]{1,30}$/;
  public static TPIDRegex = /^\d+$/;
  public static SupportRegex = '\\b(support)\\b';
  public static atMicrosoft = '@microsoft.com';
  public static atParam = '@Param';
  public static groupOperatorKeywordADOQuery = '<<group:';
  public static mailOperatorKeywordADOQuery = '<<mail:';
  public static recordPerScroll: number = 50;
  public static recordPerPage: number = 50;
  public static FeedbackToggleResponse =
    'Thank you for your submission. Your action been elevated to the Technical Feedback process for consideration by the appropriate engineering team. The Technical Feedback links can be used to navigate to the Feedback and/or Feature items.';
  public static SubStateTechnicalFeedbackResponse =
    'This action remains in the Technical Feedback process for either consideration by the appropriate engineering team or tracking progress within the committed timeframe. Further updates for the Feedback and/or Feature items can be found using the Technical Feedback links.';
  public static SubStateTechnicalFeedback = 'In Technical Feedback Review';
  public static SubStateTechnicalFeedbackResponseNew =
    'Please review the Closure Statement for the workaround summary. This action remains in the Technical Feedback process for consideration by the appropriate engineering team. Further updates for the Feedback and/or Feature items can be found using the Technical Feedback links';
  public static SubStateTechnicalFeedbackTitle =
    'We have concluded workaround efforts for this action.';
  public static btnLinkTechnicalFeedback = 'See Technical Feedback';
  public static btnLinkClosureStatement = 'See Closure Statement';
  public static helpRequested = 'has requested your help with this action for';
  public static descriptionReference = 'Please refer to Description above for more information.';
  public static DashboardActionCount = 50;
  public static MaxADOActionsCountFromAPI: number = 19995;
  public static actionCountToShow = '20000+';
  public static MaxActionsEmail = 500;
  public static RequestTimeout: number = 230000;
  public static NotificationTimeout: number = 10000;
  public static SaveSuccessOrFailureTimeout: number = 5000;
  public static NotificationBannerMsg: string = "Don't show again";
  public static MaxSubFolderLevels = 9;
  public static MaxAllowedThemes = 7;
  public static resolveDelay = 300;
  public static debounceTime: number = 500;
  public static defaultUptoDecimalPlaces: number = 3;
  public static MaxPreferredRegionOptionCount = 5;
  public static CommentsToConsiderCount = 10; //Get users mentioned in latest 10 comments for current action
  public static eventSource = 'Paste'; //Constants for PickerPlugin
  public static customerhub = 'Customer Hub';
  public static requestTypeTooltip =
    "Selecting a Request type will display only the fields associated with that Request type in 'Select Field' field button";

  public static DashboardSharedViewsHeaderKey = 'SharedViewsHeader';
  public static DashboardNoSharedViewsFoundHeaderKey = 'NoSharedViewsFound';
  public static DashboardNoSharedViewsFoundOptionKey = 'NoSharedViewsFoundOption';
  public static AOAITooltip =
    'Azure AI Capacity requests submissions are disabled from Unified Action Tracker to maintain compliance. Please initiate requests from MSX with a valid Milestone ID that meet the conditions for AI Capacity requests. For detailed steps, see the';

  public static stateAreaMapping = {
    'FY27 Azure Sponsorship for ISV': [
      'GPS Global',
      'GISV At-Scale/GPS Global',
      'GPS Global/Breadth',
      'GPS Global/GISV VC/PC',
      'Americas',
      'ANZ',
      'ASEAN',
      'Canada',
      'China',
      'France',
      'Germany & Austria',
      'Greater China',
      'Hong Kong',
      'India',
      'Japan',
      'Korea',
      'Latam',
      'MEA',
      'Netherlands',
      'North Europe',
      'South Europe',
      'Switzerland',
      'Taiwan',
      'UK & Ireland',
      'United States',
    ],
    'FY26 Azure Sponsorship for ISV': ['United States', 'GPS Global'],
    'FY25 Azure Sponsorship for ISV': ['United States', 'GPS Global'],
    'FY25 GISV SA Azure Sponsorship': ['GPS Global'],
    'FY26 GISV SA Azure Sponsorship': ['GPS Global'],
    'FY25 GISV VC/PC Azure Sponsorship': ['GPS Global/GISV VC/PC'],
    'FY26 GISV VC/PC Azure Sponsorship': ['GPS Global/GISV VC/PC'],
    'FY25 GISV Breadth Azure Sponsorship': ['GPS Global/Breadth'],
    'FY26 GISV Breadth Azure Sponsorship': ['GPS Global/Breadth'],
    'GISV At-Scale': ['GISV At-Scale/GPS Global'],
    'FY26 GISV At-Scale': ['GISV At-Scale/GPS Global'],
    'FY26 A14 Area ISV Azure Sponsorship': [
      'Americas',
      'ANZ',
      'ASEAN',
      'CEMA',
      'China',
      'France',
      'Germany',
      'Hong Kong',
      'India',
      'Japan',
      'Korea',
      'Netherlands',
      'Switzerland',
      'Taiwan',
      'UK',
      'Western Europe',
      'Canada',
      'Greater China',
      'Latam',
      'US',
      'MEA',
      'Germany & Austria',
      'UK & Ireland',
      'North Europe',
      'South Europe',
    ],
    'FY25 A14 Area ISV Azure Sponsorship': [
      'Americas',
      'ANZ',
      'ASEAN',
      'CEMA',
      'China',
      'France',
      'Germany',
      'Hong Kong',
      'India',
      'Japan',
      'Korea',
      'Netherlands',
      'Switzerland',
      'Taiwan',
      'UK',
      'Western Europe',
      'Canada',
      'Greater China',
      'Latam',
      'US',
      'MEA',
      'Germany & Austria',
      'UK & Ireland',
      'North Europe',
      'South Europe',
    ],
  };

  public static Dependency = 'Dependency';
  public static Issue = 'Issue';
  public static Risk = 'Risk';
  public static DependencyLogo = 'dependencylogosvg';
  public static IssueLogo = 'issuelogosvg';
  public static RiskLogo = 'risklogosvg';
  public static DependencySuccessMessage = 'Dependency submitted successfully.';
  public static IssueSuccessMessage = 'Issue submitted successfully.';
  public static RiskSuccessMessage = 'Risk submitted successfully.';
  public static IssueManageMessage = 'You can manage issue by clicking';
  public static RiskManageMessage = 'You can manage risk by clicking';
  public static DependencyManageMessage = 'You can manage dependency by clicking';
  public static RaidTypeFailure = 'RaidTypeFailure';
  public static RaidFailureMessage = 'Item was not added successfully';
  public static Failure = 'Failure';

  // Action Validation Messages
  public static RequiredFieldsValidationMessage =
    'Save completed with errors. Please review the Launch Information tab to resolve outstanding issues.';
  public static GPSADOWorkItemEligiblePIRDecisions = [
    'Vetted',
    'Approved to Offer (Launch)',
    'Approved to Negotiate',
  ];
  public static GPSPIRDecisionValidationMessage =
    'ADO Work Item will not be created until the Decision field is set to "Vetted", "Approved to Offer (Launch)", or "Approved to Negotiate".';

  // Inline MessageBar Error IDs for filtering
  public static InlineMessagebarErrorIds = {
    detailsTab: [
      'active-controls-stage-transition-error',
      'approvals-stage-transition-error',
      'launch-checklist-stage-transition-error',
    ],
    relatedTab: ['related-item-error'],
  };

  public static MCAPSAreaPath = 'MCAPS MCAPS Help PM';
  public static MCAPSAreaPathReplace = 'MCAPS Help Problem Management';
  public static RedButtoncontributor1Americas =
    import.meta.env.VITE_RedButtonContributorAmericas || 'UATTEST_CSAMAmericas@microsoft.com';
  public static RedButtoncontributor1Asia =
    import.meta.env.VITE_RedButtonContributorAsia || 'UATTEST_CSAMAsia@microsoft.com';
  public static RedButtoncontributor1EMEA =
    import.meta.env.VITE_RedButtonContributorEMEA || 'UATTEST_CSAMEMEA@microsoft.com';
  public static VITE_AONRequestLink =
    import.meta.env.VITE_AONRequestLink ||
    'https://app-amm-webportal-uat-eastus.azurewebsites.net/Opportunity?caseno=';
  public static ECIFAreaName = 'Japan';
  public static ECIFProgramName = 'Generic';
  public static Area_UK = 'UK';
  public static Area_UK_IRELAND = 'UK & Ireland';
  public static filterbytooltip =
    'Select a request type to display the columns associated with it.';
  public static selectedColumnsToolTip =
    "Values selected under 'Column options' get auto populated below. Mandatory Columns are displayed by default. ex: Title, Action Owner.";
  public static DayPickerStrings: IDatePickerStrings = {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],

    shortMonths: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],

    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],

    shortDays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],

    goToToday: 'Go to today',
    prevMonthAriaLabel: 'Go to previous month',
    nextMonthAriaLabel: 'Go to next month',
    prevYearAriaLabel: 'Go to previous year',
    nextYearAriaLabel: 'Go to next year',
    closeButtonAriaLabel: 'Close date picker',

    isRequiredErrorMessage: 'Field is required.',

    invalidInputErrorMessage: 'Invalid date format.',
  };

  public static DatepickerDefaults = {
    firstDayOfWeek: DayOfWeek.Sunday,
    today: new Date(Date.now()),
  };

  public static ECIFRules = {
    Segment: ['Netherlands'],
  };

  public static DeprecatedAreaPaths = {
    MCAPSCAPSL: 'MCAPS\\CAPSL',
    CAPSLTenantID: MANAGE_ACTION_FIELDS.CAPSLTenantId,
  };

  //Not a comprehensive list!
  public static ArtemisFieldPaths = {
    ID: 'fields/id',
    Title: 'fields/title',
    AssignedTeam: 'fields/areapath',
    Requestors: 'fields/requestorsinfo/uniquename',
    ActionOwner: 'fields/assignedto/uniquename',
    State: 'fields/state',
    Account: 'fields/account',
    SubState: 'fields/substate',
    RequestedDate: 'fields/requesteddate',
    Contributors: 'fields/contributorsinfo/uniquename',
    Contributor1: 'fields/contributorsinfo/uniquename',
    Contributor2: 'fields/contributorsinfo/uniquename',
    AccountableExec: 'fields/accountableexec/uniquename',
    MeetingType: 'fields/meetingtype',
    MeetingSubType: 'fields/meetingsubtype',
    MeetingTypeMCAPS: 'fields/meetingsubtypelist',
    MeetingSubTypeMCAPS: 'fields/meetingtypelist',
    Regions: 'fields/regions',
    SubRegions: 'fields/subregions',
    Areas: 'fields/areas',
    ActionPriorityField: 'fields/actionpriorityfield',
    DueDate: 'fields/duedate',
    ClosedDate: 'fields/closeddate',
    ChangedDate: 'fields/changeddate',
    CreatedBy: 'fields/createdby/uniquename',
    CreatedDate: 'fields/createddate',
    ProblemDetails: 'fields/description',
    Source: 'fields/raidentitysource',
    TenantID: 'tenantid',
    UserChangedBy: 'fields/userchangedby/uniquename',
    UserChangedDate: 'fields/userchangeddate',
    GPSPartnerOneID: 'fields/gps_partneroneid',
    GPSAskType: 'fields/gps_asktype',
    GPSRequestType: 'fields/gps_requesttype',
    ClosureStatement: 'fields/closurestatement',
    StatusUpdate: 'fields/statusupdate',
    CAPSLAMName: 'fields/capsl_amname/uniquename',
    CAPSLSuppressChildAccount: 'fields/capsl_suppresschildaccount',
    CAPSLAccountName: 'fields/capsl_accountname',
    CAPSLFirstName: 'fields/capsl_firstname',
    CAPSLLastName: 'fields/capsl_lastname',
    CAPSLTitle: 'fields/capsl_title',
    CAPSLBusinessJustification: 'fields/capsl_businessjustification',
    CAPSLContactAlias: 'fields/capsl_contactalias',
    PMYTSourceOfInvestment: 'fields/pmyt_sourceofinvestment',
    PMYTCoInvestmentAmount: 'fields/pmyt_coinvestmentamount',
    PMYTInvestmentAmount: 'fields/pmyt_investmentamount',
    PMYTSolutionArea: 'fields/pmyt_solutionarea',
    PMYTSolutionPlays: 'fields/pmyt_solutionplays',
    PMYTIndustries: 'fields/pmyt_industries',
    PMYTAreas: 'fields/pmyt_areas',
    SolutionAreaUAT: 'fields/solutionareauat',
    PMYTEstimatedStartDate: 'fields/pmyt_estimatedstartdate',
    PMYTEstimatedPipelineRevenue: 'fields/pmyt_estimatedpipelinerevenue',
    PMYTCustomerSegment: 'fields/pmyt_customersegment',
    PMYTPartnerType: 'fields/pmyt_partnertype',
    PMYTPartnerName: 'fields/pmyt_partnername',
    PMYTPartnerOneID: 'fields/pmyt_partneroneid',
    PMYTEstimatedCosellReferrals: 'fields/pmyt_estimatedcosellreferrals',
    PMYTRole: 'fields/pmyt_role',
    BusinessDivision: 'fields/businessdivision',
    ProblemType: 'fields/problemtype',
    SolutionAreaMSX: 'fields/solutionarea',
    ActionCategory: 'fields/actioncategoryfield',
    FY: 'fields/financialyear',
    Quarter: 'fields/quarter',
    Area: 'fields/area',
    EOU: 'fields/eou',
    AdditionalArea: 'fields/additionalarea',
    AdditionalEOU: 'fields/additionaleou',
    CustomerScenarioAndDesiredOutcome: 'fields/customerscenarioanddesiredoutcome',
    CustomerImpact: 'fields/CustomerImpact',
    LTExecDirect: 'fields/ltexecdirect/uniquename',
    ManagingOwner: 'fields/managingowner/uniquename',
    AgreementID: 'fields/agreementid',
    AgreementName: 'fields/agreementname',
    PackageID: 'fields/packageid',
    PackageName: 'fields/packagename',
    DeliveryBlockers: 'fields/deliveryblockers',
    Mailbox: 'fields/mailbox',
    Sender: 'fields/sender',
    ConversationID: 'fields/conversationid',
    Priority: 'fields/priority',
    BusinessImpact: 'fields/businessimpact',
    WorkaroundDetails: 'fields/workarounddetails',
    RolesImpacted: 'fields/rolesimpacted',
    ToolsImpacted: 'fields/toolsimpacted',
    L1BusinessProcess: 'fields/l1businessprocess',
    L2BusinessProcess: 'fields/l2businessprocess',
    L3BusinessProcess: 'fields/l3businessprocess',
    ActualCreatedDate: 'fields/actualcreateddate',
    TicketVolumePerMonth: 'fields/ticketvolumepermonth',
    SupportScenariosImpacted: 'fields/supportscenariosimpacted',
    ImpactType: 'fields/impacttype',
    IntakeType: 'fields/intaketype',
    StrategicCategory: 'fields/strategiccategory',
    Stakeholders: 'fields/stakeholdersinfo/uniquename',
    Synthesizer: 'fields/synthesizer',
    InternalTriageDate: 'fields/internaltriagedate',
    RejectedDate: 'fields/rejecteddate',
    TriageComments: 'fields/triagecomments',
    ExecutingTeamName: 'fields/executingteamname',
    ExecutingTeamADOLink: 'fields/executingteamadolink',
    OneListFeedback: 'fields/onelistfeedback',
    IsProductFeedback: 'fields/isproductfeedback',
    MSSalesID: 'fields/SGS_MSSalesId',
    AccountOwner: 'fields/SGS_AccountOwner/uniquename',
    PrimaryGrowthShapingContact: 'fields/SGS_PrimaryGrowthShapingContact/uniquename',
    SGSAccountName: 'fields/SGS_AccountName',
    GrowthLead: 'fields/SGS_GrowthLead/uniquename',
    AEContact: 'fields/SGS_AEContact/uniquename',
    ATSContact: 'fields/SGS_ATSContact/uniquename',
    STUContact: 'fields/SGS_STUContact/uniquename',
    CSAMContact: 'fields/SGS_CSAMContact/uniquename',
    TechEnablementSTU: 'fields/SGS_TechEnablementSTU/uniquename',
    CSAContact: 'fields/SGS_CSAContact/uniquename',
    WWCSUEngaged: 'fields/SGS_WWCSUEngaged/uniquename',
    AccountTeamBriefingStatus: 'fields/SGS_AccountTeamBriefingStatus',
    CustomerBriefingStatus: 'fields/SGS_CustomerBriefingStatus',
    GrowthShapingSteps: 'fields/SGS_GrowthShapingSteps',
    PrimaryReasonForBlockedOrAtRisk: 'fields/SGS_PrimaryReasonForBlockedOrAtRisk',
    CustomerStatusAndNextSteps: 'fields/SGS_CustomerStatusAndNextSteps',
    TechEnablementStatusAndComments: 'fields/SGS_TechEnablementStatusAndComments',
    RisksOrBlockedDetails: 'fields/SGS_RisksOrBlockedDetails',
    SGSTimezone: 'fields/SGS_Timezone',
    SGSArea: 'fields/SGS_Area',
    Subsidiary: 'fields/SGS_Subsidiary',
    Segment: 'fields/SGS_Segment',
    MACCPersona: 'fields/SGS_MACCPersona/uniquename',
    SubOUGrowthLead: 'fields/SGS_SubOUGrowthLead/uniquename',
    RegionalGrowthLead: 'fields/SGS_RegionalGrowthLead/uniquename',
    CurrentDCRegion: 'fields/SGS_CurrentDCRegion',
    ExecSponsor: 'fields/SGS_ExecSponsor/uniquename',
    ActionOwnerGroup: 'fields/actionownergroup',
    MeetingFiscalYear: 'fields/fy',
    MeetingMonth: 'fields/month',
    LinkedTechRoB: 'fields/relatedtechrobid',
    ActionCategoryField: 'fields/actioncategoryfield',
    MCAPSMeetingTypes: 'fields/meetingtype',
    MCAPSMeetingSubTypes: 'fields/meetingsubtype',
    RevenueImpacted: 'fields/revenueimpact',
    SegmentLists: 'fields/segmentslist',
    ChangedBy: 'fields/changedby/uniquename',
    Tags: 'tags/name',
    SolutionArea: 'raidrelations/msxaccounts/accounts/opportunities/solutionarealist',
    MACCAEAlias: 'fields/macc_ae_alias/uniquename',
    MACCEAlias: 'fields/macc_ce_alias/uniquename',
    MACCSSSPAlias: 'fields/macc_sssp_alias/uniquename',
    MACCRequestorManager: 'fields/macc_requestor_manager/uniquename',
    UATCountry: 'fields/uatcountry',
    MACCDurationInMonths: 'fields/macc_duration_in_months',
    MACCOpportunityRevenueSize: 'fields/opportunitysize',
    MACCExpectedMinSupportAdoption: 'fields/macc_expectedmin_support_adoption',
    MACCProposedSupportAdoption: 'fields/macc_proposed_support_adoption',
    MACCReasonForException: 'fields/macc_reason_for_exception',
    MACCIsSalesEngaged: 'fields/macc_is_sales_engaged',
    MACCCustomerHasUnifiedAgreement: 'fields/macc_customer_has_unifiedagreement',
    MACCProposedUnifiedRevenue: 'fields/macc_proposed_unifiedrevenue',
    MACCCustomersCurrentSupportAdoption: 'fields/macc_customers_current_support_adoption',
    MACCConsumptionPlan: 'fields/macc_consumption_plan',
    MACCAndSupportAgreement: 'fields/macc_and_support_agreement',
    MACCExceptionApprover: 'fields/macc_exception_approver/uniquename',
    RBTicketReason: 'fields/rb_ticketreason',
    RBCustomerTPID: 'fields/rb_customertpid',
    RBCRMAccountID: 'fields/rb_crmaccountid',
    RBCompanyName: 'fields/rb_companyname',
    RBCustomerContactName: 'fields/rb_customercontactname',
    RBCustomerContactEmail: 'fields/rb_customercontactemail',
    RBCustomerContactNumber: 'fields/rb_customercontactnumber',
    RBRegion: 'fields/rb_region',
    RBArea: 'fields/rb_area',
    RBCountry: 'fields/rb_country',
    RBInfraLocation: 'fields/rb_infralocation',
    RBCSSCaseNumber: 'fields/rb_csscasenumber',
    RBCSSCaseCreationDate: 'fields/rb_csscasecreationdate',
    RBGeneralCategory: 'fields/rb_generalcategory',
    RBGetHelpTicketNumber: 'fields/rb_gethelpticketnumber',
    RBGetHelpTicketCreationDate: 'fields/rb_gethelpticketcreationdate',
    RBUsersImpacted: 'fields/rb_usersimpacted',
    RBCustomerRevenueImpacted: 'fields/rb_customerrevenueimpacted',
    RBSSSPFullName: 'fields/rb_ssspfullname',
    RBSSSPEmail: 'fields/rb_ssspemail',
    RBBlockedProductOpportunityID: 'fields/rb_blockedproductopportunityid',
    RBMSFTProductBlockedRevenue: 'fields/rb_msftproductblockedrevenue',
    RBBlockedUSOOpportunityID: 'fields/rb_blockedusoopportunityid',
    RBUSOBlockedRevenue: 'fields/rb_usoblockedrevenue',
    RBCaseNumber: 'fields/rb_casenumber',
    RBTechnology: 'fields/rb_technology',
    RBCurrentFYRevenue: 'fields/rb_currentfyrevenue',
    RBSalesTerritory: 'fields/rb_salesterritory',
    RBCaseClosedDate: 'fields/rb_casecloseddate',
    RBNSCStartDate: 'fields/rb_nscstartdate',
  };

  //Not a comprehensive list!
  public static ArtemisStateDropdownValues = {
    InProgress: 'In Progress',
    Done: 'Done',
    OnHold: 'On Hold',
    Removed: 'Removed',
    OnTrack: 'On Track',
    AtRiskOrBlocked: 'At Risk or Blocked',
    Completed: 'Completed',
    NotStarted: 'Not Started',
  };

  //Not a comprehensive list!
  public static ArtemisSubStateDropdownValues = {
    Closed: 'Closed',
    DecisionNotToPursue: 'Decision Not to Pursue',
    PlanInPlace: 'Plan In Place',
    Resolved: 'Resolved',
    Unresolved: 'Unresolved',
    New: 'New',
    ActiveActionResolution: 'Active Action Resolution',
    AwaitingPrioritization: 'Awaiting Prioritization',
    Inactive: 'Inactive',
    Resources: 'Resources',
    Timing: 'Timing',
    Other: 'Other',
    Duplicate: 'Duplicate',
    NoActionRequired: 'No Action Required',
  };

  //Not a comprehensive list!
  public static ArtemisSolutionAreaDropdownValues = {
    Azure: 'Azure',
    BusinessApplications: 'Business Applications',
    ModernWork: 'Modern Work',
    Other: 'Other',
    Security: 'Security',
    UnifiedSupport: 'Unified Support',
  };

  //Not a comprehensive list!
  //TODO-RM mapping should be id, not value
  public static ArtemisMeetingTypeDropdownValues = {
    SBR: 'SBR',
    VSU: 'VSU',
    GlobalReviews: 'Global Reviews',
    MCAPSPrioritiesRoB: 'MCAPS Priorities RoB',
    MarketingRoB: 'Marketing RoB',
    AsiaInRegionMeetingRoB: 'Asia in-region meeting RoB',
  };

  public static ArtemisMeetingSubTypeDropdownValues = {
    EnterpriseCommercial: 'Enterprise Commercial',
    GPS: 'GPS',
    PublicSector: 'Public Sector',
    SMC: 'SMC',
    Azure: 'Azure',
    BusinessApps: 'Business Apps',
    ModernWork: 'Modern Work',
    MSUnified: 'MS Unified',
    Security: 'Security',
    UnifiedSupport: 'Unified Support',
    GlobalEnterprisePartnerReview: 'Global Enterprise Partner Review',
    IndustrySolutionsDelivery: 'Industry Solutions Delivery (ISD)',
    MCAPSRegionMeeting: 'MCAPS - Region Meeting',
    RegionOUMeeting: 'Region - OU Meeting',
    InOUMeeting: 'In OU Meeting',
    OthersIntakeTriageProcess: 'Others (Intake-Triage process)',
    RegionalCMOMarketingReviews: 'Regional CMO Marketing Reviews',
    GlobalMarketingReview: 'Global Marketing Review',
    AsiaCoreLTWeeklyStandUpMeeting: 'Asia Core LT Weekly Stand-Up Meeting',
    AsiaMCAPsPrioritiesRoBInRegion: 'Asia MCAPs Priorities RoB (In-Region)',
    CSADeepDive: 'CSA Deep Dive',
  };

  public static UATFields = {
    //Offer Keys : Start
    AMM_OFFER_AMMAPAzureAccess: 1,
    AMM_OFFER_MigrateAndSecureSolutionAssessment: 2,
    AMM_OFFER_PreSalesECIF: 3,
    AMM_OFFER_PostSalesECIF: 4,
    AMM_OFFER_FutureGrowthECIF: 5,
    AMM_OFFER_SAPRISECIF: 6,
    AMM_OFFER_MIGRATEORACLE: 7,
    AMM_OFFER_MigrationFactory: 8,
    AMM_OFFER_LocalACO: 9,
    AMM_OFFER_AVSACO: 10,
    AMM_OFFER_SAP_Azure: 11,
    AMM_OFFER_FutureGrowthACO: 12,
    AMM_OFFER_MigrateOracleACO: 13,
    AMM_OFFER_AVSExpressTCO: 14,
    AMM_OFFER_VMWareMigration: 15,
    AMM_OFFER_AnalyticsModernizationAccelerator: 16,
    AMM_OFFER_AnalyticsCompeteECIF: 17,
    AMM_OFFER_AnalyticsCompeteACO: 18,
    AMM_OFFER_GitHubCopilotHackathonECIF: 19,
    AMM_OFFER_GeneralPurposeHackathonECIF: 20,
    AMM_OFFER_ECIFFactoryCoreMigration: 21,
    AMM_OFFER_OracleODAAECIF: 22,
    AMM_OFFER_OracleODAAACO: 23,
    AMM_OFFER_SMBACO: 24,
    AMM_OFFER_SynapseHDITOFabricMigration: 25,
    AMM_OFFER_AVSPilotProgram: 26,
    AMM_OFFER_AzureFrontierOfferECIF: 27,
    AMM_OFFER_AzureFrontierOfferACO: 28,
    AMM_OFFER_SSCACO: 29,
    //FY27
    AMM_OFFER_FY27_LocalACO: 30, // Copy of local azure credit offer
    AMM_OFFER_FieldECIF: 31, // Copy of azure accelerate offer
    AMM_OFFER_CorpACO: 32, // Copy of azure frontier offer ACO
    AMM_OFFER_SpecialtyECIF: 33, //Copy of synapse to fabric offer
    AMM_OFFER_CorpECIF: 34, // Copy of azure frontier offer ECIF
    AMM_OFFER_ECIFFactory: 35, // Clone of ECIF + Factory Core Migration offer
    //Offer Keys : End
    Account: 'Custom.Account',
    AccountableExec: 'Custom.AccountableExec',
    ActionCategoryField: 'Custom.ActionCategoryField',
    ActionPriorityField: 'Custom.ActionPriorityField',
    ActiveSalesProcess: 'Custom.ActiveSalesProcess',
    ActiveSalesStage: 'Custom.ActiveSalesStage',
    ActionType: 'Custom.ActionType',
    AdditionalTheme: 'Custom.AdditionalTheme',
    AgreementId: 'Custom.AgreementId',
    AgreementName: 'Custom.AgreementName',
    Apac: 'Custom.APAC',
    BoardLane: 'System.BoardLane',
    DefaultEqualsMaxETA: 'Custom.DefaultequalsMaxETA',
    India: 'Custom.India',
    Data: 'Custom.Data',
    Application: 'Custom.Application',
    Archive: 'Custom.Archive',
    AreaField: 'Custom.AreaField',
    AskID: 'Custom.AskID',
    AssessmentStatus: 'Custom.AssessmentStatus',
    AssessmentType: 'Custom.AssessmentType',
    AssigntoCorp: UATConstants.IsProduction ? 'Custom.AssignToCorp' : 'Custom.AssigntoCorp',
    AssignToCorpDate: 'Custom.AssignToCorpDate',
    AssistedMigrationReason: UATConstants.IsProduction
      ? 'Custom.AssistedMigrationreason'
      : 'Custom.AssistedMigrationReason',
    AzureCEState: 'Custom.AzureCEState',
    AzureCESubState: 'Custom.AzureCESubState',
    AzurePriority: 'Custom.AzurePriority',
    AzServiceName: 'Custom.AZServiceName',
    ServiceRegionavail: 'Custom.ServiceRegionAvailability',
    OfferingName: 'Custom.OfferingName',
    BusinessProposal: 'Custom.BusinessProposal',
    ClosureStatement: 'Custom.ClosureStatement',
    CSGProducts: 'Custom.CSGProducts',
    CSGTest: 'Custom.CSGTest',
    CompeteBlockerEscalation: 'Custom.CompeteBlockerEscalation',
    Competitor: 'Custom.CompetitorNameDD',
    CompetitorProducts: 'Custom.CompetitorProductDD',
    OmsTenantId: 'Custom.OMSTenantID',
    Completionstatusifcombined: 'Custom.Completionstatusifcombined',
    Contributor1: 'Custom.Contributor1',
    Contributor2: 'Custom.Contributor2',
    ConversationID: 'Custom.ConversationID',
    CurrentUser: 'Custom.CurrentUser',
    CustomerImpactData: 'Custom.CustomerImpactData',
    CustomerScenarioandDesiredOutcome: 'Custom.CustomerScenarioandDesiredOutcome',
    customIndustry: 'Custom.CustomIndustry',
    CustomerCommitment: 'Custom.Customer_Commitment',
    CloudIntentreason: 'Custom.CloudIntentreason',
    ifNoStateReason: 'Custom.Cloudintentreason',
    DealSize: 'Custom.DealSize',
    DesiredOutcome: 'Custom.DesiredOutcome',
    DualUse: 'Custom.DualUse',
    ECIFReason: 'Custom.ECIFReason',
    ECIFrequestedservicestatus: 'Custom.ECIFrequestedservicestatus',
    ECIFStatus: 'Custom.ECIFStatus',
    ECIFstatusforFAM: 'Custom.ECIFstatusforFAM',
    ECIFstatusifCMA: 'Custom.ECIFstatusifCMA',
    EOU: 'Custom.EOU',
    ESE: 'Custom.ESE',
    MilestoneLink: 'Custom.MilestoneLink',
    OpportunityLink: 'Custom.OpportunityLink',
    EngContactvalue: 'Custom.EngineeringContacts',
    EngStatusValue: 'Custom.Engineeringstatus',
    Escalated: 'Custom.Escalated',
    EstimatedMonthlyUsage: 'Custom.EstimatedMonthlyUsage',
    EstMonthlyUsage: 'Custom.EstMonthlyUsage', // Field 'Not in use', Use 'Custom.EstMonthlyUsages' instead
    ExitCriteria: 'Custom.ExitCriteria',
    FAQ: 'Custom.FAQ',
    FeedbackType: UATConstants.IsProduction
      ? 'Custom.FeedbackType'
      : 'Custom.164269a5-6f7e-4335-8be1-8fd9781f47e2',
    FeedbackTypeValue: 'Custom.164269a5-6f7e-4335-8be1-8fd9781f47e2',
    FeedbackTitle: 'Custom.FeedbackTitle',
    FeedbackItemUrl: 'Custom.FeedbackItemUrl',
    FeedbackNumber: 'Custom.FeedbackNumber',
    FedSalesOpportunityDate: 'Custom.FedSalesOpportunityDate',
    FY: 'Custom.FY',
    HelpNeededField: 'Custom.HelpNeededField',
    ImpactSummary: 'Custom.ImpactSummary',
    Industry: 'Custom.Industry',
    IntenttomovetoCloud: 'Custom.IntenttomovetoCloud',

    InterestedinAssessment: 'Custom.InterestedinAssessment',
    InterestedinAssistedMigration: 'Custom.InterestedinAssistedMigration',
    InterestedinotherECIF: 'Custom.InterestedinotherECIF',
    InterestedinrenewingBREPorSA: 'Custom.InterestedinrenewingBREPorSA',
    InternalRolespecificNotes: 'Custom.InternalRolespecificNotes',
    IsBlocked: 'Custom.IsBlocked',
    IsCovid19: 'Custom.IsCovid19',
    IsPDMHelpNeeded: 'Custom.IsPDMHelpNeeded',
    Partners: 'Custom.Partners',
    PDM: 'Custom.PDM',
    PDMEmail: 'Custom.PDMEmail',
    IsitFAM: 'Custom.IsitFAM',
    LastEmailDate: 'Custom.LastEmailDate',
    LTEscalation: 'Custom.LTEscalation',
    LTExecDirect: 'Custom.LTExecDirect',
    LockUAT: 'Custom.LockUAT',
    Mailbox: 'Custom.Mailbox',
    ManagingOwner: 'Custom.ManagingOwner',
    MeetingDetails: 'Custom.MeetingDetails',
    MeetingSubType: 'Custom.MeetingSubType',
    MeetingTypeOrigin: 'Custom.MeetingTypeOrigin',
    MeetingsPitchDeck: 'Custom.MeetingsPitchDeck',
    MeetingsWorkshop: UATConstants.IsProduction
      ? 'Custom.712cc8bd-2363-410f-a0d6-f3a30e9ed34c'
      : 'Custom.MeetingsWorkshop',
    MigrationPartner: 'Custom.MigrationPartner',
    MigrationType: 'Custom.MigrationType',
    MilestoneID: 'Custom.MilestoneID',
    MilestoneActivations: 'Custom.MilestoneActivations',
    MilestoneAmount: 'Custom.MilestoneAmount',
    MilestoneEstDate: 'Custom.MilestoneEstDate',
    MilestoneReason: 'Custom.MilestoneReason',
    NonRecurring: 'Custom.NonRecurring',
    BACV: 'Custom.BACV',
    MilestoneStatus: 'Custom.MilestoneStatus',
    MilestoneWorkload: 'Custom.MilestoneWorkload',
    MilestoneName: 'Custom.MilestoneName',
    MilestoneTotalEstMonthlyUsage: 'Custom.MilestoneTotalEstMonthlyUsage',
    MitigationPlan: 'Custom.MitigationPlan',
    Program: 'Custom.NewProgram',
    PrimaryWorkload: 'Custom.Primary_Workload',
    hasFastTrack: 'Custom.HasFastTrack',
    hasUnified: 'Custom.HasUnified',
    hasD365: 'Custom.HasD365',
    hasM365: 'Custom.HasM365',
    escalationReason: 'Custom.EscalationReason',
    secondaryEscalationReason: 'Custom.SecondaryEscalationReason',
    tertiaryEscalationReason: 'Custom.TertiaryEscalationReason',
    NextAction: 'Custom.NextAction',
    Nonrenewingreason: 'Custom.Nonrenewingreason',
    Notes: 'Custom.Notes',
    OfferRelated: 'Custom.OfferRelated',
    oneAskID: UATConstants.IsProduction
      ? 'Custom.5ec0f5af-38c8-4c1a-8b12-09efd2d98d94'
      : 'Custom.00204309-6f2a-4083-8664-174cbdf151ef',
    BlockerType: 'Custom.Blocker_Type',
    CustomerPriority: 'Custom.Customer_Priority',
    OneListFeedback: 'Custom.OneListFeedback',
    OneListID: 'Custom.OneListID',
    OpportunityIds: 'Custom.OpportunityIds',
    Opportunity_ID: 'Custom.Opportunity_ID',
    Opportunity_Size: 'Custom.Opportunity_Size',
    HelpNeededOpportunity: 'Custom.HelpNeededOpportunity',
    Organization: 'Custom.Org',
    POCECIFConsumption: 'Custom.POCECIFConsumption',
    Partner: 'Custom.Partner',
    PartnerOneName: 'Custom.PartnerOneName',
    PatternGroupName: 'Custom.PatternGroup',
    ProductOpportunitySize: 'Custom.ProductOpportunitySize',
    OpportunityStatus: 'Custom.Opportunity_Outcome',
    Opportunity_Status: 'Custom.Opportunity_Status',
    Products: 'Custom.Products_New',
    multiProductIds: 'Custom.ProductIds',
    Product: 'Products(s)',
    ProductsOld: 'Custom.Products',
    ProgramNew: 'Custom.Program_NEW',
    ProgramOld: 'Custom.Program',
    PubliclyDocumented: 'Custom.PubliclyDocumented',
    QBCNotes: 'Custom.QBCNotes',
    QBCTop5Ask: 'Custom.QBCTop5Ask',
    Quarter: 'Custom.Quarter',
    QuarterSerialNumber: 'Custom.QuarterSerialNumber',
    RefID: 'Custom.RefID',
    SupportCaseLink: 'Custom.SupportCaseLink',
    RelatedIssue: 'Custom.RelatedIssue',
    RequestedBy: 'Custom.RequestedBy',
    RequestedDate: 'Custom.RequestedDate',
    Requestor: 'Custom.Requestor',
    Requestors: 'Custom.Requestors',
    SalesPlay: 'Custom.SalesPlay',
    Conversation: 'Custom.Conversation',
    ProductSuperRevSumDivision: 'Custom.ProductSuperRevSumDivision',
    ScopeValue: 'Custom.Scope',
    Segment: 'Custom.Segment',
    Sender: 'Custom.Sender',
    ServiceTreeOffering: 'Custom.ServiceTreeOfferingName',
    ServiceTreeOffering2: 'Custom.ServiceTreeOfferingName2',
    ServiceTreeOffering3: 'Custom.ServiceTreeOfferingName3',
    SolutionArea: 'Custom.SolutionArea',
    SolutionAreaList: 'Custom.SolutionAreaUAT',
    OperatingUnitList: 'Custom.OperatingUnit',
    CloudImpactedList: 'Custom.CloudImpacted',
    AnnualRevenueImpact: 'Custom.AnnualRevenueImpact',
    AnnualRevenueDecimal: 'Custom.AnnualRevenueDecimal',
    NumberofSeats: 'Custom.NumberofSeats',
    ExplanationOfNeededBy: 'Custom.WhatDateIsThisNeededBy',
    FedSalesOpportunityId: 'Custom.FedSalesOpportunityID',
    FeatureParityRequest: 'Custom.FeatureParityRequest',
    Source: 'Custom.Source',
    Status: 'Custom.Status',
    StatusUpdate: 'Custom.StatusUpdate',
    StrategicAccount: 'Custom.StrategicAccount',
    SubmittedforAWR: 'Custom.SubmittedforAWR',
    SupportCases: 'Custom.SupportCases',
    SubState: 'Custom.SubState',
    Subsegment: 'Custom.Subsegment',
    Country: 'Custom.UATCountry',
    AccountID: 'Custom.UATAccountID',
    PrimaryCompetitor: 'Custom.PrimaryCompetitor',
    SyncWithCE: 'Custom.SyncWithCE',
    TCOAnalysis: 'Custom.TCOAnalysis',
    TPID: 'Custom.TPID',
    Test: 'Custom.Test',
    Theme: 'Custom.Theme',
    CAEEngagementCategory: 'Custom.CAEEngagementCategory',
    CAEEngagementRegion: 'Custom.AzureRegion',
    CAEScenarioCohort: 'Custom.CAEScenarioCohort',
    CAEThirdPartySolution: 'Custom.CAEThirdPartySolution',
    CAEFirstPartySolution: 'Custom.AZServiceName',
    CAEFirstPartyOffering: 'Custom.OfferingName_MultiSelect',
    Topic: 'Custom.Topic',
    TopicCategory: 'Custom.TopicCategory',
    TranslatedAccountName: 'Custom.TranslatedAccountName',
    TriageValue: 'Microsoft.VSTS.Common.Triage',
    UserChangedBy: 'Custom.UserChangedBy',
    UserChangedDate: 'Custom.UserChangedDate',
    tftCreated: UATConstants.IsProduction
      ? 'Custom.36520105-2048-4366-8c5e-7ef1c19cb0e3'
      : 'Custom.c1f2fddd-c94e-4ad7-a789-972c51dc3c7c',
    tftCreatedOldOne: 'Custom.TFTCreated',
    tftCreatedOldTwo: 'Custom.a615160f-764c-4b25-b7ef-5e6da7f0cb35',
    tftCreatedNew: 'Custom.c1f2fddd-c94e-4ad7-a789-972c51dc3c7c',
    StateChangeDate: 'Microsoft.VSTS.Common.StateChangeDate',
    WorkAroundDetails: UATConstants.IsProduction
      ? 'Custom.fa152cba-9ede-4c97-8281-2985105a4bfb'
      : 'Custom.WorkaroundDetails',
    WorkloadType: 'Custom.WorkloadType',
    AcceptanceCriteria: 'Microsoft.VSTS.Common.AcceptanceCriteria',
    ActivatedBy: 'Microsoft.VSTS.Common.ActivatedBy',
    AlternativeSolutions: 'Custom.Alternativesolutions',
    AppLaunchInstructions: 'Microsoft.VSTS.Feedback.ApplicationLaunchInstructions',
    ApplicationType: 'Microsoft.VSTS.Feedback.ApplicationType',
    AppStartInformation: 'Microsoft.VSTS.Feedback.ApplicationStartInformation',
    AssociatedContext: 'Microsoft.VSTS.CodeReview.Context',
    AssociatedContextCode: 'Microsoft.VSTS.CodeReview.ContextCode',
    AssociatedContextOwner: 'Microsoft.VSTS.CodeReview.ContextOwner',
    AssociatedContextType: 'Microsoft.VSTS.CodeReview.ContextType',
    AutomatedTestId: 'Microsoft.VSTS.TCM.AutomatedTestId',
    AutomatedTestName: 'Microsoft.VSTS.TCM.AutomatedTestName',
    AutomatedTestStorage: 'Microsoft.VSTS.TCM.AutomatedTestStorage',
    AutomatedTestType: 'Microsoft.VSTS.TCM.AutomatedTestType',
    DueDate: 'Microsoft.VSTS.Scheduling.DueDate',
    Issue: 'Microsoft.VSTS.Common.Issue',
    Resolution: 'Microsoft.VSTS.Common.Resolution',
    Activity: 'Microsoft.VSTS.Common.Activity',
    FoundIn: 'Microsoft.VSTS.Build.FoundIn',
    IntegrationBuild: 'Microsoft.VSTS.Build.IntegrationBuild',
    Blocked: 'Microsoft.VSTS.CMMI.Blocked',
    TestSuitType: 'Microsoft.VSTS.TCM.TestSuiteType',
    AutomationStatus: 'Microsoft.VSTS.TCM.AutomationStatus',
    ValueArea: 'Microsoft.VSTS.Common.ValueArea',
    ClosedStatus: 'Microsoft.VSTS.CodeReview.ClosedStatus',
    ClosedStatusCode: 'Microsoft.VSTS.CodeReview.ClosedStatusCode',
    Priority: 'Microsoft.VSTS.Common.Priority',
    Rating: 'Microsoft.VSTS.Common.Rating',
    StateCode: 'Microsoft.VSTS.Common.StateCode',
    Severity: 'Microsoft.VSTS.Common.Severity',
    ContextCode: 'Microsoft.VSTS.CodeReview.ContextCode',
    BacklogPriority: 'Microsoft.VSTS.Common.BacklogPriority',
    BusinessValue: 'Microsoft.VSTS.Common.BusinessValue',
    Effort: 'Microsoft.VSTS.Scheduling.Effort',
    LocalDataSource: 'Microsoft.VSTS.TCM.LocalDataSource',
    RemainingWork: 'Microsoft.VSTS.Scheduling.RemainingWork',
    ReviewedBy: 'Microsoft.VSTS.Common.ReviewedBy',
    parameters: 'Microsoft.VSTS.TCM.Parameters',
    TestSuiteTypeId: 'Microsoft.VSTS.TCM.TestSuiteTypeId',
    TimeCriticality: 'Microsoft.VSTS.Common.TimeCriticality',
    AreaId: 'System.AreaId',
    AreaLevel1: 'System.AreaLevel1',
    AreaLevel2: 'System.AreaLevel2',
    AreaPath: 'System.AreaPath',
    AssignedTo: 'System.AssignedTo',
    AuthorizedAs: 'System.AuthorizedAs',
    AuthorizedDate: 'System.AuthorizedDate',
    BoardColumn: 'System.BoardColumn',
    BoardColumnDone: 'System.BoardColumnDone',
    ChangedBy: 'System.ChangedBy',
    ChangedDate: 'System.ChangedDate',
    CommentCount: 'System.CommentCount',
    CreatedBy: 'System.CreatedBy',
    CreatedDate: 'System.CreatedDate',
    ClosedBy: 'Microsoft.VSTS.Common.ClosedBy',
    ClosedDate: 'Microsoft.VSTS.Common.ClosedDate',
    Description: 'System.Description',
    ExternalLinkCount: 'System.ExternalLinkCount',
    Id: 'System.Id',
    IterationId: 'System.IterationId',
    IterationLevel1: 'System.IterationLevel1',
    IterationPath: 'System.IterationPath',
    NodeName: 'System.NodeName',
    PersonId: 'System.PersonId',
    Reason: 'System.Reason',
    Rev: 'System.Rev',
    RevisedDate: 'System.RevisedDate',
    State: 'System.State',
    IsAutoClosed: 'Custom.IsAutoClosed',
    Tags: 'System.Tags',
    TeamProject: 'System.TeamProject',
    Title: 'System.Title',
    Watermark: 'System.Watermark',
    WorkItemType: 'System.WorkItemType',
    PreferredPartnerName: 'Custom.PreferredPartnerName',
    Requestor1: 'Custom.Requestor1',
    LicensingProgram: 'Custom.LicensingProgram',
    PackageId: 'Custom.PackageId',
    PackageName: 'Custom.PackageName',
    DeliveryBlockers: 'Custom.DeliveryBlockers',
    MilestoneOwner: 'Custom.MilestoneOwner',
    Competitors: 'Custom.Competitors',
    CustomerGoalState: UATConstants.IsProduction
      ? 'Custom.Customergoalstate'
      : 'Custom.CustomerGoalStateField',
    CustomerScenario: 'Custom.CustomerScenario',
    CustomerImpact: 'Custom.CustomerImpact',
    CompeteParity: 'Custom.Competeparity',
    ReproSteps: 'Microsoft.VSTS.TCM.ReproSteps',
    ObservedOutput: 'Custom.ObservedOutput',
    ExpectedOutput: 'Custom.ExpectedOutput',
    EngineeringWorkItemURL: 'Custom.EngineeringWorkItemURL',
    EstimatedDateToComplete: 'Custom.EstimatedDateToComplete',
    CurrentDefault: 'Custom.CurrentDefault',
    UnblockcustomerETA: 'Custom.UnblockcustomerETA',
    PubliclyDocumentedETA: 'Custom.PubliclyDocumentedETA',
    AdditionalArea: 'Custom.AdditionalArea',
    AdditionaEOU: 'Custom.AdditionalEOU',
    OpportunityName: 'Custom.OpportunityName',
    OpportunityStage: 'Custom.OpportunityStage',
    OpportunityIntent: 'Custom.OpportunityIntent',
    SOObjective: 'Custom.SOObjective',
    ProjectName: 'Custom.ProjectName',
    ListeningCircleSessionName: 'Custom.ListeningCircleSessionName',
    ListeningCircleSessionDate: 'Custom.ListeningCircleSessionDate',
    ListeningCircleChangeLead: 'Custom.ListeningCircleChangeLeads',
    ListeningCircleFacilitator: 'Custom.ListeningCircleFacilitators',
    MicrosoftServiceRegions: 'Custom.msX_PreferredAzureRegion',
    ListeningCircleArea: 'Custom.ListeningCircleArea',
    EstMonthlyUsages: 'Custom.EstMonthlyUsages',
    TotalEstMonthlyUsages: 'Custom.MilestoneTotalEstMonthlyUsages',
    ChildItems: 'Child Items',
    RelatedItems: 'Related Items',
    RemoteRelatedActions: 'Remote Related Actions',
    TenantID: 'Custom.TenantID',
    ReferredTo: 'Custom.ReferredTo',
    HelpNeededApex: 'Custom.HelpNeededApex',
    WhenDoesCustomerWantToEngage: 'Custom.CustomerEngagementTimeLine',
    CustomerGoLiveDate: 'Custom.CustomerGoLiveDate',
    PartOfCollabAppsContent: 'Custom.PartofCollabAppsContest',
    ITandBusinessStakeholders: 'Custom.ITandBusinessstakeholders',
    ExecutiveSponsor: 'Custom.ExecutiveSponsor',
    ProjectedAppMAUfortheproposedsolution: 'Custom.ProjectedAppMAUfortheproposedsolution',
    MAUImpact: 'Custom.ApexMAUImpact',
    AnyOtherDetails: 'Custom.Anyotherdetails',
    PartnerOneID: 'Custom.PartnerOneId',
    BusinessScenario: 'Custom.ProblemDetails',
    InternalTriageDate: 'Custom.InternalTriageDate',
    BusinessDivision: 'Custom.BusinessDivision',
    TriageComments: 'Custom.TriageComments',
    Stakeholders: 'Custom.Stakeholders',
    IntakeType: 'Custom.Intake',
    ProblemType: 'Custom.ProblemType',
    StrategicCategory: 'Custom.StrategicCategory',
    RolesImpacted: 'Custom.RolesImpacted',
    BusinessImpact: 'Custom.BusinessImpact',
    TicketVolumePerMonth: 'Custom.TicketVolumePerMonth',
    ToolsImpacted: 'Custom.ToolsImpacted',
    L1BusinessProcess: 'Custom.BPAL1',
    L2BusinessProcess: 'Custom.BPAL2',
    L3BusinessProcess: 'Custom.BPAL3',
    RejectedDate: 'Custom.RejectedDate',
    SupportScenariosImpacted: 'Custom.SupportScenariosImpacted',
    Synthesizer: 'Custom.Synthesizer',
    ActualCreatedDate: 'Custom.ActualCreateddate',
    ExecutingTeamName: 'Custom.ExecutingTeamName',
    ExecutingTeamADOLink: 'Custom.ExecutingTeamADOLink',
    ApexPartnerEngage: 'Custom.IsPartnerEngage',
    ApexPartnerName: 'Custom.ApexPartnerName',
    RequestType: 'Custom.RequestType',
    RequestTypeDescription: 'Custom.RequestTypeDescription',
    HighLevelBusinessChallenge: 'Custom.HighLevelBusinessChallenge',
    AzureCapacityType: 'Custom.AzureCapacityTypeMultiline',
    AOAIRequestedType: 'Custom.AOAI_RequestType',
    AOAIRequestUrgency: 'Custom.RequestUrgency',
    AOAIUOM: 'Custom.UnitofMeasurement',
    AOAIDealType: 'Custom.AOAI_DealType',
    AOAIGPUPrice: 'Custom.AOAI_GPUPrice',
    AOAIContributors: 'Custom.AOAI_Contributors',
    IsAmendemeentSigned: 'Custom.AOAI_IsAmendmentM1072Signed',
    AOAIDateCustodyReady: 'Custom.AOAI_DateCustomerReady',
    AOAI_Model: 'Custom.AOAI_Model',
    AOAI_SubscriptionId: 'Custom.AOAI_SubscriptionID',
    AOAI_Timezone: 'Custom.AOAI_Timezone',
    AOAI_SegmentType: 'Custom.AOAI_SegmentType',
    AOAI_RequestedAmount: 'Custom.AOAI_RequestedAmount',
    AOAI_DateDecided: 'Custom.AOAI_DateDecided',
    AOAI_Amount: 'Custom.AOAI_Amount',
    AOAI_PreferredRegion: 'Custom.AOAI_PreferredRegionMultiselect',
    AOAI_Decision: 'Custom.AOAI_Decision',
    AOAI_ApprovedPrefferedRegion: 'Custom.AOAI_ApprovedPrefferedRegion',
    AzurePrefferedRegion: 'Custom.AzurePreferredRegion',
    AOAI_AppIDQuotaID: 'Custom.AOAI_QuotaID',
    AOAI_AIGBBName: 'Custom.AOAI_AIGBBName',
    AOAI_RequestedVMType: 'Custom.AOAI_RequestedVMType',
    AOAI_NumberofVMRequested: 'Custom.AOAI_NumberofVMRequested',
    AOAI_NumberofGPURequested: 'Custom.AOAI_NumberofGPURequested',
    AOAI_RequestedRegion: 'Custom.AOAI_RequestedRegion',
    AOAI_RegionFlexibility: 'Custom.AOAI_RegionFlexibility',
    AOAI_IsNonGPUACR: 'Custom.AOAI_IsNonGPUACR',
    AOAI_NonGPUACRPercentage: 'Custom.AOAI_NonGPUACRPercentage',
    AOAI_UseCaseSummary: 'Custom.AOAI_UseCaseSummary',
    AOAI_DeploymentsForTotalAmount: 'Custom.AOAI_DeploymentsForTotalAmount',
    AOAI_FrequencyOfDeployments: 'Custom.AOAI_FrequencyOfDeployments',
    AOAI_DateofFirstDeployment: 'Custom.AOAI_DateofFirstDeployment',
    AOAI_IsLLMorLMM: 'Custom.AOAI_IsLLMorLMM',
    AOAI_RequireInterconnectedCluster: 'Custom.AOAI_RequireInterconnectedCluster',
    AOAI_IsRecommendedForDWR: 'Custom.AOAI_IsRecommendedForDWR',
    AOAI_ValidatedByAIGBBs: 'Custom.AOAI_ValidatedByAIGBBs',
    AOAI_IsCustomerNeedValidatedForNCSeries: 'Custom.AOAI_IsCustomerNeedValidatedForNCSeries',
    AOAI_MainUseCategories: 'Custom.AOAI_MainUseCategories',
    AOAI_DWRRequestId: 'Custom.AOAI_DWR_RequestId',
    AOAI_RegionGranted: 'Custom.AOAI_RegionGranted',
    AOAI_AdditionalComments: 'Custom.AOAI_AdditionalComments',
    MEA_PreferredRegion: 'Custom.AOAI_PreferredRegionMultiselect',
    Nominations: 'Custom.Nominations',
    NominationType: 'Custom.NominationType',
    UATAccountID: 'Custom.UATAccountID',
    Nominated_OpportunityType: 'Custom.Nominated_OpportunityType',
    CSM_Nomination: 'Custom.CSM_Nomination',
    MW_WaveName: 'Custom.MW_WaveName',
    WaveID_Selected: 'Custom.WaveID_Selected',
    Enrolled_Contacts: 'Custom.Enrolled_Contacts',
    Enrolled_Contact_Options: 'Custom.Enrolled_Contact_Options',
    MW_Nominated_Product: 'Custom.MW_Nominated_Product',
    RB_InfraLocation: 'Custom.RB_InfraLocation',
    RB_Technology: 'Custom.RB_Technology',
    RB_Tier: 'Custom.RB_Tier',
    RB_GeneralCategory: 'Custom.RB_GeneralCategory',
    RB_TicketReason: 'Custom.RB_TicketReason',
    RB_Area: 'Custom.RB_Area',
    RB_Region: 'Custom.RB_Region',
    RB_Country: 'Custom.RB_Country',
    RB_CustomerTPID: 'Custom.RB_CustomerTPID',
    RB_CRMAccountID: 'Custom.RB_CRMAccountID',
    RB_CompanyName: 'Custom.RB_CompanyName',
    RB_CustomerContactName: 'Custom.RB_CustomerContactName',
    RB_CustomerContactEmail: 'Custom.RB_CustomerContactEmail',
    RB_CustomerContactNumber: 'Custom.RB_CustomerContactNumber',
    RB_CSSCaseNumber: 'Custom.RB_CSSCaseNumber',
    RB_GetHelpTicketNumber: 'Custom.RB_GetHelpTicketNumber',
    RB_UsersImpacted: 'Custom.RB_UsersImpacted',
    RB_CustomerRevenueImpacted: 'Custom.RB_CustomerRevenueImpacted',
    RB_GetHelpTicketCreationDate: 'Custom.RB_GetHelpTicketCreationDate',
    RB_SSSPFullName: 'Custom.RB_SSSPFullName',
    RB_SSSPEmail: 'Custom.RB_SSSPEmail',
    RB_BlockedProductOpportunityID: 'Custom.RB_BlockedProductOpportunityID',
    RB_MSFTProductBlockedRevenue: 'Custom.RB_MSFTProductBlockedRevenue',
    RB_BlockedUSOOpportunityID: 'Custom.RB_BlockedUSOOpportunityID',
    RB_USOBlockedRevenue: 'Custom.RB_USOBlockedRevenue',
    BilledRevenue: 'Custom.EstedBilledRevenue',
    RB_TerritoryName: 'Custom.RB_TerritoryName',
    RB_SalesTerritory: 'Custom.RB_SalesTerritory',
    RB_CaseNumber: 'Custom.RB_CaseNumber',
    RB_CurrentFYRevenue: 'Custom.RB_CurrentFYRevenue',
    RB_BlockedOpportunityRevenue: 'Custom.RB_BlockedOpportunityRevenue',
    RB_NSCStartDate: 'Custom.RB_NSCStartDate',
    RB_CaseClosedDate: 'Custom.RB_CaseClosedDate',
    RB_CSSCaseCreationDate: 'Custom.RB_CSSCaseCreationDate',
    CAPSL_AccountPartnerName: 'Custom.CAPSL_accountPartnerName',
    CAPSL_AccountTPID: 'Custom.CAPSL_accountTPID',
    CAPSL_BusinessJustification: 'Custom.CAPSL_BusinessJustification',
    CAPSL_ContactAlias: 'Custom.CAPSL_ContactAlias',
    CAPSL_Title: 'Custom.CAPSL_Title',
    CAPSL_FirstName: 'Custom.CAPSL_FirstName',
    CAPSL_LastName: 'Custom.CAPSL_LastName',
    CAPSL_AM_PDMName: 'Custom.CAPSL_AMName',
    CAPSL_SuppressChildAccount: 'Custom.CAPSL_SuppressChildAccount',
    CAPSL_AccountName: 'Custom.CAPSL_AccountName',
    ECIF_AdditionalContacts: 'Custom.ECIF_AdditionalContacts',
    ECIF_MultipleAdditionalContacts: 'Custom.ECIF_MultipleAdditionalContacts',
    ECIF_ApprovedDate: 'Custom.ECIF_ApprovedDate',
    ECIF_CompletionDate: 'Custom.ECIF_CompletionDate',
    ECIF_DeliveryMethod: 'Custom.ECIFDeliveryMethod',
    ECIF_EntityType: 'Custom.ECIF_EntityType',
    ECIF_IncrementalACR: 'Custom.ECIF_IncrementalACR',
    ECIF_Investment: 'Custom.ECIF_Investment',
    ECIF_InvestmentApproved: 'Custom.ECIF_InvestmentApproved',
    ECIF_ROI: 'Custom.ECIF_ROI',
    ECIF_3YearROI: 'Custom.ECIF_3YearROI',
    ECIF_IncrementalRev: 'Custom.ECIF_IncrementalRevenue',
    ECIF_ROIAttached: 'Custom.ECIF_ROIAttached',
    ECIF_Spend: 'Custom.ECIF_Spend',
    ECIF_SpendPlan: 'Custom.ECIF_SpendPlan',
    ECIF_StartDate: 'Custom.ECIF_StartDate',
    ECIF_SupplierName: 'Custom.ECIF_SupplierName',
    ECIF_SupplierType: 'Custom.ECIF_SupplierType',
    ECIF_TypeofActivity: 'Custom.ECIF_TypeofActivity',
    ECIF_TypeofFunds: 'Custom.ECIF_TypeofFunds',
    ECIF_CommericalExecutive: 'Custom.ECIF_CommericalExecutive',
    ECIF_ApprovedBy: 'Custom.ECIF_ApprovedBy',
    ECIF_ProjectDetails: 'Custom.ECIF_ProjectDetails',
    ECIF_DealDetails: 'Custom.ECIF_DealDetails',
    ECIF_ActionCategoryField: 'Custom.ActionCategoryField',
    ECIF_ExpectedOutcome: 'Custom.ECIF_ExpectedOutcome',
    ECIF_EvaluateWork: 'Custom.ECIF_EvaluateWork',
    ECIF_Rejectedby: 'Custom.ECIF_RejectedBy',
    ECIF_ProgramName: 'Custom.ECIFProgramName',
    ECIF_SupplierSameAsCustomer: 'Custom.ECIFIssuppliersameascustomer',
    ECIF_EnrollmentNumber: 'Custom.ECIFCustomerEnrollmentNumber',
    ECIF_RejectedDate: 'Custom.ECIF_RejectedDate',
    ECIF_RequestJustification: 'Custom.ECIF_RequestJustification',
    ECIF_IsNewTemplateCreate: 'Custom.ECIF_Isnewtemplatecreate',
    ECIF_BaselineRevenue: 'Custom.ECIF_BaselineRevenue',
    ECIF_Incremental_UpsellBilledRevenue: 'Custom.ECIF_Incremental_UpsellBilledRevenue',
    ECIF_ActiveUsersDevices: 'Custom.ECIF_ActiveUsersDevices',
    ECIF_SKUDiscount: 'Custom.ECIF_SKUDiscount',
    ECIF_AzureCreditDiscount: 'Custom.ECIF_AzureCreditDiscount',
    ECIF_ConsumedRevenue1year: 'Custom.ECIF_ConsumedRevenue1year',
    ECIF_ConsumedRevenue2year: 'Custom.ECIF_ConsumedRevenue2year',
    ECIF_ConsumedRevenue3year: 'Custom.ECIF_ConsumedRevenue3year',
    ECIF_RequestJustificationdealcoaching: 'Custom.ECIF_RequestJustificationdealcoaching',
    ECIF_FiscalYear: 'Custom.ECIF_FiscalYear',
    ECIF_Q1Spend: 'Custom.ECIF_Q1Spend',
    ECIF_Q2Spend: 'Custom.ECIF_Q2Spend',
    ECIF_Q3Spend: 'Custom.ECIF_Q3Spend',
    ECIF_Q4Spend: 'Custom.ECIF_Q4Spend',
    ECIF_CrossFiscalYear: 'Custom.ECIF_CrossFiscalYear',
    ECIF_CrossQ1Spend: 'Custom.ECIF_CrossQ1Spend',
    ECIF_CrossQ2Spend: 'Custom.ECIF_CrossQ2Spend',
    ECIF_CrossQ3Spend: 'Custom.ECIF_CrossQ3Spend',
    ECIF_CrossQ4Spend: 'Custom.ECIF_CrossQ4Spend',
    ECIF_ProgramNameLocalFund: 'Custom.ECIF_ProgramNameLocalFund',
    ECIF_Supplier_Type: 'Custom.ECIF_Supplier_Type',
    ECIF_WorkType: 'Custom.ECIF_WorkType',
    ECIF_DeliveryStartDate: 'Custom.ECIF_DeliveryStartDate',
    ECIF_DeliveryEndDate: 'Custom.ECIF_DeliveryEndDate',
    ECIF_SolutionApproach: 'Custom.ECIF_SolutionApproach',
    ECIF_SupplierID: 'Custom.ECIF_SupplierID',
    ECIF_SupplierContactName: 'Custom.ECIF_SupplierContactName',
    ECIF_SupplierContactEmail: 'Custom.ECIF_SupplierContactEmail',
    ECIF_DeliveryServiceManager: 'Custom.ECIF_DeliveryServiceManager',
    ECIF_ACOCreditRequestAmount: 'Custom.ECIF_ACOCreditRequestAmount',
    ECIF_ACODeliveryStartDate: 'Custom.ECIF_ACODeliveryStartDate',
    ECIF_RequestReason: 'Custom.ECIF_RequestReason',
    ECIF_DealStructure: 'Custom.ECIF_DealStructure',
    ECIF_ACORequestJustification: 'Custom.ECIF_ACORequestJustification',
    ECIF_ApprovedBy_RejectedBy: 'Custom.ECIF_ApprovedBy_RejectedBy',
    ECIF_Approved_Rejected: 'Custom.ECIF_Approved_Rejected',
    ECIF_ApprovedDate_RejectedDate: 'Custom.ECIF_ApprovedDate_RejectedDate',
    ECIF_WorkscopeId: 'Custom.ECIF_WorkscopeId',
    ECIF_WorkscopeUrl: 'Custom.ECIF_WorkscopeUrl',
    ECIF_Other_ProgramName: 'Custom.ECIF_Other_ProgramName',
    EstBilledCloseDate: 'Custom.EstBilledCloseDate',
    EstConsCompletionDate: 'Custom.EstConsCompletionDate',
    EstConsStartDate: 'Custom.EstConsStartDate',
    MaaS_RequestType: 'Custom.MaaS_Request_Type',
    MaaS_IsInterestedDHAP: 'Custom.MaaS_IsInterestedDefinitiveHealthcareDataConcierge',
    MaaS_Industry: 'Custom.MaaS_Industry',
    MaaS_IsInterestedAccountProfile: 'Custom.MaaS_IsInterestedAccountProfile',
    MaaS_IsInterestedOrgChat: 'Custom.MaaS_IsInterestedOrgChat',
    MaaS_MCEM_Stage: 'Custom.MaaS_MCEM_Stage',
    MaaS_OU: 'Custom.MaaS_OU',
    MaaS_Segment: 'Custom.MaaS_Segment',
    MaaS_SubSegment: 'Custom.MaaS_SubSegment',
    MaaS_Marketer_Role: 'Custom.MaaS_Marketer_Role',
    MaaS_Seller_Role: 'Custom.MaaS_Seller_Role',
    MaaS_Role: 'Custom.MaaS_Role',
    MaaS_DocumentCreationDate: 'Custom.MaaS_DocumentCreationDate',
    MaaS_DocumentLink: 'Custom.MaaS_DocumentLink',
    MaaS_Area: 'Custom.MaaS_Area',
    MaaS_TechTargetexistingAccount: 'Custom.MaaS_TechTargetexistingAccount',
    MaaS_UpdateExistingAccount: 'Custom.MaaS_UpdateExistingAccount',
    MaaS_ReplacemyExistingList: 'Custom.MaaS_ReplacemyExistingList',
    MaaS_SpecificCountry: 'Custom.MaaS_SpecificCountry',
    MaaS_AdditionalComments: 'Custom.MaaS_AdditionalComments',
    MaaS_AccountCRMID: 'Custom.MaaS_AccountCRMID',
    MaaS_CustomerWebURL: 'Custom.MaaS_CustomerWebURL',
    MaaS_SpecificNameForBDM: 'Custom.MaaS_SpecificNameForBDM',
    MaaS_SpecificTitleForBDM: 'Custom.MaaS_SpecificTitleForBDM',
    MaaS_tpids: 'Custom.MaaS_tpids',
    MaaS_AccountName: 'Custom.MaaS_AccountName',
    MaaS_ChildAccount: 'Custom.MaaS_ChildAccount',
    MaaS_EventName: 'Custom.MaaS_EventName',
    MaaS_EventType: 'Custom.MaaS_EventType',
    MaaS_EventDate: 'Custom.MaaS_EventDate',
    MaaS_EventRegistrationURL: 'Custom.MaaS_EventRegistrationURL',
    MaaS_MarketingPlay: 'Custom.MaaS_MarketingPlay',
    MaaS_SMPPApprovalID: 'Custom.MaaS_SMPPApprovalID',
    MaaS_DataCaptureDate: 'Custom.MaaS_DataCaptureDate',
    MaaS_LeadSourceType: 'Custom.MaaS_LeadSourceType',
    MaaS_IsInterestedDefinitiveHealthcareDataConcierge:
      'Custom.MaaS_IsInterestedDefinitiveHealthcareDataConcierge',
    State_InProgress: 'In Progress',
    State_Dispatched: 'Dispatched',
    State_OnHold: 'On Hold',
    State_Done: 'Done',
    State_Removed: 'Removed',
    SubState_Noactionrequired: 'No Action Required',
    SubState_New: 'New',
    SubState_Resolved: 'Resolved',
    SubState_NA: 'N/A',
    SubState_Reopened: 'Re-opened',
    MaaS_EventContactListpassword: 'Custom.MaaS_EventContactListPassword',
    MaaS_PrivacyApprovalEmail: 'Custom.MaaS_PrivacyApprovalEmail',
    MaaS_EventSponsorshipAgreement: 'Custom.MaaS_EventSponsorshipAgreement',
    Maas_Privacy_Approval_Email: 'Privacy approval email',
    Maas_Event_Sponsorship_Agreement: 'Event sponsorship agreement',
    RevenueImpact: 'Custom.RevenueImpact',
    MaccImpact: 'Custom.MACCImpact',
    MaccDuration: 'Custom.MACCDuration',
    //Partner Co-Marketing
    PMYT_Areas: 'Custom.PMYT_Areas',
    PMYT_CampaignName: 'Custom.PMYT_CampaignName',
    PMYT_CoInvestmentAmount: 'Custom.PMYT_CoInvestmentAmount',
    PMYT_SourceOfInvestment: 'Custom.PMYT_SourceOfInvestment',
    PMYT_InvestmentAmount: 'Custom.PMYT_InvestmentAmount',
    PMYT_SolutionPlays: 'Custom.PMYT_SolutionPlays',
    PMYT_Industries: 'Custom.PMYT_Industries',
    PMYT_EstimatedStartDate: 'Custom.PMYT_EstimatedStartDate',
    PMYT_EstimatedPipelineRevenue: 'Custom.PMYT_EstimatedPipelineRevenue',
    PMYT_PartnerType: 'Custom.PMYT_PartnerType',
    PMYT_CustomerSegment: 'Custom.PMYT_CustomerSegment',
    PMYT_EstimatedCosellReferrals: 'Custom.PMYT_EstimatedCosellReferrals',
    // GSI Fields
    GSI_Title: 'Custom.GSI_Title',
    GSI_AssignedTo: 'Custom.GSI_AssignedTo',
    GSI_Requestors: 'Custom.GSI_Requestors',
    GSI_AreaPath: 'Custom.GSI_AreaPath',
    GSI_ActionCategoryField: 'Custom.GSI_ActionCategoryField',
    GSI_AreaField: 'Custom.GSI_AreaField',
    GSI_EOU: 'Custom.GSI_EOU',
    GSI_State: 'Custom.GSI_State',
    GSI_SubState: 'Custom.GSI_SubState',
    GSI_TPID: 'Custom.GSI_TPID',
    GSI_PartnerOneId: 'Custom.GSI_PartnerOneId',
    GSI_PartnerOneName: 'Custom.GSI_PartnerOneName',
    GSI_BudgetProgram: 'Custom.GSI_BudgetProgram',
    GSI_CreditAmountUSD: 'Custom.GSI_CreditAmountUSD',
    GSI_CreditStartDate: 'Custom.GSI_CreditStartDate',
    GSI_EnrollmentId: 'Custom.GSI_enrollmentid',
    GSI_PartnerType: 'Custom.GSI_PartnerType',
    GSI_DealStructure: 'Custom.GSI_dealstructure',
    GSI_CommercialExecutiveCE: 'Custom.GSI_commercialexecutivece',
    GSI_Emails: 'Custom.GSI_Emails',
    GSI_RequestReason: 'Custom.GSI_requestreason',
    GSI_Approver: 'Custom.GSI_approver',
    GSI_BusinessJustification: 'Custom.GSI_BusinessJustification',
    GSI_StatusUpdate: 'Custom.GSI_statusupdate',
    GSI_CreatedDate: 'Custom.GSI_createddate',
    GSI_CreatedBy: 'Custom.GSI_createdby',
    GSI_UserChangedBy: 'Custom.GSI_UserChangedBy',
    GSI_RaidRelations: 'Custom.GSI_RaidRelations',
    // CoSell
    CoSellSubCategoryTopic: 'Custom.CoSell_SubCategoryTopic',
    CoSellActivity: 'Custom.CoSell_Activity',
    CoSellPartnerRegion: 'Custom.CoSell_PartnerRegion',
    CoSellPartnerCountry: 'Custom.CoSell_PartnerCountry',
    CoSellPartnerArea: 'Custom.CoSell_PartnerArea',
    CoSellDeliveryLanguageSupport: 'Custom.CoSell_DeliveryLanguageSupport',
    CoSellPartnerEngaged: 'Custom.CoSell_PartnerEngaged',
    CoSellMCEMSalesStage: 'Custom.CoSell_MCEMSalesStage',
    CoSellDealStatus: 'Custom.CoSell_DealStatus',
    CoSellPartnerContactName: 'Custom.CoSell_PartnerContactName',
    CoSellPartnerEmail: 'Custom.CoSell_PartnerEmail',
    CoSellReferralID: 'Custom.CoSell_ReferralID',
    CoSellDealValue: 'Custom.CoSell_DealValue',
    CoSellDealCreatedDate: 'Custom.CoSell_DealCreatedDate',
    CoSellDateAccepted: 'Custom.CoSell_DateAccepted',
    CoSellDateExpired: 'Custom.CoSell_DateExpired',
    CoSellExpectedExpirationDate: 'Custom.CoSell_ExpectedExpirationDate',
    CoSellMicrosoftCoOwner: 'Custom.CoSell_MicrosoftCoOwner',
    CoSellRequestCategory: 'Custom.CoSell_RequestCategory',
    CoSellMicrosoftOwner: 'Custom.CoSell_MicrosoftOwner',
    CoSellAssignmentDate: 'Custom.CoSell_AssignmentDate',
    MaccAeAlias: 'MACC_AE_Alias',
    MaccCeAlias: 'MACC_CE_Alias',
    MaccSSSPAlias: 'MACC_SSSP_Alias',
    RAID_Type: 'Custom.RAID_Type',
    Raid_Priority: 'Custom.Raid_Priority',
    RAID_Status: 'Custom.RAID_Status',
    Raid_ResolutionPlan: 'Custom.Raid_ResolutionPlan',
    Raid_ResolutionProgress: 'Custom.Raid_ResolutionProgress',
    Raid_DateOpened: 'Custom.Raid_DateOpened',
    Raid_DateClosed: 'Custom.Raid_DateClosed',
    Raid_RelatedItems: 'Custom.Raid_RelatedItems',
    Raid_RelatedItemID: 'Custom.Raid_RelatedItemID',
    Raid_RelatedItemTitle: 'Custom.Raid_RelatedItemTitle',
    Raid_RelatedItemStatus: 'Custom.Raid_RelatedItemStatus',
    Raid_RelatedItemType: 'Custom.Raid_RelatedItemType',
    //GPS
    GPS_AcknowledgementforReadingTheGovernanceFramework:
      'Custom.GPS_AcknowledgementforReadingTheGovernanceFramework',
    GPS_PIRReviewDate: 'Custom.GPS_PIRReviewDate',
    GPS_DiscussionType: 'Custom.GPS_DiscussionType',
    GPS_RequestType: 'Custom.GPS_RequestType',
    GPS_Business: 'Custom.GPS_Business',
    GPS_DealEconomics: 'Custom.GPS_DealEconomics',
    GPS_Product: 'Custom.GPS_Product',
    GPS_AskType: 'Custom.GPS_AskType',
    GPS_CustomerSegment: 'Custom.GPS_CustomerSegment',
    GPS_PartnerOneID: 'Custom.GPS_PartnerOneID',
    GPS_PartnerOneName: 'Custom.GPS_PartnerOneName',
    GPS_Areas: 'Custom.GPS_Areas',
    GPS_StartDate: 'Custom.GPS_StartDate',
    GPS_EndDate: 'Custom.GPS_EndDate',
    GPS_InvestmentType: 'Custom.GPS_InvestmentType',
    GPS_EngagementType: 'Custom.GPS_EngagementType',
    GPS_PayoutType: 'Custom.GPS_PayoutType',
    GPS_InvestmentAmount: 'Custom.GPS_InvestmentAmount',
    GPS_InvestmentDescription: 'Custom.GPS_InvestmentDescription',
    GPS_Stage: 'Custom.GPS_Stage',
    GPS_MigratedDate: 'Custom.GPS_MigratedDate',
    GPS_InvestmentComplexity: 'Custom.GPS_InvestmentComplexity',
    GPS_StrategyLead: 'Custom.GPS_StrategyLead',
    GPS_EscalationDate: 'Custom.GPS_EscalationDate',
    GPS_ExecutionPathQubitID: 'Custom.GPS_ExecutionPathQubitID',
    GPS_ContractSignDate: 'Custom.GPS_ContractSignDate',
    GPS_PartnerType: 'Custom.GPS_PartnerType',
    GPS_MCEMStagetext: 'Custom.GPS_MCEMStagetext',
    GPS_DueDate: 'Custom.GPS_DueDate',
    GPS_PolicyDeclaration: 'Custom.GPS_PolicyDeclaration',

    GPS_BusinessCaseSubmitted: 'Custom.GPS_BusinessCaseSubmitted',
    GPS_PIRReviewScheduled: 'Custom.GPS_PIRReviewScheduled',
    GPS_FinancialModelSubmitted: 'Custom.GPS_FinancialModelSubmitted',
    GPS_OpsIntakeRequired: 'Custom.GPS_OpsIntakeRequired',
    GPS_OpsIntakeMeetingDate: 'Custom.GPS_OpsIntakeMeetingDate',
    GPS_OpsIntakeComplete: 'Custom.GPS_OpsIntakeComplete',
    GPS_NextStepsCompleted: 'Custom.GPS_NextStepsCompleted',
    GPS_ApprovedInQubit: 'Custom.GPS_ApprovedInQubit',

    GPS_PIRDecisionDate: 'Custom.GPS_PIRDecisionDate',
    GPS_PIRManager: 'Custom.GPS_PIRManager',
    GPS_PIRDecision: 'Custom.GPS_PIRDecision',
    GPS_PIRManagerNextStepsExceptionsNotes: 'Custom.GPS_PIRManagerNextStepsExceptionsNotes',

    GPS_OperationsApproval: 'Custom.GPS_OperationsApproval',
    GPS_OperationsPIRStakeholderName: 'Custom.GPS_OperationsPIRStakeholderName',
    GPS_OperationsNextStepsExceptionsNotes: 'Custom.GPS_OperationsNextStepsExceptionsNotes',
    GPS_DraftContract: 'Custom.GPS_DraftContracts',

    GPS_CELAReview: 'Custom.GPS_CELAReview',
    GPS_CELAPIRStakeholderName: 'Custom.GPS_CELAPIRStakeholderName',
    GPS_CELANextStepsExceptionsNotes: 'Custom.GPS_CELANextStepsExceptionsNotes',

    GPS_ComplianceReview: 'Custom.GPS_ComplianceReview',
    GPS_CompliancePIRStakeholder: 'Custom.GPS_CompliancePIRStakeholder',
    GPS_ComplianceNextStepsExceptionsNotes: 'Custom.GPS_ComplianceNextStepsExceptionsNotes',

    GPS_CorpAccountingReview: 'Custom.GPS_CorpAccountingApproval',
    GPS_CorpAccountingPIRStakeholder: 'Custom.GPS_CorpAccountingPIRStakeholder',
    GPS_CorpAccountingNextStepsExceptionsNotes: 'Custom.GPS_CorpAccountingNextStepsExceptionsNotes',
    GPS_ExpectedIncrementalRevenue: 'Custom.GPS_ExpectedIncrementalRevenue',
    GPS_FinanceApproval: 'Custom.GPS_FinanceApproval',
    GPS_FinancePIRStakeholder: 'Custom.GPS_FinancePIRStakeholder',
    GPS_FinanceNextStepsExceptionsNotes: 'Custom.GPS_FinanceNextStepsExceptionsNotes',
    GPS_FundingSource: 'Custom.GPS_FundingSource',
    GPS_FundingType: 'Custom.GPS_FundingType',
    GPS_RevAdjust: 'Custom.GPS_RevAdjust',
    GPS_Opex: 'Custom.GPS_Opex',
    GPS_ACO: 'Custom.GPS_ACO',
    GPS_ECIF: 'Custom.GPS_ECIF',
    GPS_Sponsorship: 'Custom.GPS_Sponsorship',
    GPS_Skilling: 'Custom.GPS_Skilling',
    GPS_InternalResources: 'Custom.GPS_InternalResources',
    GPS_BusinessCase: 'Custom.GPS_BusinessCase',
    GPS_FinancialModel: 'Custom.GPS_FinancialModel',
    GPS_PartnerList: 'Custom.GPS_PartnerList',
    GPS_ControlList: 'Custom.GPS_ControlList',
    GPS_Notes: 'Custom.GPS_Notes',
    GPS_ComplianceandOngoingMonitoring: 'Custom.GPS_ComplianceandOngoingMonitoring',
    GPS_PIR_CASEID: 'Custom.GPS_PIR_CASEID',
    GPS_MCEMStages: 'Custom.GPS_MCEMStages',
    GPS_StrategyApproval: 'Custom.GPS_StrategyApproval',
    GPS_StrategyStakeholderName: 'Custom.GPS_StrategyStakeholderName',
    GPS_StrategyNextSteps: 'Custom.GPS_StrategyNextSteps',
    GPS_FMVAssessmentApproval: 'Custom.GPS_FMVAssessmentApproval',
    GPS_OtherAmount: 'Custom.GPS_OtherAmount',
    GPS_LaunchManagerName: 'Custom.GPS_LaunchManagerName',
    GPS_GLPCCode: 'Custom.GPS_GLPCCode',
    GPS_SAPGLCode: 'Custom.GPS_SAPGLCode',
    GPS_GLCode: 'Custom.GPS_GLCode',
    GPS_PCCode: 'Custom.GPS_PCCode',
    GPS_PIRManagerChangeStatus: 'Custom.GPS_PIRManagerChangeStatus',
    GPS_RelatedCaseID: 'Custom.GPS_RelatedCaseID',
    GPS_ChangeRequestType: 'Custom.GPS_ChangeRequestType',
    GPS_FinancialImpact: 'Custom.GPS_FinancialImpact',
    GPS_IsIncentiveTargetingSuite1: 'Custom.GPS_IsIncentiveTargetingSuite1',
    GPS_FMVAssessmentNeeded: 'Custom.GPS_FMVAssessmentNeeded',
    GPS_IsSignificantDesignChange: 'Custom.GPS_IsSignificantDesignChange',
    GPS_IsStateOwnedPartner: 'Custom.GPS_IsStateOwnedPartner',
    GPS_ACVettingStatusfromSOECheckAttach: 'Custom.GPS_ACVettingStatusfromSOECheckAttach',
    GPS_IsTier1HighRiskCountry: 'Custom.GPS_IsTier1HighRiskCountry',
    GPS_IsLSPCSPDistiorECIFSupplier: 'Custom.gps_IsLSPCSPDistiorECIFSupplier',
    GPS_IsNewPayment: 'Custom.GPS_IsNewPayment',
    GPS_IsFinancialDataUsage: 'Custom.GPS_IsFinancialDataUsage',
    GPS_CalcPartnerIdType: 'Custom.GPS_CalcPartnerIdType',
    GPS_CalcPartnerId: 'Custom.GPS_CalcPartnerId',
    GPS_PartnerPaymentLocationId: 'Custom.GPS_PartnerPaymentLocationId',
    GPS_CalcDataSource: 'Custom.GPS_CalcDataSource',
    GPS_DataSourceOwner: 'Custom.GPS_DataSourceOwner',
    GPS_CalcOwner: 'Custom.GPS_CalcOwner',
    GPS_IsCalcFileCompleted: 'Custom.GPS_IsCalcFileCompleted',
    GPS_ADOId: 'Custom.GPS_ADOId',
    GPS_PaymentLever: 'Custom.GPS_PaymentLever',
    GPS_PaymentProgram: 'Custom.GPS_PaymentProgram',
    GPS_EarningType: 'Custom.GPS_EarningType',
    GPS_PaymentMethod: 'Custom.GPS_PaymentMethod',
    GPS_PaymentFrequency: 'Custom.GPS_PaymentFrequency',
    GPS_FinalPaymentMilestone: 'Custom.GPS_FinalPaymentMilestone',
    GPS_PartnerTransactingType: 'Custom.GPS_Calc_PartnerType',
    GPS_AccountingTreatment: 'Custom.GPS_AccountingTreatment',
    GPS_NumberOfEngagementsImpacted: 'Custom.GPS_NumberOfEngagementsImpacted',
    GPS_EFRApprovedDate: 'Custom.GPS_EFRApprovedDate',
    GPS_CalcReviewNeeded: 'Custom.GPS_CalcReviewNeeded',
    GPS_CalcReviewDate: 'Custom.GPS_CalcReviewDate',
    GPS_CalcReviewCompleted: 'Custom.GPS_CalcReviewCompleted',
    GPS_TCType: 'Custom.GPS_TCType',
    GPS_PartnerContactInformation: 'Custom.GPS_PartnerContactInformation',
    GPS_ComplexityLevel: 'Custom.GPS_ComplexityLevel',
    GPS_ComplexityDrivers: 'Custom.GPS_ComplexityDrivers',
    GPS_ExecutionGovernance: 'Custom.GPS_ExecutionGovernance',

    // Launch Checklist Fields
    GPS_custom_calc: 'Custom.GPS_custom_calc',
    GPS_newdatasource: 'Custom.GPS_newdatasource',
    GPS_new_poe: 'Custom.GPS_new_poe',
    GPS_partner_support: 'Custom.GPS_partner_support',
    GPS_event_mgmt: 'Custom.GPS_event_mgmt',
    GPS_newpc_mci_cap: 'Custom.GPS_newpc_mci_cap',
    GPS_new_consent: 'Custom.GPS_new_consent',
    GPS_field_nomination: 'Custom.GPS_field_nomination',
    GPS_custom_eligibility: 'Custom.GPS_custom_eligibility',
    GPS_pc_reporting: 'Custom.GPS_pc_reporting',
    GPS_internal_reporting: 'Custom.GPS_internal_reporting',
    GPS_perf_metric: 'Custom.GPS_perf_metric',
    GPS_mci_policy_update: 'Custom.GPS_mci_policy_update',
    GPS_pipadointegrations: 'Custom.GPS_pipadointegrations',

    // Survey Questions Fields
    GPS_surveyrequestitle: 'Custom.gps_surveyrequestitle',
    GPS_surveycontentowner: 'Custom.gps_surveycontentowner',
    GPS_surveyurgency: 'Custom.gps_surveyurgency',
    GPS_surveytype: 'Custom.gps_surveytype',
    GPS_surveyaudience: 'Custom.gps_surveyaudience',
    GPS_surveydescription: 'Custom.gps_surveydescription',
    GPS_surveyworkshopcode: 'Custom.gps_surveyworkshopcode',
    GPS_isalreadyexistsinlocalizedcontentlibrary:
      'Custom.gps_isalreadyexistsinlocalizedcontentlibrary',
    GPS_surveydisplayname: 'Custom.gps_surveydisplayname',
    GPS_surveyrequestedlaunchdate: 'Custom.gps_surveyrequestedlaunchdate',

    // Field/BG Business Approval
    GPS_FieldBGBusinessApprovalDocName: 'Custom.GPS_FieldBGBusinessApprovalDocName',
    GPS_FieldBGBusinessApprovalDocUrl: 'Custom.GPS_FieldBGBusinessApprovalDocUrl',
    GPS_FieldBGBusinessApproval: 'Custom.GPS_FieldBGBusinessApproval',
    GPS_FieldBGBusinessApprovalDate: 'Custom.GPS_FieldBGBusinessApprovalDate',
    GPS_FieldBGBusinessApprovalUploadedDate: 'Custom.GPS_FieldBGBusinessApprovalUploadedDate',
    GPS_FieldBGBusinessApprovalName: 'Custom.GPS_FieldBGBusinessApprovalName',

    // Field/BG Finance Approval
    GPS_FieldBGFinanceApprovalDocName: 'Custom.GPS_FieldBGFinanceApprovalDocName',
    GPS_FieldBGFinanceApprovalDocUrl: 'Custom.GPS_FieldBGFinanceApprovalDocUrl',
    GPS_FieldBGFinanceApproval: 'Custom.GPS_FieldBGFinanceApproval',
    GPS_FieldBGFinanceApprovalDate: 'Custom.GPS_FieldBGFinanceApprovalDate',
    GPS_FieldBGFinanceApprovalUploadedDate: 'Custom.GPS_FieldBGFinanceApprovalUploadedDate',
    GPS_FieldBGFinanceApprovalName: 'Custom.GPS_FieldBGFinanceApprovalName',

    // LT Business Approval
    GPS_FieldLTBusinessApprovalAttach: 'Custom.GPS_FieldLTBusinessApprovalAttach',
    GPS_FieldLTBusinessApprovalDocName: 'Custom.GPS_FieldLTBusinessApprovalDocName',
    GPS_FieldLTBusinessApprovalDocUrl: 'Custom.GPS_FieldLTBusinessApprovalDocUrl',
    GPS_FieldLTBusinessApproval: 'Custom.GPS_FieldLTBusinessApproval',
    GPS_FieldLTBusinessApprovalDate: 'Custom.GPS_FieldLTBusinessApprovalDate',
    GPS_FieldLTBusinessApprovalUploadedDate: 'Custom.GPS_FieldLTBusinessApprovalUploadedDate',
    GPS_FieldLTBusinessApprovalName: 'Custom.GPS_FieldLTBusinessApprovalName',

    // LT Finance Approval (for Corp funded)
    GPS_FieldLTFinancialApprovalAttach: 'Custom.GPS_FieldLTFinancialApprovalAttach',
    GPS_FieldLTFinancialApprovalDocName: 'Custom.GPS_FieldLTFinancialApprovalDocName',
    GPS_FieldLTFinancialApprovalDocUrl: 'Custom.GPS_FieldLTFinancialApprovalDocUrl',
    GPS_FieldLTFinancialApproval: 'Custom.GPS_FieldLTFinancialApproval',
    GPS_FieldLTFinancialApprovalDate: 'Custom.GPS_FieldLTFinancialApprovalDate',
    GPS_FieldLTFinancialApprovalUploadedDate: 'Custom.GPS_FieldLTFinancialApprovalUploadedDate',
    GPS_FieldLTFinancialApprovalName: 'Custom.GPS_FieldLTFinancialApprovalName',

    // Finance Approval - Document properties
    GPS_FinanceApprovalDocName: 'Custom.GPS_FinanceApprovalDocName',
    GPS_FinanceApprovalDocUrl: 'Custom.GPS_FinanceApprovalDocUrl',
    GPS_FinanceApprovalDate: 'Custom.GPS_FinanceApprovalDate',
    GPS_FinanceApprovalUploadedDate: 'Custom.GPS_FinanceApprovalUploadedDate',

    // Strategy Approval - Document properties
    GPS_StrategyApprovalAttach: 'Custom.GPS_StrategyApprovalAttach',
    GPS_StrategyApprovalDocName: 'Custom.GPS_StrategyApprovalDocName',
    GPS_StrategyApprovalDocUrl: 'Custom.GPS_StrategyApprovalDocUrl',
    GPS_StrategyApprovalDate: 'Custom.GPS_StrategyApprovalDate',
    GPS_StrategyApprovalUploadedDate: 'Custom.GPS_StrategyApprovalUploadedDate',

    // Operations Approval - Document properties
    GPS_OperationsApprovalAttach: 'Custom.GPS_OperationsApprovalAttach',
    GPS_OperationsApprovalDocName: 'Custom.GPS_OperationsApprovalDocName',
    GPS_OperationsApprovalDocUrl: 'Custom.GPS_OperationsApprovalDocUrl',
    GPS_OperationsApprovalDate: 'Custom.GPS_OperationsApprovalDate',
    GPS_OperationsApprovalUploadedDate: 'Custom.GPS_OperationsApprovalUploadedDate',

    // Corp Accounting Approval - Document properties
    GPS_CorpAccountingApprovalDocName: 'Custom.GPS_CorpAccountingApprovalDocName',
    GPS_CorpAccountingApprovalDocUrl: 'Custom.GPS_CorpAccountingApprovalDocUrl',
    GPS_CorpAccountingApprovalDate: 'Custom.GPS_CorpAccountingApprovalDate',
    GPS_CorpAccountingApprovalUploadedDate: 'Custom.GPS_CorpAccountingApprovalUploadedDate',

    // Compliance Review
    GPS_ComplianceReviewDocName: 'Custom.GPS_ComplianceReviewDocName',
    GPS_ComplianceReviewDocUrl: 'Custom.GPS_ComplianceReviewDocUrl',
    GPS_ComplianceReviewDate: 'Custom.GPS_ComplianceReviewDate',
    GPS_ComplianceReviewUploadedDate: 'Custom.GPS_ComplianceReviewUploadedDate',

    // CELA Review
    GPS_CELAReviewDocName: 'Custom.GPS_CELAReviewDocName',
    GPS_CELAReviewDocUrl: 'Custom.GPS_CELAReviewDocUrl',
    GPS_CELAReviewDate: 'Custom.GPS_CELAReviewDate',
    GPS_CELAReviewUploadedDate: 'Custom.GPS_CELAReviewUploadedDate',

    // EEA Strategy Approval - Document properties
    GPS_EEAStrategyApprovalAttach: 'Custom.GPS_EEAStrategyApprovalAttach',
    GPS_EEAStrategyApproverDocName: 'Custom.GPS_EEAStrategyApprovalDocName',
    GPS_EEAStrategyApproverDocUrl: 'Custom.GPS_EEAStrategyApprovalDocUrl',
    GPS_EEAStrategyApproverDate: 'Custom.GPS_EEAStrategyApprovalDate',
    GPS_EEAStrategyApproverUploadedDate: 'Custom.GPS_EEAStrategyApprovalUploadedDate',
    GPS_EEAStrategyApprover: 'Custom.GPS_EEAStrategyApprover',

    // EEA Finance Approval - Document properties
    GPS_EEAFinanceApprovalAttach: 'Custom.GPS_EEAFinanceApprovalAttach',
    GPS_EEAFinanceApproverDocName: 'Custom.GPS_EEAFinanceApprovalDocName',
    GPS_EEAFinanceApproverDocUrl: 'Custom.GPS_EEAFinanceApprovalDocUrl',
    GPS_EEAFinanceApproverDate: 'Custom.GPS_EEAFinanceApprovalDate',
    GPS_EEAFinanceApproverUploadedDate: 'Custom.GPS_EEAFinanceApprovalUploadedDate',
    GPS_EEAFinanceApprover: 'Custom.EEAFinanceApprover',

    //AMM
    AMM_NextBestAction: 'Custom.AMM_NextBestAction',
    AMM_NextBestActionDetails: 'Custom.AMM_NextBestActionDetails',
    AMM_ActionTaken: 'Custom.AMM_ActionTaken',
    AMM_ActionTakenDetails: 'Custom.AMM_ActionTakenDetails',
    AMM_CancellationReason: 'Custom.AMM_CancellationReason',
    AMM_CancellationAdditionalDetails: 'Custom.AMM_CancellationAdditionalDetails',
    AMM_RejectionReason: 'Custom.AMM_RejectionReason',
    AMM_RejectionAdditionalDetails: 'Custom.AMM_RejectionAdditionalDetails',
    AMM_DisqualificationReason: 'Custom.AMM_DisqualificationReason',
    AMM_DisqualificationAdditionalDetails: 'Custom.AMM_DisqualificationAdditionalReason',
    AMM_Offer: 'Custom.AMM_Offer',
    AMM_RequestType: 'Custom.AMM_RequestType',
    AMM_CaseCreatedDateTime: 'Custom.AMM_CaseCreatedDateTime',
    AMM_PendingActionFrom: 'Custom.AMM_PendingActionFrom',
    AMM_OfferEngineStatus: 'Custom.AMM_OfferEngineStatus',
    AMM_OriginatingCaseID: 'Custom.AMM_OriginatingCaseID',
    AMM_ProgramFunding: 'Custom.AMM_ProgramFunding',
    AMM_AAOfferType: 'Custom.AMM_AAOferType',
    AMM_SponsorshipAmount: 'Custom.AMM_SponsorshipAmount',
    AMM_CaseDuration: 'Custom.AMM_CaseDuration',
    AMM_SLARemainingTime: 'Custom.AMM_SLARemainingTime',
    AMM_CaseNotes: 'Custom.AMM_CaseNotes',
    AMM_ProjectSize: 'Custom.AMM_ProjectSize',
    AMM_TotalACR: 'Custom.AMM_TotalACR',
    AMM_PreOrPost: 'Custom.AMM_PreOrPost',
    AMM_RequestJustification: 'Custom.AMM_RequestJustification',
    AMM_MotivationForAssessment: 'Custom.AMM_MotivationForAssessment',
    AMM_CurrentEnvironmentAndTargetOutcomes: 'Custom.AMM_CurrentEnvironmentAndTargetOutcomes',
    AMM_EstimatedEnvironmentSize: 'Custom.AMM_EstimatedEnvironmentSize',
    AMM_ExpectedProjectStartDate: 'Custom.AMM_ExpectedProjectStartDate',
    AMM_ExpectedProjectEndDate: 'Custom.AMM_ExpectedProjectEndDate',
    AMM_AccountOwnerID: 'Custom.AMM_AccountOwnerID',
    AMM_TermsandConditionEmail: 'Custom.AMM_TermsandConditionEmail',
    AMM_SupportRegion: 'Custom.AMM_SupportRegion',
    AMM_MicrosoftCCRecipient: 'Custom.AMM_MicrosoftCCRecipient',
    AMM_CustomerCCList: 'Custom.AMM_CustomerCCList',
    AMM_needawaveplan: 'Custom.AMM_needawaveplan',
    AMM_RequesterArea: 'Custom.AMM_RequesterArea',
    AMM_DeliveryPartnerEligibility: 'Custom.AMM_DeliveryPartnerEligibility',
    AMM_TotalForOfferEligibleACR: 'Custom.AMM_TotalForOfferEligibleACR',
    AMM_EnrollmentNumber: 'Custom.AMM_EnrollmentNoOrBillingAcID',
    AMM_MigrationorModernization: 'Custom.AMM_IsThisAssessmentMeantToAddressAppMigration',
    AMM_SolutionAssessmentType: 'Custom.AMM_SolutionAssessmentType',
    AMM_MigrationPartnerAttached: 'Custom.AMM_MigrationPartnerAttached',
    AMM_ScopeOfWork: 'Custom.AMM_ScopeOfWork',
    // AMM_Doyouhavecustomercommitmenttostartmigrationinnext3to5weeks:
    //   'Custom.AMM_doyouhavecustomercommitmenttostartmigrationinnext3to5weeks',
    AMM_CustomerCommitment: 'Custom.AMM_CustomerCommitment',
    AMM_CSAEmailAddress: 'Custom.AMM_CSAEmailAddress',
    AMM_CSA2EmailAddress: 'Custom.AMM_CSA2EmailAddress',
    AMM_CSA3EmailAddress: 'Custom.AMM_CSA3EmailAddress',
    AMM_AEATSEmailAddress: 'Custom.AMM_AEATSEmailAddress',
    AMM_SpecialistEmailAddress: 'Custom.AMM_SpecialistEmailAddress',
    AMM_CSAMName: 'Custom.AMM_CSAMName',
    AMM_CSASecurity: 'Custom.AMM_CSASecurity',
    AMM_TermsAndConditionEmailID: 'Custom.AMM_TermsAndConditionEmailID',
    AMM_CommunicationPreference: 'Custom.AMM_CommunicationPreference',
    AMM_CustomerContactName: 'Custom.AMM_CustomerContactName',
    AMM_CustomerContactEmail: 'Custom.AMM_CustomerContactEmail',
    AMM_CustomerAcceptedTheAssessment: 'Custom.AMM_CustomerAcceptedTheAssessment',
    Amm_Migrationpartnerattached: 'Custom.AMM_MigrationPartnerAttached',
    AMM_MigrationPartner: 'Custom.AMM_MigrationPartner',
    AMM_MigrationPartnerEmail: 'Custom.AMM_MigrationPartnerEmail',
    AMM_AEAlias: 'Custom.AMM_AEAlias',
    AMM_ATSAlias: 'Custom.AMM_ATSAlias',
    AMM_SSTSAlias: 'Custom.AMM_SSOrTSAlias',
    AMM_SecuritySSPAlias: 'Custom.AMM_SecuritySSPAlias',
    AMM_AppSSPAlias: 'Custom.AMM_AppSSPAlias',
    AMM_HelpCategory: 'Custom.AMM_HelpCategory',
    AMM_SubTitle: 'Custom.AMM_SubTitle',
    AMM_HelpDescription: 'Custom.AMM_HelpDescription',
    AMM_NeedhelpPitchingTheValue: 'Custom.AMM_NeedhelpPitchingTheValue',
    AMM_ServiceLocation: 'Custom.AMM_ServiceLocation',
    AMM_ACOID: 'Custom.AMM_ACOID',
    AMM_ACOCE: 'Custom.AMM_ACOCE',
    AMM_AcoCreditRequestAmount: 'Custom.AMM_AcoCreditRequestAmount',
    AMM_DealStructure: 'Custom.AMM_DealStructure',
    AMM_RequestReason: 'Custom.AMM_RequestReason',
    AMM_FutureGrowthOffer: 'Custom.AMM_FutureGrowthOffer',
    AMM_1stYearACR: 'Custom.AMM_1stYearACR',
    AMM_ECIFSpendPlan: 'Custom.AMM_ECIFSpendPlan',
    AMM_DoyouwanttorequestACOinadditiontoECIF: 'Custom.AMM_DoyouwanttorequestACOinadditiontoECIF',
    AMM_IsThereACompetitiveBid: 'Custom.AMM_IsThereACompetitiveBid',
    AMM_RequestAmount: 'Custom.AMM_RequestAmount',
    AMM_FTA: 'Custom.AMM_FTA',
    AMM_ESI: 'Custom.AMM_ESI',
    AMM_MicrosoftDefenderforCloud: 'Custom.AMM_MicrosoftDefenderforCloud',
    AMM_PrimarySolutionPlay: 'Custom.AMM_PrimarySolutionPlay',
    AMM_IncrementalRevenueROI: 'Custom.AMM_IncrementalRevenueROI',
    AMM_FinancialYear: 'Custom.AMM_FinancialYear',
    AMM_AAOfferType_EASponsorship: 'EA Sponsorship',
    AMM_AAOfferType_MCAEASponsorship: 'MCA-E Sponsorship',
    AMM_IsThisRequestRelated: 'Custom.AMM_IsThisRequestRelatedToPublicOrGovernmentOwnedEntity',
    AMM_DoesThisIncludeHighRiskDeal: 'Custom.AMM_DoesThisIncludeHighRiskDeal',
    AMM_SAPOffer: 'Custom.AMM_SAPOffer',
    MM_RIValuePerMonth: 'Custom.AMM_RIValuePerMonth',
    AMM_IsThereAnyExistingMACCOrCTCOrMC: 'Custom.AMM_IsThereAnyExistingMACCOrCTCOrMC',
    AMM_IsThereExistingACO: 'Custom.AMM_IsThereExistingACO',
    AMM_AVSOffer: 'Custom.AMM_AVSOffer',
    AMM_SSCACOOffer: 'Custom.AMM_SSCACOOffer',
    AMM_IsThisAVMCToAVSMigration: 'Custom.AMM_IsThisAVMCToAVSMigration',
    AMM_AVSRIRegion: 'Custom.AMM_AVSRIRegion',
    AMM_AVSRIQuantity: 'Custom.AMM_AVSRIQuantity',
    AMM_FutureGrowthECIF: 'Custom.AMM_FutureGrowthECIF',
    AMM_PRIMARYAzurevirtualmachineVMseries: 'Custom.AMM_PRIMARYAzurevirtualmachineVMseries',
    AMM_3YearACR: 'Custom.AMM_3YearACR',
    AMM_SponsoringCountry: 'Custom.AMM_SponsoringCountry',
    AMM_OfferEngineCaseID: 'Custom.AMM_OfferEngineCaseID',
    AMM_OfferEngineType: 'Custom.AMM_OfferEngineType',
    AMM_FirstYearROI: 'Custom.AMM_1stYearROI',
    AMM_ThirdYearROI: 'Custom.AMM_3rdYearROI',
    AMM_LinkWorkScope: 'Custom.AMM_LinkWorkScope',
    AMM_LicensingProgram: 'Custom.AMM_LicensingProgram',
    AMM_TypeOfFundingNeeded: 'Custom.AMM_TypeofFundingNeeded',
    AMM_SupportedScenario: 'Custom.AMM_SupportedScenario',
    AMM_Program: 'Custom.AMM_Program',
    AMM_WillThisRequestRequireAdditionalAttendees:
      'Custom.AMM_WillthisrequestrequireadditionalAttendees',
    AMM_AzureFutureGrowthOffer: 'Custom.AMM_AzureFutureGrowthOffer',
    AMM_ECIFFutureGrowthOffer: 'Custom.AMM_ECIFFutureGrowthOffer',
    AMM_SupplierType: 'Custom.AMM_SupplierType',
    AMM_IsSupplierSameEndCustomer: 'Custom.AMM_IsSupplierSameEndCustomer',
    AMM_HelpType: 'Custom.AMM_HelpType',
    AMM_AvsOfferRequestCorrespond: 'Custom.AMM_AvsOfferRequestCorrespond',
    AMM_CustomerPeakWorkload: 'Custom.AMM_CustomerPeakWorkload',
    AMM_ThirdPartySecuritySolution: 'Custom.AMM_3rdPartySecuritySolution',
    AMM_MoreDetailsOnSecurity: 'Custom.AMM_SecurityMigration',
    AMM_MSeriesConsumption: 'Custom.AMM_4SeriesConsumption',
    AMM_CMF_Milestones: 'Custom.AMM_CMF_Milestones',
    CMF_Result: 'Custom.CMF_Result',
    AMM_OfferDeskRegion: 'Custom.AMM_OfferDeskRegion',
    AMM_CheckCMFId: 'Custom.AMM_CheckCMFId',
    AMM_WhatAreTheVMsSizes: 'Custom.AMM_VMSizeDetails',
    AMM_HowManyVMsIsTheCustomerDeploying: 'Custom.AMM_VMDeploymentCount',
    AMM_ECIFOfferEngineType: 'Custom.AMM_ECIFOfferEngineType',
    AMM_ECIFOfferEngineStatus: 'Custom.AMM_ECIFOfferEngineStatus',
    AMM_ECIFOfferEngineCaseID: 'Custom.AMM_ECIFOfferEngineCaseID',
    AMM_AVSOfferEngineType: 'Custom.AMM_AVSOfferEngineType',
    AMM_AVSOfferEngineStatus: 'Custom.AMM_AVSOfferEngineStatus',
    AMM_AVSOfferEngineCaseID: 'Custom.AMM_AVSOfferEngineCaseID',
    AMM_CMFOfferEngineType: 'Custom.AMM_CMFOfferEngineType',
    AMM_CMFOfferEngineStatus: 'Custom.AMM_CMFOfferEngineStatus',
    AMM_CMFOfferEngineCaseID: 'Custom.AMM_CMFOfferEngineCaseID',
    AMM_LinuxDiscount: 'Custom.AMM_HasLinuxDiscountBeenProvided',
    AMM_IsPartnerInitiated: 'Custom.AMM_IsPartnerInitiated',
    AMM_Partner: 'Custom.AMM_Partner',
    AMM_GBBAlias: 'Custom.AMM_GBBAlias',
    AMM_OfferConsiderations: 'Custom.AMM_OfferConsiderations',
    AMM_PlannedDateofAssessment: 'Custom.AMM_PlannedDateofAssessment',
    AMM_PlannedDateofMigration: 'Custom.AMM_PlannedDateofMigration',
    AMM_DoyouhavetheRVToolsExport: 'Custom.AMM_DoYouHaveTheRVToolsExport',
    AMM_FieldEmpowermentDiscount: 'Custom.AMM_FieldEmpowermentDiscount',
    AMM_AzureRegion: 'Custom.AMM_AzureRegion',
    AMM_AlreadyRunningAtleast1AVSClusterInTheTargetRegion:
      'Custom.AMM_AlreadyRunningAtleast1AVSClusterInTheTargetRegion',
    AMM_NodeType: 'Custom.AMM_NodeType',
    AMM_ReservedInstances: 'Custom.AMM_ReservedInstances',
    AMM_CPUUtilization: 'Custom.AMM_CPUUtilization',
    AMM_MemoryUtilization: 'Custom.AMM_MemoryUtilization',
    AMM_CPUOverSubscription: 'Custom.AMM_CPUOverSubscription',
    AMM_MemoryOverCommitFactor: 'Custom.AMM_MemoryOvercommitFactor',
    AMM_FTTSettings: 'Custom.AMM_FTTSettings',
    AMM_DedupeAndCompressionFactor: 'Custom.AMM_DedupeAndCompressionFactor',
    AMM_Currency: 'Custom.AMM_Currency',
    AMM_MicrosoftSellerContactAccountExecutive: 'Custom.AMM_MicrosoftSellerContactAccountExecutive',
    AMM_CustomerName: 'Custom.AMM_CustomerName',
    AMM_CustomerContact: 'Custom.AMM_CustomerContact',
    AMM_IsAwareOfApprovedECIFSuppliers: 'Custom.AMM_IsAwareOfApprovedECIFSuppliers',
    AMM_WillNotStartWithoutApprovedPO: 'Custom.AMM_WillNotStartWithoutApprovedPO',
    AMM_IsECIFFundingForQualifiedActivities: 'Custom.AMM_IsECIFFundingForQualifiedActivities',
    AMM_IsSupplierSameAsEndCustomer: 'Custom.AMM_IsSupplierSameAsEndCustomer',
    AMM_ExpectedBidCloseDate: 'Custom.AMM_ExpectedBidCloseDate',
    AMM_AzureRegion2: 'Custom.AMM_AzureRegion2',
    AMM_NodeType2: 'Custom.AMM_NodeType2',
    AMM_ReservedInstances2: 'Custom.AMM_ReservedInstances2',
    AMM_CPUUtilization2: 'Custom.AMM_CPUUtilization2',
    AMM_MemoryUtilization2: 'Custom.AMM_MemoryUtilization2',
    AMM_CPUOverSubscription2: 'Custom.AMM_CPUOverSubscription2',
    AMM_FTTSettings2: 'Custom.AMM_FTTSettings2',
    AMM_Currency2: 'Custom.AMM_Currency2',
    AMM_MemoryOverCommitFactor2: 'Custom.AMM_MemoryOverCommitFactor2',
    AMM_DedupeAndCompressionFactor2: 'Custom.AMM_DedupeAndCompressionFactor2',
    AMM_VMWareVCFAnnualLicenseCostPerPhysicalCore:
      'Custom.AMM_VMWareVCFAnnualLicenseCostPerPhysicalCore',
    AMM_CustomerWillBYOVMWareVCFLicense: 'Custom.AMM_CustomerWillBYOVMWareVCFLicense',
    AMM_TriageOwner: 'Custom.AMM_TriageOwner',
    //Non AI Capacity
    NoNAI_RequestType: 'Custom.NoNAI_RequestType',
    NoNAI_SKU1: 'Custom.NoNAI_SKU_1',
    NoNAI_UOM1: 'Custom.NoNAI_UOM_1',
    NoNAI_Quantity1: 'Custom.NoNAI_Quantity_1',
    NoNAI_SKU2: 'Custom.NoNAI_SKU_2',
    NoNAI_UOM2: 'Custom.NoNAI_UOM_2',
    NoNAI_Quantity2: 'Custom.NoNAI_Quantity_2',
    NoNAI_SKU3: 'Custom.NoNAI_SKU_3',
    NoNAI_UOM3: 'Custom.NoNAI_UOM_3',
    NoNAI_Quantity3: 'Custom.NoNAI_Quantity_3',
    NoNAI_SR: 'Custom.NoNAI_SR',
    NoNAI_ICM: 'Custom.NoNAI_ICM',
    NoNAI_SubscriptionID: 'Custom.NoNAI_SubscriptionID',
    NoNAI_WorkloadImpact: 'Custom.NoNAI_WorkloadImpact',
    NoNAI_Regional: 'Custom.NoNAI_Regional',
    NoNAI_IsCPL: 'Custom.NoNAI_IsCPL',
    // V6 SKU Commitment Fields
    NoNAI_V6_AlternateSKU: 'Custom.NoNAI_V6_AlternateSKU',
    NoNAI_V6_CommittedETA: 'Custom.NoNAI_V6_CommittedETA',
    NoNAI_V6_CommittedSKU: 'Custom.NoNAI_V6_CommittedSKU',
    NoNAI_V6_CommittedQuantity: 'Custom.NoNAI_V6_CommittedQuantity',
    NoNAI_V6_CommittedComments: 'Custom.NoNAI_V6_CommittedComments',
  };

  public static Tenants = {
    RoB: 'RoB',
    Marketplace: 'Marketplace',
  };

  //Be cautious while modifying the order of items in the list as it is used to map ECIF offers
  public static ECIFOffersKeysList = [
    UATConstants.UATFields.AMM_OFFER_AnalyticsCompeteECIF,
    UATConstants.UATFields.AMM_OFFER_PreSalesECIF,
    UATConstants.UATFields.AMM_OFFER_PostSalesECIF,
    UATConstants.UATFields.AMM_OFFER_FieldECIF,
    UATConstants.UATFields.AMM_OFFER_FutureGrowthECIF,
    UATConstants.UATFields.AMM_OFFER_SAPRISECIF,
    UATConstants.UATFields.AMM_OFFER_GeneralPurposeHackathonECIF,
    UATConstants.UATFields.AMM_OFFER_GitHubCopilotHackathonECIF,
    UATConstants.UATFields.AMM_OFFER_ECIFFactoryCoreMigration,
    UATConstants.UATFields.AMM_OFFER_OracleODAAECIF,
    UATConstants.UATFields.AMM_OFFER_AnalyticsModernizationAccelerator,
    UATConstants.UATFields.AMM_OFFER_VMWareMigration,
    UATConstants.UATFields.AMM_OFFER_SynapseHDITOFabricMigration,
    UATConstants.UATFields.AMM_OFFER_AzureFrontierOfferECIF,
    UATConstants.UATFields.AMM_OFFER_CorpECIF,
    UATConstants.UATFields.AMM_OFFER_SpecialtyECIF,
    UATConstants.UATFields.AMM_OFFER_ECIFFactory,
  ];

  //Be cautious while modifying the order of items in the list as it is used to map ACO offers
  public static ACOOffersKeysList = [
    UATConstants.UATFields.AMM_OFFER_AnalyticsCompeteACO,
    UATConstants.UATFields.AMM_OFFER_SMBACO,
    UATConstants.UATFields.AMM_OFFER_OracleODAAACO,
    UATConstants.UATFields.AMM_OFFER_AVSACO,
    UATConstants.UATFields.AMM_OFFER_SAP_Azure,
    UATConstants.UATFields.AMM_OFFER_FutureGrowthACO,
    UATConstants.UATFields.AMM_OFFER_LocalACO,
    UATConstants.UATFields.AMM_OFFER_FY27_LocalACO,
    UATConstants.UATFields.AMM_OFFER_AzureFrontierOfferACO,
    UATConstants.UATFields.AMM_OFFER_CorpACO,
    UATConstants.UATFields.AMM_OFFER_SSCACO,
  ];

  //ACO Offers excluded from MDA nomination toggle
  public static MDAACOOffersKeysList = [...UATConstants.ACOOffersKeysList];

  //CMF Offers
  public static CMFOffersKeysList = [UATConstants.UATFields.AMM_OFFER_MigrationFactory];

  //Offers list where AMM Triage Onwer is mandate when Do you want to request ACO in addition to ECIF is Yes
  public static OffersBelongsToAMMTriageOwnerMandate = [
    UATConstants.UATFields.AMM_OFFER_OracleODAAECIF,
    UATConstants.UATFields.AMM_OFFER_AnalyticsCompeteECIF,
    UATConstants.UATFields.AMM_OFFER_AzureFrontierOfferECIF,
    UATConstants.UATFields.AMM_OFFER_FutureGrowthECIF,
    UATConstants.UATFields.AMM_OFFER_MIGRATEORACLE,
    UATConstants.UATFields.AMM_OFFER_PostSalesECIF,
  ];

  //Below list of AMM offer ids are retired . if anychanges in the future we can add them to this list to avoid any confusion in the system and cross check with the retired offer Business rules.
  public static RetiredAMMOfferIds = [
    UATConstants.UATFields.AMM_OFFER_PreSalesECIF,
    UATConstants.UATFields.AMM_OFFER_PostSalesECIF,
    UATConstants.UATFields.AMM_OFFER_FutureGrowthECIF,
    UATConstants.UATFields.AMM_OFFER_SAPRISECIF,
    UATConstants.UATFields.AMM_OFFER_MIGRATEORACLE,
    UATConstants.UATFields.AMM_OFFER_LocalACO,
    UATConstants.UATFields.AMM_OFFER_AVSACO,
    UATConstants.UATFields.AMM_OFFER_SAP_Azure,
    UATConstants.UATFields.AMM_OFFER_FutureGrowthACO,
    UATConstants.UATFields.AMM_OFFER_MigrateOracleACO,
    UATConstants.UATFields.AMM_OFFER_AVSExpressTCO,
    UATConstants.UATFields.AMM_OFFER_VMWareMigration,
    UATConstants.UATFields.AMM_OFFER_AnalyticsModernizationAccelerator,
    UATConstants.UATFields.AMM_OFFER_AnalyticsCompeteECIF,
    UATConstants.UATFields.AMM_OFFER_AnalyticsCompeteACO,
    UATConstants.UATFields.AMM_OFFER_GitHubCopilotHackathonECIF,
    UATConstants.UATFields.AMM_OFFER_GeneralPurposeHackathonECIF,
    UATConstants.UATFields.AMM_OFFER_ECIFFactoryCoreMigration,
    UATConstants.UATFields.AMM_OFFER_OracleODAAECIF,
    UATConstants.UATFields.AMM_OFFER_OracleODAAACO,
    UATConstants.UATFields.AMM_OFFER_SynapseHDITOFabricMigration,
    UATConstants.UATFields.AMM_OFFER_AVSPilotProgram,
    UATConstants.UATFields.AMM_OFFER_AzureFrontierOfferECIF,
    UATConstants.UATFields.AMM_OFFER_AzureFrontierOfferACO,
  ];

  //Excluding the retired AMM offers from the list of ECIF offers.
  public static ActiveECIFOffersKeysList = UATConstants.ECIFOffersKeysList.filter(
    (offerId) => !UATConstants.RetiredAMMOfferIds.includes(offerId)
  );

  public static EXcludedTenants = {
    CoSell: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d',
    PartnerInvestment: '4b9ac222-75ab-4566-a4e4-c9553896f290',
  };

  public static RAIDFields = {
    AccountableExec: 'accountableexec',
    TriageOwner: 'triageOwner',
    ApprovedBy: 'approvedby',
    AreaPath: 'areapath',
    Areas: 'areas',
    AssignedTo: 'assignedto',
    Businessimpact: 'businessimpact',
    ChangedBy: 'changedby',
    ChangedDate: 'changeddate',
    Collapsed: 'collapsed',
    CollapseAll: 'collapseAll',
    CollapseAllTitle: 'Collapsed All',
    ClosedDate: 'closeddate',
    ContributorInfoDisplayName1: 'Contributor 1',
    ContributorInfoDisplayName2: 'Contributor 2',
    ContributorsDisplayName: 'Contributors',
    ContributorsInfo: 'contributorsinfo',
    ContributorsInfo1: 'contributorsinfo1',
    ContributorsInfo2: 'contributorsinfo2',
    Contributor1: 'contributorsinfo',
    Contributor2: 'contributor2',
    CreatedBy: 'createdby',
    CreatedDate: 'createddate',
    Description: 'description',
    DueDate: 'dueDate',
    DisplayName: 'displayname',
    Estedbilledrevenue: 'estedbilledrevenue',
    Expanded: 'expanded',
    ExpandALL: 'expandAll',
    ExpandAllTitle: 'Expand All',
    ID: 'ID',
    Industry: 'industry',
    MilestoneId: 'milestoneid',
    Milestones: 'milestones',
    OpportunityOutcome: 'opportunity_outcome',
    OpportunityStage: 'opportunityStage',
    RaidCorrelation: 'raidCorrelation',
    LinkedTechRoB: 'relatedtechrobid',
    RAIDFields: 'raidfields',
    RaidId: 'raidid',
    RaidType: 'raidtype',
    Raidrelations: 'raidrelations',
    Regions: 'regions',
    Requestor: 'requestorsinfo',
    RequestedDate: 'requesteddate',
    RbArea: 'rb_area',
    RbBlockedopportunityrevenue: 'rb_approver',
    RbBlockedproductopportunityid: 'rb_blockedproductopportunityid',
    RbBlockedusoopportunityid: 'rb_blockedusoopportunityid',
    RbCasecloseddate: 'rb_casecloseddate',
    RbCasenumber: 'rb_casenumber',
    RbCompanyname: 'rb_companyname',
    RbCrmaccountid: 'rb_crmaccountid',
    RbCsscasecreationdate: 'rb_csscasecreationdate',
    RbCsscasenumber: 'rb_csscasenumber',
    RbCustomercontactemail: 'rb_customercontactemail',
    RbCustomercontactname: 'rb_customercontactname',
    RbCustomercontactnumber: 'rb_customercontactnumber',
    RbCustomerrevenueimpacted: 'rb_customerrevenueimpacted',
    RbCustomertpid: 'rb_customertpid',
    RbGeneralcategory: 'rb_generalcategory',
    RbGethelpticketcreationdate: 'rb_gethelpticketcreationdate',
    RbGethelpticketnumber: 'rb_gethelpticketnumber',
    RbInfralocation: 'rb_infralocation',
    RbMsftproductblockedrevenue: 'rb_msftproductblockedrevenue',
    RbNscstartdate: 'rb_nscstartdate',
    RbRegion: 'rb_region',
    RbSalesterritory: 'rb_salesterritory',
    RbSsspemail: 'rb_ssspemail',
    RbSsspfullname: 'rb_ssspfullname',
    RbTechnology: 'rb_technology',
    RbTier: 'rb_tier',
    RbTicketreason: 'rb_ticketreason',
    RbUsoblockedrevenue: 'rb_usoblockedrevenue',
    RbUsersimpacted: 'rb_usersimpacted',
    RbCountry: 'rb_country',
    RbCurrentfyrevenue: 'rb_currentfyrevenue',

    State: 'state',
    SubState: 'substate',
    Subsegment: 'subsegment',
    SolutionArea: 'solutionarealist',
    SubRegions: 'subregions',
    Title: 'title',
    uniqueName: 'uniquename',
    displayName: 'displayname',
    CAPSLAmName: 'capsl_amname',
    CoSellAssignmentDate: 'cosell_assignmentdate',
    CoSellAssignedDate: 'cosell_assigneddate',
    CoSellResolutionDate: 'cosell_resolutiondate',
    GPSStartDate: 'gps_startdate',
    GPSContractAsignedDate: 'gps_contractsigndate',
    GPSDueDate: 'gps_duedate',
    GPSEndDate: 'gps_enddate',
    GPSEscalationDate: 'gps_escalationdate',
    GPSMIgrationDate: 'gps_migrateddate',
    GPSOpsintakeMeetingDate: 'gps_opsintakemeetingdate',
    GPSPirDecisionDate: 'gps_pirdecisiondate',
    GPSPirReviewDate: 'gps_pirreviewdate',
    SGSDCRegions: 'DCRegionFrom',
    TagName: 'name',
    TPID: 'raidrelations/msxaccounts/tpid',
  };

  public static DefaultColumnOptions: DisplayColumn[] = [
    { internalName: 'System.Id', displayName: 'ID', isDefault: true },
    { internalName: 'System.Title', displayName: 'Title', isDefault: true },
    {
      internalName: 'System.AssignedTo',
      displayName: 'Action Owner',
      isDefault: true,
    },
    {
      internalName: 'Custom.RAID_Type',
      displayName: 'RAID Type',
      isDefault: true,
    },
    {
      internalName: 'Custom.Account',
      displayName: 'Account Name',
      isDefault: true,
    },
    { internalName: 'System.State', displayName: 'State', isDefault: true },
    {
      internalName: 'System.ChangedDate',
      displayName: 'Changed Date',
      isDefault: true,
    },
  ];

  public static DefaultAnalyticsViewColumnOptions: DisplayColumn[] = [
    {
      internalName: 'WorkItemId',
      displayName: 'Work Item Id',
      isDefault: true,
    },
    { internalName: 'Title', displayName: 'Title', isDefault: true },
    {
      internalName: 'Area/AreaPath',
      displayName: 'Area Path',
      isDefault: false,
    },
    {
      internalName: 'AssignedTo/UserName',
      displayName: 'Assigned To',
      isDefault: false,
    },
    {
      internalName: 'Custom_Account',
      displayName: 'Account',
      isDefault: true,
    },
    {
      internalName: 'Iteration/IterationPath',
      displayName: 'Iteration Path',
      isDefault: false,
    },
    { internalName: 'State', displayName: 'State', isDefault: false },
    {
      internalName: 'WorkItemType',
      displayName: 'Work Item Type',
      isDefault: false,
    },
  ];

  public static ColumnOptionDisplayColumn = 'displayColumn';
  public static PickListFields: string[] = [
    UATConstants.UATFields.State,
    UATConstants.UATFields.OneListFeedback,
    UATConstants.UATFields.FeatureParityRequest,
    UATConstants.UATFields.Priority,
    UATConstants.UATFields.Rating,
    UATConstants.UATFields.StateCode,

    UATConstants.UATFields.ClosedStatus,
    UATConstants.UATFields.ClosedStatusCode,
    UATConstants.UATFields.IsBlocked,
    UATConstants.UATFields.Severity,
    UATConstants.UATFields.SyncWithCE,
    UATConstants.UATFields.WorkItemType,
    UATConstants.UATFields.Activity,
    UATConstants.AssignToCorp,
    UATConstants.AssignToCorpProd,
    UATConstants.UATFields.CloudIntentreason,
    UATConstants.UATFields.Completionstatusifcombined,
    UATConstants.UATFields.Escalated,
    UATConstants.UATFields.MeetingTypeOrigin,
    UATConstants.UATFields.Nonrenewingreason,
    UATConstants.UATFields.WorkloadType,
    UATConstants.UATFields.Issue,
    UATConstants.UATFields.TriageValue,
    UATConstants.UATFields.Activity,

    UATConstants.UATFields.Blocked,
    UATConstants.UATFields.FeedbackTypeValue,
    UATConstants.UATFields.AssistedMigrationReason,
    UATConstants.UATFields.Tags,
    UATConstants.UATFields.Reason,
    UATConstants.UATFields.IsCovid19,
    UATConstants.UATFields.IsitFAM,
    UATConstants.UATFields.IterationPath,
    UATConstants.UATFields.AreaPath,
    UATConstants.UATFields.LTEscalation,
    UATConstants.UATFields.AdditionalTheme,
    UATConstants.UATFields.BusinessProposal,
    UATConstants.UATFields.Theme,
    UATConstants.UATFields.MaaS_IsInterestedAccountProfile,
    UATConstants.UATFields.MaaS_IsInterestedOrgChat,
    UATConstants.UATFields.MaaS_UpdateExistingAccount,
    UATConstants.UATFields.MaaS_ReplacemyExistingList,
    MACCConstants.UATFields.MACCIS_Sales_Engaged,
    UATConstants.UATFields.GPS_FinanceApproval,
    UATConstants.UATFields.GPS_PolicyDeclaration,
    UATConstants.UATFields.GPS_CELAReview,
    UATConstants.UATFields.GPS_OperationsApproval,
    UATConstants.UATFields.GPS_FinanceApproval,
    UATConstants.UATFields.GPS_CorpAccountingReview,
    UATConstants.UATFields.GPS_ComplianceandOngoingMonitoring,
    UATConstants.UATFields.GPS_BusinessCaseSubmitted,
    UATConstants.UATFields.GPS_PIRReviewScheduled,
    UATConstants.UATFields.GPS_FinancialModelSubmitted,
    UATConstants.UATFields.GPS_OpsIntakeRequired,
    UATConstants.UATFields.GPS_OpsIntakeComplete,
    UATConstants.UATFields.GPS_NextStepsCompleted,
    UATConstants.UATFields.GPS_ApprovedInQubit,
    UATConstants.UATFields.GPS_ApprovedInQubit,
    UATConstants.UATFields.GPS_CustomerSegment,
    UATConstants.UATFields.GPS_StrategyApproval,
    UATConstants.UATFields.GPS_FMVAssessmentApproval,

    //New Ignore Fields
    UATConstants.UATFields.BoardColumn,
    UATConstants.UATFields.AssociatedContextType,
    UATConstants.UATFields.MeetingsPitchDeck,
    UATConstants.UATFields.MeetingsWorkshop,
    UATConstants.UATFields.TCOAnalysis,
    UATConstants.UATFields.POCECIFConsumption,
    UATConstants.UATFields.SubmittedforAWR,
    UATConstants.UATFields.ValueArea,
    UATConstants.UATFields.IntegrationBuild,
    UATConstants.UATFields.BoardColumnDone,
    UATConstants.UATFields.TestSuiteTypeId,
    UATConstants.UATFields.AutomationStatus,
    UATConstants.UATFields.FAQ,
    UATConstants.UATFields.OfferRelated,
    UATConstants.UATFields.DeliveryBlockers,
    UATConstants.UATFields.AdditionalArea,
    UATConstants.UATFields.AdditionaEOU,
    UATConstants.UATFields.OpportunityIntent,
    UATConstants.UATFields.OpportunityStage,
    UATConstants.UATFields.ListeningCircleArea,
    UATConstants.UATFields.SolutionAreaList,
    UATConstants.UATFields.PMYT_SolutionPlays,
    UATConstants.UATFields.PMYT_Areas,
  ];

  public static MSFieldsToIgnore: string[] = [
    UATConstants.UATFields.ContextCode,
    UATConstants.UATFields.BacklogPriority,
    UATConstants.UATFields.Blocked,
    UATConstants.UATFields.BusinessValue,
    UATConstants.UATFields.Effort,
    UATConstants.UATFields.LocalDataSource,
    UATConstants.UATFields.parameters,
    UATConstants.UATFields.Priority,
    UATConstants.UATFields.RemainingWork,
    UATConstants.UATFields.Resolution,
    UATConstants.UATFields.ReviewedBy,
    UATConstants.UATFields.TestSuiteTypeId,
    UATConstants.UATFields.TimeCriticality,
    UATConstants.UATFields.TeamProject,
    UATConstants.UATFields.WorkItemType,
    UATConstants.UATFields.ValueArea,
    UATConstants.UATFields.IntegrationBuild,
    UATConstants.UATFields.PubliclyDocumented,
    UATConstants.UATFields.BoardColumn,
    UATConstants.UATFields.BoardColumnDone,
    UATConstants.UATFields.AutomationStatus,
    UATConstants.UATFields.TestSuitType,
    UATConstants.UATFields.TestSuiteTypeId,
    UATConstants.UATFields.AutomationStatus,
    UATConstants.UATFields.Apac,
    UATConstants.UATFields.BoardLane,
    UATConstants.UATFields.DefaultEqualsMaxETA,
    UATConstants.UATFields.India,
    UATConstants.UATFields.Data,
    UATConstants.UATFields.AcceptanceCriteria,
    UATConstants.UATFields.ActivatedBy,
    UATConstants.UATFields.AlternativeSolutions,
    UATConstants.UATFields.AppLaunchInstructions,
    UATConstants.UATFields.AppStartInformation,
    UATConstants.UATFields.AssociatedContext,
    UATConstants.UATFields.AssociatedContextCode,
    UATConstants.UATFields.AssociatedContextOwner,
    UATConstants.UATFields.AssociatedContextType,
    UATConstants.UATFields.AutomatedTestId,
    UATConstants.UATFields.AutomatedTestName,
    UATConstants.UATFields.AutomatedTestStorage,
    UATConstants.UATFields.AutomatedTestType,
    UATConstants.UATFields.AzServiceName,
    UATConstants.UATFields.ServiceRegionavail,
    UATConstants.UATFields.OfferingName,
    //disabled or duplicate fields
    UATConstants.UATFields.tftCreatedOldOne,
    UATConstants.UATFields.tftCreatedOldTwo,
    UATConstants.UATFields.OneListID,
    UATConstants.UATFields.FeedbackTitle,
    UATConstants.UATFields.FeedbackItemUrl,
    UATConstants.UATFields.FeedbackNumber,
    UATConstants.UATFields.CustomerScenarioandDesiredOutcome,
    UATConstants.UATFields.CustomerImpactData,
    UATConstants.UATFields.WorkAroundDetails,
    UATConstants.UATFields.Competitor,
    UATConstants.UATFields.MeetingsPitchDeck,
    UATConstants.UATFields.MeetingsWorkshop,
    UATConstants.UATFields.TCOAnalysis,
    UATConstants.UATFields.POCECIFConsumption,
    UATConstants.UATFields.SubmittedforAWR,
    UATConstants.UATFields.CompeteBlockerEscalation,
    UATConstants.UATFields.QuarterSerialNumber,
  ];
  //Fields not exists in ADO so we are ignoring these
  public static MSFieldsToIgnoreList2: string[] = [
    UATConstants.UATFields.TeamProject,
    UATConstants.UATFields.AzServiceName,
    UATConstants.UATFields.ServiceRegionavail,
    UATConstants.UATFields.OfferingName,
    UATConstants.UATFields.CompeteBlockerEscalation,
    UATConstants.UATFields.Apac,
    UATConstants.UATFields.India,
    UATConstants.UATFields.Data,
    UATConstants.UATFields.ProgramNew,
    UATConstants.UATFields.ProgramOld,
    UATConstants.UATFields.ProductsOld,
    UATConstants.UATFields.MilestoneReason,
  ];

  public static ActionAliases: string[] = [
    'Action Owner',
    'Requestor',
    'Accountable Exec',
    'Managing Owner',
    'Contributor 1',
    'Contributor 2',
    'Changed By',
    'LT/ExecDirect',
    'ECIF_ApprovedBy',
    'ECIF_RejectedBy',
    'ECIF_AdditionalContacts',
    'ECIF_CommericalExecutive',
    'CoSell Microsoft Owner',
    'CoSell Microsoft CoOwner',
    'ECIF Approved By Rejected By',
    'GPS CELA PIR StakeholderName',
    'GPS Compliance PIR Stakeholder',
    'GPS Corp Accounting PIR Stakeholder',
    'GPS Finance PIR Stakeholder',
    'GPS Finance Stakeholder',
    'GPS Operations PIR Stakeholder',
    'GPS Operations Stakeholder',
    'GPS PIR Manager',
    'GPS PIR Manager/Design Lead',
    'GPS Strategy Stakeholder Name',
    'GPS CELA Stakeholder',
    'GPS Strategy Lead',
    'GPS Launch Manager',
    'GPS Compliance Stakeholder',
    'GPS Corp Accounting Stakeholder',
    'MACC CE Name',
    'MACC AE Name',
    'MACC Exception Approver',
    'Services Seller Name',
    "MACC Requestor's Manager",
    'AMM GBB Alias',
    'RB Approver',
    'AMM Triage Owner',
  ];
  public static ActionBooleans: string[] = [
    'Assign To Corp',
    'COVID-19 Help Needed?',
    'LTEscalation',
    'Executive Sponsor',
    'IT and Business Stakeholders',
  ];
  public static ExcelDates: string[] = [
    'Changed Date',
    'Created Date',
    'Requested Date',
    'Closed Date',
    'Assign To Corp Date',
    'Authorized Date',
    'Revised Date',
    'Actual Created Date',
    'AMM Case Created DateTime',
    'AMM Expected Project StartDate',
    'AMM Expected Project EndDate',
    'AOAI_DateCustomerReady',
    'AOAI_DateDecided',
    'AOAI_DateofFirstDeployment',
    'CoSell Assignment Date',
    'CoSell Date Accepted',
    'CoSell Date Expired',
    'CoSell Deal Created Date',
    'CoSell Expected Expiration Date',
    'Due Date',
    'ECIF ACO Delivery Start Date',
    'ECIF Approved Date Rejected Date',
    'ECIF_ApprovedDate',
    'ECIF_CompletionDate',
    'ECIF_RejectedDate',
    'ECIF_StartDate',
    'Est Billed close Date',
    'Est Cons Completion Date',
    'Est Cons Start Date',
    'Estimated Date To Complete',
    'Explanation of needed by Date',
    'GPS Contract SignDate',
    'GPS Due Date',
    'GPS EFR Meeting Date',
    'GPS Investment End Date',
    'GPS Investment Start Date',
    'GPS Migrated Date',
    'GPS PIR DecisionDate',
    'GPS Review Date',
    'Internal Triage Date',
    'MaaS Data Capture Date',
    'MaaS Document Creation Date',
    'MaaS Event Date',
    'Milestone Est. Date',
    'Opportunity Lost Date',
    'PMYT_EstimatedStartDate',
    'RB CaseClosedDate',
    'RB GetHelpTicketCreationDate',
    'RB NSCStartDate',
    'Rejected Date',
    'Technical Feedback Review Date',
    'User Changed Date',
  ];
  public static ActionRichTextColumns: string[] = [
    'Description',
    'Desired Outcom',
    'Strategic Account',
    'Executive Summary',
    'Impact Summary',
    'Internal Role specificNotes',
    'QBCNotes',
    'Related Issue',
    'Strategic Account',
    'Mitigation Plan',
    'Notes',
    'Exit Criteria',
    'Next Action',
    'Status Update',
    'Closure Statement',
    'GPS Financial Economics',
    'GPS Investment Description',
    'GPS Finance NextSteps Exceptions Notes',
    'GPS Strategy Next steps Exceptions Notes',
    'GPS Notes',
    'GPS Corp Accounting NextSteps Exceptions Notes',
    'GPS Strategy Approval',
    'GPS PIR Manager/Design Lead Next Steps Exceptions Notes',
    'GPS Operations Approval',
    'GPS Operations NextSteps Exceptions Notes',
    'GPS CELA NextSteps Exceptions Notes',
    'GPS Compliance NextSteps Exceptions Notes',
    'GPS Ongoing Monitoring NextSteps Exceptions Notes',
    'ECIF_RequestJustification',
    'ECIF_DealDetails',
    'ECIF_EvaluateWork',
    'ECIF_ExpectedOutcome',
    'ECIF ACO Request Justification',
    'ECIF Request Justification deal coaching',
  ];

  public static ActionDoubleColumns: string[] = ['Est. Monthly Usage (USD)'];

  public static AdoValidDateFormats: string[] = [
    "@StartOfDay('-7d')",
    '@StartOfWeek',
    '@StartOfMonth',
    '@StartOfYear',
  ];

  public static CompareFieldOperators: string[] = [
    '= [Field]',
    '<> [Field]',
    '> [Field]',
    '< [Field]',
    '>= [Field]',
    '<= [Field]',
  ];

  public static MultiselectOperators: string[] = ['In', 'Not In'];

  public static GroupOperators: string[] = ['In Group', 'Not In Group'];

  public static PickListwithAnyFields: string[] = ['workItemType'];

  public static BulkUploadUserMessages = {
    InsertActionsUploaded: 'Insert Actions are submitted successfully',
    InsertActionUploaded: 'Insert Action is submitted successfully',
    UpdateActionsUploaded: 'Update Actions are submitted successfully',
    UpdateActionUploaded: 'Update Action is submitted successfully',
    UploadTimeout:
      'Importing Action process timed out. Please try again with no more than 100 Actions per file.',
    InvalidUploadFile:
      'Please upload valid .xlsx file with file size limit 4MB ( Template can be downloaded from download template)',
    FileValidation: 'File Validation',
    Uploaded: 'Uploaded',
    Completed: 'Completed',
    CompletedSolid: 'CompletedSolid',
    UploadComplete: 'Upload Complete!',
    UploadedFiles: 'Uploaded Files',
  };

  public static EmailPanel = {
    cosellEmail:
      UATConstants.Project === 'Unified Action Tracker'
        ? 'coselloperations@microsoft.com'
        : 'coselloperationsuat@microsoft.com',
    cosellName:
      UATConstants.Project === 'Unified Action Tracker'
        ? 'Co-Sell Operations Service'
        : 'Co-sell Operations Service UAT',
    gpsEmail:
      UATConstants.Project === 'Unified Action Tracker'
        ? 'askpir@microsoft.com'
        : 'gpsInvestments-test@microsoft.com',
    gpsName:
      UATConstants.Project === 'Unified Action Tracker'
        ? 'GPS Operations Service'
        : 'GPS Operations Service UAT',

    regionalSkillingEmail: UATConstants.RegionalSkillingEmailMail,
    regionalSkillingName:
      UATConstants.Project === 'Unified Action Tracker'
        ? 'Regional Skilling Desk Mailbox UAT'
        : 'Regional Skilling Desk Mailbox',
  };

  public static PermissionMsgs = {
    DontHavePermission: "You don't have permission to access Unified Action Tracker",
    ContactAdminToGrantAccess:
      'Please contact your global administrator to grant you the appropriate access.',
    SystemViewEditAccess: "You don't have permission to edit System View",
    GridEditAccess: "You don't have permission to edit Grid View",
    DisableFiledsAccess: 'Only Action Owner have access to edit Fields',
    redirectMessag: 'Preparing direct landing page...',
    redirectNotAuthMessage: 'You are not authorized to MCAPS Help PM. Redirect to dashboard',
  };

  public static RelatedTabErrorMsgs = {
    DefaultError: 'Please enter URL.',
    InvalidURL: 'Remote workitem url is not valid.',
    SameOrgItem: 'A remote work item cannot link to the same organization.',
    DuplicateItem: 'A remote work item link already exist. If not saved, please click Save.',
    InternalError: 'Cannot add remote link due to Internal Error.',
  };
  public static MenuList = {
    TechnicalFeedback: 'Technical Feedback',
    Details: 'Details',
    Related: 'Related',
    Attachments: 'Attachments',
    History: 'History',
    AOAICapacity: 'AOAI Capacity',
    CSR: 'CSR',
    CAE: 'CAE',
    CAPSL: 'CAPSL',
    AHPM: 'MCAPS Help PM',
    APEX: 'MW Programs',
    McapsMaaS: 'MCAPS MaaS',
    IAT: 'IAT',
    VSUQBC: 'VSU/QBC',
    RedButton: 'Red Button',
    Compete: 'Compete',
    CSG: 'CSG',
    GlobalTeam: 'Global Team',
    Products: 'Product(s)',
    ECIF: 'ECIF Pre Approval',
    Nominations: 'Customer Hub',
    MEA: 'MEA',
  };
  public static ECIFAsiaFields: string[] = [
    UATConstants.UATFields.ECIF_BaselineRevenue,
    UATConstants.UATFields.ECIF_ConsumedRevenue1year,
    UATConstants.UATFields.ECIF_ConsumedRevenue2year,
    UATConstants.UATFields.ECIF_ConsumedRevenue3year,
    UATConstants.UATFields.ECIF_Q1Spend,
    UATConstants.UATFields.ECIF_Q2Spend,
    UATConstants.UATFields.ECIF_Q3Spend,
    UATConstants.UATFields.ECIF_Q4Spend,
    UATConstants.UATFields.ECIF_CrossQ1Spend,
    UATConstants.UATFields.ECIF_CrossQ2Spend,
    UATConstants.UATFields.ECIF_CrossQ3Spend,
    UATConstants.UATFields.ECIF_CrossQ4Spend,
    UATConstants.UATFields.ECIF_IncrementalRev,
    UATConstants.UATFields.ECIF_Incremental_UpsellBilledRevenue,
    UATConstants.UATFields.ECIF_AzureCreditDiscount,
    UATConstants.UATFields.ECIF_SKUDiscount,
    UATConstants.UATFields.ECIF_AzureCreditDiscount,
    UATConstants.UATFields.ECIF_ACOCreditRequestAmount,
  ];

  public static ECIFRegion: string[] = ['asia'];
  public static ErrorMessages = {
    RequestReasonMandatory: 'Request Reason is mandatory',
    BudgetProgramMandatory: 'Budget Program is mandatory',
    FieldMandatory: 'This field is mandatory',
    LaunchChecklistFieldMandatory: 'This field is mandatory. Please select "Yes" or "No".',
    SurveyRequestTitleMandatory: 'Request Title is required.',
    SurveyUrgencyMandatory: 'Urgency is required.',
    SurveyNewOrEditMandatory: 'This field is required.',
    SurveyAudienceMandatory: 'Survey Audience is required.',
    SurveyNewVerbiageMandatory: 'New verbiage selection is required.',
    SurveyGoLiveDateMandatory: 'Go-live date is required.',
    SurveyUploadInvalidType: 'Only .xlsx files are allowed.',
    SurveyUploadMaxSize: 'Max file size cannot be more than 10 MB.',
    SurveyUploadMaxCount: 'You can upload a maximum of 10 files.',
    PipAdoCreationError: 'Error creating work Item.',
    PipAdoUpdationError: 'Error updating work Item.',
    EnterAppIDQuotaID: 'Please Enter Request ID value ',
    OptionMandatory: 'Options is mandatory for all selected users.',
    SelectMandatory: 'Contacts is mandatory.',
    TitleMandatory: 'Title is mandatory',
    MaxContacts: 'Please select only Max. contacts allowed for the wave',
    ConfirmMandatory: 'Confirmation is mandatory',
    ActionOwnerMandatory: 'Action Owner is mandatory',
    CompetitorNameMandatory: 'Competitor Name is mandatory',
    CompetitorProductMandatory: 'Competitor Product is manadatory',
    CompetitorProductPerCompetitorMandatory: 'Missing Products for one or more Competitors.',
    CompetitorProductLimitExceeded:
      'Only 10 Competitor Name and Competitor Product selections are allowed.',
    OmsTenantIdMandatory: 'Tenant ID is mandatory.',
    TenantIdInvalid: 'Enter a valid Tenant ID.',
    CSMMandatory: 'CSAM associated with this request is mandatory',
    AssignedCorpMandatory: 'Please select Yes/No',
    AccountMandatory: 'Account is mandatory',
    AccountCRMIDMandatory: 'Account CRM ID is mandatory',
    AreaMandatory: 'Area is mandatory',
    TimezoneMandatory: 'Time Zone is mandatory',
    AccountIDMandatory: 'Account Id is mandatory',
    BDMSpecificNameMandatory: 'Specific Name is mandatory',
    BDMSpecificTitleMandatory: 'Specific Title is mandatory',
    BilledRevenueMandatory: 'Est. Billed Revenue is mandatory',
    BilledCloseDateMandatory: 'Est. Billed Close Date is mandatory',
    ProductIsMandatory: 'Product(s) is mandatory',
    ProductsRequiredForAllSelectedBilledOpportunities:
      'Products must be selected for all selected Billed Opportunities.',
    LicensingProgramMandatory: 'Licensing Program is mandatory',
    OpportunityStatusManadatory: 'Opportunity Status is mandatory',
    ExistTechTargAccMandatory: 'Selection of TechTarget Account is mandatory',
    SpecificCountryMandatory: 'Specific Country is mandatory',
    CustomerWebURLMandatory: 'Customer website URL is mandatory',
    InvalidCustomerWebURLMandatory: 'Invalid Customer website URL',
    InvalidPartnerName: 'Invalid Partner Name',
    InvalidPartnerID: 'Invalid Partner ID',
    DescriptionMandatory: 'What help is needed is mandatory',
    FieldErrorMessage: 'Show Field Error Message',
    Deleted: 'If you delete the',
    RemovedAllUsers: 'view, it will be removed for all users. Are you sure you want to continue?',
    OpportunityMandatory: 'Opportunity ID is mandatory',
    OpportunityName: 'Opportunity Name is mandatory',
    OpportunityCustomerNameMandatory: 'Customer Name  is mandatory',
    OpportunityRevenueSizeMandatory: 'MACC Opportunity Revenue Size is mandatory',
    ProposedUnifiedContractRevenueMandatory: 'MACC Proposed Unified Contract Revenue is mandatory',
    DurationInMonthMandatory: 'MACC Duration in Months is mandatory',
    ProposedUnifiedDurationInMonthMandatory: 'Proposed Unified Duration in Months is mandatory',
    AnnualizedMACCValue: 'Annualized MACC Value is mandatory',
    AnnualizedUnifiedRevenueMandatory: 'Annualized Unified Revenue is mandatory',
    ExpectedMinimumSupportAdoptionMandatory: 'Expected Minimum Unified Adoption should be numeric',
    ProposedSupportAdoptionMandatory: 'Proposed Unified Adoption should be numeric',
    ProposedDFCAttachePercentage: 'Proposed DFC attach % is mandatory',
    OperatingUnitMandatory: 'Operating Unit is mandatory',
    TPIDMandatory: 'TPID is mandatory',
    NominationIntentMandatory: 'Nomination Intent is mandatory',
    NominationIntent: 'Nomination Intent',
    MilestoneworkloadMandatory: 'Selected milestone must meet the pre-requisites',
    ProductMandatory: 'Product is mandatory',
    MilestoneMandatory: 'Please select the milestone',
    FeedbaclDialog: 'Action already exists',
    WaveMandatory: 'Wave is mandatory',
    OpportunityIntentMandatory: 'Opportunity Intent is mandatory',
    MeetingTypeMandatory: 'Meeting Type is mandatory',
    AgreementIDMandatory: 'Agreement Id is mandatory',
    AgreementNameMandatory: 'Agreement Name is mandatory',
    SubRegionTypeMandatory: 'Sub Region is mandatory',
    RequestorErrorMsgs: 'Requestor is mandatory',
    AeAliasMandatory: 'AE Alias is mandatory',
    RequestorManagerMandatory: 'Requestor Manager is mandatory',
    CustomerUnifiedAgreementMandatory: 'Customer Unified Agreement is mandatory',
    CustomerExistingCloudMandatory: 'Customer Existing Cloud is mandatory',
    ProposedUnifiedRevenueMandatory: 'Proposed Unified Revenue is mandatory',
    StakeHolderError: 'Please select valid stakeholder name or email',
    PartnerErrorMessage: 'Partner is mandatory',
    PDMErrorMessage: 'PDM is mandatory',
    PartnerDialog: 'No Partner attached to this Opportunity',
    PDMDialog: 'No Primary PDM assigned to this Partner',
    ThemeMandatory: 'Theme is mandatory',
    AssignedToMandatory: 'Assigned to is mandatory',
    PriorityMandatory: 'Please select the priority',
    StatesMandatory: 'Please select the status',
    ResolutionPlanMandatory: 'Mitigation Plan is mandatory',
    PlanUntilResolutionMandatory: 'Plan until resolution is mandatory',
    DateOpenedMandatory: 'Date Opened is mandatory',
    DateClosedMandatory: 'Date Closed is mandatory',
    DateOpenedGreaterError: 'Date Closed should be greater than Date Opened',
    ThemeCountInvalid: `Maximum limit of ${UATConstants.MaxAllowedThemes} Themes is allowed per Action`,
    AdditionalThemeMandatory: 'Additional theme is mandatory',
    DueDateMandatory: 'Due Date is mandatory',
    HaveNoPermissionsToViewResource:
      'The resource you are accessing does not exist or you do not have permissions to view it.',
    HaveNoPermissionsToViewResourceNotSignedIn:
      'The resource you are accessing does not exist or you do not have permissions to view it because you are not signed in.',
    TPIDNotFoundMandatory: 'TPID not found in the system. Please verify or try again later.',
    QBCNotesMandatory: 'QBC Notes is mandatory',
    StategicAccountMandatory: 'Strategic Account is mandatory',
    DesiredOutcomeMandatory: 'Customer Scenario & Desired Outcome is mandatory',
    CustomerScenarioDesiredOutcome: 'Customer Scenario & Desired Outcome',
    ImpactSummaryMandatory: 'Customer Impact is mandatory',
    CustomerPriorityMandatory: 'Customer Priority is mandatory',
    SolutionAreaUATMandatory: 'Solution Area is mandatory',
    BusinessDivisionMandatory: 'Business Division is mandatory',
    ToolsImpactedMandatory: 'Tools Impacted is mandatory',
    PreferredPartnernameMandatory:
      "Preferred Partner Name is required for Meeting Type 'Partner Sharing'",
    PackageIdMandatory: 'Package Id is mandatory',
    PackageNameMandatory: 'Package Name is mandatory',
    DeliveryBlockersMandatory: 'Delivery Blockers are mandatory',
    InvalidNameError: 'Name is required and should be unique',
    FolderPathError: 'Please select a folder to save the view',
    MeetingSubTypeErrorMessage: 'Meeting Sub-Type is mandatory',
    InvalidTPId: 'Invalid TPID. Only numbers are allowed',
    MaaSInvalidTPID: 'Invalid TPID',
    MultiTPIDS: "Please find the Invalid TPID's",
    InvalidFYRev: 'Invalid Current FY Revenue. Only numbers are allowed',
    EOObjectiveMandatory: 'E&O Objective is mandatory',
    ProjectNameMandatory: 'Project Name is mandatory',
    ListeningCircleSessionNameMandatory: 'Listening Circle Session Name is mandatory',
    ListeningCircleSessionDateMandatory: 'Listening Circle Session Date is mandatory',
    ListeningCircleSessionNameLengthError:
      'Listening Circle Session Name cannot be longer than 50 characters',
    AssignedTeamError: 'This is a required field for selected Assigned team',
    DescriptionError: 'Description is mandatory',
    selectValidEmail: 'Please select valid name or email',
    BusinessImpactError: 'Business Impact is mandatory',
    EstMonthlyUsageValidation:
      'Only numbers upto 3 decimal places are allowed for Est. Monthly Usage (USD).',
    BusinessImpactMandatory: 'Business Impact is mandatory',
    ExecutingTeamNameMandatory: 'Executing Team Name is mandatory',
    ExecutingTeamADOLinkMandatory: 'Executing Team ADO Link is mandatory',
    CloudImpactedMandatory: 'Cloud Impact is mandatory',
    WorkAroundDetailsMandatory: 'Work Around Details is mandatory',
    AmendementSignedMandatory: 'Please select Yes/No',
    AddBusinessImpact: 'Click to add Business Impact',
    RequestorsNotGreaterFive: 'Requestors should not be greater than 5',
    ContributorsDuplicateValidation: 'Contributors must not be duplicated',
    OpportunityNameMandatory: 'Opportunity Name is mandatory',
    SupplierAsCustomer: 'Supplier same as customer is mandatory',
    PrimaryCompetitorMandatory: 'Primary Competitor is mandatory',
    Segmentmandatory: 'Segment is mandatory',
    SegmentGroupmandatory: 'Segment Group is mandatory',
    SubSegmentmandatory: 'SubSegment is mandatory',
    primaryCompetitorError: 'Primary Competitor is mandatory',
    OpportunityStageMandatory: 'Opportunity Stage is mandatory',
    IndustryMandatory: 'Industry is mandatory',
    CustomIndustryMandatory: 'Custom Industry is mandatory',
    EOUMandatory: 'EOU is mandatory',
    EntityTypeMandatory: 'Entity Type is mandatory',
    DeliveryMethodMandatory: 'Delivery Method is mandatory',
    SolutionAreaMandatory: 'Solution Area is mandatory',
    CustomerSegmentMandatory: 'Customer Segment is mandatory',
    AskTypeMandatory: 'Ask Type is mandatory',
    ProductsMandatory: 'Products is mandatory',
    StateMandatory: 'State is mandatory',
    SubStateMandatory: 'Sub State is mandatory',
    RequestJustificationMandatory: 'Request Justification is mandatory',
    MotivationForAssessmentMandatory: 'Motivation for Assessment is mandatory',
    CurrentEnvironmentAndTargetOutcomesMandatory:
      'Current Environment & Target Outcomes is mandatory',
    EstimatedEnvironmentSizeMandatory: 'Estimated Environment Size is mandatory',
    HelpDescriptionMandatory: 'Help Description is mandatory',
    DealDetailsMandatory: 'Deal Details is mandatory',
    DealRequestJustificationsMandatory: 'Deal Justifications is mandatory',
    CategoryMandatory: 'Category is mandatory',
    StakeholdersNotGreaterFive: 'Stakeholders should not be greater than 5',
    selectValidNameEmail: 'Please select valid owner name or email',
    AssignedTeamMandatory: 'Assigned Team is mandatory',
    AccountTPIDMandatory: 'Account TPID is mandatory',
    PleaseEnterValidNumber: 'Please enter valid number',
    PleaseSelectModelOption: 'Please Select Model Option',
    EnterSubscriptionID: 'Enter Subscription ID',
    PleaseSelectPreferredOption: 'Please Select Preferred Region Option',
    PreferredMaxError: 'Maximum 10 Regions can be selected ',
    CustomerEngagementMandatory: 'Customer Engagement Activity TimeLine is mandatory',
    ProposedSolutionactivityMandatory: 'Proposed solution/activity go-live date is mandatory',
    PartnerNameMandatory: 'Partner Name is mandatory',
    CoInvestmentAmountMandatory: 'Co-Investment Amount is mandatory',
    RequestTypeMandatory: 'Request Type is mandatory',
    BusinessChallengeMandatory: 'High Level Business Challenge is mandatory',
    OnlyDecimalValuesDealSize: 'Only decimal values are allowed for Deal Size',
    TPIDMandatoryAssignedTeam: 'TPID is mandatory for selected assigned team',
    FYmandatory: 'FY is mandatory',
    QuarterMandatory: 'Quarter is mandatory',
    AccountableexecMandatory: 'Accountable exec is mandatory',
    ManagingOwnerMandatory: 'Managing owner is mandatory',
    InvalidOpportunityID: 'Invalid Opportunity ID',
    ProductOpportunitySize: 'Only number are allowed for Product Opportunity Size.',
    ActionCategoryMandatory: 'Action Category is mandatory',
    AMPDMandatory: 'AM / PDM Name is mandatory',
    SuppressChildAccountMandatory: 'Suppress Child Account is mandatory',
    PleaseEnterNumbers: 'Please enter numbers only',
    PleaseEnterAction: 'Please enter valid action',
    PleaseIntegervalue: 'Please provide an integer value.',
    ViewNameMandatory: 'View name is mandatory',
    CustomerTPIDPOIDMandatory: 'Customer TPID/POID  is mandatory',
    // DesiredOutcomeMandatory:'Desired Outcome is mandatory',
    // CustomerEngagementMandatory:'Customer Engagement Activity is mandatory',
    AttachmentsMandatory: 'Attachments are mandatory',
    DateMandatory: ' What date needed by is mandatory',
    opportunityLostDateMandatory: 'opportunity Lost Date is mandatory',
    InvalidAzureCapacityID: 'Invalid Azure Capacity Type ID',
    InvalidMilestoneStatusReason: 'Invalid Milestone Status Reason',
    InvalidHelpValue: 'Invalid help needed Value',
    SelectRequestType: 'Please Select Request Type',
    EnterNumericvalue: 'Please Enter Numeric value',
    Pickdate: 'Pick a date',
    TicketReasonMandatory: 'Primary blocker is mandatory',
    CustomerTPIDMandatory: 'Customer TPID  is mandatory',
    InvalidCustomerTPID: 'Invalid Customer TPID',
    RedButtonAdditionalRequestWarning:
      'This TPID already has Red Button request(s) this fiscal year (Action ID(s): {actionIds}). Additional requests require justification and may be rejected.',
    RedButtonCustomerTPIDValidationUnavailable:
      'Customer TPID could not be validated. Please try again.',
    CRMAccountMandatory: 'CRM Account Id  is mandatory',
    CustomerNameMandatory: 'Customer Company Name  is mandatory',
    CustomerContactNameMandatory: 'Customer Contact Name  is mandatory',
    CustomerContactEmailMandatory: 'Customer Contact Email  is mandatory',
    RegionMandatory: 'Region is mandatory',
    CountryMandatory: 'Country is mandatory',
    AliasMandatory: 'Alias is mandatory',
    TitlesShouldUnique:
      'Title is required and should be unique with a maximum length of 50 characters.',
    GroupinglevelsAllowed: 'Grouping upto 3 levels are allowed.',
    FieldOperatorMandatory: 'Field and Operator are mandatory.',
    FieldOperatorMandatoryFollowUpShare: 'Please specify them before sharing the view',
    FieldOperatorMandatoryFollowUpApply: 'Please specify them before applying the view',
    FieldOperatorMandatoryFollowUpClone: 'Please specify them before cloning the view',
    GroupingValidationError: 'GroupingValidationError',
    InfraLocationMandatory: 'Infra Location is mandatory',
    CSSNumberMandatory: 'CSS Case Number is mandatory',
    GeneralCategoryMandatory: 'General Category is mandatory',
    UsersImpactNumberMandatory: 'Users Impact Number is mandatory',
    RevenueImpactNumberMandatory: 'Revenue Impact Number is mandatory',
    TypeFundMandatory: 'Type of Fund is mandatory',
    InvestmentAskMandatory: 'Investment Ask is mandatory',
    ECIFRequestAmountMandatory: 'ECIF Request Amount is Mandatory',
    InvestmentAskNumeric: 'Investment Ask should be numeric',
    ECIFRequestAmountNumeric: 'ECIF Request Amount should be numeric',
    StartDateMandatory: 'Project Start Date is mandatory',
    CompletionDateMandatory: 'Estimated Project Completion Date is mandatory',
    DateGreaterStartDate: 'Completion Date should be greater than Start Date',
    deliveryEndDateGreaterStartDate: 'Delivery End Date should be greater than Start Date',
    TypeActivityMandatory: 'Type of Activity is mandatory',
    ROIMandatory: 'ROI is mandatory',
    ROINumeric: 'ROI should be numeric',
    fiscalYearMandatory: 'Fiscal Year is mandatory',
    IncrementalACRMandatory: 'Incremental ACR is mandatory',
    ROIAttachedMandatory: 'ROI attachment is mandatory',
    ECIFSpendPlanMandatory: 'ECIF Spend Plan is mandatory',
    IncrementalRevenueMandatory: 'Incremental Revenue is mandatory',
    ECIFSpendMandatory: 'ECIF Spend is mandatory',
    ECIFSpendNumeric: 'ECIF Spend accepts only decimal values',
    ECIFSpendCurrency: 'ECIF spend should be numeric',
    IncrementalRevenueNumeric: 'Incremental Revenue should be numeric',
    ExpectedOutcomeMandatory: 'Expected Outcome Details is mandatory',
    ApproverMandatory: 'Approver is mandatory',
    EvaluationDetailsMandatory: 'Evaluation Details is mandatory',
    ProgramNameMandatory: 'Program Name is mandatory',
    EnterProgramNameMandatory: 'Please Enter Program Name',
    supplierTypeMandatory: 'Supplier Type is mandatory',
    WorkTypeMandatory: 'Work Type is mandatory',
    InvestmentApprovedMandatory: 'Investment Approved is mandatory',
    InvestmentApprovedNumeric: 'Investment Approved should be numeric',
    RejectedByMandatory: 'Rejected By is mandatory',
    ApprovedOrRejectedByMandatory: 'Approved/Rejected By is mandatory',
    ApprovedDateMandatory: 'Approved Date is mandatory',
    ApprovedOrRejectedDateMandatory: 'Approved/Rejected Date is mandatory',
    DocumentCreationDate: 'Document Creation Date is Mandatory',
    DocumentLink: 'Document Link is Mandatory',
    InvalidDocumentLink: ' Invalid Document Link',
    InvalidSupportCaseLink: ' Invalid Support Case Link',
    InvalidSupportCaseLengthExceedsLink: ' Only 255 characters are allowed',
    RejectedDateMandatory: 'Rejected Date is mandatory',
    RequesterEmailMandatory: 'Requestor Email is mandatory',
    RoleSelectionMandatory: 'Role is mandatory',
    DHAPSelectionMandatory: 'Interested in Definitive Healthcare is mandatory',
    SpecificRoleMandatory: 'Specific Role is mandatory',
    OUMandatory: 'Organizational unit is mandatory',
    MCEMMandatory: 'MCEM is mandatory',
    // PMYT
    CampaignInvestmentNameMandatory: 'Campaign Name is mandatory',
    PartnerCoInvestmentAmountMandatory: 'Partner Co-Investment amount is mandatory',
    SourceOfInvestmentMandatory: 'Source Of Investment is mandatory',
    SolutionPlaysMandatory: 'Solution Plays is mandatory',
    EstimatedStartDateMandatory: 'Start Date is mandatory',
    EstimatedPipelineRevenueErrorMandatory: 'Estimated Pipeline Revenue is mandatory',
    EstimatedCosellReferralsMandatory: 'Estimated Cosell Referrals Investment is mandatory',
    NoNZeroValue: 'Value should be greater than 0',
    RequestTypeFundMandatory: 'Request Type is mandatory',
    AdditionalContactMandatory: 'Additional Contact is Mandatory',
    AdditionalOwnerssNotGreaterFive: 'Additional Contacts should not be greater than 5',
    WorkscopeItemMandatory: 'WorkscopeItem is Mandatory',
    // CoSell
    OpportunityIDMandatory: 'Opportunity ID mandatory',
    ReferralIDMandatory: 'Referral ID mandatory',
    SubCategoryTopicMandatory: 'SubCategory/Topic is mandatory',
    ActivityMandatory: 'Activity is mandatory',
    PartnerContactNameMandatory: 'Partner Name is mandatory',
    PartnerEmailMandatory: 'Partner Email is mandatory',
    PartnerEmailValid: 'Invalid Email',
    PartnerOneIDMandatory: 'Partner ID is mandatory',
    PartnerRegionMandatory: 'Partner Region is mandatory',
    PartnerAreaMandatory: 'Partner Area is mandatory',
    PartnerCountryMandatory: 'Partner Country is mandatory',
    DeliveryLanguageSupportMandatory: 'Delivery Language is mandatory',
    DealStatusMandatory: 'Deal Status is mandatory',
    DealValueMandatory: 'Deal Value is mandatory',
    RequestCategoryMandatory: 'Request Category mandatory',
    segmentMandatory: 'Segment is mandatory',
    subSegmentMandatory: 'Sub Segment is mandatory',
    MicrosoftOwnerMandatory: 'Microsoft owner is mandatory',
    DateAcceptedMandatory: 'Date Accepted is mandatory',
    DateExpiredMandatory: 'Date Expired is mandatory',
    MAXTPIDValidation: 'Number of TPIDs should not be greater than 500',
    OpportunityIDError: 'Something went wrong',
    BusinessTypeMandatory: 'Business Type is mandatory',
    GPSRequestReasonMandatory: 'Request Reason is mandatory',
    FinancialEconomicsMandatory: 'Financial Economics is mandatory',
    GPSStartDateMandatory: 'Start Date is mandatory',
    GPSEndDateMandatory: 'End Date is mandatory',
    GPSStartDateValid: 'End Date should be greater than Start Date',
    InvestmentAmountMandatory: 'Investment Amount is mandatory',
    GPSIncrementalRevenueMandatory: 'Incremental Revenue is mandatory',
    FMVAssessmentNeededMandatory: 'FMV Assessment Needed is mandatory',
    InvestmentAmountNumeric: 'Investment Amount should be numeric',
    GPSPIRReviewDateMandatory: 'PIR Review Date is mandatory',
    GPSPolicyDeclareMandatory: 'Policy Declaration mandatory',
    GPSPartnerOneNameMandatory: 'Partner Name is mandatory',
    GPSPartnerOneIDMandatory: 'Partner ID is mandatory',
    GPSPartnerOneNameMatchMandatory: 'Invalid Partner Name',
    GPSPartnerOneIDMatchMandatory: 'Invalid Partner ID',
    GPSPartnerOneIDInvalid: 'No Data found',
    GPSInvalidFinancialURLMandatory: 'Invalid Financial website URL',
    GPSInvalidBusinessURLMandatory: 'Invalid Business website URL',
    GPSIncrementalRevenueNumeric: 'Incremental Revenue should be numeric',
    GPSRevAdjustNumeric: 'RevAdjust should be numeric',
    GPSOpexNumeric: 'Opex should be numeric',
    GPSACONumeric: 'ACO should be numeric',
    GPSECIFNumeric: 'ECIF should be numeric',
    GPSSponsorshipNumeric: 'Sponsorship should be numeric',
    GPSSkillingNumeric: 'Skilling should be numeric',
    GPSInternalResNumeric: 'Internal Resources should be numeric',
    GPSPIRCaseIdDateMandatory: 'PIR Case ID Date is mandatory',
    GPSMCEMStageMandatory: 'MCEM Stage is mandatory',
    GPSMigrationDateMandatory: 'Migration Date is mandatory',
    GPSInvestmentComplexityMandatory: 'Investment Complexity is mandatory',
    GPSControlType: 'Control Type is mandatory',
    GPSControlOwnerType: 'Control Owner is mandatory',
    GPSControlActivity: 'Control Activity is mandatory',
    GPSControlFrequency: 'Control Frequency is mandatory',
    GPSControlActivitiesDoc: 'Control Activities documentation is mandatory',
    GPSControlStartDate: 'Control Start Date is mandatory',
    GPSControlEndDate: 'Control End Date is mandatory',
    GPSTaskOwner: 'Owner is mandatory',
    GPSsubject: 'Title is mandatory',
    GPSDueDate: 'Due Date is mandatory',
    GPSDescription: 'Description is mandatory',
    GPSStatus: 'Status is mandatory',
    GPSStrategyApproval: 'Strategy Approval is mandatory',
    GPSFinancialApproval: 'Financial Approval is mandatory',
    GPSForecastRevenue: 'Forecast revenue is mandatory',
    GPSForecastAmountNumeric: 'Forecast Revenue should be numeric',
    GPSInvestmentOwner: 'Investment Owner is mandatory',
    InvalidReferralIDError: 'Invalid Referral ID',
    CosellInvalidTPID: 'Invalid TPID',
    GPSChangeRequestTypeMandatory: 'Change Request Type is mandatory',
    GPSFinancialImpactMandatory: 'Financial Impact is mandatory',
    GPSGLPCCodeMandatory: 'GL/PC Code is mandatory',
    GPSSAPGLCodeMandatory: 'SAP GL Code is mandatory',
    GPSIncentiveTargetingSuiteMandatory: 'Incentive Targeting Suite is mandatory',
    //AMM
    ActionTaken: 'Action Taken is mandatory',
    NextBestAction: 'Next Best Action is mandatory',
    NbarStatus: 'NBAR Status is mandatory',
    NbarRejectionReason: 'NBAR Rejection Reason is mandatory',
    NbarRejectionDetails: 'NBAR Rejection Details is mandatory',
    CancellationReason: 'Cancellation Reason is mandatory',
    RejectionReason: 'Rejection Reason is mandatory',
    DisqualificationReason: 'Disqualification Reason is mandatory',
    AAOfferTypeReason: 'AA Offer Type is mandatory',
    AccountOwnerIDReason: 'Account Owner ID is mandatory',
    SponsorshipAmountReason: 'Sponsorship Amount is mandatory',
    SupportRegionReason: 'Support Region is mandatory',
    CustomerCCListReason: 'Customer CC List is mandatory',
    OriginatingCaseIDNumeric: 'Originating Case ID should be numeric',
    PendingActionFrom: 'Pending Action From is mandatory',
    SponsorshipAmountNumericReason: 'Sponsorship Amount should be numeric',
    CustomerContactName: 'Customer Contact Name is mandatory',
    CustomerContactEmail: 'Customer Contact Email is mandatory',
    CustomerContactEmailValid: 'Please enter valid Email addresses',
    CustomerCCEmailValid: 'Please enter valid Email addresses seperated by a semicolon',
    limitCustomerCCEmail: 'You can only add up to 5 email addresses.',
    CustomerAcceptedtheAssessment: 'Customer Accepted the Assessment is mandatory',
    FieldisMandatory: 'Field is mandatory',
    MigrationPartnerAttached: 'Migration Partner Attached is mandatory',
    MigrationPartner: 'Migration Partner is mandatory',
    MigrationPartnerEmail: 'Migration Partner Email is mandatory',
    TermsandConditionEmailReason: 'Terms and Condition Email is mandatory',
    ScopeOfWorkReason: 'Scope of work is mandatory',
    enrollmentNumberReason: 'Enrollment Number is mandatory',
    CommunicationPreferenceMandatory: 'Communication Preference is mandatory',
    HelpTypeMandatory: 'Help Type is mandatory',
    RequestorOnBehalfOfErrorMsgs: 'Request on behalf Of is mandatory',
    PreOrPostMandatory: 'Pre/Post is mandatory',
    ExpectedprojectstartdateMandatory: 'Expected project start date is mandatory',
    ExpectedprojectenddateMandatory: 'Expected project end date is mandatory',
    SponsoringCountryReason: 'Sponsoring Country is mandatory',
    IncrementalRevenueROIReason: 'Incremental Revenue/ROI is mandatory',
    FirstYearACRReason: '1st Year ACR  is mandatory',
    PrimarySolutionReason: 'Primary Solution is mandatory',
    MicrosoftDefenderReason: 'Microsoft Defender for Cloud is mandatory',
    ESIReason: 'ESI is mandatory',
    FTAReason: 'FTA is mandatory',
    RequestAmountReason: 'Request Amount is mandatory',
    ECIFSpendPlanReason: 'ECIF Spend Plan is mandatory',
    CompetitiveBidReason: 'Competitive Bid is mandatory',
    RequestACOReason: 'ACO in addition to ECIF is mandatory',
    ACOIDReason: 'ACO – ID is mandatory',
    ACOCEReason: 'ACO – CE is mandatory',
    ACOCreditRequestAmountReason:
      'ACO Credit Request Amount (USD) is mandatory & Should be greater than 0',
    DealStructureReason: 'Deal Structure is mandatory',
    futureGrowthOfferReason: 'Future Growth Offer is mandatory',
    dealStructureReason: 'Deal Structure is mandatory',
    requestReasonMandatory: 'Request Reason is mandatory',
    ServiceLocationReason: 'Service Location is mandatory',
    ACOCreditRequestAmountNumericReason: 'ACO Credit Request Amount should be numeric',
    ValidationMessageNoSelectedPeople: 'Please select a valid person',
    RevenueisMandatory: 'Revenue is Mandatory',
    VMsSizesMandatory: 'VMs Sizes is mandatory',
    VMDeploymentCountMandatory: 'VM Deployment Count is mandatory',
    PlannedDateofAssessmentReason: 'Planned Date of Assessment is mandatory',
    PlannedDateofMigrationReason: 'Planned Date of Migration is mandatory',
    MicrosoftSellerContactMandatory: 'Microsoft Seller Contact/Account Executive is mandatory',
    CustomerNameisMandatory: 'Customer Name is mandatory',
    CustomerContactMandatory: 'Customer Contact is mandatory',
    MaaSEventNameMandatory: 'Event Name is mandatory',
    MaaSEventDateMandatory: 'Event Date is mandatory',
    MaaSEventRegistrationURLMandatory: 'Event Registration URL is mandatory',
    MaaSInvalidEventRegistrationURL: 'Invalid Event Registration URL',
    MaaSEventTypeMandatory: 'Event Type is mandatory',
    MaaSMarketingPlayMandatory: 'Marketing Play is mandatory',
    MaaSSMPPApprovalIDMandatory: 'SMPP Approval ID is mandatory',
    MaaSDataCaptureDateMandatory: 'Data Capture Date is mandatory',
    MaaSLeadSourceTypeMandatory: 'Lead Source Type is mandatory',
    MaaSEventContactListpasswordMandatory: 'Event Contact List Password is mandatory',
    MaaSPrivacyApprovalEmailMandatory: 'Privacy Approval Email is mandatory',
    MaaSEventsponcorshipagreementMandatory: 'Event sponsorship agreement is mandatory',
    AvsOfferRequestReason: 'AVS Offer is mandatory',
    isScalarCompeteOrBICompeteMandatory: 'Please select Scalar Compete or BI Compete or both',
    VMCToAVSMigrationReason: 'VMC to AVS Migration is mandatory',
    AvsRIQuantityMandatory: 'AVS RI Quantity is mandatory',
    AvsRIRegionMandatory: 'AVS RI Region is mandatory',
    SAPOfferReason: 'Which SAP Offer does this request correspond to is mandatory',
    PrimaryAzureVMseriesReason: 'PRIMARY Azure virtual machine (VM) series is mandatory',
    ThreeYearACRReason: '3 Year ACR is mandatory',
    OfferEngineCaseIDMandatory: 'Offer Engine Case ID is mandatory',
    offerEngineStatusMandatory: 'Offer Engine Status is mandatory',
    InvestmentTitleMandatory: 'Investment Title is mandatory',
    InvestmentDescriptionMandatory: 'Investment Description is mandatory',
    InvestmentStartDateMandatory: 'Investment Start Date is mandatory',
    InvestmentEndDateMandatory: 'Investment End Date is mandatory',
    BaselineRevenueMandatory: 'Baseline Revenue is mandatory',
    BaselineRevenueNumeric: 'Baseline Revenue should be numeric',
    SKUDiscountMandatory: 'SKU Discount is Mandatory',
    SKUDiscountNumeric: 'SKU Discount should be numeric',
    AzureCreditDiscountMandatory: 'Azure Credit Discount is Mandatory',
    AzureCreditDiscountNumeric: 'Azure Credit Discount should be numeric',
    ECIFsolutionApproachMandatory: 'Solution Approach is Mandatory',
    ECIFsupplierNameMandatory: 'Supplier Name is Mandatory',
    ECIFsupplierIDMandatory: 'Supplier ID is Mandatory',
    ECIFsupplierIDNumeric: 'Supplier ID should be numeric',
    ECIFsupplierContactNameMandatory: 'Supplier Contact Name is Mandatory',
    ECIFsupplierContactEmailMandatory: 'Supplier Contact Email is Mandatory',
    ECIFsupplierContactEmailValid: 'Invalid Email',
    ECIFDeliveryServiceManagerMandatory: 'Delivery Service Manager is Mandatory',
    ECIFACODeliveryStartDateMandatory: 'ACO Delivery Start Date is Mandatory',
    ECIFACOCreditRequestAmountMandatory: 'ACO Credit Request Amount is Mandatory',
    ECIFEnrollmentIDMandatory: 'Enrollment ID is Mandatory',
    ECIFEnrollmentIDNumeric: 'Enrollment ID should be numeric',
    ECIFCommercialExecutiveMandatory: 'Commercial Executive is Mandatory',
    ECIFRequestReasonMandatory: 'Request Reason is Mandatory',
    ECIFDealStructureMandatory: 'Deal Structure is Mandatory',
    ECIFACORequestJustificationMandatory: 'ACO Request Justification is Mandatory',
    ECIFAzureCreditOfferMandatory: 'Azure Credit Offer is Mandatory',
    ECIFCreditRequestAmountMandatory: 'Credit Request Amount is Mandatory',
    ECIFCreditRequestAmountNumeric: 'Credit Request Amount should be numeric',
    ECIFReasonMandatory: 'Approved Or Rejected is Mandatory',
    ExpectedBidCloseDateMandatory: 'Expected Bid Close Date is mandatory',
    ECIFRequestedAmount: 'ECIF Requested Amount is mandatory',
    CurrencyReason: 'Currency is mandatory',
    //PMYT Error Messages
    PMYTPartnerMandatory: 'Partner Name is mandatory',
    PMYTPartnerTypeMandatory: 'Partner Type is mandatory',
    PMYTCustomerSegmentMandatory: 'Customer Segment is mandatory',
    TitleErrorMsg: 'Only 250 characters are allowed',
    CancellationAdditionalDetails: 'Cancellation Additional Details is mandatory',
    //SGS
    MSSalesIdMandatory: 'MS Sales ID is mandatory',
    AccountOwnerMandatory: 'Account Owner is mandatory',
    PrimaryGrowthShapingContactMandatory: 'Primary Growth Shaping Contact is mandatory',
    AccountNameMandatory: 'Account Name is mandatory',
    GrowthLeadMandatory: 'Growth Lead is mandatory',
    AEContactMandatory: 'AE Contact is mandatory',
    ATSContactMandatory: 'ATS Contact is mandatory',
    STUContactMandatory: 'STU Contact is mandatory',
    CSAMContactMandatory: 'CSAM Contact is mandatory',
    CSAContactMandatory: 'CSA Contact is mandatory',
    WWCSUEngagedNameMandatory: 'Tech Enablement CSU is mandatory',
    AccountTeamBriefingStatusMandatory: 'Account Team Briefing Status is mandatory',
    AccountTeamBriefingDateMandatory: 'Account Team Briefing Date is mandatory',
    CustomerBriefingStatusMandatory: 'Customer Briefing Status is mandatory',
    CustomerBriefingDateMandatory: 'Customer Briefing Date is mandatory',
    GrowthShapingStepsMandatory: 'Growth Shaping Steps is mandatory',
    GrowthShapingStateMandatory: 'Growth Shaping State is mandatory',
    PrimaryReasonForBlockedOrAtRiskMandatory: 'Primary Reason for Blocked or At-Risk is mandatory',
    RisksOrBlockedDetailsMandatory: 'Risks or Blocked Details is mandatory',
    FieldAccountabilityMandatory: 'Field Accountability Unit is mandatory',
    SubsidiaryMandatory: 'Field Subsidiary is mandatory',
    MACCPersonaMandatory: 'MACC Persona is mandatory',
    RegionalGrowthLeadMandatory: 'Regional Growth Lead is mandatory',
    InvalidMSSalesID: 'Invalid MS Sales ID',
    TitlesLength255Mandatory: 'Title cannot be longer than 255 characters.',
    MaxWordLimitExceeded: 'Allowed only 1000 words, Maximum word limit exceeded',
    StageTransitionBlocked: 'Stage Transition Blocked  ',
    StageTransitionBlockDescription:
      'Cannot move to implementation stage while there are active controls. Please resolve all active controls first.',
    ApprovalRequired: 'Approval required to proceed to Implementation stage.',
    LaunchChecklistError: 'Launch Information required to proceed to Implementation stage',
    LaunchChecklistTransitionError:
      'All Launch Information fields must be set to "Yes" or "No" to proceed to Implementation stage for Commercial Activities.',
    ApprovalRequiredForCompletedStage: 'Approval required to proceed to Completed stage.',
    GLPCCodeRequiredForImplementation: 'GL/PC Code is required to move to Implementation.',
    GLCodeRequiredForImplementation: 'GL Account is required to move to Implementation.',
    PCCodeRequiredForImplementation: 'Profit Center is required to move to Implementation.',
    PartnerContactInfoRequiredForImplementation:
      'Partner Contact Information is required to move to Implementation.',
    StateChangeBlockedByStageValidation:
      'Cannot change State to "{0}" because the current Stage "{1}" has pending validation requirements that must be resolved first.',
    StateChangeRequiresStageCompletion:
      'State "{0}" requires Stage to be "Completed". Please resolve all Stage transition validations before changing State.',
    CaseNotesRequired: 'Case Notes is Mandatory',
    ExceptionTypeRequired: 'Select one option for Exception type',
    ExceptionDecisionRequired: 'Select one option for Exception decision',
    SellerAttestationRequired: 'Select one option for Seller Attestation',
    PartnerNameRequired: 'Partner Name is Mandatory',
    TierRequired: 'Select one option for Tier',
    SubTierRequired: 'Select one option for Sub-Tier',
    DCRegionFromRequired: 'DC Region From is Mandatory Field',
    CustomerCommitmentStatusRequired: 'Customer Status is Mandatory',
    GrowthShapingETARequired: 'Growth Shaping ETA is Mandatory',
    WorkScopeNotValidURL: 'Please enter valid URL for work scope link',
    //Partner Skilling
    RSCampaignMandatory: 'Campaign is mandatory',
    RSPartnerTypeMandatory: 'Partner Type is mandatory',
    RSDeliveryLanguageMandatory: 'Delivery Language is mandatory',
    RSPartnerOneIDMandatory: 'Partner One ID is mandatory',
    PDMNameMandatory: 'PDM Name is mandatory',
    SkillAttachmentMandatory: 'Skilling Plan Attachment is mandatory',
    IfOffersError: 'If Others Offers is mandatory',
    RSPartnerRegion1Mandtaory: 'Partner Region 1 is mandatory',
    RSPartnerCountry1Mandtaory: 'Partner Country is mandatory',
    WhatRegionIsRequestedForAVSPilot: 'What region is requested for AVS Pilot is mandatory',
    AVSSKURequestedForPilot: 'What AVS VCF BYOL SKU is requested for this pilot is mandatory',
    CompeteSalesPlayAndSolutionAreaError:
      'The selected MSX Opportunity does not include the Copilot and Agents at Work Solution Play or the AI in the flow of human ambition Conversation in AI Business Solutions. To continue, please update or choose another MSX Opportunity.',
    BillingAccountIDMandatory: 'Billing Account ID is mandatory',
    BillingAccountIDAlphanumeric: 'Billing Account ID should be alphanumeric',
    ScopeIsMandatory: 'Project Scope is mandatory',
    ExceptionTypeMandatory: 'Exception Type is mandatory',
    ExceptionDecisionMandatory: 'Exception Decision is mandatory',
    SellerAttestationMandatory: 'Seller Attestation is mandatory',
    PartnerTierMandatory: 'Partner Tier is mandatory',
    ExceptionReasonMandatory: 'Exception Reason is mandatory',
    CommentPickerValidation: 'One or more mentioned users are inactive or invalid',
    DiscussionSectionErrorMessage: 'New comment has invalid user id please change it!',
    InvalidFileTypeExcel: 'Invalid file type. Please upload an Excel file (.xlsx or .xls).',
    InvalidFileType: 'Invalid file type.',
    FileSizeExceedsLimit: 'File size cannot exceed 10 MB',
    CorruptedExcelFile:
      'The file appears to be corrupted or is not a valid Excel file. Please upload a valid file.',
    InvalidFile: 'Invalid file. Please select a valid and readable file and try again.',
    MaxAttachmentsAllowed: 'Maximum 3 attachments allowed',
    AMMTriageOwnerMandatory: 'AMM Triage Owner is mandatory',
    UniqueTPIDAllowed: 'The TPID you entered already exists in the SGS. Please enter another TPID.',
    ECIFSpentPlan: 'Grand Total must be equal to Request Amount',
    ProjectStartDate: 'Expected Project Start Date should be less than to End Date',
    ProjectEndDate: 'Expected Project End Date should be less than to Start Date',
  };

  public static PlaceholdersAndTitles = {
    ADOWorkItems: 'ADO Work Items',
    TechnicalFeedback: 'Technical Feedback',
    ResponseIrrelevant: 'The response is irrelevant.',
    ResponseInaccurate: 'The response is inaccurate.',
    Feature: 'Feature',
    OpenView: 'Open View',
    SelectedFields: 'Selected Fields',
    PleaseClick: 'Please click',
    ChildActions: 'Child Actions',
    Feedback: 'Feedback',
    Action360AgentFeedback: 'Help improve Action 360 Agent - share your feedback',
    RelatedItems: 'Related Items',
    RelatedInformation: 'RELATED INFORMATION',
    WorkItemlink: 'Work item link',
    Details: 'Details',
    Related: 'Related',
    AndOr: 'And/Or',
    Error: 'Error',
    CSR: 'CSR',
    CAE: 'CAE',
    CAPSL: 'CAPSL',
    USMaaS: 'MaaS',
    SGCI: 'SGCI',
    SGSTitle: 'SGS',
    PSTitle: 'RegionalSkilling',
    MSRTitle: 'MicrosoftSecurityResearch',
    MSecR: 'Microsoft Security Research',
    APEX: 'MW Programs',
    IAT: 'IAT',
    AHPM: 'MCAPS Help PM',
    VSUQBC: 'VSU/QBC',
    RedButton: 'Red Button',
    Compete: 'ABS',
    CompeteAccordian: 'Compete',
    CSG: 'CSG',
    Date: 'Date',
    Operator: 'Operator',
    Value: 'Value',
    Attachments: 'Attachments',
    GlobalTeam: 'Global Team',
    History: 'History',
    Products: 'Product(s)',
    Nominations: 'Customer Hub',
    Nomination: 'Nominations',
    SelectedWave: 'Selected Wave',
    NominationsTitle: 'Customer Hub Nomination',
    MeetingsPitchDeck: 'Meetings Pitch Deck',
    MeetingsWorkshop: 'Meetings Workshop',
    TCOAnalysis: 'TCO Analysis',
    POCECIFConsumption: 'POC/ECIF - Consumption',
    RequestorTitle: 'Person or People requesting/reporting the Action',
    UnexpectedError: 'An unexpected error has occurred',
    SomethingWentWrong: 'Sorry, Something went wrong',
    RequestorPlaceholder: 'Search Requestors',
    ActionTitlePlaceholder: 'Please Enter Title',
    StakeholderPlaceholder: 'Search Stakeholders',
    RequestedDateTitle: 'Date at which Action was requested/created',
    RegionalSkilling: 'Regional Skilling',
    CreateText: 'Create',
    Save_close: 'Save & Close',
    save: 'Save',
    GTT: 'GTT',
    WorkaroundDetailsplaceholder:
      'Requestor click to provide information on what has been done to attempt to resolve the action or provide a workaround solution',
    CustomerImpactTitle:
      'Click to add what happens to the customer if the solution is not provided? Or is provided later than the date needed by?',
    LTTitle: 'Leadership Team',
    CustomerScenarioDesiredOutcomeTitle:
      'Click to add what is the customer/partner trying to do? And why? What is the solution requested?',
    PDMHelpNeededTitle: 'Check if action needs PDM help',
    PDMHelpNeededLabel: 'Is Help Needed from Shared Partner?',
    BusinessProposal: 'Business Proposal',
    SubmittedAWR: 'Submitted for AWR',
    PartnerTitle: 'Shared Partner',
    PDMTitle: 'Primary PDM',
    PDMEmail: 'PDM Email',
    ClosureStatementplaceholder:
      'Click to add high level summary of the situation, if it was resolved, and how it was resolved.',
    StatusUpdateplaceholder:
      'Click to add action update summaries. Input entries in reverse chronological order.   M/D – [Name]: Entry 1/20 – John Smith: I entered this as a example.',
    ClosureStatementTitle: ' Closure Statement',
    StatusUpdateTitle: 'Status Update',
    DiscussionTitle: 'Discussion',
    StatusUpdate: 'Status Update',
    TabShow: 'tab-show',
    TabHide: 'tab-hide',
    Tabs: 'Tabs',
    Decision: 'Decision',
    SelectMilestoneReason: 'Select Milestone Reason',
    ViewTask: 'View',
    MilestoneReason: 'Milestone Reason',
    SelectMilestoneNonRecurring: 'Select Non Recurring',
    SelectNominationIntent: 'Select Nomination Intent',
    MilestoneLinkTooltip: 'Click to visit MSX milestone page',
    OpportunityLinkTooltip: 'Click to visit MSX opportunity page',
    MilestoneID: 'Milestone ID',
    CopyMilestoneIDToolTip: 'Copy Milestone ID',
    CopyOpportunityIDToolTip: 'Copy Opportunity ID',
    TimeZone: 'Time Zone',
    OpportunityID: 'Opportunity ID',
    Example: '(Ex: 7-ABCDEFGHI)',
    AgreementId: 'Agreement Id',
    AgreementName: 'Agreement Name',
    lineRemove: 'line-copyRemove',
    milestoneid: 'milestoneid',
    Milestone: 'Milestone(s)',
    NO: 'No',
    YES: 'Yes',
    Name: 'Name',
    DateCreated: 'Date Created',
    Share: 'Share',
    Favorite: 'Favorite',
    Unfavorite: 'Unfavorite',
    ApplyView: 'Apply View',
    Customer: 'Customer',
    Competition: 'Competition',
    AdditionalContext: 'Additional Context',
    opportunityid: 'opportunityid',
    TPID: 'TPID',
    tpid: 'tpid',
    actionID: 'ActionId',
    FileName: 'File Name',
    getCRMAccountId: 'CRMAccountId',
    EnterTPID: 'Enter TPID',
    EnterEventName: 'Enter Event Name',
    EventName: 'EventName',
    EventDate: 'EventDate',
    EventRegistrationURL: 'EventRegistrationURL',
    SMPPApprovalIDText: 'SMPPApprovalID',
    EventContactListPassword: 'EventContactListPassword',
    ClickHereToAddLink: 'Click Here To Add Link',
    SMPPApprovalID: 'SMP-XXXX-TEST',
    EnterEventContactListPassword: 'Enter Event Contact List Password',
    solutionAreaMSXlabel: 'Solution Area (MSX)',
    solutionAreaMSXtitle: 'Solution Area under which Action falls',
    solutionAreaMSXplaceholder: 'Select Solution Area (MSX)',
    SolutionAreaUAT: 'Solution Area',
    BusinessDivision: 'Business Division',
    ToolsImpacted: 'Tools Impacted',
    RolesImpacted: 'Roles Impacted',
    IntakeType: 'Intake Type',
    SolutionAreaUATplaceholder: 'Select Solution Area',
    BusinessDivisionplaceholder: 'Select Business Division',
    ToolsImpactedplaceholder: 'Select Tools Impacted',
    RolesImpactedplaceholder: 'Select Roles Impacted',
    IntakeTypeplaceholder: 'Select Intake Type',
    OperatingUnitplaceholder: 'Select Operating Unit',
    AdditionalTheme: 'Additional Theme',
    SelectAdditionalTheme: 'Select Additional Theme',
    Theme: 'Theme',
    EngagementCategory: 'CAE Engagement Category',
    EngagementRegion: 'CAE Engagement Region',
    ScenarioCohort: 'CAE Scenario Cohort',
    FirstPartyOffering: 'CAE First Party Offering',
    FirstPartySolution: 'CAE First Party Solution',
    ThirdPartySolution: 'CAE Third Party Solution',
    SelectTheme: 'Select Theme',
    SelectSubState: 'Select Sub State',
    SelectState: 'Select State',
    SelectRAIDStatus: 'Select Status',
    StateTitleandlabel: 'State',
    SubStatelabel: 'SubState',
    SubStateTitleandlabel: 'Sub State',
    subStates: 'subStates',
    workItemStates: 'workItemStates',
    HelpNeeded: 'Help Needed',
    HelpNeededApex: 'Help Needed APEX',
    PartOfCollabAppsContest: 'Part of Collab Apps Contest',
    WhenDoesTheCustomerWantToEnage: 'When does the Customer want to engage?',
    CustomerGoLiveDate:
      'When does the Customer want to go live with proposed solution or activity?',
    ActivityGoLiveDate: 'Proposed solution/activity go-live date?',
    ExecutiveSponsor: 'Is there an Executive sponsor from Customer to this engagement?',
    BusinessAndITStakeholders: 'Are there IT and Business stakeholders for this engagement?',
    PreequisitesWorkload:
      '(Pre-Requisites: Non-zero Est. MAU, Est.Date is in the future and workload type same as wave workload)',
    NoMilestonesDisplay: 'No Milestones to display',
    ReferredTo: 'Referred To',
    ProjectedAppMAUForTheProposedSolution: 'Projected App MAU for the Proposed Solution',
    MAUImpact: 'MAU Impact',
    HelpNeededTitle: 'Choose most suitable options for your requirement',
    HelpNeededPlaceholder: 'Select Help Needed',
    NA: 'NA',
    CopyTooltipMsg: 'Copy Link to Share',
    MoreOptions: 'More Options',
    ApplyTooltipMsg: 'Apply View',
    SharedWithMe: 'sharedwithme',
    IgnoreDefaultDate: '01/01/0001',
    ActionState: 'Action State',
    ActionStatePlaceholder: 'Select Action State',
    TechnicalFeedbackStatus: 'TechnicalFeedbackStatus',
    RelatedLinks: 'RelatedLinks',
    Userchangeddate: 'Userchangeddate',
    Assigneddate: 'Assigneddate',
    Statechangedate: 'Statechangedate',
    LastV2Changed: 'LastV2Changed',
    ChildRelation: 'System.LinkTypes.Hierarchy-Forward',
    ParentRelation: 'System.LinkTypes.Hierarchy-Reverse',
    RelatedRelation: 'System.LinkTypes.Related',
    LinkedItemsLabel: 'Linked Items',
    ChildItemsLabel: 'Child Items',
    RelatedHyperLinks: 'Hyperlink',
    TriageActions: 'Triage Actions',
    ACRWeeklyScrum: 'ACR Weekly Scrum',
    MWWeeklyScrum: 'MW Weekly Scrum',
    BAWeeklyScrum: 'BA Weekly Scrum',
    Reports: 'Reports',
    Core: 'CORE',
    Api: 'API',
    Graph: 'GRAPH',
    Devops: 'DEVOPS',
    SupportDeliveryHelpNeededPlaceholder:
      'Briefly describe blocker and action plan.\nPlease call out the help needed and assign owner.',
    SupportDeliveryHelpNeededLabel: 'What is the blocker, action plan, and help needed?',
    PartnerSharing: 'Partner Sharing',
    Sharing: 'Sharing',
    ActionFoundOrhidden: 'Action is either not found or hidden',
    ActionNotFound404Error: 'You do not have permissions to view requests submitted by others',
    ActionNotAuthorized: 'You are not authorized to view this request',
    UnResolvedUrlMessage: 'Current url is not able to resolve',
    RequestorsAccessHidden: 'Requestors does not have access',
    MemberAccessRestricted: 'Only members of Regional Skilling Desk can edit the request',
    MSecRAccessRestricted: 'Only members of Microsoft Security Research can edit the request',
    AOAICapacityAccessRestricted:
      "This request can no longer be edited. If there's an error, please submit a new UAT.",
    AOAICapacityCreateNotice:
      'Review all information carefully before submitting. Submitted UATs cannot be edited. Missing or incorrect details will require submitting a new UAT.',
    ActionId: 'Action Id',
    LoadingDetails: 'Loading Details ......',
    ActionUpdatedSuccessfully: ' Action updated successfully',
    FieldUpdatedSuccessfully: ' Field(s) updated successfully',
    FieldUpdateError: ' Field(s) faild to update',
    ActionValidationMessage: 'Please complete all required fields to save',
    GPSValidationMessage: 'Please fill PIR mandatory fields',
    EmailLinkingSucessfully: 'You have linked an email to the current action',
    EmailDeLinkingSucessfully:
      'Delink Email An Email have been successfully delinked from the current action',
    ActionUpdateFailed: 'Sorry, Action update failed',
    CopyLink: 'Copy Action Link',
    CopyRiskLink: 'Copy Risk Link',
    CopyIssueLink: 'Copy Issue Link',
    CopyDepLink: 'Copy Dependency Link',
    Capelogosvg: 'capelogosvg',
    SelectSolutionArea: 'Select Solution Area',
    SelectMeetingType: 'Select Meeting Type',
    PreferredPartnerNameUAT: 'Preferred Partner Name',
    EnterPreferredPartnerName: 'Enter Preferred Partner Name (UAT)',
    PartnerOneNameMSX: 'Partner One Name (MSX)',
    EnterPartnerOneNameMSX: 'Enter Partner One Name (MSX)',
    EnterPartnerOneId: 'Enter Partner One Id',
    PackageId: 'Package Id',
    PackageName: 'Package Name',
    EnterPackageId: 'Enter Package Id',
    EnterPackageName: 'Enter Package Name',
    DeliveryBlockers: 'DeliveryBlockers',
    EnterDeliveryBlockers: 'Select Delivery Blockers',
    DeliveryBlockersLabel: 'Delivery Blockers',
    AssignedTeam: 'Assigned Team',
    OperatingUnit: 'Operating Unit',
    SelectAssignedTeam: 'Select Assigned Team',
    ActionCategory: 'Action Category',
    SelectActionCategory: 'Select Action Category',
    TypeOfBlocker: 'Type of Blocker',
    FundingInformation: 'Funding Information',
    Category: 'Category',
    CaplCategory: 'Action Category',
    SelectCategory: 'Select Category',
    TypeOfFund: 'Type of Fund',
    SelectTypeOfFund: 'Select Type of Fund',
    InvestmentAsk: 'Investment Ask $',
    SelectInvestmentAsk: 'Enter Investment Ask in $',
    EouTitle: 'Enterprise Operating Unit',
    Eou: 'EOU',
    SelectEou: 'Select EOU',
    EouTooltip: 'Please select a value in the Area field, before selecting an EOU',
    IntegerTooltip: 'Only 10 numeric values are accepted',
    MilestoneWorkload: 'Milestone Workload',
    SelectMilestoneWorkload: 'Select Milestone Workload',
    MeetingType: 'Meeting Type',
    MeetingTypeTitle: 'Forum in which the Action was surfaced',
    SelectedMeetingTypeOrigin: 'Unlocking EA/CSP/MCA-E Renewals',
    SelectedMeetingTypeOriginTitle:
      'Select "Unlocking EA/CSP/MCA-E Renewals" for licensing strategy misalignment between Microsoft, customer and partner. This escalation process ensures your case will be reviewed by key stakeholders who can offer solutions to help you resolve blockers. This is applicable to EMEA and ASIA. AMERICAS will continue to submit escalations via Pathfinder. Refer to link above for details.',
    RemovePDMEmail: 'remove PDM Email',
    Industry: 'Industry',
    SelectIndustry: 'Select Industry',
    SelectHelpNeededApex: 'Select Help Needed APEX',
    Area: 'Area',
    SelectArea: 'Select Area',
    Timezone: 'Time Zone',
    SelectTimezone: 'Select Time Zone',
    AreaTitle: 'Account Area Name',
    SelectUpdateExisting: 'Select Existing account',
    CsgProductList: 'CSG Product List',
    SelectCsgProductList: 'Select CSG Product List',
    MilestoneOwner: 'Milestone Owner',
    MilestoneOwnerPlaceholder: 'Not Available',
    MilestoneOwnerCopyMessage: 'Milestone Owner added as Contributor 1',
    ShowContactCard: 'Show contact card',
    ContactCard: 'ContactCard',
    ChevronDown: 'ChevronDown',
    AddTo: 'AddTo',
    Add: 'Add',
    ChevronDownMed: 'ChevronDownMed',
    ChevronRightMed: 'ChevronRightMed',
    NavigateBack: 'NavigateBack',
    NavigateBackMirrored: 'NavigateBackMirrored',
    PageEdit: 'PageEdit',
    Label: 'LABEL',
    SalesPlay: 'Sales Play',
    Conversations: 'Conversations',
    SendEmailLabel: 'Email Action',
    SendRiskLabel: 'Email Risk',
    SendIssueLabel: 'Email Issue',
    SendDepLabel: 'Email Dependency',
    AdditionalArea: 'Additional Area',
    AdditionalEOU: 'Additional EOU',
    AdditionalAreaPlaceholder: 'Select Additional Area',
    AdditionalEOUPlaceholder: 'Select Additional EOU',
    SelectOpportunityStatus: 'Select Opportunity Outcome',
    DoneStateDialogPrompt:
      'Have you informed the Requestor of the outcome prior to closing this action?',
    CancelStateDialogPrompt:
      'Have you informed the Requestor of the outcome prior to cancelling this action?',
    CloseStatementError: 'Please provide a descriptive closure statement.',
    General: 'General',
    DMP: 'DMP',
    Assessment: 'Assessment',
    ECIFrequestsexceptFAMandCMAAMA: 'ECIF requests except FAM and CMA/AMA',
    AssistedMigration: 'Assisted Migration',
    BREPSAandOtheDetails: 'BREP, SA, and other on-prem renewals',
    Global: 'Global',
    CustomerInformation: 'Customer Information',
    BlockerInformation: 'Blocker Information',
    EngineeringInformation: 'Engineering Information',
    Information: 'Information',
    ListeningCirclesDetails: 'Listening Circles Details',
    Limit: 'Limit',
    OpportunityIntentPlaceholder: 'Select Opportunity Intent',
    OpportunityIntent: 'Opportunity Intent',
    MicrosoftServiceRegions: 'Microsoft Service Region',
    SelectMicrosoftServiceRegion: 'Select Microsoft Service Region',
    OpportunityStatus: 'Opportunity Status',
    EnterOpportunityStatus: 'Enter Opportunity Status',
    Consumption: 'Consumption',
    Enter: 'Enter',
    Space: ' ',
    Escape: 'Escape',
    SelectMeetingSubType: 'Select Meeting Sub-Type',
    MeetingSubType: 'Meeting Sub-Type',
    ConflictDialogTitle: 'Action update failed',
    ConflictCloseButtonLabel: 'Close',
    ConflictDialogText:
      'This Action has been changed by someone else since you opened it. You will need to refresh it and discard your changes.',
    Cancel: 'Cancel',
    Reload: 'Reload',
    MarkAsDefaultView: 'Mark as Default',
    SemiColon: ';',
    comma: ',',
    EstMonthlyUsage: 'Est. Monthly Usage (USD)',
    NonRecurring: 'Non Recurring?',
    BACV: 'BACV',
    NonRecurringPlaceholder: 'Enter Non Recurring',
    BACVPlaceholder: 'Enter BACV',
    EstMonthlyUsagePlaceholder: 'Enter Est. Monthly Usage',
    ExitCriteria: 'Exit Criteria',
    AnyOtherDetails: 'Any other details',
    NextAction: 'Next Action',
    InvestmentApproved: 'Investment Approved',
    SelectInvestmentApproved: 'Investment Approved in $',
    RejectedBy: 'Rejected By',
    Description: 'Description',
    DealDetails: 'Deal Details',
    SelectProjectOrDealDetails: 'Click to add Project or Deal Details',
    ProjectStartDate: 'Start Date',
    ProjectCompletionDate: 'End Date',
    TypeOfActivity: 'Type of Activity',
    SelectTypeActivity: 'Select Type of Activity',
    ROI: 'Total ROI',
    IncrementalACR: 'Incremental Revenue attached ?',
    ROIAttached: 'ROI attached ?',
    DownloadROITemplate: 'Download ROI Template',
    IncrementalRevenue: 'Incremental Revenue',
    ECIFSpend: 'ECIF Spend',
    SelectIncrementalACR: 'Select',
    SpendPlan: 'ECIF spend plan attached ?',
    SelectSpendPlan: 'Select',
    AdditionalContact: 'Additional contacts to be notified',
    ExpectedOutcome: 'Expected Outcome',
    SelectExpectedOutcome: 'List specific KPIs adapted to monitor the succes of the case.',
    ExecutiveCE: 'Commercial executive CE for the customer',
    ApprovedBy: 'Approved By',
    ApprovedDate: 'Approved Date',
    ApprovedOrRejectedDate: 'Approved Date/Rejected Date',
    ApprovedOrRejectedBy: 'Approved By/Rejected By',
    ApprovedOrRejected: 'Approved/Rejected',
    AmountNeeded: 'How did you evaluate the ECIF amount needed to cover the work?',
    ApproversNote: 'Only approvers can edit request.',
    CostBreakdown: '(Describe how you got to the ECIF amount, cost break down)',
    SupplierSameAsCustomer: 'Is supplier same as customer?',
    SelectSupplierSameAsCustomer: 'Select',
    EnrollmentNo: 'Enrollment Number',
    SelectEnrollmentNo: 'Enter Enrollment Number',
    ECIFProgramName: 'ECIF Program Name',
    SelectECIFProgramName: 'Select ECIF Program Name',
    EnterECIFProgramName: 'Enter Program Name',
    LicensingProgram: 'Licensing Program',
    SelectLicensingProgram: 'Select Licensing Program',
    BusinessImpact: 'Business Impact',
    WorkaroundDetails: 'Workaround Details',
    WorkAround: 'Workaround',
    NeededDueDate: 'What date is this needed by (Due Date)',
    HighLevelBusinessChallenge: 'High Level Business Challenge',
    CustomerGoalState: 'Customer Goal State',
    CustomerScenario: 'Customer Scenario',
    CustomerImpact: 'Customer Impact',
    CompeteParity: 'Compete Parity',
    AlternativeSolutions: 'Alternative Solutions',
    AddMitigationPlan: 'Click to add Mitigation Plan',
    AddImpactSummary: 'Click to add Impact Summary',
    OfferRelated: 'Offer Related',
    ReproSteps: 'Repro Steps',
    ObservedOutput: 'Observed Output',
    ExpectedOutput: 'Expected Output',
    Notes: 'Notes',
    RelatedIssueEscalation: 'Related Issue/Escalation',
    PoweredByCloudGPT: 'Powered by CloudGPT',
    MitigationPlan: 'Mitigation Plan',
    ImpactSummary: 'Impact Summary',
    DesiredOutcome: 'Desired Outcome',
    DesiredOutcomeTitle:
      'Click to add what is the customer/partner trying to do? And why? What is the solution requested?',
    StrategicAccount: 'Strategic Account',
    QBCNotes: 'QBC Notes',
    InternalRolespecificNotes: 'Internal Role-specific Notes',
    HideCharts: 'Hide Charts',
    ShowCharts: 'Show Charts',
    Comments: 'Comments',
    Whathelpisneeded: 'What help is needed?',
    WhathelpisneededPlaceholder: 'Click to add Description',
    CompeteWhatHelpIsNeededPlaceholder: `Sample Text: "A user needs to extract all items from an Excel file and count the total number of entries. The expectation is an accurate numeric result, but Copilot returns inconsistent or incorrect counts.
    Example Prompts and Responses:
    Prompt 1: "Extract all product names from column A and tell me how many items are listed." Observed Issue: Copilot retrieves the list but reports 87 items when the actual count is 92.
    Prompt 2:"Count the number of unique customer IDs in column B."  Observed Issue: Copilot includes duplicates in the count, returning 1,200 instead of the correct 1,045.
    Prompt 3: "Summarize the total number of rows with non-empty values in column C."Observed Issue: Copilot misinterprets blank cells and reports incorrect totals"`,
    CompeteCustomerScenarioPlaceholder: `Sample Text: "Microsoft Products in play: M365 Copilot, Copilot Chat, Copilot Studio Support Requested: SME Product & Engineering Support to address performance gaps in M365 Copilot vs. ChatGPT and provide direct feedback channels to improve user experience and provide a white glove experience to Contoso by providing short-term improvements."`,
    CompeteCustomerImpactPlaceholder: `Sample Text: "Contoso (S500) signed new 5-year commitments in July 2024 (July 24 – June 29) across all major solution areas including M365 Copilot at scale (ahead of consumption plan: 8,500 out of 9,500 Copilot licenses deployed, 90% active usage)"`,
    ActionOwnerMandatory: 'Action owner is mandatory',
    WaveDescription: 'Wave Description',
    ActionOwner: 'Action Owner',
    CommercialExecutive: 'Commercial Executive CE for the customer',
    Requestors: 'Requestor(s)',
    Requestor: 'Requestor',
    ProblemRequestors: 'Problem requestors',
    AccountableExec: 'Accountable Exec',
    ExecDirect: 'Exec Direct',
    ManagingOwner: 'Managing Owner',
    Contributor1: 'Contributor 1',
    Contributor2: 'Contributor 2',
    Ese: 'ESE',
    ESEFixedAccount:
      'ESE(Enterprise Security Executive)is the equivalent of AE in CSG. Sometimes ESE submits the request and sometimes the escalation comes from different source and the requestor is different than the ESE. Requestor can be anyone, but ESE is fixed for account',
    AdditionalFilters: 'Filters',
    SelectFilters: 'Select Filter(s)',
    BullseyeTarget: 'BullseyeTarget',
    filters: 'Filters',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    TenantID: 'Tenant ID',
    TenantIDFedReqPlaceHolder:
      'For seats-based products, provide the alphanumerical Tenant ID from Lynx to help the Product Groups assess priority.',
    PrevRow: 'PrevRow',
    NextRow: 'NextRow',
    Edit: 'Single click To Edit',
    Engagement: 'Engagement',
    BusinessScenario: 'Problem Details',
    BusinessScenarioTitle: 'BusinessScenario',
    InternalTriageDate: 'Internal Triage Date',
    TriageComments: 'Triage Comments',
    Stakeholders: 'Stakeholders',
    ProblemType: 'Problem Type',
    ProblemTypePlaceholder: 'Select Problem Type',
    StrategicCategory: 'Strategic Category',
    StrategicCategoryPlaceholder: 'Select Strategic Category',
    Workaround: 'Work Around',
    TicketVolumePerMonth: 'Ticket Volume Per Month',
    ProcessOrToolsImpacted: 'Process Or Tools Impacted',
    L1BusinessProcess: 'L1 Business Process (BPAL1)',
    L2BusinessProcess: 'L2 Business Process (BPAL2)',
    L3BusinessProcess: 'L3 Business Process (BPAL3)',
    L1BusinessProcessPlaceholder: 'Select L1 Business Process (BPAL1)',
    L2BusinessProcessPlaceholder: 'Select L2 Business Process (BPAL2)',
    L3BusinessProcessPlaceholder: 'Select L3 Business Process (BPAL3)',
    RejectedDate: 'Rejected Date',
    SupportScenariosImpacted: 'Support Scenarios Impacted',
    SupportScenariosPlaceholder: 'Click to add Support Scenario Impacted',
    Synthesizer: 'Synthesizer',
    AddSynthesizer: 'Click to add Synthesizer',
    ActualCreatedDate: 'Actual Created Date',
    ExecutingTeamName: 'Executing Team Name',
    ExecutingTeamNamePlaceholder: 'Select Executing Team Name',
    ExecutingTeamADOLink: 'Executing Team ADO Link',
    AhpmTitle: 'MCAPS Help Problem Management',
    CSRTitle: 'Customer Success Room',
    CustomerTPID: 'Customer TPID',
    EnterCustomerTPID: 'Enter Customer TPID',
    CRMAccountId: 'CRM Account ID',
    EnterCRMAccountId: 'Enter CRM Account ID',
    CustomerCompanyName: 'Customer Company Name',
    EnterCustomerCompanyName: 'Enter Customer Company Name',
    CustomerContactName: 'Customer Contact Name',
    EnterCustomerContactName: ' Enter Customer Contact Name',
    CustomerContactEmail: 'Customer Contact Email',
    EnterCustomerContactEmail: 'Enter Customer Contact Email',
    CustomerContactPhoneNumber: 'Customer Contact Phone Number',
    EnterCustomerContactPhoneNumber: 'Enter Phone Number',
    Region: 'Region',
    Country: 'Country',
    CssCaseNumber: 'CSS Case Number',
    TheValueMustBeANumber: 'The value must be a number',
    SelectGeneralCategory: 'Select General Category',
    GetHelpTicketNumber: 'GetHelp Ticket Number (if applicable)',
    GetHelpTicketCreationDate: 'GetHelp Ticket Creation Date (if applicable)',
    SelectDate: 'Select Date',
    SelectDueDate: 'Select Due Date',
    ImpactNumbersUsers: "Impact Numbers: Users (Company's staff + End Users)",
    ImpactNumbersRevenue: 'Impact Numbers: Revenue for the customer',
    TicketReason: 'Select the primary blocker',
    WhathelpisneededTooltip: `Briefly describe the customer's issues and provide any additional relevant information in 2-3 sentences.`,
    CRMAccountIdTooltip: 'Can be found in CALC and Dynamics',
    CustomerCompanyNameTooltip: 'Name of the company associated with the TPID/CRM Account ID',
    CustomerContactNameTooltip:
      'Full name of the individual who will be contacted while troubleshooting the issue',
    CustomerContactEmailTooltip:
      'Email of the individual who will be contacted while troubleshooting the issue',
    CustomerContactPhoneNumberTooltip:
      'Phone no. of the individual who will be contacted while troubleshooting the issue',
    CssCaseNumberTooltip: 'Can be 16 digits or 8 digits. 14 digits for GCR',
    ImpactNumbersRevenueTooltip: 'Since beginning of the issue',
    FullNameSSSPInfo: 'Information for the SSSP attached to the customer.',
    MSXIDProductSaleInfo:
      'Information for Product Sale and/or a Unified Support Contract opportunities in Dynamics.',
    MSXSupportContactInfo:
      'An active CRM Opportunity for a Product Sale and/or a NSC Sale MUST be present for the case to be reviewed.',
    FullNameSSSPPlaceholder: 'Enter Full Name of the Support Specialist (SSSP)',
    EmailSSSPPlaceholder: 'Enter Email of the Support Specialist (SSSP)',
    MSXPlaceholder: 'Enter MSX (Opportunity) ID of the blocked product sale',
    ValueNumberPlaceholder: 'The value must be a number',
    CurrentFYPlaceholder: 'Enter Current FY Revenue',
    RBCaseNumberPlaceholder: 'Enter RB Case Number',
    NSCPlaceholder: 'Enter MSX (Opportunity) ID of the blocked NSC Unified Support Contract',
    NSCStartDate: 'Support NSC Start Date',
    RBCaseCloseDate: 'RB Case Close Date',
    RequestedDate: 'Requested Date',
    BusinessImpactStatement: 'Business Impact Statement',
    SelectRegion: 'Select Region',
    SelectCountry: 'Select Country',
    CSSCaseCreationDate: 'CSS Case Creation Date',
    InputMustBeNumericOnly: 'Input must be numeric only',
    MCAPSHelpPM: 'MCAPS Help PM',
    IntakeRequest: 'Intake Request',
    AOAITitle: 'Azure AI Capacity',
    AOAICapacity: 'Azure AI Capacity',
    AOAIAssignedTeam: 'MCAPS AOAI Capacity',
    AOAICapacity_Old: 'AOAI Capacity',
    NonAICapacityNodeName: 'Capacity (Non-AI)',
    SelectRequestType: 'Please Select Request Type',
    IsAmandementSignedToolTip: 'Amendment M1072 full policy details available ',
    RequestTypeGPU: '3P GPU (GPU)',
    RequestTypeGPUToolTip:
      'If the request is for NC Series 3P GPUs, the customer can request directly through their Azure portal',
    RequestTypeProvision: 'Provisioned Classic (PTU-C)',
    RequestTypePaygo: 'PAYGO (TPM)',
    RequestTypeProvisionManaged: 'Provisioned Managed (PTU-M)',
    RequestTypeDatazoneStandard: 'DatazoneStandard',
    RequestTypeAccess: 'Access Request',
    Here: 'here',
    Send: 'Send',
    AOAI: 'Azure AI Capacity',
    MEA: 'MEA Bedrock Requests',
    MEATitle: 'MEA Bedrock Request -',
    ClearFilters: 'Clear Filters',
    ClearFiltersIcon: 'ClearFilter',
    Morefilters: 'More filters',
    PoweredByAction360: 'Powered by Action 360 Platform',
    PrivacyStatement: 'Privacy Statement',
    CookiesStatement: 'Cookies Statement',
    Microsoft: '© Microsoft',
    TermsOfUse: 'Terms of Use',
    AnyRegion: 'Any Region',
    selectTag: 'Please select an option',
    selectDealType: 'Select Deal type',
    AccountPartnerName: 'Account TPID / Partner Name',
    EnterAccountPartnerName: 'Enter Account / Partner Name',
    TPIDPOID: 'TPID / POID',
    EnterTPIDPOID: 'Enter TPID / POID',
    FirstName: 'First Name',
    EnterFirstName: ' Enter First Name',
    LastName: 'Last Name',
    EnterLastName: ' Enter Last Name',
    Alias: 'Alias',
    EnterAlias: ' Enter Alias',
    Title: 'Title',
    GPSTitlePlaceholder: 'Insert your partner investments name',
    PMYTTitlePlaceholder: 'Input Campaign Name',
    IssueId: 'Issue Id',
    BusinessJustification: 'Business Justification',
    EnterBusinessJustification: 'Enter Business Justification',
    AMPDMName: 'AM / PDM Name',
    SuppressChildAccount: 'Suppress Child Account',
    GeneralCategory: 'General Category (Check all that apply)',
    select: 'Select',
    CreateRedButtonSMCAction: 'Create Red Button SMC Request',
    CreateRedButtonTSIAction: 'Create Red Button TSI Request',
    CreateCAPSLAction: 'Create CAPSL Request',
    CreateUSMaaSAction: 'Create MaaS Request',
    CreateMaccException: 'Create new MACC Unified Purple Empowerment request',
    CreateMSFedAction: 'Create Microsoft Federal Request',
    CreatePMYTAction: 'Create New Partner Marketing Yield Tracker Request',
    CreateSGSAction: 'Create New Strategic Growth Shaping Request',
    CreatePSAction: 'Create New Regional Skilling Request',
    CreateGSIAction: 'Create GSI ACO PIR Request',
    CreateAzureISVAction: 'Create New Azure Sponsorship for ISV Request',
    CreateEcifAction: 'Create New ECIF Action Request',
    CreateMEAAction: 'Create New MEA Bedrock Request',
    CreateMSRAction: 'Create New Microsoft Security Research Request',
    CreateGeneralAction: 'Create New General Action Request',
    CreateFieldAction: 'Create New Field Action Request',
    CreateNonAIFieldAction: 'Create New Non AI Capacity Request',
    CreateNonAICapacityPlanFieldAction: 'Create New Capacity Plan Request',
    CreateMCAPSLeaderROBTitle: 'Create New MCAPS Leader RoB Request',
    CreateMcapsCoSellTiltle: 'Create New CoSell Action Request',
    CreatePartnerInvestmentAction: 'Create New Partner Investment Request',
    DownloadTemplate: 'Download Template',
    BulkImportActions: 'Bulk Import Actions',
    CreateCompeteAction: 'Create Compete Request',
    CreateAPEXAction: 'Create New APEX Action Request',
    CreateSupportAction: 'Create New Support Delivery Scrum Action Request',
    CreateMCAPSHelpPMRequest: 'Create MCAPS Help Problem Management Request',
    CreateMCAPSHelpArtemisRequest: 'Create MCAPS Help Problem Management Request',
    CreateMWHubsRequest: 'Create MWHub Nomination Request',
    CreateRedButtonRequest: 'Create Red Button Request',
    CreateRedButtonActionToolTip: `The form title will change based on the selected "Assigned Team". Please select the appropriate "Assigned Team"`,
    TeachingBubbleToolTip:
      'Learn more about creating and managing Actions in the Unified Action Tracker. Click below link to open a PPT with detailed information.',
    FieldRequest: 'Field Request',
    GeneralRequest: 'General Request',
    APEXRequest: 'APEX Request',
    MWHubCustomerRequest: 'Customer Hub Nomination',
    RedButtonCreateRequest: 'Red Button Request',
    BulkImportActionsMenu: 'Bulk Import',
    DownloadTemplateMenu: 'Download Template',
    AHPMRequest: 'MCAPS Help PM Request',
    CompeteRequest: 'Compete Request',
    MCAPSLeaderRoB: 'MCAPS Leader RoB',
    AzureSponsorshipISV: 'Azure Sponsorship for ISV',
    MSFed: 'Microsoft Federal',
    TechROB: 'Tech RoB',
    Approver: 'Approver',
    regionAmericas: 'Americas',
    regionAsia: 'Asia',
    regionEMEA: 'EMEA',
    amm_synapsededicatedpoolworkload: 'Select the Migration Scenario',
    amm_factoryengagedformigration: 'Is Factory engaged to complete the Factory migration scope?',
    ECIF: 'ECIF Pre Approval',
    GPS: 'Partner Investments',
    GSI: 'GSI ACO PIR',
    MCAPS_GSI: 'MCAPS GSI ACO PIR',
    GPSEmailTemplate: 'GPS Investments',
    GPSComplianceCheckList: 'Compliance Checklist',
    GPSLaunchChecklist: 'Launch Information',
    GPSSurveyQuestions: 'Survey Questions',
    GPSSurveyRequestTitle: 'Request Title',
    GPSSurveyRequestTitlePlaceholder: 'Enter request title',
    GPSSurveyRequestTitleHelpText:
      'Please include an identifying name for the engagement or group of engagements to which this request should apply, and/or a simple description of the nature of the request.',
    GPSSurveyFTEBusinessOwner:
      'Please list the FTE business owner or primary point of contact for the content of the engagement(s) impacted by this request.',
    GPSSurveyFTEBusinessOwnerPlaceholder: 'Enter FTE business owner',
    GPSSurveyFTEBusinessOwnerHelpText:
      'Please list the FTE business owner or primary point of contact for the content of the engagement(s) impacted by this request.',
    GPSSurveyUrgency: 'Urgency',
    GPSSurveyUrgencyPlaceholder: 'Select urgency',
    GPSSurveyNewOrEdit:
      'Is this a request to build net new survey form(s) on new survey codes or to edit a published form on an existing code?',
    GPSSurveyNewOrEditPlaceholder: 'Select an option',
    GPSSurveyAudience: 'Survey Audience',
    GPSSurveyAudiencePlaceholder: 'Select survey audience',
    GPSSurveyAudienceHelpText: 'Customer Survey = customer attestation form',
    GPSSurveyBusinessJustification:
      'If your request is non-standard, please provide any additional details, business justification or specifics explaining why this change is needed.',
    GPSSurveyBusinessJustificationPlaceholder: 'Enter business justification',
    GPSSurveyMCICodes:
      'Provide identical MCI survey code(s) to be used by MCI ops team for engagement config, including sizes if applicable. Leave blank to apply change to all MCI engagements currently accepting new claims.',
    GPSSurveyMCICodesPlaceholder: 'Enter MCI survey code(s)',
    GPSSurveyNewVerbiage:
      'Does this request include any new verbiage that does not already exist in localized content library?',
    GPSSurveyNewVerbiageSupportedContentPrefix: 'See tab "Supported Content Library" in ',
    GPSSurveyNewVerbiageSupportedContentSuffix: ' for all currently localized content.',
    GPSSurveyNewVerbiagePlaceholder: 'Select an option',
    GPSSurveyPCEngagementName:
      'If the name of an existing engagement is changing in Partner Center, please enter the new Partner Center engagement name(s) (50 char limit version). Otherwise leave blank.',
    GPSSurveyPCEngagementNamePlaceholder: 'Enter Partner Center engagement name(s)',
    GPSSurveyPCEngagementNameHelpText:
      'Must identically match Partner Center <50 char limit config name(s).',
    GPSSurveyGoLiveDate: 'What is the requested go-live date?',
    GPSSurveyGoLiveDatePlaceholder: 'Select a date',
    GPSSurveyGoLiveDateHelpText:
      'Go-live = visible to partners & customers. Default = 1st of next month. Off cycle dates = urgent + simple fixes (typo, incorrect question type etc). Off-cycle verbiage rewrites may trigger leadership review. Changes to existing surveys will only impact claims created on/after the go-live date. Existing claims will use survey version that was live at the time of claim creation.',
    GPSSurveyUploadIntroPrefix: 'Download a copy of ',
    GPSSurveyUploadIntroSuffix:
      '. Follow the guidance in the template to complete your content file(s) and upload here.',
    GPSSurveyContentTemplateUrl: 'https://aka.ms/MCISurveyContentTemplate',
    GPSSurveyUploadHelpText:
      'Note: Survey team can accept placeholder requests submitted before content is finalized. SLA for survey requests will be calculated from the date that finalized content is attached or linked in Survey Request ADO item.',
    GPSSurveyUploadConstraints: '10 files max, 10 MB limit per file. Allowed file types: .xlsx',
    GPSSurveyUploadBrowse: 'Browse File',
    GPSDoesThisDeal:
      'Does this investment involve a Government/ State-Owned partner* OR is the partner located in a high-risk country**?',
    GPSPleaseSendEmail:
      'Please send an email to onevetsn@microsoft.com to request AC vetting status and share the information with MCAPS Finance Compliance and upload here.',
    GPSAttachmentLabel: 'Add Approval Attachment',
    GPSAcceptableFile: 'Acceptable file formats: ',
    GPSAcceptableFileFormats:
      'Text, Email, PDF, Image, Video, Audio, Microsoft Project(Each file must be under 50 MB)',
    AllowedSizeLimitPerAttachment: 'Size limit of an attachment: ',
    MaxSizeLimitOfAttachment: 'Max Size limit: ',
    AllowedNumOfAttachment: 'Number of attachments allowed: ',
    GPSNoNeedToAnswer:
      'No need to answer any of the next questions. These partners are already AC vetted.',
    GPSPartnerSupplier: 'Is the Partner an LSP, CSP, Disti or current ECIF supplier?',
    GPSMCAPSFinanceCompliance:
      'Use the onevetsn@microsoft.com to request AC vetting status and share with MCAPS Finance Compliance and upload here',
    GPSIsFinancialData:
      'Is there a new type of customer/partner financial data being collected, or is existing customer/partner financial data being used in a manner that has not been previously implemented?',
    GPSGovernmentStateOwnedPartner: `* Government/ State-Owned partner (refer to the <a href="https://nam06.safelinks.protection.outlook.com/?url=https%3A%2F%2F3pc.microsoft.com%2Fonesoe&data=05%7C02%7CDana.Brad%40microsoft.com%7Cfd91acae03554d8c28bf08ddb0313365%7C72f988bf86f141af91ab2d7cd011db47%7C1%7C0%7C638860447552978729%7CUnknown%7CTWFpbGZsb3d8eyJFbXB0eU1hcGkiOnRydWUsIlYiOiIwLjAuMDAwMCIsIlAiOiJXaW4zMiIsIkFOIjoiTWFpbCIsIldUIjoyfQ%3D%3D%7C0%7C%7C%7C&sdata=EyKZdbnVh8Hfmes3Ttg0q5yLVmBa7sryQQ47dm%2FyohQ%3D&reserved=0" target="_blank" rel="noopener noreferrer">Microsoft OneSOE</a> to determine if a partner is SOE)<br/>** High-risk country (refer to the <a href="https://microsoft.sharepoint.com/teams/PartnerInvestmentsandIncubation/Shared Documents/Working Files/WIP/UAT/UAT FY26/../../../../../../OneComplianceInsightsHub/SitePages/Geo-Risk-Assessment.aspx?xsdata=MDV8MDJ8di1kYWRleWVtaUBtaWNyb3NvZnQuY29tfGU0ZjA3MTBmOTRhODQ5YjM3MGUxMDhkZDc2YWQ3OTg0fDcyZjk4OGJmODZmMTQxYWY5MWFiMmQ3Y2QwMTFkYjQ3fDF8MHw2Mzg3OTcyMDk2NTMyNDEwNDh8VW5rbm93bnxUV0ZwYkdac2IzZDhleUpGYlhCMGVVMWhjR2tpT25SeWRXVXNJbFlpT2lJd0xqQXVNREF3TUNJc0lsQWlPaUpYYVc0ek1pSXNJa0ZPSWpvaVRXRnBiQ0lzSWxkVUlqb3lmUT09fDB8fHw%3d&sdata=SjY3bEs4dkwza0JnUXJaazY4KzhrOGlwTVBYUFNlTTYwbW1ZRHRIUkt6ST0%3d" target="_blank" rel="noopener noreferrer">Geography Risk Assessment (GRA) Program</a> to determine if the Partner is in a Tier 1 (Higher AC Risk) Country)`,
    GPSNoFurtherAction: 'No further action.',
    GPSRequestAC:
      'Use the <a href="mailto:onevetsn@microsoft.com" target="_blank">onevetsn@microsoft.com</a> to request AC vetting status or <a href="https://www.onevet.com/home" target="_blank">https://www.onevet.com/home</a> and share the status with Compliance Stakeholder.',
    GPSBasedOnTheMS:
      'Based on the <a href="https://microsoft.sharepoint.com/teams/PartnerInvestmentsandIncubation/Shared Documents/Working Files/WIP/UAT/UAT FY26/../../../../../../../sites/mspolicy/SitePages/PolicyProcedure.aspx?policyprocedureid=MSPOLICY-804079558-5" target="_blank">MS Privacy Policy</a> act accordingly and send documentation to <a href="mailto:askpir@microsoft.com" target="_blank">askpir@microsoft.com</a> to explaining how personal data will be handled.',
    GPSCalculationInformation: 'Calculation Information',
    GPSCalculationPartner: 'Calculation Partner ID Type',
    GPSCalculationPartnerId: 'Calculation Partner ID',
    GPSPartnerPaymentLocationId: 'Partner Payment Location ID',
    GPSCalculationDataSource: 'Calculation Data Source',
    GPSDataSourceOwner: 'Data Source Owner',
    GPSCalculationOwner: 'Calculation Owner',
    GPSRemoveCalculationOwner: 'Remove Calculation Owner',
    GPSADOId: 'GPS ADO ID',
    GPSCalcFileCompleted: 'Calc file Completed',
    GPSLaunchChecklistCompleted: 'Launch Information Completed',
    GPSComplianceChecklistCustomCalculation:
      'Requires a custom calculation (not natively supported by MCI)',
    GPSComplianceChecklistDataSource:
      'Requires new Data Source discovery or validation (eligibility and calculation)',
    GPSComplianceChecklistNewPOEProcess: 'Update Copilot prompt/agent review',
    GPSComplianceChecklistNewCustomerParter: 'Requires new/customer Partner  Support Process',
    GPSComplianceChecklistEventManagement:
      'Requires new process to event management capability (outside the standard 1:many)',
    GPSComplianceChecklistParterCenter: 'Requires new partner center / MCI capability (C+E)',
    GPSComplianceChecklistParterConsentCapability:
      'Create or update Partner Surveys and/or Customer Attestation Forms',
    GPSComplianceChecklistNominationProcess: 'Uses a field nomination process',
    GPSComplianceChecklistPartnerEligibility:
      'Configure MAQ override for Customer/Partner Eligibility',
    GPSComplianceChecklistPartnerCenterReporting: 'Requires custom Partner Center reporting',
    GPSComplianceChecklistCustomInternalReporting: 'Requires a new custom Internal Reporting',
    GPSComplianceChecklistCustomerPartnerPerformanceMetric:
      'Create/update metrics for Partner Performance Measurement',
    GPSComplianceChecklistMCIPolicyGuide: 'Update MCI policy guide/mini-guide',
    EntityType: 'Entity Type',
    EntityTypePlaceholder: 'Select Entity Type',
    DeliveryMethod: 'Delivery Method',
    DeliveryMethodPlaceholder: 'Select Delivery Method',
    SolutionArea: 'Solution Area',
    ECIFPreApproval_Category: 'ECIF Pre-Approval',
    NonAICapacity_NodeName: 'Capacity (Non-AI)',
    stateDone: 'Done',
    StateCLosed: 'Closed',
    StateDecision: 'Decision Not to Pursue',
    RequestJustification: 'Request Justification',
    MotivationForAssessment: 'Motivation for Assessment',
    CurrentEnvironmentAndTargetOutcomes: 'Current Environment & Target Outcomes',
    EstimatedEnvironmentSize: 'Estimated Environment Size',
    SupporRequestJustification: 'Support/Coaching Request Justification',
    RequestJustificationDealCoaching: 'Request Justification (for deal coaching)',
    ECIFFundInformation: 'ECIF Fund Information',
    ApproversNoteTicket: 'ticket',
    ApproversNoteTooltip:
      'Approvers must be part of Security Group to be able to edit requests. Please submit a support ticket to be added to this group.',
    ApproversNoteTooltipBefore:
      'Approvers must be part of Security Group to be able to edit requests. Please submit a support',
    ApproversNoteTooltipAfter: 'to be added to this group.',
    DealDetailsWatermark:
      'Short Overview of Deal or Success Engagement (2-3 lines executive summary of what? and why? with sufficient high-level context and background)',
    DiscussionInfo: 'Corp Engineering Guidance for Capacity and Support UATs',
    LikeFeedbackLabel: 'What do you like?',
    LikeFeedbackPlaceholder: 'How can the response be improved?',
    DisLikeFeedbackLabel: 'What did you dislike?',
    DisLikeFeedbackResponseLabel: 'What was the issue with you with the response?',
    DisLikeFeedbackPlaceholder: 'Provide details without including personal information',
    irrelevant: 'The response is irrelevant',
    inaccurate: 'The response is inaccurate',
    LikeFeedbackMessage:
      'Your feedback on the Copilot experience will help us improve the quality of answers Copilot providers.',
    ImportantNotifications: 'Important Notifications',
    AskAnything: 'Ask me anything...',
    BulkEdit: 'Bulk Edit',
    BulkEditText: 'Bulk Edit supported for only action owner at this time',
    MarkAsRelateText:
      "Click on in 'Select action' dropdown to select parent and mark related actions",

    McapsMaaS: 'MCAPS MaaS',
    ClickSelectFile: 'Click to select file',
    UploadFile: 'UploadFile',
    Unauthorized: 'unauthorized',
    UserProfile: 'User Profile',
    EnableSyncwithCE: 'Enable Sync with CE',
    InvalidFile: 'Invalid file',
    ValidationComplete: 'Validation Complete',
    ActionFound: 'Action Found',
    ValidNewAction: 'Valid New Action',
    ActionForUpdate: 'Action for Update',
    InvalidAction: 'Invalid Action',
    DownloadValidationReport: 'Download Validation Report',
    Download: 'Download',
    DownloadResult: 'Download Results',
    Delete: 'Delete',
    CETDetails: 'CET Details',
    SuccessfullyValidatedActions: 'Successfully Validated All Actions',
    FailedActionsReport: 'Failed Actions Report',
    InProcess: ' In Process... ',
    VSTSLogo: 'VSTSLogo',
    ContinueToImport: 'Continue To Import',
    Continue: 'Continue',
    ResetColumnOptions: 'Reset Column Options',
    VerifyView: 'Verify View',
    Thanks: 'Thanks!',
    Thanksdot: 'Thanks.',
    ColumnUpdatedSuccessfully: 'Column options updated successfully.',
    ColumnIncludedSuccessfully: 'Column options included successfully.',
    QBCTopAsk: 'QBC Top 5 Ask',
    SelectQBCTopAsk: 'Select QBC Top 5 Ask',
    AzurePriority: 'Azure Priority',
    Triage: 'Triage',
    Competitors: 'Competitors',
    ClickCustomerGoalState: 'Click to add Customer Goal State',
    ClickCustomerScenario: 'Click to add Customer Scenario',
    ClickImpactSummary: 'Click to add Impact Summary',
    ClickCompeteParity: 'Click to add Compete Parity',
    ClickAlternativeSolutions: 'Click to add Alternative Solutions',
    ClickReproSteps: 'Click to add Repro Steps',
    ClickObservedOutput: 'Click to add Observed Output',
    ClickExpectedOutput: 'Click to add Expected Output',
    EngineeringContacts: 'Engineering Contacts',
    SelectStatus: 'Select Status',
    EngineeringStatus: 'Engineering Status',
    EngineeringWorkitemURL: 'Engineering Workitem URL',
    CurrentDefault: 'Current Default',
    ActionTitle: 'Action Title',
    RaidTitle: 'RAID Title',
    Select: 'Select',
    SelectCustomerAcceptedAssignment: 'Select Customer Accepted the Assignment',
    CAPERequest: 'CAPE Request',
    CAPE: 'CAPE',
    AnalyticsView: 'Analytics View',
    AnalyticsViewIcon: 'AnalyticsView',
    RequestHelpSupport: 'Request Help/Support',
    Help: 'Help',
    RequestAccess: 'Request Access',
    RequestAccessText: 'to request access',
    UATDiscussion: 'Discussion',
    Tasks: 'Tasks',
    AllActivity: 'All Activity',
    Assignee: 'Assignee',
    SelectValueStateFieldSubState:
      'Please select a value in the State field, before selecting a Sub State',
    GPSPIRReviewDateInfo: 'Note: PIR team will confirm the date after reviewing the case',
    GPSRequestReasonTooltip: 'Select the reason for this request.',
    SLTResponsibleActionExec: 'SLT Responsible for the Action Exec',
    LTExecDirect: 'LT/Exec Direct',
    EmailConversation: 'Email Conversation',
    IndividualTrackingClosureOwner: 'The individual that is tracking the action closure Owner',
    LastEmailDate: 'Last Email Date',
    LatestEmailSentDate: 'Latest Email sent date',
    OpportunityLostDate: 'Opportunity Lost Date',
    FeatureParityRequest: 'Feature parity request',
    FeatureParityRequestLabel: 'Is this a feature parity request?',
    EstimatedDateToComplete: 'Estimated Date to Complete',
    UnblockCustomerETA: 'Unblock Customer ETA',
    DefaultEqualsETA: 'Default Equals Max ETA',
    PubliclyDocumentedETA: 'Publicly Documented ETA',
    SelectCSAMSubmittingNomination:
      'Please select the CSAM on behalf of whom you are submitting this nomination',
    ListeningCircleSessionDate: 'Listening Circle Session Date',
    RelatedFeedback: 'Related Feedback',
    State: 'State',
    Statuses: 'Status',
    OfferingName: 'Offering Name',
    ServiceName: 'Service Name',
    ScoreByOffering: 'Score By Offering',
    RelatedFeature: 'Related Feature',
    PlannedSemester: 'Planned Semester',
    TargetDate: 'GA Target Quarter',
    ProjectedPublicDate: 'Projected Public Preview Quarter',
    Preview: 'Preview',
    ProjectedPrivateDate: 'Projected Private Preview Quarter',
    RemoveCriteria: 'Remove criteria',
    IdentityPicker: 'Identity picker',
    FeedbackRequest: 'Provide Feedback and Feature Request',
    UATUserGuide: 'User Guide',
    NoIdentitiesFound: 'No identities found',
    Showing: 'Showing',
    results: 'results',
    GroupSelectedClauses: 'Group selected clauses',
    ViewManagement: 'View Management',
    CreateActionMore: 'Create Action and More',
    Unassigned: 'Unassigned',
    ContentInformation: 'Content Information',
    UnifiedActionTrackerTool: 'Unified Action Tracker Tool',
    RemoveRequestor: 'Remove Requestor',
    RemoveActionOwner: 'Remove Action Owner',
    WorkingOnIt: 'Working on it..',
    RemoveAccountableExec: 'remove Accountable Exec',
    AccountableExecSearch: 'Search Accountable Exec',
    LTSearch: 'Search LT',
    ContributorSearch: 'Search Contributor',
    Contributors: 'Contributors',
    ManagingOwnerSearch: 'Search Managing Owner',
    RemoveLTExec: 'remove LT/Exec Direct',
    RemoveManagingOwner: 'remove Managing Owner',
    RemoveContributor1: 'remove Contributor 1',
    RemoveContributor2: 'remove Contributor 2',
    RemoveACOCE: 'remove ACO-CE',
    PartnerOneId: 'Partner One Id',
    Mailbox: 'Mailbox',
    MailboxBlockerCreated: 'Mailbox from which this Blocker is created',
    Sender: 'Sender',
    PersonRequestingeEmail: 'Person requesting help by Email',
    ConversationID: 'Conversation ID',
    PrimaryWorkload: 'Primary Workload',
    SelectPrimaryWorkload: 'Select Primary Workload',
    HasFastTrack: 'Has FastTrack?',
    SelectHasFastTrack: 'Select Has FastTrack',
    SelectHasUnified: 'Select Has Unified',
    HasUnified: 'Has Unified?',
    HasD365DEEDE: 'Has D365 DE/EDE?',
    SelectHasD365DEEDE: 'Select Has D365 DE/EDE',
    HasM365DEEDE: 'Has M365 DE/EDE?',
    SelectHasM365DEEDE: 'Select Has M365 DE/EDE',
    PrimaryEscalationReason: 'Primary Escalation Reason',
    SelectPrimaryEscalationReason: 'Select Primary Escalation Reason',
    SecondaryEscalationReason: 'Secondary Escalation Reason',
    SelectSecondaryEscalationReason: 'Select Secondary Escalation Reason',
    TertiaryEscalationReason: 'Tertiary Escalation Reason',
    SelectTertiaryEscalationReason: 'Select Tertiary Escalation Reason',
    Program: 'Program',
    ProjectName: 'Project Name',
    ProjectNameOther: 'Project Name (Other)',
    // adding New text for components:
    AddOtherDetails: 'Click to add any other details',
    EnterActionTitle: 'Please enter action title',
    EnterURL: 'Enter URL',
    EnterEngineeringContact: 'Enter Engineering Contact',
    EnterCompetitors: 'Enter Competitors',
    EnterDescription: 'Enter brief description',
    SelectTriage: 'Select Triage',
    Loading: 'Loading...',
    LoadingBusinessRules: 'Loading Business Rules...',
    CapLoading: 'Loading...',
    ApplyingView: 'Applying Selected View',
    loadingChart: 'loading charts...',
    dataLoadingShimmerTitle: 'dataLoadingShimmer',
    ValidatingURL: 'Validating URL...',
    AddComment: 'Add a comment',
    Comment: 'Comment',
    NoCommentsYet: 'No comments yet',
    EditInADO: 'Edit in ADO',
    Maximize: 'Maximize',
    Minimize: 'Minimize',
    LinkClipboard: 'Link copied to clipboard',
    ClickToExpand: 'Click to Expand',
    ClickToCollapse: 'Click to Collapse',
    Collapse: 'Collapse',
    Save: 'Save',
    AttachFile: 'Attach File',
    CreateAction: 'Create Action',
    ExpandToFullscreen: 'Expand To Fullscreen',
    CustomViewDirectory: 'Custom View Directory',
    AddNewView: 'Add New View',
    NewView: 'New View',
    StatusErrorFull: 'StatusErrorFull',
    ScaleVolume: 'ScaleVolume',
    ChromeFullScreen: 'ChromeFullScreen',
    FullScreen: 'FullScreen',
    Export: 'Export',
    CommentSolid: 'CommentSolid',
    Unknown: 'Unknown',
    PeopleAdd: 'PeopleAdd',
    peopleAddFluent: 'peopleAddFluent',
    Solid: 'Solid',
    UserWarning: 'UserWarning',
    edit: 'Edit',
    ExploreContent: 'ExploreContent',
    AnalyticsViewValidate:
      'We recommend that you verify your analytics view. This will validate your data so that you can use it in Power BI',
    CreateFolder: 'Create folder',
    AssignedToCorp: 'Assigned To Corp',
    AssignedToCorpMessage:
      'Choosing "Yes" will submit the action to the Corporate Triage Team and a resource will be assigned within 1 business day.',
    Priority: 'Priority',
    PriorityofAction: 'Priority of Action',
    SubState: 'Sub State',
    TopParentID: 'Top Parent ID',
    CustomerAccountName: 'Customer/Account TPID',
    Outcome: 'Outcome',
    Older: 'Older',
    Attach: 'Attach',
    MilestoneIdSuccess: 'Opportunity Milestone ID from MSX success opportunity Id',
    PreferredAzureRegion: 'Preferred Azure Region',
    AzureCapacityType: 'Azure Capacity Type',
    MilestoneStatus: 'Milestone Status Reason',
    SegmentType: 'Segment Type',
    Segment: 'Segment',
    RequestedAmount: 'Requested Amount',
    AppQuotaID: 'Request ID',
    SubscriptionID: 'Subscription ID',
    Amount: 'Amount',
    ApprovedAmount: 'Approved Amount',
    PartnerName: 'Partner Name',
    RequestType: 'Request Type',
    ECIFSelectRequestType: 'Select Request Type',
    RequestStatus: 'Request Status',
    AzureCEState: 'Azure CE State',
    AzureCESubState: 'Azure CE Sub State',
    Competitor: 'Competitor',
    CompeteBlockerEscalationOwner: 'Compete Blocker Escalation & Owner',
    DealSize: 'Deal Size ($)',
    PartnerEngagedwithSign: 'Partner Engaged?',
    NominationType: 'Nomination Type',
    FilterByName: 'Filter by name',
    UnifiedActionTracker: 'Unified Action Tracker',
    FormerlyUAT: '(formerly UAT)',
    CompetitorName: 'Competitor Name',
    CompetitorProduct: 'Competitor Product',
    CompetitorProductNotSpecified: 'Not Specified',
    OmsTenantId: 'Tenant ID',
    TenantIdHelpText:
      'Enter the Tenant ID as a GUID (standard hexadecimal format containing only numbers, letters A–F, and hyphens). Example: 550e8400-e29b-41d4-a716-446655440000.',
    SupportCases: 'Support Cases',
    UnifiedActionTrackerBrandName: 'Unified Action Tracker (UAT) - soon to be Action 360',
    SearchActions: 'Search All Items',

    FilterValues: 'Filter Values',
    Subject: 'Subject',
    Note: 'Note',
    Filter: 'Filter',
    Info: 'Info',
    Apply: 'Apply',
    SearchByKeyword: 'Search by keyword',
    StatusCircleErrorX: 'StatusCircleErrorX',
    Body: 'Body',
    EmailBody: 'Body',
    Selected: 'Selected',
    ClickSelectGetRelevantResults:
      'Click to select columns or start typing to get relevant results',
    SelectView: 'Select View',
    EditView: 'Edit View',
    InspectView: 'Inspect View',
    View: 'View',
    Hide3: 'Hide3',
    MiniExpandMirrored: 'MiniExpandMirrored',
    MiniContractMirrored: 'MiniContractMirrored',
    NavigateExternalInline: 'NavigateExternalInline',
    WarningSolid: 'WarningSolid',
    ChevronUp: 'ChevronUp',
    ExploreContentSingle: 'ExploreContentSingle',
    ChromeClose: 'ChromeClose',
    Robot: 'copilot',
    ShowAllViews: 'Show All Views',
    Folder: 'Folder',
    SelectFolder: 'Select Folder',
    SelectTags: 'Select Tags',
    Search: 'Search',
    Searching: 'Searching...',
    SearchSelectedView: 'Search Selected View',
    ErrorBadge: 'ErrorBadge',
    DownloadDocument: 'DownloadDocument',
    MigrationPartner: 'Migration Partner',
    MeetingDetails: 'Meeting Details',
    DueDate: 'Due Date',
    FinancialYear: 'Financial Year',
    Quarter: 'Quarter',
    CloudImpacted: 'Cloud Impacted',
    EOU: 'EOU',
    HQScrumPatterns: 'HQ Scrum Patterns',
    PatternStatus: 'Pattern Status',
    RefID: 'RefID',
    TFTCreated: 'TFT Created',
    ReferenceNumberTFTCreated: 'Reference number of the TFT item when created',
    URLRemoteWorkItem: 'URL of remote work item',
    SuggestedResponses: 'Suggested Responses',
    SearchOptions: 'Search options',
    SelectColumns: 'Click to select columns',
    SelectColumn: 'Select Columns',
    Columns: 'Columns',
    To: 'To',
    ColumnOptions: 'Column Options',
    DisableColumnOptionToolTip: 'Do not have permissions to edit column options for this view.',
    ClickToAdd: 'Click to Add',
    ClickTo: 'Click to',
    Browse: 'browse',
    Upload: 'Upload',
    Files: 'files.',
    Today: 'Today',
    ExcelLogo: 'ExcelLogo',
    Yesterday: 'Yesterday',
    LastSevenDays: 'Last Seven Days',
    LastThirtyDays: 'Last Thirty Days',
    CustomizeFieldSelection: 'Customize the field selection',
    AddUpdateRemoveView: 'Add, update, or remove optional fields in the view.',
    Fields: 'Fields',
    Field: 'Field',
    Up: 'Up',
    Down: 'Down',
    from: 'From',
    cc: 'Cc',
    Bcc: 'Bcc',
    SearchBymessageCategory: 'Search by message, category',
    Type: 'Type',
    Link: 'Link',
    Message: 'Message',
    ExpiryDate: 'Expiry Date',
    Action: 'Action',
    Actions: 'Actions',
    NoActions: 'No Actions',
    Updating: 'Updating ...',
    FlameSolid: 'FlameSolid',
    CollapseContentSingle: 'CollapseContentSingle',
    DismissAll: 'Dismiss All',
    Account: 'Account',
    AccountTPID: 'Account TPID',
    OpportunityTitle: 'Opportunity Title',
    AccountID: 'Account ID',
    CustomerAccountID: 'Customer/Account ID',
    AccountName: 'Account Name',
    TopicCategory: 'Topic Category',
    FedSalesOpportunityId: 'FedSales Opportunity ID',
    Milestonestatus: 'Milestone Status',
    ClearMilestoneId: 'Clear milestone id',
    InvalidMilestoneID: 'Invalid Milestone ID',
    MilestoneActivations: 'Milestone Activations',
    CustomerCommitment: 'Customer Commitment',
    MilestoneEstimatedDate: 'Milestone Estimated Date',
    OpportunityDetails: 'Opportunity Details',
    OpportunityAndMilestoneInfo: 'Opportunity and Milestone Information',
    ActionLevelTotals: 'Action-level Totals',
    OpportunityIDSHeading: 'Opportunity ID',
    OpportunityIdsMulti: 'Opportunity IDs',
    OpportunityIDSDetails: 'Opportunity ID Details',
    MilestoneIDSForOpportunityHeading: 'Milestone IDs associated with Opportunity ID',
    MileStoneIdDetails: 'Milestone ID Details',
    MilestoneIDSHeading: 'Milestone ID',
    MilestoneIdsMulti: 'Milestone IDs',
    OpportunityInformationCaps: 'OPPORTUNITY INFORMATION',
    OpportunityName: 'Opportunity Name',
    OpportunityStage: 'Opportunity Stage',
    Product: 'Product(s)',
    ProductOpportunitySize: 'Product Opportunity Size',
    NoProductsDisplay: 'No Products to display',
    EnrollContacts: 'Enroll Contacts',
    Max: 'Max.',
    Contacts: 'Contacts',
    OneAskID: 'OneAsk ID',
    SupportCaseLink: 'Support Case Link',
    TranslatedAccountName: 'Translated Account Name',
    CustomIndustry: 'Custom Industry',
    SelectCustomIndustry: 'Select Custom Industry',
    Subsegment: 'Subsegment',
    PrimaryCompetitor: 'Primary Competitor',
    EnterPrimaryCompetitor: 'Enter Primary Competitor',
    DisplaysPrimaryCompetitor: 'Displays Primary Competitor for the customer',
    FeedbackType: 'Feedback Type',
    GoLiveDate: 'Go Live Date',
    CustomerPartnerSolutionRequested:
      'What is the customer/partner trying to do? & why? what is your solution requested?',
    CustomerSolutionProvidedNeeded:
      'What happens to the customer if the solution is not provided? or is provided later than the date needed by?',
    DateCustomerReady: 'Date Customer Ready',
    PreferredRegion: 'Preferred Region',
    SelectWave: 'Select a Wave',
    DescribeCustomerExpecting: 'Describe what the customer is expecting and when you are done',
    DescribeSustomerExpectingNext: 'Describe what the customer is expecting next',
    DateDecided: 'Date Decided',
    RequestTypeDescription: 'Request Type Description',
    WhatDateNeededBy: 'What date is this needed by?',
    Maxlength2000: '(Max length is 2000)',
    NoAttachments: 'No attachments',
    NoAttachmentAdded: 'No Attachment added',
    NoDataFound: 'No data found',
    ActionCount: 'Action Count',
    NavigationBar: 'Navigation Bar',
    AddTags: 'Add Tags',
    Tags: 'Tags',
    Tag: 'Tag',
    ActionNotFound: 'Action Not Found',
    StateGraph: 'State Graph',
    CommentsReadonly: 'Comments are read-only',
    OpportunitySize: 'Opportunity Size',
    OpportunitySizeWithUSD: 'Opportunity Size (USD)',
    Application: 'Application',
    RequestedBy: 'Requested By',
    EmailidUserSubmittedCompliant: 'The email id of the user who submitted the compliant By',
    AnnualRevenueCompleting: 'Annual Revenue Impact of Completing Ask',
    AnnualRevenueCompletingCurrency: 'Estimated Monthly Usage ($)',
    EnterAnnualRevenueCompletingCurrency: 'Provide usage $ from FedSales on a monthly basis',
    NumberOfSeats: 'Number of Seats',
    EnterNumberOfSeats: 'Provide if your request is for a seat-based product',
    ExplanationDate: 'Explanation of need by date',
    IsBlocked: 'Is Blocked',
    PubliclyDocumented: 'Publicly Documented',
    AmmMyTasks: 'AMM My Tasks',
    AmmAllTasks: 'AMM All Tasks',
    // Adding PlaceHolerText:
    SelectFeedbackType: 'Select Feedback Type',
    EnterAccountID: 'Enter Account ID',
    SelectOpportunityStage: 'Select Opportunity Stage',
    PrimaryCompetitorCustomer: 'Primary Competitor for the customer',
    EnterCountry: 'Enter Country',
    EnterSubsegment: 'Enter Subsegment',
    EnterTranslatedAccountName: 'Enter Translated Account Name',
    EnterOneAskID: 'Enter OneAsk ID',
    EnterSupportCaseLink: 'Enter Support Case Link',
    EnterProduct: 'Enter Product(s)',
    SelectProduct: 'Select Product(s)',
    EnterOpportunityName: 'Enter Opportunity Name',
    EnterCustomerCommitment: 'Enter Customer Commitment',
    EnterMilestoneActivations: 'Enter Milestone Activations',
    FromFedSales: 'From FedSales',
    SelectTopicCategory: 'Select Topic Category',
    SelectAzurePriority: 'Select Azure Priority',
    EnterRefID: 'Enter Ref ID',
    EnterTFT: 'Enter TFT',
    EnterPatternStatus: 'Enter Pattern Status',
    EnterHQScrumPatterns: 'Enter HQ Scrum Patterns',
    SelectEOU: 'Select EOU',
    SelectQuarter: 'Select Quarter',
    SelectMonth: 'Select Month',
    SelectFinancialYear: 'Select Financial Year',
    EnterMeetingDetails: 'Enter Meeting Details',
    DetailslikeQuarter: 'Other meeting details like quarter, date etc.',
    EnterMigrationPartner: 'Enter Migration Partner',
    EnterAgreementName: 'Enter Agreement Name',
    EnterAgreementId: 'Enter Agreement Id',
    EnterActualCreatedDate: 'Enter Actual Created Date',
    EnterTicketVolumePerMonth: 'Enter Ticket Volume Per Month',
    EnterInternalTriageDate: 'Enter Internal Triage Date',
    EnterExecutingTeamADOLink: 'Enter Executing Team ADO Link',
    EnterRejectedDate: 'Enter Rejected Date',
    PleaseSelectYesOrNo: 'Please select yes or no',
    IsAmendmentM1072Signed: 'Is Amendment M1072 signed?',
    SelectPriority: 'Select Priority',
    EnterAccount: 'Enter Account',
    EnterChildAccount: 'Enter Child Account',
    EnterOpportunityID: 'Select Opportunity ID',
    SearchOpportunity: 'Search Opportunity ID or Opportunity Name',
    SearchMilestone: 'Search Milestone ID or Milestone Name',
    EnterOppID: 'Enter Opportunity ID',
    SelectOutcome: 'Select Outcome',
    EnterMilestoneId: 'Enter Milestone Id',
    EnterPreferredAzureRegion: 'Enter Preferred Azure Region',
    EnterAzureCapacityType: 'Enter Azure Capacity Type',
    EnterMilestoneStatus: 'Enter Milestone Status Reason',
    EnterSegment: 'Enter Segment',
    EnterSegmentType: 'Enter Segment Type',
    EnterRequestedAmount: 'Enter Full Incremental Requested Amount',
    EnterRequestedAmountPaygo: 'Enter Full Total Amount Requested (e.g., 1000000).',
    EnterRequestedAmount3PGPU: 'Please add the number of GPUs needed.',
    EnterAppQuotaID: 'Enter Request ID',
    EnterSubscriptionID: 'Enter Subscription ID',
    EnterApprovedAmount: 'Enter Approved Amount',
    EnterGoLiveDate: 'Enter Activity Go Live Date',
    EnterPartnerName: 'Enter Partner Name',
    EnterAzureCEState: 'Enter Azure CE State',
    EnterAzureCESubState: 'Enter Azure CE Sub State',
    EnterCompetitor: 'Select Competitor Name',
    EnterCompeteBlockerEscalationOwner: 'Enter compete blocker escalation & owner',
    EnterDealSize: 'Enter Deal Size',
    PartnerEngaged: 'Partner Engaged',
    EnterSubject: 'Enter subject',
    EnterNote: 'Enter note',
    EnterName: 'Enter Name',
    EnterFolderName: 'Please enter folder name',
    EnterCompetitorProduct: 'Select Competitor Product',
    EnterOmsTenantId: 'Enter Tenant ID',
    EnterTitle: 'Please enter title',
    SelectMilestoneStatus: 'Select Milestone Status',
    Status: 'Status',
    EnterSupportCases: 'Enter Support Case IDs',
    AddRelatedEscalation: 'Click to add Related Issue/Escalation',
    IntentMoveCloud: 'Intent to move to Cloud',
    InterestedInAssessment: 'Interested in assessment',
    AssessmentType: 'Assessment type',
    Partner: 'Partner',
    ECIFStatusCMAAMA: 'ECIF status if CMA/AMA',
    AssessmentServiceStatus: 'Assessment service status',
    StatusofAssessment: 'Status of Assessment',
    Completionstatuscombined: 'Completion status if combined',
    InterestedOtherECIF: 'Interested in other ECIF',
    YesStateReason: 'If yes, state reason',
    ECIFReason: 'ECIF Reason',
    ECIFStatus: 'ECIF status',
    ECIFRequestedServiceStatus: 'ECIF requested service status',
    AssistedMigrationOption: 'Is assisted migration an option?',
    InterestedAssistedMigration: 'Interested in Assisted Migration',
    ECIFStatusFAM: 'ECIF status for FAM',
    MigrationServicestatus: 'Migration Service status',
    AssistedMigrationReason: 'Assisted Migration Reason',
    MigrationType: 'Migration Type',
    InterestedInRenewing: 'Interested in renewing',
    RenewingBREPSA: 'Renewing BREP/SA',
    NoStateReason: 'If no, state reason',
    NonRenewingReason: 'Non-Renewing Reason',
    EOObjective: 'E&O Objective',
    ListeningCircleSessionName: 'Listening Circle Session Name',
    ListeningCircleLead: 'Listening Circle Change Lead',
    RemoveListeningCircleLead: 'remove Listening Circle Change Lead',
    RemoveListeningCircleFacilitator: 'remove Listening Circle Facilitator',
    ListeningCircleFacilitator: 'Listening Circle Facilitator',
    LTEscalation: 'LT Escalation',
    AddNewCriteria: 'Add new criteria',
    DownloadActions: 'Download',
    EmailActions: 'Email',
    AskID: 'AskID',
    QuarterSerialNumber: 'Quarter Serial Number',
    AssignedTo: 'Assigned To',
    ProductFeedbackEngineering:
      'In Addition, please submit this Action as product feedback to Engineering.',
    AssignToCorp: 'Assign to Corp',
    AnalyticsViews: 'Analytics Views',
    Analytics: 'Analytics',
    ShareYourComments: 'Share your comments',
    ClosePopupModal: 'Close popup modal',
    RemoveUser: 'Remove user',
    SendEmailUsersList: 'Send email users list',
    MakeDefaultView: 'Make as default view',
    Back: 'Back',
    SaveAs: 'Save As',
    Include: 'Change and include',
    Update: 'Update',
    NoResultsFound: 'No Results Found',
    WaveReport: 'Wave Report',
    Mail: 'Mail',
    Chat: 'Chat',
    ChevronRightSmall: 'ChevronRightSmall',
    GlobalNavButton: 'GlobalNavButton',
    NoSubFolders: 'No sub-folders.',
    FabricFolder: 'FabricFolder',
    StartChat: 'Start chat',
    ReportsTo: 'Reports to',
    ChevronDownSmall: 'ChevronDownSmall',
    Phone: 'Phone',
    Location: 'Location',
    CustomerCommunicationTeamsEmail:
      'I confirm I have the pre-requisites, and I have secured intent from my customer for this nomination. I understand that the customer attendees will be receiving communication via Teams and email. [Note:These notifications are sent manually]',
    More: 'More',
    ImportActions: 'Import Actions',
    DragColumnsReorder: 'Selected Columns (Drag the columns to reorder)',
    SelectedColumns: 'Selected Columns',
    ToggleSelection: 'Toggle selection',
    ToggleSelectionAllItems: 'Toggle selection for all items',
    RowCheckbox: 'Row checkbox',
    leftRightArrowkeysNavigate: 'Use left and right arrow keys to navigate between commands',
    CommentsADOReadOnly: 'Comments posted at ADO are read-only',
    TakeAMinute: 'This might take a minute..',
    Post: 'Post',
    CommentShouldntEmpty: 'Comment should not be empty',
    ExcludeItemsCompletedBefore: 'Exclude work items that were completed before',
    IncreaseValueByOne: 'Increase value by 1',
    DecreaseValueByOne: 'Decrease value by 1',
    ExcludeItemsCompletedRollingPreiod:
      'Exclude work items that was completed before rolling preiod',
    ExcludeItemsCompletedStartDate: 'Exclude work items that were completed before the start date',
    Present: 'Present',
    Granularity: 'Granularity',
    ShowDataDailyWeeklyMonthlyPeriod:
      'Set individual rows to show data for daily,weekly,or monthly period',
    DefineTime: 'Define how far back in time the view should go',
    NavBasicExample: 'Nav basic example',
    OpportunitylostNotProvided:
      'On what date will this opportunity be lost if ask is not provided?',
    SensitiveCustomerData:
      'How is this ask an improvement for the customer? What does success look like? Do not list sensitive customer data',
    NotlistSensitiveCustomerData:
      'What is the customer doing without this ask? Do not list sensitive customer data',
    PullFedSalesOpportunityRevenue:
      'Pull from FedSales Opportunity and provide revenue on annual basis',
    Following: 'Following',
    UnFollow: 'UnFollow',
    Follow: 'Follow',
    Copy: 'Copy',
    CreateCAPERequest: 'Create CAPE Request',
    EnableNewExperience: 'Enable New Experience',
    RevertExperience: 'Revert Experience',
    ListeningCircleArea: 'Listening Circle Area',
    CopilotInsights: 'Copilot Insights',
    CopilotBeta: 'Copilot (Beta)',
    Beta: 'Beta',
    Copilot: 'Copilot',
    BasicDetails: 'Basic Details',
    AccountInformation: 'Account Information',
    AccountInformationCaps: 'ACCOUNT INFORMATION',
    OpportunityInformation: 'Opportunity Information',
    MilestoneInformation: 'Milestone Information',
    BasicInformation: 'BASIC INFORMATION',
    AOAIRequestedCapacity: 'AOAI Requested Capacity',
    AOAIApprovedCapacityNotChange: 'AOAI Approved Capacity (DO NOT CHANGE - CORP USE)',
    ApprovedPreferredRegion: 'Approved Preferred Region',
    CAEEngagementDetails: 'CAE Engagement Details',
    ServiceDetails: 'Service Details',
    Contact: 'Contact',
    MilestoneDetails: 'Milestone Details',
    Support: 'Support',
    Email: 'Email',
    DualUse: 'Dual Use',
    FederalDetails: 'Federal Details',
    CustomerHub: 'Customer Hub',
    Remove: 'Remove',
    Others: 'Others',
    Other: 'Other',
    VSUQBCInfo: 'VSU/QBC Info',
    AskDetails: 'Ask Details',
    ImpactSumary: 'Impact Summary',
    NotAuthorizedViewPage: 'You are not authorized to view this page',
    Ok: 'OK',
    Recommended: 'Recommended',
    ParentItems: 'Parent Items',
    BackToWindow: 'Back to Window',
    BackToWindowIcon: 'BackToWindow',
    New: 'New',
    InProgress: 'In Progress',
    NewAnalyticsView: 'New analytics view',
    ManageAnalyticsView: 'Manage analytics view',
    CanceledRatingFeeback: 'CanceledRatingFeeback',
    FeedbackID: 'Feedback ID',
    ID: 'ID',
    FilterBy: 'Filter By',
    onUnFavorite: 'onUnFavorite',
    onFavorite: 'onFavorite',
    highlight: 'highlight',
    SavedSuccessfully: 'View saved successfully.',
    UpdatedSuccessfully: 'View updated successfully.',
    SummaryFor: 'Summary for',
    ActionSubmittedSuccessfully: 'Action submitted successfully.',
    NominationActionSubmittedSuccessfully: 'Nomination Action submitted successfully.',
    HearBackThreeDays: 'You and your customers will hear back from MW work team in 3 days.',
    ManageAction: 'You can manage this action',
    Create: 'Create',
    BackToOverview: 'Back to Overview',
    ThumbnailView: 'ThumbnailView',
    Relationship: 'Relationship',
    SyncToPC: 'SyncToPC',
    TabCenter: 'TabCenter',
    CloudUpload: 'CloudUpload',
    Globe: 'Globe',
    GroupObject: 'GroupObject',
    Tiles: 'Tiles',
    SubmitActionProductFeedbackEngineering:
      'In addition, please submit this action as product feedback to engineering',
    MicrosoftProjectCompressedAllowed:
      'Files <=50MB of type Office,Text,Email,PDF,Image,Video,Audio,Microsoft Project,Compressed are allowed',
    AssistanceCorp:
      'Assigned Only escalate to Corp. if the item has been discussed in the local area Scrum and was not resolved, requiring Corp. assistance Corp?',
    // New constant added
    Technology: 'Technology',
    SelectTechnology: 'Select Technology',
    InvalidOpportunityID: 'Invalid Opportunity ID',
    SalesTerritory: 'Sales Territory',
    EnterSalesTerritory: 'Enter Sales Territory',
    AccountPartnerNameMandatory: 'Account/Partner Name  is mandatory',
    EnterAccountName: 'Enter Account Name',
    PartnerID: 'Partner ID',
    EnterPartnerID: 'Enter Partner ID',
    SupplierName: 'Supplier Name',
    EnterSupplierName: 'Enter Supplier Name',
    SupplierType: 'Supplier Type',
    FilterRequestType: 'Filter by Request type',
    filterByRequestType: 'filterByRequestType',
    CloudOnPrem: 'Cloud vs On-prem',
    SelectCSSCreationDate: 'Select CSS Case Creation Date',
    Additionalcontacts: 'Additional contacts',
    RemoveAdditionalContact: 'remove Additional Contact',
    CommercialCE: 'Commercial executive CE',
    RemoveCustomerExecutive: 'Remove Customer Executive',
    RemoveApprover: 'Remove Approver',
    RemoveRejecter: 'Remove Rejecter',
    RemoveApproverOrRejecter: 'Remove Approver or Rejecter',
    EmailSupportSpecialistLabel: 'Email of the Support Specialist (SSSP)',
    AmountblockedRevenueLabel: 'Amount of blocked revenue for Microsoft product sale',
    AmountBlockedOpportunityLabel: 'Amount of blocked revenue for NSC Unified opportunity',
    ClearChatNNotAvailable: 'Clear chat now available!',
    ClearChatHistory: 'Try it! You can now clear chat history from the Copilot window',
    RemoveAlias: 'remove alias',
    RemoveAMPDM: 'remove AM / PDM',
    RBCaseNumber: 'RB Case Number',
    CurrentFYRevenue: 'Current FY Revenue',
    EstBilledRevenue: 'Est. Billed Revenue',
    EstBilledRevenueWithUSD: 'Est. Billed Revenue (USD)',
    BilledRevenue: 'Billed Revenue',
    EnterBilledRevenue: 'Enter Est. Billed Revenue',
    DragDropEilesHere: 'Drag and drop file here',
    Or: 'or',
    uploadFailed: 'Upload Failed!',
    BrowseFile: 'Browse for a file',
    uploadFile: 'Browse File',
    FilterColumnName: 'Filter by column name',
    ClickhereWrite: 'Click here to write',
    FilterNameEmail: 'Filter By Name Or Email',
    SelectL1Process: 'Select L1 Business Process',
    SelectL2Process: 'Select L2 Business Process',
    SelectL3Process: 'Select L3 Business Process',
    SelectReferredTo: 'Select Referred to',
    CollabAppContest: 'Collab App contest',
    CustomerWantEngage: 'When does customer want to engage',
    IsfeatureRequest: 'Is this feature parity request',
    EstimatedRows: 'Estimated rows:',
    AnalyticsverifiedPowerBI:
      'Your analytics view has been verified and is ready to use with Power BI.',
    RateYourExperience: 'Rate your experience ',
    ThankGivingfeedback: 'Thank you for giving rating feedback!',
    AddDescription: 'Click to add Description',
    AddExitCriteria: 'Click to add Exit Criteria',
    AddNextAction: 'Click to add Next Action',
    AddExceptionJustification: 'Click to add Exception Justification',
    Model: 'Model',
    Assigneen: 'Assigneen',
    Like: 'Like',
    Dislike: 'Dislike',
    NoFiltersSelected: 'No filters selected',
    ClearAllFilters: 'Clear all filters',
    ClearSelection: 'Clear Selections',
    SearchAndAdd: 'Search and Add',
    SearchAndAddSubText: 'Start searching for requestors to add as a filter',
    AddNew: 'New View',
    //CoSell
    SubCategoryTopic: 'Sub Category/Topic',
    SelectSubCategoryTopic: 'Select Sub Category/Topic',
    Activity: 'Activity',
    MicrosoftCoOwner: 'Microsoft Co-owner',
    MicrosoftOwner: 'Microsoft Owner',
    EnterMicrosoftCoOwner: 'Enter Microsoft Co-owner',
    EnterMicrosoftOwner: 'Enter Microsoft Owner',
    PartnerContactName: 'Partner Contact Name',
    EnterPartnerContactName: 'Enter Partner Contact Name',
    PartnerEmail: 'Partner Email',
    EnterPartnerEmail: ' Enter Partner Email',
    DeliveryLanguageSupport: 'Delivery Language Support',
    SelectDeliveryLanguageSupport: 'Select Language',
    SelectPartnerEngaged: 'Select Partner',
    MCEMSalesStage: 'MCEM Sales Stage',
    SelectMCEMSalesStage: 'Select MCEM',
    DealStatus: 'Deal Status',
    SelectDealStatus: 'Select Deal Status',
    DealValue: 'Deal Value (USD)',
    EnterDealValue: 'Enter Value in $',
    DealCreatedDate: 'Deal Created Date',
    DateAccepted: 'Accepted Date',
    DateExpired: 'Date Expired',
    ExpectedExpirationDate: 'Expected Expiration Date',
    ReferralID: 'Referral ID',
    EnterReferralID: 'Enter Referral ID',
    PartnerRegion: 'Partner Region',
    SelectPartnerRegion: 'Select Partner Region',
    PartnerCountry: 'Partner Country',
    SelectPartnerCountry: 'Select Partner Country',
    SelectAction: 'Select action',
    SelectActionPlaceholder: 'Select',
    SelectLinkType: 'Select Link Type',
    PartnerArea: 'Partner Area',
    SelectPartnerArea: 'Select Partner Area',
    CoSellActivity: ' Select Activity',
    CoSell: 'CoSell',
    RemoveMicrosoftCoOwner: 'Remove MicrosoftCoOwner',
    RemoveMicrosoftOwner: 'Remove Microsoft Owner',
    MicrosoftCoOwnerPlaceholder: 'Search Microsoft Co-Owner',
    MicrosoftOwnerPlaceholder: 'Search Microsoft Owner',
    CoSellRequestCategory: 'Request Category',
    SelectCoSellRequestCategory: 'Select Request Category',
    AssignedDate: 'Assigned Date',
    CoSellDescription: 'Description',
    SelectReferralID: 'Select Referral ID',
    EditText: 'Bulk Edit',
    DeleteText: 'Bulk Delete',
    EditIcon: 'Edit',
    DeleteIcon: 'Delete',
    MarkAsRelate: 'Mark as related',
    MarkAsRelateIcon: 'Relationship',
    ConflictDeleteDialogTitle: 'Delete selected actions?',
    ConflictDeleteDialogContent:
      'You are about to permanently delete the selected actions. This action cannot be undone.',

    RequestRevenueImpact: 'Request Revenue Impact',
    RevenueImpact:
      'Revenue Impact (Non-MACC or Blocked monthly amount in USD for entire request scope)',
    MACCImpact: 'MACC Impact (Total MACC amount in USD)',
    MACCDuration: 'MACC Duration (Number of Years associated with the MACC Impact) ',
    EnterReveueImpact: 'Enter Revenue Impact',
    EnterMACCImpact: 'Enter MACC Impact',
    EnterMACCDuration: 'Enter MACC Duration',
    ReportedBy: 'Reported By',
    RiskMitigationPlan: 'Mitigation Plan',
    RiskMitigationProgress: 'Risk Mitigation Progress',
    ResolutionPlan: 'Plan Until Resolution',
    ResolutionProgress: 'Resolution progress',
    DateOpened: 'Date Opened',
    DateClosed: 'Date Closed',
    MaaSRequestType: 'Request Type',
    SelectMaaSRequestType: 'Select Request Type',
    SelectMaaSCRMID: 'Select CRM-ID',
    SelectOpportunity: 'Select Opportunity Id',
    MaaSIntrestedAccontProfiling: 'Interested in Account Profiling for this account?',
    MaaSIntrestedDefinitiveHealthcareAccontProfiling:
      'Interested in Definitive Healthcare Data Concierge for this account?',
    MaaSIntrestedOrgCharting: 'Interested in Org Charting for this account?',
    MaaSRequerterEmail: 'Email',
    MaaSRole: 'Role',
    MaaSRoleCategory: ' Specific Role',
    SelectMarketerMaaSSpecificRole: 'Select Marketer Specific Role',
    SelectSellerMaaSSpecificRole: 'Select Seller Specific Role',
    MaaSIndustry: 'Industry',
    SelectMaaSIndustry: 'Select Industry',
    MaaSSegment: 'Segment Group',
    MaaSSubSegment: 'Sub Segment',
    SelectMaaSSegment: 'Select Segment',
    SelectMaaSSegmentGroup: 'Select Segment Group',
    SelectMaaSSubSegment: 'Select SubSegment',
    MaaSOU: 'Organizational Unit',
    MaaSOUShortForm: 'OU',
    SelectMaaSOU: 'Select Organizational Unit',
    MaaSMCEMStage: 'MCEM Stage',
    SelectMaaSStage: 'Select Stage',
    MaasDocumentCreationDate: 'Document Creation Date',
    MaaSDocumentLink: 'Document Link',
    MaaSEnterLink: 'Enter URL',
    MaaSAdditionalComments: 'Additional Comments',
    AccountCRMID: 'Account CRM-ID',
    CustomerWebsiteURL: 'Customer website URL',
    MaasMaxForm: 'Request details items can be added, with a maximum limit of 5.',
    MaaSBDMMaxForm:
      'Specific Organizational Title and Specific Name of BDMs can be added, with a maximum limit of 5.',
    MaaSTPID: 'TPID',
    MaaSAccount: 'Account',
    MaaSChildAccount: 'Child Account',
    MaasCheckboxOption1: 'Add Accounts to My List',
    MaasCheckboxOption1Caption:
      'Provide entire Account List along with New Accounts you want to add. No Accounts will be removed from your existing list with this selection. If any accounts need to be removed, please select "Replace my Existing List".',
    MaasCheckboxOption2: 'Replace my Existing List',
    MaasCheckboxOption2Caption:
      'List replacement should be used when you need to add and remove accounts from your existing list. Make sure to copy and paste your entire account list into the "Bulk Upload" dialogue box, even if some of the accounts are already in your customized view.',
    MaaSAdditem: 'Add Account',
    MaaSAddBDM: 'Add BDM',
    MaaSRemoveitem: 'Remove Account',
    MaaSRemoveBDM: 'Remove BDM',
    isExistingTechTargetACC: 'Do you have an existing TechTarget Account',
    MaaSBDMSpecificName: "Specific name of BDM's",
    MaaSSpecialOrgTitle: "Specific Organizational Titles of BDM's",
    SelectOption: 'Select option',
    MaasSpecificCountry: 'Specific Country (Subsidiary Name)',
    MaaSBDM: 'BDM',
    MaaSItem: 'Item 1',
    MaaSMultipleTPIDCaption:
      'You can input multiple TPIDs (upto 500) by separating them with comma.',
    MaaSLoadCRMIDUsingTPID: 'Load Data Based on TPID',
    MaaSLoadOppIDUsingCRMID: 'Load Data Based on CRMID',
    MaaSEventName: 'Event Name',
    MaaSEventDate: 'Event Date',
    MaaSEventRegistrationURL: 'Event Registration URL',
    MaaSEventType: 'Event Type',
    MaaSSelectText: 'Select',
    MaaSSelectEventDate: 'Select Event Date',
    MaaSSelectEventType: 'Select Event Type',
    MaaSSelectMarketingPlay: 'Select Marketing Play',
    MaaSSelectDataCaptureDate: 'Select Data Capture Date',
    MaaSSelectLeadSourceType: 'Select Lead Source Type',
    MaaSMarketingPlay: 'Marketing Play',
    MaaSSMPPApprovalID: 'SMPP Approval ID',
    MaaSSMPPApprovalIDToolTip:
      'Sales & Marketing Privacy Program (SMPP) approval ID can be found in the email subject line of your privacy approval. If you need help with the approval or finding the ID contact SMPP by clicking *https://aka.ms/smppcontacts*',
    MaaSDataCaptureDate: 'Data Capture Date',
    MaaSLeadSourceType: 'Lead Source Type',
    MaaSEventContactListpassword: 'Event Contact List Password',
    MaaSPrivacyApprovalEmail: 'Privacy Approval Email',
    MaaSAttachmentMessage:
      'File Number Limit: 4; Maximum File Size Limit: 20MB(All 4 files); Allowed File(s) Types: MSG, JPG, JPEG, PNG',
    MaaSAttachmentSAMessage:
      'File Number Limit: 4; Maximum File Size Limit: 20MB(All 4 files); Allowed File(s) Types: PDF',
    MaaSEventsponcorshipagreement: 'Event Sponsorship Agreement',
    MultiSort: 'Sort by',
    MultiSortText: 'Currently, the column sorting supports upto 2 columns',
    SelectLanguage: 'Select Language',
    MACC: 'MACC',
    MACCException: 'MACC Exception',
    DefenderForCloud: 'MDC Attach Purple Exception',
    MACCManageTab: 'MACC Unified Purple Empowerment',
    MACCTenantName: 'MACC Support',
    FeedbackFeatureRequest: 'Provide Feedback and Feature Request',
    Documentation: 'Application Documentation',
    UATisNowAction360: 'UAT is now Action 360',
    TechRobProcessDocumentation: 'TechRoB Process Documentation',
    RequestHelpSupport2: 'Request Help Support',
    UATDisscussion: 'Add a comment. Use @ to engage collaborator(s) in discussion',
    UATDiscussionforIsAutoClosed:
      'To engage the MCAPS Technical Feedback team for assistance, please provide a detailed comment and at mention mcapstechfeedback@microsoft.com.',
    GPSPartnerAddItem: 'Add Partner',
    GPSRemovePartner: 'Remove Partner',
    GPSEligiblePartner: 'Partner Name ',

    GPSControlType: 'Control Type',
    GPSControlTypePlaceholder: 'Search and add',
    GPSControlActivity: 'Control Activity',
    GPSControlActivityPlaceholder: 'Click here to add text',
    GPSControlOwnerName: 'Control Owner Name',
    GPSControlFrequency: 'Control Frequency',
    GPSControlActivitiesDoc: 'Control Activities documentation',
    GPSControlStartDate: 'Control Start Date',
    GPSControlEndDate: 'Control End Date',

    GPSPIRDecisionDate: 'PIR Decision Date',
    GPSPIRCaseID: 'PIR Case ID',
    GPSPIRManager: 'PIR Manager',
    GPSPIRDecision: 'Decision',
    GPSPIRReviewDate: 'Review Date',
    GPSPIRManagerNextStepsExceptionNotes: 'PIR Manager Next Steps, Exception, Notes',

    GPSStage: 'Stage',
    GPSBusinessCaseSubmitted: 'Business Case Submitted?',
    GPSFinanicialModelSubmitted: ' Financial Model Submitted?',
    GPSPIRReviewScheduled: 'PIR Review Scheduled?',
    GPSOpsIntakeRequired: 'Ops Intake Required?',
    GPSOpsIntakeComplete: 'Ops Intake Complete?',
    GPSOpsIntakeMeetingDate: 'Ops Intake Meeting Date',
    GPSNextStepsCompleted: 'Next Steps Completed?',
    GPSApprovedInQubit: 'Approved In Qubit?',
    GPSContractSignedDate: 'Contract Signed Date',
    GPSBusinessProcessCompleted: 'Business Process Completed',

    GPSOperationsApproval: 'Operations Approval',
    GPSOperationsPIRStakeholderName: 'Operations PIR Stakeholder Name',
    GPSOperationsNextStepsExceptionsNotes: 'Operations Next Steps, Exceptions, Notes',
    GPSDraftContract: 'Draft Contract',
    GPSOperationsNextStepsExceptionsNotesPlacehoder: 'Enter Exception Details',
    GPSOperationsNextStepsExceptionsNotesPlaceholder: 'Click here to add text',
    GPSDraftContractPlaceholder: 'Click here to add link',

    GPSFinancePIRStakeholder: 'Finance PIR Stakeholder',
    GPSFinanceNextStepsExceptionsNotes: 'Finance Next Steps, Exceptions, Notes',
    GPSFundingSource: 'Funding Source',
    GPSFundingType: 'Funding Type',
    GPSRevAdjust: 'RevAdjust (USD)',
    GPSOpex: 'Opex (USD)',
    GPSACO: 'ACO (USD)',
    GPSECIF: 'ECIF (USD)',
    GPSSponsorship: 'Sponsorship (USD)',
    GPSSkilling: 'Skilling (USD)',
    GPSInternalResources: 'Internal Resources (USD)',
    ClickHereToAddText: 'Click here to add text',
    GPSUSD: ' (USD)',
    GPSInvestmentOwner: 'Investment Owner',
    GPSControlAddItem: 'Add Control',
    GPSRemoveControl: 'Remove Control',
    GPSControlOwnerPartner: 'Control Owner',
    GPSBusinessType: 'Business Type',
    GPSBusinessTemplate: 'View Business Case template',
    GPSFinancialModel: 'View Financial Model template',
    GPSDeclarePolicy:
      'I have reviewed the following policies and agree to adhere to the required process',
    GPSCommercial: 'Commercial Governance Framework',
    GPSDPS: 'DPS Governance Framework',
    GPSDocumentation: 'Documentation',
    GPSFinancialInformation: 'Financial Information',
    GPSOperationsInformation: 'Operations Information',
    GPSPIR: 'PIR',
    GPSComplianceInformation: 'Compliance Information',
    GPSCorpAccountingInformation: 'Corp Accounting Information',
    GPSBusinessProcess: 'Business Process',
    GPSOnGoingMontioring: 'Ongoing Monitoring',
    GPSPolicy: 'Policy',
    GPSExpectedIncRevenue: 'Expected Incremental Revenue (USD)',
    GPSExpectedIncRevenueDPS: 'Expected Incremental Revenue',
    EnterNumericValue: 'Enter Value in Number',
    EnterNumericValueAskTypeChange:
      'Include the incremental revenue this change will  drive when compared with the already approved investment',
    EnterNumericValueAskTypeException:
      'Include the incremental revenue this exception will  drive when compared with the already approved investment',
    GPSCaseDetails: 'Case Details',
    GPSRequestType: 'Request Type',
    GPSRequestReason: 'Request Reason',
    GPSSelectRequestReason: 'Select Request Reason',
    SelectGPSRequestType: 'Select Request Type',
    SelectBusinessType: 'Select Business Type',
    PartnerOneID: 'Partner One ID',
    EligiblePartnerID: 'Eligible Partner ID',
    GPSEligiblePartnerID: 'Enter Partner ID',
    PartnerOneName: 'Partner Name',
    GPSEnterPartnerOneId: 'Enter value in Numbers',
    RemovePartnerOneName: 'remove Partner Name',
    RemoveEligiblePartner: 'remove Eligible Partner',
    RemoveInvestmentOwner: 'remove Investment Owner',
    GPSForecastedRevenue: 'Forecasted Revenue (USD)',
    GPSInvestmentAmount: 'Investment Amount (USD)',
    GPSInvestmentAmountAskTypeChange:
      'Include the incremental amount needed or the savings this change will  drive',
    GPSInvestmentAmountAskTypeException:
      'Include the incremental amount needed or the savings this exception will  drive',
    GPSGLPCCode: 'GL/PC Code',
    GPSSAPGLCode: 'SAP GL Code',
    GPSPaymentLever: 'Payment Lever',
    GPSPaymentProgram: 'Payment Program',
    GPSEarningType: 'Earning Type',
    GPSPaymentMethod: 'Payment Method',
    GPSPaymentFrequency: 'Payment Frequency',
    GPSFinalPaymentMilestone: 'Final Payment Milestone',
    GPSFinalPaymentMilestonePlaceholder: 'Select a future date',
    GPSFinalPaymentMilestoneHint:
      'Expected end date for final partner payments. Only future dates are allowed.',
    GPSPartnerTransactingType: 'Partner Transacting Type',
    SelectGPSPartnerTransactingType: 'Select Partner Transacting Type',
    GPSPaymentFrequencyPlaceholder: 'Examples: Quarterly, Monthly, Number of Days',
    GPSTCType: 'T&C Type',
    GPSPartnerContactInformation: 'Partner Contact Information',
    GPSComplexityLevel: 'Complexity Level',
    GPSComplexityDrivers: 'Complexity Drivers',
    GPSExecutionGovernance: 'Execution Governance',
    GPSFinanceApproval: 'Finance Approval',
    GPSStrategyApproval: 'Strategy Approval',
    SelectFinanceApproval: 'Select Finance Approval',
    SelectStrategyApproval: 'Select Strategy Approval',
    GPSFinancialEconomics: 'Financial Economics',
    GPSFinancialEconomicsPlaceholdersDPS: 'Click here to add Financial Economics',
    GPSFinancialEconomicsPlaceholders:
      'Provide total investment amount, the incremental revenue the investment is driving and  the ROI',
    GPSStartDate: 'Investment Start Date (UTC)',
    GPSEndDate: ' Investment End Date (UTC)',
    EnterMCEMStage: 'Enter MCEM Stage',
    GPSInvestmentType: 'Investment Type',
    GPSEngagementType: 'Engagement Type',
    GPSPayoutType: 'Payout Type',
    GPSProduct: 'Product',
    GPSAskType: 'Ask Type',
    GPSCustomerSegment: 'Customer Segment',
    GPSPartnerType: 'Partner Type',
    SelectGPSPartnerType: 'Select Partner Type',
    GPSAccountingTreatment: 'Accounting Treatment',
    GPSComplianceandOngoingMonitoring: 'Compliance and Ongoing Monitoring',

    GPSComplianceReview: 'Compliance Review',
    GPSCompliancePIRStakeHolderName: 'Compliance PIR Stakeholder Name',
    GPSComplianceNextStepsExceptionsNotes: 'Compliance Next Steps, Exceptions, Notes',
    GPSOngoingNextStepsExceptionsNotes: 'Ongoing Monitoring Next Steps, Exceptions, Notes',
    GPSCELAReview: 'CELA Review',
    GPSCELAIRStakeHolderName: 'CELA PIR Stakeholder Name',
    GPSCELANextStepsExceptionsNotes: 'CELA Next Steps, Exceptions, Notes',

    GPSCorpAccountingReview: 'Corp Accounting Approval',
    GPSCorpAccountingPIRStakeHolderName: 'Corp Accounting PIR Stakeholder Name',
    GPSCorpAccountingNextStepsExceptionsNotes: 'Corp Accounting Next Steps, Exceptions, Notes',

    GPSURLPlaceholder: 'Click here to add URL',
    GPSBusinessCaseLabel: 'Business Case ',
    GPSFinancialModelLabel: 'Financial Model ',
    FindStepForModel: 'Please find steps to attach Business Case and Financial Model:',
    ForBusinessCase:
      "For Business Case, Please Click on 'View Business Case template' to fill all the required fields and attach a copy of the file.",

    ForFinancialModel:
      "For Financial Model, Please Click on 'View Financial Model template' to fill all the required fields and attach a copy of the file.",
    AttachDocumentsChangeRequest:
      'Attach documents as required for Financial Model, Terms and Conditions, Amendment or Business Letter - Draft, List of Eligible Partner IDs, Finance Approval (if available), CELA Approval (if available), Compliance Approval (if available), Operation Approval (if available), Corp Accounting Approval (if available) ',
    AttachDocumentsNewRequest:
      'Attach documents as required for Business Case, Financial Model, Field/BG Business Approval, Field/BG Finance Approval, Exception Approvals, Terms and Conditions – Draft, List of Eligible Partner IDs',
    GPSMigratedDate: 'Migrated Date',
    GPSInvestmentComplexity: 'Investment Complexity',
    GPSStrategyLead: 'Strategy Lead',
    GPSLaunchManager: 'Launch Manager',
    GPSFinanceStakeholder: 'Finance Stakeholder',
    GPSCorpAccountingStakeholder: 'Corp Accounting Stakeholder',
    GPSCELAStakeholder: 'CELA Stakeholder',
    GPSComplianceStakeholder: 'Compliance Stakeholder',
    GPSOperationsStakeholder: 'Operations Stakeholder',
    GPSDueDate: 'Due Date',
    GPSEscalationDate: 'Escalation Date',
    GPSExecutionPath: 'Execution Path/Qubit ID',
    GPSContactSignDate: 'Contract Sign Date',
    GPSEnterExecutionPath: 'Enter Execution Path/Qubit ID',
    GPSEnterCalculationPartnerType: 'Please enter Calculation Partner ID Type',
    GPSEnterCalculationPartnerId: 'Please enter Calculation Partner ID',
    GPSEnterPartnerPaymentLocationId: 'Please enter Partner Payment Location ID',
    GPSEnterCalculationDataSource: 'Please enter Calculation Data Source',
    GPSEnterCalculationOwner: 'Please enter Calculation Owner',
    GPSEnterADOId: 'Please enter ADO ID',
    RemoveStrategyLead: 'remove Strategy Lead',
    MultipatnerInvetment: 'Multi Partner Investment',
    MultipatnerText: 'You can add multiple partners. A maximum of 30 partners can be added',
    EmailTask: 'Tasks',
    AddTasks: 'AddTask',
    CreateTasks: 'Create Task',
    EditTask: 'Task',
    TasksRedIconTooltipMsg: 'Due Date Approaching Fast',
    Owner: 'Owner',
    GPSDeletePartnerItem: 'Delete Partner',
    GPSDeleteControlItem: 'Delete Control',
    GPSCaseIdFramework: 'If this deal belongs to a framework, provide the Case ID',
    GPSCaseIdInvestmentMarket: 'Provide the Case ID of the investment in market',
    GPSEnterCaseID: 'Enter the Case ID',
    GPSCaseIdToolTip: 'The selected Case ID will be linked as the parent of this request.',
    GPSFinancialImpact: 'Is there a financial impact?',
    GPSChangeRequestType: 'Change Request Type',
    GPSSelectChangeRequestType: 'Select Change Request Type',

    CosellAssignmentDate: 'Assignment Date',
    CosellCreatedDate: 'Created Date',
    CosellStatusUpdatePlaceHolder: 'Enter Status Update',
    CosellClosureNotePlaceHolder: 'Enter Closure Note',
    CosellOpportunityIDPlaceHolder: 'Enter Opportunity ID',
    CosellOpportunityID: 'Opportunity ID',
    CoSellSubCategoryTitle: 'Sub Category',
    CosellSubCategoryPlaceholder: 'Enter Sub Category',
    //AMM
    AMM: 'AMM',
    AMMNode: 'AMM Offer Desk',
    AMMOfferDesk: 'AMM Offer Desk',
    AMMName: 'AMM',
    AONResubmissionBannerMessage:
      'The request has been resubmitted by the seller. Please refer to the History tab for details of the changes',
    CaseCreatedDateandTime: 'Case Created Date & Time',
    CaseCreatedTime: 'Case Created Time',
    RequestOnBehalfOf: 'Request On Behalf Of',
    HelpType: 'Help Type',
    ProgramID: 'Program ID',
    RequestOrigin: 'Origin',
    Offer: 'Offer',
    OfferType: 'Offer Type',
    CommunicationPreference: 'Communication Preference',
    CaseNotes: 'Case Notes',
    ExceptionTypeOptions: 'ACR / Partner / “ACR and Partner”',
    ExceptionDecisionOptions: 'Approved / Rejected',
    NamePlaceholder: 'please enter Partner Name',
    SellerAttestationOptions: 'Yes / No',
    PendingActionFrom: 'Pending Action From',
    ProjectStatus: 'Project Status',
    ProjectAdditionalDetails: 'Project Additional Details',
    NextBestAction: 'Next Best Action',
    NextBestActionDetails: 'Next Best Action Details',
    ActionTaken: 'Action Taken',
    ActionTakenDetails: 'Action Taken Details',
    OfferEngineStatus: 'Offer Engine Status',
    OriginatingCaseID: 'Originating Case ID',
    CancellationReason: 'Cancellation Reason',
    CancellationAdditionalDetails: 'Cancellation Additional Details',
    RejectionReason: 'Rejection Reason',
    RejectionAdditionalDetails: 'Rejection Additional Details',
    DisqualificationReason: 'Disqualification Reason',
    DisqualificationAdditionalDetails: 'Disqualification Additional Details',
    CaseDuration: 'Case Duration',
    slaRemainingTime: 'SLA Remaining Time',
    RequesterTimeZone: 'Requester Time Zone',
    RequesterTimeZonePlaceholder: 'Enter Requester Time Zone',
    RequesterDepartment: 'Requester Department',
    RequesterDepartmentPlaceholder: 'Enter Requester Department',
    RequesterArea: 'Requester Area',
    RequesterAreaPlaceholder: 'Enter Requester Area',
    MilestoneName: 'Milestone Name',
    Workload: 'Workload',
    CMFOffer: ' CMM Offer',
    SubmitNomination: 'Submit Nomination to Cloud Factory',
    CMFOfferInfo:
      'Toggle this option to send nomination to Cloud Factory, upon saving the request, nomination will be submitted to cloud factory',
    ScopeOfWork: 'Scope of Work',
    SelectScopeOfWork: 'Select Scope of Work',
    AAOfferType: 'AA Offer Type',
    SelectAAOfferType: 'Select AA Offer Type',
    SelectMigrationPartnerAttached: 'Select Migration Partner Attached',
    ProgramFunding: 'Program/Funding',
    EnrollmentNumber: 'Enrollment Number or Billing Account ID (if existing Account only)',
    EnrollmentNumberPlaceholder: 'Enrollment Number or Billing Account ID',
    AccountOwnerID: 'Account Owner ID',
    TermsandConditionEmail: 'Terms and Condition Email',
    SponsorshipAmount: 'Sponsorship Amount',
    SupportRegion: 'Support Region',
    SelectSupportRegion: 'Select Support Region',
    CustomerCCList: 'Customer CC List',
    MicrosoftCCRecipient: 'Microsoft CC Recipient',
    MicrosoftCCRecipientPlaceholder: 'Enter Microsoft CC Recipient',
    ActiveSalesStage: 'Active Sales Stage',
    SelectActionTaken: 'Select Action Taken',
    SelectNextBestAction: 'Select Next Best Action',
    SelectCancellationReason: 'Select Cancellation Reason',
    SelectRejectionReason: 'Select Rejection Reason',
    SelectDisqualificationReason: 'Select Disqualification Reason',
    SelectMileStoneId: 'Select Milestone ID',
    SelectOfferEngineStatus: 'Select Offer Engine Status',
    SelectPendingActionFrom: 'Select Pending Action From',
    SelectCommunicationPreference: 'Select Communication Preference',
    SelectHelpType: 'Select Help Type',
    SelectScenarioRequest: 'Select the scenario most appropriate for this request',
    isThisHyperScalerOrBICompete: 'Is this a hyper scaler compete or a BI compete?',
    SelectAppropriateRequest: 'Select Appropriate Request',
    CustomerAcceptedtheAssessment: 'Customer Accepted the Assessment',
    DoyouneedhelppitchingthevalueoftheSolutionAssessmenttothecustomer:
      'Do you need help pitching the value of the Solution Assessment to the customer?',
    MigrationPartnerAttached: 'Migration Partner Attached',
    MigrationPartnerEmail: 'Migration Partner Email',
    DoyouneedaWaveplanaspartoftheassessmenttopreventbreakageofanyappdependencies:
      'Do you need a Wave plan as part of the assessment to prevent breakage of any app dependencies?',
    Isthisassessmentmeanttoaddressappmigrationmodernization:
      'Is this assessment meant to address app migration/modernization?',
    AEAlias: 'AE Alias',
    ATSAlias: 'ATS Alias',
    SSTSAlias: 'SS/TS Alias',
    SecuritySSPAlias: 'Security SSP Alias',
    AppSSPAlias: 'App SSP Alias',
    MicrosoftccRecipientsPlaceholder: 'Enter Requester Area',
    CSAEmail: 'CSA Email Address',
    CSA2Email: 'CSA2 Email Address',
    CSA3Email: 'CSA3 Email Address',
    AEATSEmail: 'AE/ATS Email Address',
    CSAMName: 'CSAM Name',
    CSASecurity: 'CSA Security',
    SpecialistEmail: 'Specialist Email Address',
    RemoveCSAEmail: 'Remove CSA Email',
    RemoveCSA2Email: 'Remove CSA2 Email',
    RemoveCSA3Email: 'Remove CSA3 Email',
    RemoveAEATSEmail: 'Remove AE/ATS Email',
    RemoveSpecialistEmail: 'Remove Specialist Email',
    SubSegment: 'Sub-segment',
    AMMSupportRequest: 'Help',
    AMMDraft: 'Draft',
    ProjectSize: 'Project Size',
    ProjectSizePlaceholder: 'Enter Project Size',
    DeliveryPartnerEligibility: 'Delivery - Partner Eligibility',
    DeliveryPartnerEligibilityPlaceholder: 'Enter Delivery - Partner Eligibility',
    TotalACR: 'Total ACR',
    TotalACRPlaceholder: 'Enter Total ACR',
    TotalForOfferEligibleACR: 'Total for Offer Eligible ACR',
    TotalForOfferEligibleACRPlaceholder: 'Enter Total for Offer Eligible ACR',
    PrePost: 'Pre/Post',
    ExpectedProjectStartDate: 'Expected Project Start Date (UTC)',
    ExpectedProjectEndDate: 'Expected Project End Date (UTC)',
    SelectProjectStartDate: 'Select Project Start Date',
    selectProjectEndDate: 'Select Project End Date',
    RequestSubTitle: 'Request Sub Title',
    RequestSubTitlePlaceholder: 'Enter the Request Subtitle',
    HelpDescription: 'Help Description',
    SLARemainingTime: 'SLA Remaining Time',
    ServiceLocation: 'Service Location',
    SelectServiceLocation: 'Select Service Location',
    EnterServiceLocation: 'Enter Service Location',
    SponsoringCountry: 'Sponsoring Country',
    SelectSponsoringCountry: 'Select Sponsoring Country',
    SelectIncrementalRevenueROI: 'Select Incremental Revenue/ROI',
    IncrementalRevenueROI: 'Incremental Revenue/ROI',
    FirstYearACR: '1st Year ACR',
    FirstYearACRPlaceholder: 'Enter 1st Year ACR',
    PrimarySolution: 'What is the primary solution play supporting this project?',
    SolutionPlay: 'Migrate and Securely Manage Win Server, SQL Server and Linux (includes AVS)',
    MicrosoftDefender: 'Microsoft Defender for Cloud',
    ESI: 'ESI',
    FTA: 'FTA',
    RequestAmount: 'Request Amount',
    RequestAmountlaceholder: 'Enter Request Amount',
    ECIFSpendPlan: 'ECIF Spend Plan',
    ECIFSpendPlanPlaceholder: 'Enter ECIF Spend Plan',
    CompetitiveBid: 'Is this a Competitive Bid?',
    RequestACO: 'Do you want to request ACO in addition to ECIF?',
    MicrosoftAgentP3AndFabric3YrRI:
      'Does the customer intend to buy Microsoft Agent P3 and Fabric 3Yr RI over pay-go?',
    WhenDoTheyExpectToTransactAgentP3AndFabricRI:
      'When do they expect to transact the Agent P3 and Fabric RI?',
    ACOID: 'ACO – ID #',
    ACOIDPlaceholder: 'Enter ACO – ID #',
    ACOCE: 'ACO – CE',
    SEARCH_ACOCE: 'Search ACO – CE',
    ACOCEPlaceholder: 'Enter ACO – CE',
    ACOCreditRequestAmount: 'ACO Credit Request Amount (USD)',
    ACOCreditRequestAmountPlaceholder: 'Enter ACO Credit Request Amount (USD)',
    CreditRequestAmount: 'Credit Request Amount (USD)',
    DealStructure: 'Deal Structure',
    Revenue: 'Revenue',
    FiscalYear: 'Fiscal Year',
    Quarter1: 'Quarter 1',
    Quarter2: 'Quarter 2',
    Quarter3: 'Quarter 3',
    Quarter4: 'Quarter 4',
    Total: 'Total',
    TotalSpend: 'Total Spend',
    EnterAmount: 'Enter Amount',
    RemainingAmount: 'Remaining Amount',
    // SLARemainingTime: 'SLA Remaining Time',
    // ServiceLocation: 'Service Location',
    // SelectServiceLocation: 'Select Service Location',
    RequestReason: 'Request Reason',
    SelectRequestReason: 'Select Request Reason',
    SelectDealStructure: 'Select Deal Structure',
    SelectTheFutureGrowthOffer: 'Select the Future Growth Offer',
    IsThisRequestRelated:
      'Is this request related to Public / Government or State Owned Entity (SOE) tender offer (RFP, RFI or bid)?',
    DoesThisIncludeHighRiskDeal:
      'Does this include High Risk Deal? Refer to EMP Guide 2195 to know what are consider as High risk deals',
    WhichSAPOfferDoesCorrespond: 'Which SAP Offer does this request correspond to?',
    WhatisRIValueVMPerMonth: 'What is the RI Value for D,E,M series VM per month?',
    ExistingMACCCTCMC: 'Is there any existing MACC/CTC/MC?',
    ExcistingACO: 'Is there existing ACO?',
    SelectSAPOffer: 'Select SAP Offer',
    SelectRIPerMonth: 'Enter What is the RI Value for D,E,M series VM per month',
    AVSOffer: 'Which AVS offer does this request correspond to?',
    SelectAVSOffer: 'Select AVS Offer',
    SSCACOOffer: 'Service Specific CACO Offer',
    SelectSSCACOOffer: 'Select Service Specific CACO Offer',
    isThisAVMCToAVSMigration: 'Is this a VMC to AVS migration?',
    AvsRIQuantity:
      'What is the planned quantity of AVS RIs (1 or 3 YR RI) that the customer plans to purchase? ',
    EnterAvsRIQuantity: 'Enter the planned quantity of AVS RIs',
    avsRIRegion: 'In what region does the customer intend to deploy the new AVS RI’s?',
    EnterAvsRIRegion: 'Enter the region of the AVS RIs',
    FutureGrowthOffer: 'Azure Incubation Offer',
    PrimaryAzureVirtualMachine: `What is the PRIMARY Azure virtual machine (VM) series for this project?`,
    ThreeYearACR: '3rd Year ACR',
    ThreeYearACRPlaceholder: 'Enter 3rd Year ACR',
    EnterValue: 'Enter Value',
    CMFRequestCreated: 'CMF Request Created',
    AMMOfferDeskRegion: 'AMM Offer Desk Region',
    SelectAMMOfferDeskRegion: 'Select AMM Offer Desk Region',
    HowManyVMsIsTheCustomerDeploying: 'How many VMs is the customer deploying?',
    WhatAreTheVMsSizes:
      'What are the VMs sizes? Please provide details of sizes and quantities for each size​',
    EnterWhatAreTheVMsSizes: 'Enter the VMs sizes',
    AVSOfferAzureGPCNative: 'Azure GPC (Native)',
    AVSOfferAVSGoBig: 'AVS Go Big',
    AVSOfferAVSCorp: 'AVS Corp',
    WhatMigrationWillBeCompleted: 'What migration will be completed for this project?',
    EligibleMonths: 'Eligible Months',
    AcrCalculation: 'ACR Calculation (USD)',
    MilestoneCategory: 'Milestone Category',
    CMFApplicable: 'CMF Applicable',
    ECIF_OfferEngineStatus: 'ECIF Offer Engine Status',
    ECIF_OfferEngineCaseID: 'ECIF Offer Engine Case ID',
    ECIF_OfferEngineType: 'ECIF Offer Engine Type',
    CMF_OfferEngineCaseID: 'CMF Offer Engine Case ID',
    CMF_OfferEngineStatus: 'CMF Offer Engine Status',
    CMF_OfferEngineType: 'CMF Offer Engine Type',
    ProjectScenario: 'Select if any of the following scenarios applies to your project',
    PartnerOrganizationName: 'Partner Organization Name',
    DeliveryGroup: 'Select the group involved with this delivery',
    NominationFactoryAssessment:
      'Is this a nomination related to a request for a Factory Assessment for a Deal desk or AWR deal?',
    ISD_PDOCPOCName: 'ISD / ISV/ PDOC /Partner POC Name',
    ISD_PDOCPOCEmail: 'ISD/ ISV/ PDOC /Partner POC Email',
    // ACO refers to AVS as per customer requirement
    AVS_OfferEngineStatus: 'ACO Offer Engine Status',
    AVS_OfferEngineCaseID: 'ACO Offer Engine Case ID',
    AVS_OfferEngineType: 'ACO Offer Engine Type',
    MigrationToAVSOnly: 'Migration to AVS Only',
    MigrationtoAVSAndAzureGPC: 'Migration to AVS and Azure GPC (Native)',
    LinuxDiscount: 'Has Linux discount been provided to this customer already?',
    IsPartnerInitiated: 'Is Partner Initiated',
    GBBAlias: 'GBB Alias',
    OfferConsiderations: 'Offer Considerations',
    OfferConsiderationsPlaceholder: 'Enter Offer Considerations',
    PlannedDateofAssessment: 'Planned Date of Assessment',
    PlannedDateofMigration: 'Planned Date of Migration',
    DoyouhavetheRVToolsExport: 'Do you have the RV Tools Export?',
    FieldEmpowermentDiscount: 'Field Empowerment Discount',
    AzureRegion: 'Azure Region 1',
    AlreadyRunningAtleast1AVSClusterInTheTargetRegion:
      'Already running at least 1 AVS cluster in the target region',
    NodeType: 'Node Type 1',
    ReservedInstances: 'Reserved Instances 1',
    CPUUtilization: 'CPU Utilization 1 (%)',
    MemoryUtilization: 'Memory Utilization 1 (%)',
    CPUOverSubscription: 'CPU Oversubscription 1',
    MemoryOverCommitFactor: 'Memory Overcommit Factor 1',
    FTTSettings: 'FTT Settings 1',
    DedupeAndCompressionFactor: 'Dedupe and Compression Factor 1',
    Currency: 'Currency',
    Currency1: 'Currency 1',
    CustomerName: 'Customer Name',
    CustomerContact: 'Customer Contact',
    MicrosoftSellerContactOrAccountExecutive: 'Microsoft Seller Contact/Account Executive',
    AzureRegion2: 'Azure Region 2',
    NodeType2: 'Node Type 2',
    ReservedInstances2: 'Reserved Instances 2',
    CPUUtilization2: 'CPU Utilization 2 (%)',
    MemoryUtilization2: 'Memory Utilization 2 (%)',
    CPUOverSubscription2: 'CPU Oversubscription 2',
    FTTSettings2: 'FTT Settings 2',
    Currency2: 'Currency 2',
    MemoryOverCommitFactor2: 'Memory Overcommit Factor 2',
    DedupeAndCompressionFactor2: 'Dedupe and Compression Factor 2',
    VMWareVCFAnnualLicenseCostPerPhysicalCore: 'VMWare VCF Annual License Cost per Physical Core',
    CustomerWillBYOVMWareVCFLicense: 'Customer will BYO VMWare VCF license ',
    SelectTheRightServiceFromTheListBelow: 'Select the right service from the list below',
    DoesYourCustomerHaveOneOfTheBelowListedPartnersAsOneOfTheirLeadingSI:
      'Does your customer have one of the below listed partners as one of their leading SI?',
    SILeadingPartner: 'SI leading Partner - Other',
    SelectPlatformBeingMigrated:
      'Select the Database/Data Warehouse/Hadoop platform being migrated',
    DatabaseORDataWarehouseORHadoopOROther: 'Database/Data Warehouse/Hadoop - Other',
    DoesTheCustomerHaveASignedSOWWithThePartner:
      'Does the customer have a signed SOW with the partner?',
    DoesTheCustomersHaveADateOrDeadlineToMoveToAzure:
      'Does the customers have a date or deadline to move to Azure',
    WhatAreTheETLELTTechnologiesDoesYourCustomerPlanToMigrateToCloudNative:
      'What are the ETL/ELT technologies? Does your customer plan to migrate to Cloud native?',
    CustomerExecutiveSponsor: 'Customer Executive Sponsor',
    OfferSMECaseNotes: 'Offer SME Case Notes',
    OfferSMEDecision: 'Offer SME Decision',
    CompeteScenario: 'Compete Scenario',
    BigQueryWorkloadIsPresent:
      'Are you aware of the competitive workloads present (AWS/GCP/Snowflake on AWS)?',
    RelationshipWithGCPBigQueryOwner:
      'Do you have a relationship with the owner of the competing analytics services?',
    PleaseDescribeTheCompetitiveLandscapeBetweenGoogleCloudPlatformAndAzureAtTheCustomer:
      'Please describe the competitive landscape at the customer. (e.g. what is the competitive solution being used for? Where is it deployed? How is it being positioned to the customer?)',
    CreditAmountRequested: 'Credit Amount Requested (USD)',
    AreYouAwareOfWhereTheCompetitorWorkloadIsPresent:
      'Are you aware of where the competitor workload is present?  If so, where?',
    DoYouHaveARelationshipWithTheOwnerOfExistingCompetitorServices:
      'Do you have a relationship with the owner of  existing competitor services?',
    PleaseDescribeTheLandscapeBetweenOurCompetitorAndAzureAtTheCustomer:
      'Please describe the landscape between our competitor and Azure at the customer.',
    AnticipatedDeliveryMethod: 'Anticipated Delivery Method',
    AdditionalOpportunityIds: 'Additional Opportunity IDs',
    AccountTeamAlignment: 'Account Team Alignment',
    CustomerInterest: 'Customer Interest',
    AdditionalInsightsAndOrRelevantContextThatMakesThisCustomerAGoodCandidateForANomination:
      'Briefly state any additional insights and/or relevant context that makes this customer a good candidate for a nomination.',
    WhatAreTheWorkloadsStrategicPillarsAndOrUseCasesThatYouIntendToCoverInTheHack:
      'What Are The Workloads, Strategic Pillars, And/Or Use Cases That You Intend To Cover In The Hack?',
    ConfirmAnyConditionsApplyForProject:
      'Please confirm if ANY of the following conditions apply for this project',
    SelectWhichScenarioBestSuitsYourProject: 'Select which scenario best suits your project',
    ProjectScopeDistribution: 'Project Scope Distribution',
    ProjectScopeDistributionSeller: 'Project Scope Distribution (Seller)',
    ProjectScopeDistributionRecommended: 'Project Scope Distribution (Factory Recommended)',
    ProjectScopeDistributionFinal: 'Project Scope Distribution (Final)',
    ProjectScopeDistributionPartnerAccordionLabel: 'Partner',
    ProjectScopeDistributionFactoryAccordionLabel: 'Factory',
    ProjectScopeDistributionPartnerAllocation: 'Partner Allocation',
    ProjectScopeDistributionFactoryAllocation: 'Factory Allocation',
    ProjectScopeDistributionSliderDescription:
      'Drag the below slider to adjust the allocation. The values below will update automatically.',
    ProjectScopeDistributionResetTooltip:
      'Click to reset to the seller recommended allocation. The values below will update automatically.',
    HasPartnerBeenSelectedAndAligned:
      'Has Partner been selected and is aligned with customer’s scope of work prior to August 1, 2025, and introducing Factory into project would jeopardize the deal?',
    FactoryExceptionBusinessJustification:
      'Please provide a brief business justification on why the Factory should not be included in this specific project. The Azure Offer Navigator Manager will follow up you with within 24 hours to discuss the exception request and work with you on next steps',
    AreYouReadyToStartTheProject: 'Are you ready to start the project?',
    DoYouNeedAQuickQuote:
      'Do you need a quick quote to provide the Customer / Partner for discussions on the project?',
    EnrollmentID: 'Enrollment ID',
    IsNominationForCspLicensingAgreement:
      'Is this nomination for a customer with a CSP licensing agreement?',
    WhatIsTheCustomerTenantID: 'What is the customer’s Tenant ID?',
    NominationForAnIndirectOrDirectCSPPartner:
      'Is this nomination for an Indirect or Direct CSP Partner?',
    WhatIsCSPPartnerCompanyName: 'What is CSP Partner Company Name?',
    WhatIsCSPIndirectResellerCompanyName: 'What is CSP Indirect Reseller Company Name?',
    IsTheECIFSupplierTheSameAsTheReseller: 'Is the ECIF supplier the same as the reseller?',
    CustomerAgreementReviewerContactName: 'Customer Agreement Reviewer Contact Name',
    CustomerAgreementReviewerContactEmail: 'Customer Agreement Reviewer Contact Email',
    PartnerOrResellerAgreementReviewerContactName:
      'Partner or Reseller Agreement Reviewer Contact Name',
    PartnerOrResellerAgreementReviewerContactEmail:
      'Partner or Reseller Agreement Reviewer Contact Email',
    IsCustomerLookingForDeployingOracleDatabase:
      'Is customer looking for deploying Oracle Database@Azure offering through Azure Marketplace',
    IsMSXOpportunityCreatedForOracle:
      'Is MSX opportunity created according to Oracle Database@Azure MSX guidance',
    AreOracleACRWorkloadsEstimatedInMSX:
      'Are Oracle Database@Azure pull-thru ACR workloads estimated in MSX opportunity',
    isHighRiskDeal:
      'Does this include High Risk Deal? (Refer to EMP Guide 2195 to know what are consider as High-risk deals)',
    isPublicSectorTender:
      'Is this request related to Public / Government or State-Owned Entity (SOE) tender offer (RFP, RFI, or bid)?',
    //PMYT
    PMYT: 'Partner Marketing Yield Tracker',
    RemovePartnerName: 'Remove Partner Name',
    PartnerIDPartnerOneID: 'Partner ID / Partner One ID',
    PartnerIDPartnerOneIDPlaceholder: 'Enter Partner ID/ Partner One ID',
    PartnerNamePlaceholder: 'Enter Partner Name',
    PMYT_CampaignName: 'Campaign Name',
    PMYT_CampaignNamePlaceholder: 'Enter Campaign Name',
    CampaignInvestmentType: 'Campaign/Investment Type',
    CampaignInvestmentTypePlaceholder: 'Enter Amount',
    PartnerSourceOfInvestment: 'Source of Investment',
    PartnerSourceOfInvestmentPlaceholder: 'Select Source of Investment',
    PartnersCoInvestmentAmount: 'Co-Investment Amount (USD)',
    PartnersCoInvestmentAmountPlaceholder: 'Enter Amount',
    PartnerInvestmentAmount: 'Investment Amount (USD)',
    PartnerMarketingYieldTracker: 'Partner Co Marketing',
    SolutionPlays: 'Solution Play',
    SelectSolutionPlays: 'Select Solution Plays',
    EstimatedStartDateoftheCampaign:
      'Estimated Start Date of the Campaign or 1st Campaign of Investment',
    EstimatedStartDate: 'Estimated Start Date of the Campaign',
    EstimatedPipelineRevenue: 'Estimated Pipeline Revenue (USD) as a Result of the Investment',
    EstimatedPipelineRevenueUSD: 'Estimated Pipeline Revenue (USD)',
    EstimatedPipelineRevenuePlaceholder: 'Enter Amount',
    PMYT_PartnerType: 'Partner Type',
    PMYT_PartnerTypePlaceholder: 'Select Partner Type',
    PMYT_CustomerSegment: 'Customer Segment',
    PMYT_CustomerSegmentPlaceholder: 'Select Customer Segment',
    EstimatedCosellReferrals: 'Estimated # Cosell Referrals as a Result of the Investment',
    EstimatedCosellReferralsPlaceholder: 'Enter Cosell Referrals',
    PMYT_Industry: 'Industry',
    PMYT_SolutionArea: 'Solution Area',
    PMYT_Area: 'Area',
    PMYT_SolutionPlays: 'Solution Plays',
    OfferEngineCaseID: 'Offer Engine Case ID',
    OfferEngineCaseIDPlaceholder: 'Enter Offer Engine Case ID',
    OfferEngineType: 'Offer Engine Type',
    SelectOfferEngineType: 'Select Offer Engine Type',
    SelectAVSOfferEngineStatus: 'Select AVS Offer Engine Status',
    FirstYearROI: '1st Year ROI',
    FirstYearROIPlaceholder: 'Enter 1st Year ROI',
    ThirdYearROI: '3rd Year ROI',
    ThirdYearROIPlaceholder: 'Enter 3rd Year ROI',
    Linkworkscope: 'Link work scope',
    Linkworkscopeplaceholder: 'Enter Link work scope',
    ScopeText: 'Project Scope',
    ScopeTextplaceholder: 'Enter Project Scope',
    // LicensingProgram: 'Licensing Program',
    LicensingProgramplaceholder: 'Enter Licensing Program',
    MCAPSLeaderROBRequest: 'MCAPS Leader RoB Request',
    RedButtonSMCRequest: 'Red Button SMC Request',
    RedButtonTSIRequest: 'Red Button TSI Request',
    CAPSLRequest: 'CAPSL Request',
    CreateMCAPSHelpRequest: 'Create MCAPS Help Request',
    CreateMACCSUPPORTRequest: 'Create MACC Unified Purple Empowerment Request',
    CreatePartnerMarketingYieldTracker: 'Create Partner Marketing Yield Tracker',
    McapsHelpMaccExceptionRequest: 'MCAPS Help MACC Exception Request',
    SGSRequest: 'SGS Request',
    PSRequest: 'Regional Skilling Request',
    MSRRequest: 'Microsoft Security Research Request',
    ManageSGSRequest: 'Manage SGS Request',
    AzureISVRequest: 'Azure Sponsorship for ISV Requests',
    Domain: 'microsoft.com',
    InvestmentTitle: 'Investment Title',
    InvestmentDescription: 'Investment Description',
    IsIncentiveTargetingSuite:
      'Is this incentive targeting Teams Essential, Teams Enterprise or one of these Suites*?',
    IsIncentiveTargetingSuiteNote:
      '*Suites: Microsoft 365 Business Basic, Microsoft 365 Business Standard, Microsoft 365 Business Premium, Office 365 E1, Office 365 E3, Office 365 E5, Microsoft 365 E3, and Microsoft 365 E5, Microsoft 365 F1, F3, Office 365 F3 and any successor or substantially equivalent versions of these suites targeted at knowledge workers in business, enterprise, and public sector organizations.',
    PlaceholderInvestmentDescriptionAskTypeNew:
      'Provide business justification, description of the investment ask including high level construct information',
    PlaceholderInvestmentDescriptionAskTypeChange:
      'Provide business justification, what is changing and the impact',
    PlaceholderInvestmentDescriptionAskTypeException:
      'Provide business justification for the exception request including the partner name and the $ amount.',
    ThirdPartySecuritySolution:
      'Please list the name of 3rd party security solution that is in place (i.e., Prisma Cloud, AWS Security Hub).',
    MoreDetailsOnSecurity:
      'Please share more details on Security for this migration. Response will be reviewed by AMM Offer Desk to determine next steps.',
    CustomerPeakWorkload: `What is the customer's peak workload utilization in MIPS, ZiiPS, and IFLs?`,
    MseriesConsumption: 'What is the past 4 months M series Consumption?',
    DeLinkEmailConfirmationTitle: 'Do you want to delink this email?',
    DeLinkEmailConfirmationMessage:
      "Delinking the email from the current action will remove it from the email list. However, it will remain available under 'Link Existing Email' list if you wish to link it again.",
    LinkExistingEmail: 'Link existing email',
    DeLinkEmail: 'Delink email',
    LinkExistingEmailbtntext: 'Link to current action',
    DeLinkEmailbtntext: 'Delink from current action',
    SMCRedButton: 'SMC Red Button',
    SMCRedButtonSearch: 'SME&C Red Button',
    RedButtonTSI: 'Red Button TSI',
    McapsHelpPMNode: 'MCAPS Help PM',
    McapsHelpPMTenantName: 'MCAPS Help PM',
    RedButtonTenantName: 'Red Button',
    PartnerInvestmentsTenantName: 'PartnerInvestments',
    ChildRelationReverse: 'System.LinkTypes.Hierarchy-Reverse',
    RelatedRelationRemote: 'System.LinkTypes.RemoteRelated',
    ECIFBilledRevenueInformation: 'Billed Revenue Information',
    ECIFBaselineRevenue: 'Baseline Revenue (USD $)',
    ECIFBilledRevenue: 'Est. Billed Revenue (USD $)',
    ECIFBilledCloseDate: 'Est. Billed Close Date',
    ECIFIncrementalBilledRevenue: 'Incremental/Upsell Billed Revenue (USD $)',
    ECIFConsumedRevenueInformation: 'Consumed Revenue Information (USD$)',
    ECIFDealOverviewJustification: 'Deal Overview / Justification',
    ECIFActiveUsersOrDevices: 'Active Users/Devices',
    ECIFSKUDiscount: 'SKU Discount (if applicable) (USD $)',
    ECIFAzureCreditDiscount: 'Azure Credit Discount (if applicable) (USD $)',
    ECIFConsumedRevenuefor1year: 'Consumed Revenue for 1 year',
    ECIFConsumedRevenuefor2year: 'Consumed Revenue for 2 year',
    ECIFConsumedRevenuefor3year: 'Consumed Revenue for 3 year',
    ECIFEstConsStartDate: 'Est. Cons. Start Date',
    ECIFEstConsCompletionDate: 'Est. Cons. Completion Date',
    ECIFSupportInformation: 'Support Information',
    ECIFRequestAmount: 'ECIF Request Amount (USD $)',
    ECIFRequestAmountInfo: 'Enter amount from workforce',
    ECIFY1ACRInfo: 'Consumed revenue for 1 Year is the revenue for first year',
    ECIFY2ACRInfo: 'Consumed revenue for 2 year is second year ACR only',
    ECIFY3ACRInfo: 'Consumed revenue for 3 year is third year ACR only',
    EnterECIFRequestAmount: 'Enter ECIF Request Amount (USD $)',
    FxROI: '1 Year ROI',
    FxROI3: '3 Year ROI',
    EnterFxROI: 'Enter 1 Year ROI',
    EnterFxROI3: 'Enter 3 Year ROI',
    ECIFFiscalYear: 'Fiscal Year',
    SelectECIFFiscalYear: 'Select Fiscal Year',
    ECIFSolutionApproach: 'ECIF Solution Approach',
    EnterECIFSolutionApproach: 'Click to write ECIF Solution Approach',
    ECIFProgramNameLocalFund: 'Program Name',
    SelectECIFProgramNameLocalFund: 'Select Program Name',
    SelectSupplierType: 'Select Supplier Type',
    RenamedCosellSMCRequestCategory: 'Co-sell - SMC-C',
    ECIFWorkType1: 'Work Type',
    ECIFWorkType2: 'Type of activity of the solution approach',
    SelectWorkType: 'Select Work Type',
    ECIFDeliveryStartDate: 'Delivery Start Date',
    ECIFDeliveryEndDate: 'Delivery End Date',
    ECIFSolutionProvided: 'Solution Approach / Solution to be Provided',
    ECIFSupplier: 'Supplier',
    ECIFEnterSupplier: 'Enter Supplier',
    ECIFSupplierID: 'Supplier ID',
    ECIFEnterSupplierID: 'Enter Supplier ID',
    ECIFSupplierContactName: 'Supplier Contact Name',
    ECIFEnterECIFSupplierContactName: 'Enter Supplier Contact Name',
    ECIFSupplierContactEmail: 'Supplier Contact Email',
    ECIFEnterSupplierContactEmail: 'Enter Supplier Contact Email',
    ECIFInternalPartner: 'ECIF Internal Partner',
    ECIFDeliveryServiceManager: 'Delivery Service Manager',
    RemoveECIFDeliveryServiceManager: 'Remove Delivery Service Manager',
    ECIFAzureCreditOffer: 'Azure Credit Offering Information',
    ECIFACODeliveryStartDate: 'ACO Delivery Start Date',
    ECIFACOCreditRequestAmount: 'ACO Credit Request Amount (USD $)',
    ECIFEnterACOCreditRequestAmount: 'Enter ACO Credit Request Amount (USD $)',
    ECIFEnrollmentID: 'Enrollment ID',
    ECIFEnrollmentNumber: 'Enrollment Number',
    ECIFEnterEnrollmentID: 'Enter Enrollment ID',
    ECIFCommercialExecutive: 'Commercial Executive',
    ECIFRequestReason: 'Request Reason',
    ECIFDealStructure: 'Deal Structure',
    ECIFACORequestJustification: 'ACO Request Justification',
    ECIFSelectRequestReason: 'Select Request Reason',
    ECIFSelectDealStructure: 'Select Deal Structure',
    ECIFEnterACORequestJustification: 'Enter ACO Request Justification',
    ECIFRoutingInfo: 'Routing Information',
    ECIFExternalPartner: 'ECIF External Partner',
    ECIFTitle: 'ECIF',
    ECIFBilled: 'Billed',
    MASSTitle: 'MaaS',
    DWRRequestDialogTitle:
      'Are you sure you want to save the request and create a DWR Tracker request?',
    DWRRequestDialogSubText: `This will save the request and create the action in the DWR Tracker application, as the 'Recommend for DWR' option is enabled. Please note that this action cannot be undone immediately.`,
    DWRInfo: 'This is a DWR tracker request created via DWR Tracker portal.',
    ECIFWorkscopeItem: 'Workscope Item',
    ECIFRefreshTooltip: 'Refresh to fetch updated Workscope Items for the items newly created.',
    ECIFRefresh: 'Refresh',
    ECIFCreateNewWorkItem: 'Create a New Workscope Item',
    OpenInNewWindow: 'OpenInNewWindow',

    //SGS
    MSSalesId: 'MS Sales ID',
    MSSalesIdPlaceholder: 'Enter MS Sales ID',
    AccountOwner: 'Account Owner',
    AccountOwnerPlaceholder: 'Search Account Owner',
    SubRegion: 'Sub Region',
    SubRegionPlaceholder: 'Enter Sub Region',
    PrimaryGrowthShapingContact: 'Primary Growth Shaping Contact',
    PrimaryGrowthShapingContactPlaceholder: 'Search Primary Growth Shaping Contact',
    GrowthLead: 'Growth Lead',
    GrowthLeadPlaceholder: 'Search Growth Lead',
    AEContact: 'AE Contact',
    AEContactPlaceholder: 'Search AE Contact',
    ATSContact: 'ATS Contact',
    ATSContactPlaceholder: 'Search ATS Contact',
    STUContact: 'STU Contact',
    STUContactPlaceholder: 'Search STU Contact',
    CSAMContact: 'CSAM Contact',
    CSAMContactPlaceholder: 'Search CSAM Contact',
    CSAContact: 'CSA Contact',
    CSAContactPlaceholder: 'Search CSA Contact',
    WWCSUEngaged: 'Tech Enablement CSU',
    WWCSUEngagedPlaceholder: 'Search Tech Enablement CSU',
    AccountTeamBriefingStatus: 'Account Team Briefing Status',
    AccountTeamBriefingStatusPlaceholder: 'Select Account Team Briefing Status',
    AccountTeamBriefingDate: 'Account Team Briefing Date',
    CustomerBriefingStatus: 'Customer Briefing Status',
    CustomerBriefingDate: 'Customer Briefing Date',
    GrowthShapingSteps: 'Growth Shaping Steps',
    GrowthShapingState: 'Growth Shaping State',
    PrimaryReasonForBlockedOrAtRisk: 'Primary Reason for Blocked or At-Risk',
    CustomerStatusAndNextSteps: 'Customer Status and Next Steps',
    CustomerStatusAndNextStepsPlaceholder: 'Enter Customer Status and Next Steps',
    TechEnablementStatusComments: 'Tech Enablement Status & Comments',
    TechEnablementStatusCommentsPlaceholder: 'Enter Tech Enablement Status & Comments',
    TechEnablementSTU: 'GBB Contact',
    TechEnablementSTUPlaceholder: 'Search GBB Contact',
    RisksOrBlockedDetails: 'Risks or Blocked Details',
    RisksOrBlockedDetailsPlaceholder: 'Enter Risks or Blocked Details',
    DateApproved: 'Date Approved',
    LastUpdatedDate: 'Last Updated Date',
    FieldAccountability: 'Field Accountability Unit',
    SelectFieldAccountability: 'Select Field Accountability Unit',
    Subsidiary: 'Field Subsidiary',
    MACCPersona: 'MACC Persona',
    MACCPersonaPlaceholder: 'Search MACC Persona',
    SubGrowthLead: 'BVA Contact',
    SubGrowthLeadPlaceholder: 'Search BVA Contact',
    RegionalGrowthLead: 'Regional Growth Lead',
    RegionalGrowthLeadPlaceholder: 'Search Regional Growth Lead',
    SubmitterContact: 'Submitter Contact',
    SubmitterContactPlaceholder: 'Search Submitter Contact',
    CMFSupportRequired: 'CMF Support Required',
    CMFSupportRequiredPlaceholder: 'Select CMF Support Required',
    CurrentDestinationDC: 'Current DC Region',
    CurrentDestinationDCPlaceholder: 'Select Current DC Region',
    CurrentTotalPipeline: 'Current Total Pipeline (USD)',
    ECIFApproved: 'ECIF Approved (USD)',
    ECIFRequested: 'ECIF Requested (USD)',
    ACOApproved: 'ACO Approved (USD)',
    ActiveAgreement: 'Active Agreement',
    ActiveAgreementPlaceholder: 'Enter Active Agreement',
    ExecSponsor: 'Exec Sponsor',
    ExecSponsorPlaceholder: 'Search Exec Sponsor',
    //SGS: 'SGS',
    SegmentPlaceholder: 'Enter Segment',
    SubsidiaryPlaceholder: 'Select Field Subsidiary',
    CurrentDCRegion: 'Select Current DC Region',
    DCRegionFromSelect: 'Select DC Region From',
    DCRegionToSelect: 'Select DC Region To',
    SGSTenant: '5461fe99-0356-49d5-be95-63cbbe8a187a',
    RoBTenant: '6f76ed48-1b55-4951-920a-90b995e4d5cc',
    CoSellTenant: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d',
    PMYTTenant: 'af315f0d-0028-49ed-b197-1aaf897d56c8',
    MarkedRelatedItemSuccessfully: 'Action(s) marked successfully!',
    AzureISVTenant: 'af315f0d-0028-49ed-b197-1bbf897d56c8',
    ExpectedECIFRequestedAmount: 'Expected ECIF requested amount (USD)',
    EnterECIFRequestedAmount: ' Enter ECIF Requested Amount',
    IsAwareOfApprovedECIFSuppliers:
      'I am aware that I can only use approved ECIF suppliers that have been through ECIF RFP Process',
    WillNotStartWithoutApprovedPO:
      'I confirm that I will not start work on the ECIF project until the related PO has been approved',
    ECIFFundingForQualifiedActivities:
      'My Request for ECIF funding relates to activities that qualify for ECIF',
    SupplierSameAsEndCustomer: 'Is the Supplier the same as the end customer?',
    MAAPRelated: 'Is this related to Marketplace & Azure Acceleration Program (MAAP)?',
    SelectCurrency: 'Select Currency',
    SelectApprovedECIFSuppliers: 'Select Approved ECIF Suppliers',
    AMMExpectedBidCloseDate: 'Expected Bid Close Date',
    SelectBidCloseDate: 'Select Bid Close Date',
    EnterBidCloseDate: 'Enter Bid Close Date',
    AMMTriageOwner: 'AMM Triage Owner',
    TriageOwner: 'Triage Owner',
    NumberOfGPURequestedValidationError: `Can't be smaller than 'Number of VMs requested'. Value should be 8 times the 'Number of VMs requested' field value.`,
    NumberOfGPURequestedMandatory: 'Number Of GPUs Requested is mandatory',
    Approvals: 'Approvals',
    ActionDefaultSuccessSubtitle: 'selected actions have been deleted',
    ActionDefaultSuccess: '**Request Submitted** You can view the Request details here.',
    ActionPartnerInvestmentSuccess: '**Thank You!** Your Request has been successfully submitted.',
    ActionDefaultFailure:
      '**Oops! Something went wrong** We couldn’t complete your Request due to an unexpected error. Please try again in a moment.',
    ActionRedButtonSuccess:
      "**Thank you for your interest in Red Button!** We've received your Request, and our screening team will begin reviewing it shortly.",
    ActionSubTitleRedButton:
      '• If approved, a Red Button CSAM will reach out to the customer, with you included in the correspondence.\n• If rejected, you will be notified and provided with further options for escalation.',
    ActionAOAISuccess:
      '**Thanks! Your Request has been successfully submitted.** Please note that most ND series requests require processing through AI DWR, with the following prerequisites:',
    ActionSubTitleAOAI:
      '• 1-year RI minimum\n• GBB validation\n• Deal desk validation\nThe AI triage team will reach out to inform you about your specific case. You can manage this action here.',
    scopeDashboard: 'dashboard',
    scopeHubOverview: 'hubOverview',
    EEAApproverHyperLink:
      'https://microsoft.sharepoint.com/:b:/t/PartnerInvestmentFramework/PartnerInvestmentcouncil/EVCAJhEEemhLoM32uE_yiQ4B3x5ww24Mn2tL7GmbEQCxVQ?e=aiQTvn',
    GPSCustomDealSinglePartner: 'Custom deal single partner',
    GPSNewRequest: 'New Request',
    BlockerType: 'Blocker Type',
    SelectBlockerType: 'Select Blocker Type',
    CustomerPriority: 'Customer Priority',
    SelectCustomerPriority: 'Select Customer Priority',
    HasUnifiedSupport: 'Has Unified Support',
    SchipolFlag: 'Schiphol Flag',
    Tier: 'Tier',
    SubTier: 'Sub-Tier',
    SelectSubTier: 'Select Sub-Tier',
    AccountCohort: 'Account Cohort',
    DCRegionFrom: 'DC Region From',
    DCRegionTo: 'DC Region To',
    Tier2: 'Tier 2',
    SubTier2: 'Tier 2 (FY26)',
    P3: 'P3',
    CustomerCommitmentStatus: 'Customer Status',
    CustomerCommitmentHelperText:
      'Add at least one region with commitment status, ETA, and blocker details using “Add Region.” You can add up to ${COUNT} regions.',
    GrowthShapingETA: 'Growth Shaping ETA',
    RisksOrBlockedOtherDetails: 'Risks / Blocker / Other Details',
    RisksOrBlockedOtherDetailsPlaceHolder: 'Enter Risks / Blocker / Other Details',
    OnTrackErrorDisplayTitle: 'No UAT Action for "On Track" Milestone',
    OnTrackErrorDisplayDescription: `Non-AI Capacity request creation is prohibited for "On Track" category. It is allowed for "Blocked" and "At Risk" milestones only. Use the milestone belonging to "Blocked" or "At Risk" category.`,
    Authorisation: 'Authorisation',
    //AMM R9.0 : Start
    AVSPilotRequirementsCommunicated:
      'Have the AVS Pilot requirements been communicated to the customer before submitting this form?',
    IsCustomerConsumingAzure: 'Is the customer currently consuming Azure?',
    WhatRegionIsRequestedForAVSPilot: 'What region is requested for this AVS Pilot?',
    AVSSKURequestedForPilot: 'What AVS VCF BYOL SKU is requested for this pilot?',
    SubscriptionIDForAVSPilot:
      'Please provide the Subscription ID the customer will use for this AVS Pilot (example: 9f1846b7-6fb4-4934-9f05-8e385233a55f)',
    CustomerHasBroadcomTryAndBuyVCFLicense:
      'Does the customer have the Broadcom Try & Buy VCF license (90-day) required for AVS nodes to be activated for the pilot?',
    IsPartnerInvolvedInPilot: 'Is a partner involved in the pilot?',
    PartnerFullPublicName:
      'Please provide the partner’s full company name (no acronyms, e.g., PricewaterhouseCoopers, not PWC)',
    IsFieldCSAEngagedToSupportThisAVSPilot: 'Is a Field CSA engaged to support this AVS Pilot?',
    IsAVSGBBEngagedToSupportThisAVSPilot: 'Is an AVS GBB engaged to support this AVS Pilot?',
    SelectWhatRegionIsRequestForAVSPilot: 'Select region requested for AVS Pilot',
    SelectAVSSKURequestedForPilot: 'Select AVS VCF BYOL SKU requested for the pilot',
    //AMM R9.0 : End
    SharePointToggleLabel: 'Submit the AVS Pilot to ',
    SharePointToggleDescription:
      'Turning this toggle ON will submit the nomination to the SharePoint list when the request is saved',
    CompeteOppIdHelperText:
      'Ensure the MSX Opportunity includes the Copilot and Agents at Work Solution Play or the AI in the flow of human ambition Conversation, under the AI Business Solutions Solution Area.',
    MilestoneIdHelperText: 'Add Milestone ID for accurate impact assessment.',
    PrimaryReasonForBlockedOrAtRiskPlaceHolder: 'Select Primary Reason for Blocked or At-Risk',
    SubmitNominationMDA: 'Submit the nomination to MSX Deal Assist (MDA)',
    MDAOfferInfo:
      'Toggle this option to send ECIF Nomination to MSX Deal Assistance, i.e. nomination will be submitted to MSX Deal Assistance upon saving the request',
    MDAACOOfferInfo:
      'Toggle this option to send ACO Nomination to MSX Deal Assistance, i.e. nomination will be submitted to MSX Deal Assistance upon saving the request',
    MDAACOWITHINECIFOfferInfo:
      'Toggle this option to send ACO Nomination to MSX Deal Assistance, i.e. nomination will be submitted to MSX Deal Assistance upon saving the request',
    MDAAAOfferInfo:
      'Toggle this option to send AA Nomination to MSX Deal Assistance, i.e. nomination will be submitted to MSX Deal Assistance upon saving the request',
    MDABenchMarkTCOOfferInfo:
      'Toggle this option to send Benchmark TCO Nomination to MSX Deal Assistance, i.e. nomination will be submitted to MSX Deal Assistance upon saving the request',
    MDASuccessDescriptionForEcif: 'Nomination successfully submitted to MDA For ECIF',
    MDASuccessDescriptionForAco: 'Nomination successfully submitted to MDA For ACO',
    MDASuccessDescriptionForAA: 'Nomination successfully submitted to MDA For Azure Access',
    MDASuccessDescriptionForBenchMarkTCO:
      'Nomination successfully submitted to MDA For Benchmark TCO',
    BillingAccountID: 'Billing Account ID (Associated with Account owner ID)',
    BillingAccountIDPlaceholder: 'Enter Billing Account ID (Associated with Account owner ID)',
    //AMM R10.0 : Start
    IsException: 'IsException',
    IsExceptionSelect: 'Select Is Exception',
    ExceptionType: 'Exception Type',
    SelectExceptionType: 'Select Exception Type',
    ExceptionDecision: 'Exception Decision',
    SelectExceptionDecision: 'Select Exception Decision',
    SellerAttestation: 'Seller Attestation',
    SelectSellerAttestation: 'Select Seller Attestation',
    ExceptionReason: 'Exception Reason',
    EnterExceptionReason: 'Enter Exception Reason',
    //AMM R10.0 : End
    GSI_Emails: 'Enter Email(s) to CC',
    //ACO Release
    DoesCustomerHaveExistingMACC:
      'Does the customer have an existing MACC or is willing to sign a new MACC?',
    AMMRequestAmountLocalCurrency: 'Request Amount (in Local Currency)',
    AMMRequestAmounUSD: 'Request Amount (in USD)',
    ACOReleaseStructure: 'ACO Release Structure',
    UpfrontDetails: 'Upfront Details',
    TimeBaseDetails: 'Time Based (TACO) Details',
    ConsumptionBasedDetails: 'Consumption Based (CACO) Details',
    ACOReleaseMilestone: 'Milestone',
    ACOconsumptionTargetUSD: 'Consumption Target (USD)',
    ACOReleaseECAD: 'Estimated Credit Application Date',
    ACOReleaseECED: 'Estimated Credit Expiration Date',
    CreditValueUSD: 'Credit Value (USD)',
    creditValidityMonths: 'Credit Validity (Months)',
    CreditApplicationDate: 'Credit Application Date',
    CreditExpirationDate: 'Credit Expiration Date',
    ConsumptionTargetUSD: 'Consumption Target (USD)',
    TargetStartDate: 'Target Start Date',
    TargetEndDate: 'Target End Date',
    CSPLabel: 'This is a CSP request. Nomination progress is not currently available via this tool',
    //AMM FY27
    BigArea: 'Big Area',
    PriorityDetails: 'Priority Details',
    PrimaryConversations: 'What is the primary conversations supporting this project?',
    SecondaryWorkload: 'Secondary Workload',
    AcknowledgeThat: 'I acknowledge that',
    EnterPrimaryConversationDetails: 'Enter primary conversation details',
    SystemUpdatedInfo: 'This value updated from system',
    ProjectStartDateAONError:
      'Something went wrong while fetching the details, please try again later.',
    ProjectStartDateAONFailure:
      'Offer is not eligible for selected date, try selecting a different date and retry.',
    ProjectStartDateIsNotEligible: 'Offer is not eligible for selected date.',
    RefreshTooltip: 'Refresh system-generated values based on the latest data.',
    RichTextEditorMaxLengthExceeded: 2000,
    RichTextEditorSecondMaxLengthExceeded: 1900,
    FactoryReviewSLA: 'Factory review SLA',
    MajorityAcrWorkloadType: 'Majority of ACR is from which workload?',
    IncludeFactoryOption:
      'As the majority of ACR is from Factory supported workload(s), you have the option to include Factory. Would you like to?',
    JustificationIfROILessThan5X: 'Seller Justification ( If ROI is less than 5X )',
    HasCustomerCompletedAzureSolutionAssessment:
      'Has your customer previously completed an Azure Solution Assessment?',
    HasPartnerActivatedFactoryBenefits: 'Has the Partner activated Factory benefits?',
    IsFactoryTeamSupportiveOfSplit:
      'Is the Factory team supportive of the existing ECIF + Factory split shared in the nomination by seller?',
    SellerAcceptedFactoryRecommendedSplit: 'Seller accepted factory recommended split ?',
    ReasonForSellerNotAcceptingSplit: 'Reason for Seller Not accepting the split',
    JustificationIfECIFFactorySplitDifferent:
      'Seller Justification ( If ECIF + Factory Split is different from Recommended )',
    ReasonForFactoryRecommendedSplit: 'Reason for factory recommended Split',
    TriageNotesForSellerFeedback: 'Triage Notes for Seller Feedback',
    ProvideReasoningForDifferentOffer:
      'Provide the reason for selecting different offer than recommended',
    PartnerSpecializationExceptionConfirmation: 'Partner Specialization Exception Confirmation',
    SpecializationExceptionJustification: 'Specialization Exception Justification (Optional)',
  };
  public static StaticDataConfig = {
    Filter: {
      Title: UATConstants.PlaceholdersAndTitles.SelectFilters,
      submitButton: UATConstants.PlaceholdersAndTitles.Apply,
      LeftSectionTitle: 'Filter',
      RightSectionTitle: 'Filter Values',
    },
    BulkEdit: {
      Title: UATConstants.PlaceholdersAndTitles.BulkEdit,
      submitButton: UATConstants.PlaceholdersAndTitles.Save,
      LeftSectionTitle: 'Fields',
      RightSectionTitle: 'Value',
    },
  };

  public static SubSegments = {
    SMCCommercialCorporate: 'SM&C Commercial - Corporate',
    SMCEducationCorporate: 'SM&C Education - Corporate',
    SMCGovernmentCorporate: 'SM&C Government - Corporate',
    SMECCorporateCommercial: 'SME&C - Corporate Commercial',
    SMECCorporateEducation: 'SME&C - Corporate Education',
    SMECCorporateGovernment: 'SME&C - Corporate Government',
  };

  public static MCAPSROBPlaceholdersAndTitles = {
    AccountableExec: 'Accountable Exec',
    RemoveAccountableExec: 'Remove Accountable Exec',
    Region: 'Region',
    SubRegion: 'Sub Region',
    ProblemDetails: 'Description',
    BusinessImpact: 'Business Impact',
    Priority: 'Priority',
    DueDate: 'Estimated Completion Date',
    ProblemDetailsPlaceholder:
      'Please Enter Action Problem Statement and articulate what needs to be addressed through the action (description, objective and who is affected)',
    BusinessImpactPlaceholder: 'Please Enter Business Impact in details',
    NextStepsPlaceholder: 'Please Enter Next steps/Asks',
    StatusUpdate: 'Please Enter Status Update',
    IncrementalRevenue: 'Incremental Revenue',
    IncrementalRevenuePlaceholder: 'Please Enter Incremental Revenue',
    NumberofCustomersImpacted: 'Number of Customers Impacted',
    NumberofCustomersImpactedPlaceholder: 'Please Enter Number of Customers Impacted',
    CostSavings: 'Cost Savings',
    CostSavingsPlaceholder: 'Please Enter Cost Savings',
    Other: 'Other',
    OtherPlaceholder: 'Please Enter Other',
  };
  public static ECIFAsiaRequiredTitles = {
    Asia: 'Asia',
    ECIF: 'ECIF',
    DealCoaching: 'Deal Coaching',
    Billed: 'Billed',
    Consumption: 'Consumption',
    BilledConsumption: 'Billed;Consumption',
    ECIFACO: 'ECIF & ACO',
    supplierExternal: 'External',
    supplierInternal: 'Internal',
    ACO: 'ACO',
    Generic: 'Generic',
    Lost: 'Lost',
    Won: 'Won',
    Other: 'Other',
  };
  public static GPSInvestmentTitles = {
    CustomDealMultipartner: 'Custom Deal multi-partner',
    CustomDealMultipartnerFramework: 'Custom deal multi-partner (Framework)',
    CustomDealSinglePartner: 'Custom deal single partner',
    Campaign: 'Campaign',
    Commercial: 'Commercial',
    DPS: 'DPS',
    Completed: 'Completed',
    InProgress: 'In Progress',
    PartnerInvestmentRequest: 'Partner Investment Request',
    New: 'New',
    Prepare: 'Prepare',
    NewRequest: 'New Request',
    ChangeRequest: 'Change Request',
    Exception: 'Exception',
    Consultation: 'Consultation',
    ReviewChangeRequest: 'Review Change Request',
    ReviewForApprovalToOffer: 'Review for Approval to Offer',
    Activities: 'Activities',
  };

  public static AOAIRequiredTitles = {
    Status: 'Blocked',
    MilestoneStatusReason: 'Capacity/Service Availability',
    AzureCapacityType: 'Azure OpenAI Service',
    Subsegment: 'Subsegment',
    EnterSubsegment: 'Enter Subsegment',
    InterestedAssistedMigration: 'Interested in Assisted Migration',
    ECIFStatusFAM: 'ECIF status for FAM',
    MigrationServicestatus: 'Migration Service status',
    AssistedMigrationReason: 'Assisted Migration Reason',
    MigrationType: 'Migration Type',
    InterestedInRenewing: 'Interested in renewing',
    RenewingBREPSA: 'Renewing BREP/SA',
    NoStateReason: 'If no, state reason',
    NonRenewingReason: 'Non-Renewing Reason',
    EOObjective: 'E&O Objective',
    ListeningCircleSessionName: 'Listening Circle Session Name',
    ListeningCircleLead: 'Listening Circle Change Lead',
    RemoveListeningCircleLead: 'remove Listening Circle Change Lead',
    RemoveListeningCircleFacilitator: 'remove Listening Circle Facilitator',
    ListeningCircleFacilitator: 'Listening Circle Facilitator',
    LTEscalation: 'LT Escalation',
    AddNewCriteria: 'Add new criteria',
    DownloadActions: 'Download Actions',
    EmailActions: 'Email Actions',
    AskID: 'AskID',
    QuarterSerialNumber: 'Quarter Serial Number',
    AssignedTo: 'Assigned To',
    ProductFeedbackEngineering:
      'In Addition, please submit this Action as product feedback to Engineering.',
    AssignToCorp: 'Assign to Corp',
    AnalyticsViews: 'Analytics Views',
    Analytics: 'Analytics',
    ShareYourComments: 'Share your comments',
    ClosePopupModal: 'Close popup modal',
    RemoveUser: 'Remove user',
    SendEmailUsersList: 'Send email users list',
    MakeDefaultView: 'Make as default view',
    Back: 'Back',
    SaveAs: 'Save As',
    Update: 'Update',
    NoResultsFound: 'no results found',
    WaveReport: 'Wave Report',
    Mail: 'Mail',
    Chat: 'Chat',
    ChevronRightSmall: 'ChevronRightSmall',
    GlobalNavButton: 'GlobalNavButton',
    NoSubFolders: 'No sub-folders.',
    FabricFolder: 'FabricFolder',
    StartChat: 'Start chat',
    ReportsTo: 'Reports to',
    ChevronDownSmall: 'ChevronDownSmall',
    Phone: 'Phone',
    Location: 'Location',
    CustomerCommunicationTeamsEmail:
      'I confirm I have the pre-requisites, and I have secured intent from my customer for this nomination. I understand that the customer attendees will be receiving communication via Teams and email. [Note:These notifications are sent manually]',
    More: 'More',
    ImportActions: 'Import Actions',
    DragColumnsReorder: 'Selected Columns (Drag the columns to reorder)',
    SelectedColumns: 'Selected Columns',
    ToggleSelection: 'Toggle selection',
    ToggleSelectionAllItems: 'Toggle selection for all items',
    RowCheckbox: 'Row checkbox',
    leftRightArrowkeysNavigate: 'Use left and right arrow keys to navigate between commands',
    CommentsADOReadOnly: 'Comments posted at ADO are read-only',
    TakeAMinute: 'This might take a minute..',
    Post: 'Post',
    CommentShouldntEmpty: 'Comment shouldn&apos;t be empty',
    ExcludeItemsCompletedBefore: 'Exclude work items that were completed before',
    IncreaseValueByOne: 'Increase value by 1',
    DecreaseValueByOne: 'Decrease value by 1',
    ExcludeItemsCompletedRollingPreiod:
      'Exclude work items that was completed before rolling preiod',
    ExcludeItemsCompletedStartDate: 'Exclude work items that were completed before the start date',
    Present: 'Present',
    Granularity: 'Granularity',
    ShowDataDailyWeeklyMonthlyPeriod:
      'Set individual rows to show data for daily,weekly,or monthly period',
    DefineTime: 'Define how far back in time the view should go',
    NavBasicExample: 'Nav basic example',
    OpportunitylostNotProvided:
      'On what date will this opportunity be lost if ask is not provided?',
    SensitiveCustomerData:
      'How is this ask an improvement for the customer? What does success look like? Do not list sensitive customer data',
    NotlistSensitiveCustomerData:
      'What is the customer doing without this ask? Do not list sensitive customer data',
    PullFedSalesOpportunityRevenue:
      'Pull from FedSales Opportunity and provide revenue on annual basis',
    Following: 'Following',
    UnFollow: 'UnFollow',
    Follow: 'Follow',
    Copy: 'Copy',
    CreateCAPERequest: 'Create CAPE Request',
    ListeningCircleArea: 'Listening Circle Area',
    CopilotInsights: 'Copilot Insights',
    CopilotBeta: 'Copilot (Beta)',
    Beta: 'Beta',
    Copilot: 'Copilot',
    BasicDetails: 'Basic Details',
    AccountInformation: 'Account Information',
    OpportunityInformation: 'Opportunity Information',
    MilestoneInformation: 'Milestone Information',
    AOAIRequestedCapacity: 'AOAI Requested Capacity',
    AOAIApprovedCapacityNotChange: 'AOAI Approved Capacity (DO NOT CHANGE - CORP USE)',
    CAEEngagementDetails: 'CAE Engagement Details',
    ServiceDetails: 'Service Details',
    Contact: 'Contact',
    MilestoneDetails: 'Milestone Details',
    Support: 'Support',
    Email: 'Email',
    DualUse: 'Dual Use',
    FederalDetails: 'Federal Details',
    CustomerHub: 'Customer Hub',
    Remove: 'Remove',
    Others: 'Others',
    VSUQBCInfo: 'VSU/QBC Info',
    AskDetails: 'Ask Details',
    ImpactSumary: 'Impact Summary',
    NotAuthorizedViewPage: 'You are not authorized to view this page',
    Ok: 'OK',
    Recommended: 'Recommended',
    ParentItems: 'Parent Items',
    BackToWindow: 'Back to Window',
    BackToWindowIcon: 'BackToWindow',
    New: 'New',
    NewAnalyticsView: 'New analytics view',
    ManageAnalyticsView: 'Manage analytics view',
    CanceledRatingFeeback: 'CanceledRatingFeeback',
    FeedbackID: 'Feedback ID',
    ID: 'ID',
    FilterBy: 'Filter By',
    onUnFavorite: 'onUnFavorite',
    onFavorite: 'onFavorite',
    highlight: 'highlight',
    SavedSuccessfully: 'View saved successfully.',
    UpdatedSuccessfully: 'View updated successfully.',
    SummaryFor: 'Summary for',
    ActionSubmittedSuccessfully: 'Action submitted successfully.',
    NominationActionSubmittedSuccessfully: 'Nomination Action submitted successfully.',
    HearBackThreeDays: 'You and your customers will hear back from MW work team in 3 days.',
    ManageAction: 'You can manage this action',
    Create: 'Create',
    ThumbnailView: 'ThumbnailView',
    Relationship: 'Relationship',
    SyncToPC: 'SyncToPC',
    TabCenter: 'TabCenter',
    CloudUpload: 'CloudUpload',
    Globe: 'Globe',
    GroupObject: 'GroupObject',
    Tiles: 'Tiles',
    SubmitActionProductFeedbackEngineering:
      'In addition, please submit this action as product feedback to engineering',
    MicrosoftProjectCompressedAllowed:
      'Files <=50MB of type Office,Text,Email,PDF,Image,Video,Audio,Microsoft Project,Compressed are allowed',
    AssistanceCorp:
      'Assigned Only escalate to Corp. if the item has been discussed in the local area Scrum and was not resolved, requiring Corp. assistance Corp?',
    // New constant added
    Technology: 'Technology',
    SelectTechnology: 'Select Technology',
    InvalidOpportunityID: 'Invalid Opportunity ID',
    SalesTerritory: 'Sales Territory',
    EnterSalesTerritory: 'Enter Sales Territory',
    AccountPartnerNameMandatory: 'Account/Partner Name  is mandatory',
    EnterAccountName: 'Enter Account Name',
    PartnerID: 'Partner ID',
    EnterPartnerID: 'Enter Partner ID',
    SupplierName: 'Supplier Name',
    EnterSupplierName: 'Enter Supplier Name',
    SupplierType: 'Supplier Type',
    FilterRequestType: 'Filter by Request type',
    filterByRequestType: 'filterByRequestType',
    CloudOnPrem: 'Cloud vs On-prem',
    SelectCSSCreationDate: 'Select CSS Case Creation Date',
    Additionalcontacts: 'Additional contacts',
    RemoveAdditionalContact: 'remove Additional Contact',
    CommercialCE: 'Commercial executive CE',
    RemoveCustomerExecutive: 'remove Customer Executive',
    RemoveApprover: 'remove Approver',
    RemoveRejecter: 'remove Rejecter',
    EmailSupportSpecialistLabel: 'Email of the Support Specialist (SSSP)',
    AmountblockedRevenueLabel: 'Amount of blocked revenue for Microsoft product sale',
    AmountBlockedOpportunityLabel: 'Amount of blocked revenue for NSC Unified opportunity',
    ClearChatNNotAvailable: 'Clear chat now available!',
    ClearChatHistory: 'Try it! You can now clear chat history from the Copilot window',
    RemoveAlias: 'remove alias',
    RemoveAMPDM: 'remove AM / PDM',
    RBCaseNumber: 'RB Case Number',
    CurrentFYRevenue: 'Current FY Revenue',
    EstBilledRevenue: 'Est. Billed Revenue',
    BilledRevenue: 'Billed Revenue',
    EnterBilledRevenue: 'Enter Est. Billed Revenue',
    DragDropEilesHere: 'Drag and drop files here',
    Or: 'or',
    BrowseFile: 'Browse for a file',
    FilterColumnName: 'Filter by column name',
    ClickhereWrite: 'Click here to write',
    FilterNameEmail: 'Filter by name or email',
    SelectL1Process: 'Select L1 Business Process',
    SelectL2Process: 'Select L2 Business Process',
    SelectL3Process: 'Select L3 Business Process',
    SelectReferredTo: 'Select Referred to',
    CollabAppContest: 'Collab App contest',
    CustomerWantEngage: 'When does customer want to engage',
    IsfeatureRequest: '"Is this feature parity request',
    EstimatedRows: 'Estimated rows:',
    AnalyticsverifiedPowerBI:
      'Your analytics view has been verified and is ready to use with Power BI.',
    RateYourExperience: 'Rate your experience ',
    ThankGivingfeedback: 'Thank you for giving rating feedback!',
    AddDescription: 'Click to add Description',
    AddExitCriteria: 'Click to add Exit Criteria',
    AddNextAction: 'Click to add Next Action',
    Model: 'Model',
    Assigneen: 'Assigneen',
    Like: 'Like',
    Dislike: 'Dislike',
    NoFiltersSelected: 'No filters selected',
    ClearAllFilters: 'Clear all filters',
    AddNew: 'New View',
    HelpNeeded: 'Azure Service/Product Availability',
  };

  public static MaaSDHAPDefaultArea = {
    key: 'United States',
    text: 'United States',
    id: 1,
  };
  public static MaaSRequiredTitles = {
    orgCharting: 'Organization Charting',
    AccountProfiling: 'Account Profiling',
    DHAP: 'Definitive Healthcare Account Profiling',
    Dispatched: 'Dispatched',
    Partner: 'Partner Programs',
    TechTarget: 'TechTarget for New Prospects',
    ZoomInfo: 'ZoomInfo for New Prospects',
    BusinessDecision: 'Business Decision Maker Profiling',
    Marketer: 'Marketer',
    Seller: 'Seller',
    updateExistingValue: 'Update Existing Account',
    US: 'United States',
    BasicTitlestaticText: 'Request for ',
    emptyReqType: 'Select Request Type',
    LeadUpload: 'Lead Upload',
  };

  public static PartnerSkillingTitles = {
    SubCampaign: 'Sub Campaign',
    IfOffers: 'If Others',
    PartnerType: 'Partner Type',
    PartnerOneId: 'Partner One ID',
    PartnerName: 'Partner Name',
    PartnerCountry: 'Partner Country',
    PartnerRegion: 'Partner Region',
    PartnerCountry1: 'Partner Country1',
    PartnerRegion1: 'Partner Region1',
    PDMName: 'PDM Name',
    DeliveryLanguage: 'Delivery Language',
    PartnerContactName: 'Partner Contact Name',
    PartnerEmail: 'Partner Email',
    SkillingPlanAttachment: 'Skilling Plan Attachment',
    Yes: 'Yes',
    No: 'No',
    DefaultDeliveryLanguage: 'English',
    PDMPlaceholder: 'Search PDM Name',
    SelectCampaign: 'Select Campaign',
    SelectDeliveryLanguage: 'Select Delivery Language',
    SelectSkillingPlanStatus: 'Select Skilling Plan Status',
    Model: 'Model',
    SelectModel: 'Select Model',
    AccountType: 'Account Type',
    SelectAccountType: 'Select Account Type',
    SkillingId: 'Skilling ID',
    SkillingIdPlaceholder: 'Enter Skilling ID',
    SkillingIdNumericError: 'Skilling ID must contain numeric values only',
    ALL: 'All',
    AnyOtherCountry: 'Any other country',
    Americas: 'Americas',
    Others: 'Others',
    rs_skillingplanstatus: 'rs_skillingplanstatus',
    rs_requesttype: 'rs_requesttype',
    rs_campaign: 'rs_campaign',
    PartnerSkilling: 'PartnerSkilling',
  };

  public static Views = {
    AssignedToMe: 'Assigned To Me',
    AssignedToOrg: 'Assigned To Organization',
    AllActions: 'All Actions',
    SystemViews: 'System Views',
    MyViews: 'My Views',
    AddNew: '+ Add New',
    SharedWithMe: 'Shared With Me',
    RequestedByMe: 'Requested by Me',
  };
  public static UATKeyTerms = {
    AssignedTeam: 'Assigned Team',
    landingPageFlag: '?landingPage=false',
    AreaPathCustom: 'areaPathCustom',
    AreaPath: 'areaPath',
    AssignToCorp: 'assignToCorp',
    DateSelectionFormat: 'dateSelectionFormat',
    Action: 'Action',
    tagName: 'tagName',
    tagMaxLength: 50,
    ellipses: '...',
    UNIFIED_ACTION_TRACKER: 'Unified Action Tracker',
    rbArea: 'rbArea',
    rbRegion: 'rbRegion',
    rbCountry: 'rbCountry',
    rbTechnology: 'rbTechnology',
    rbTier: 'rbTier',
    rbTicketReason: 'rbTicketReason',
    rbGeneralCategory: 'rbGeneralCategory',
    rbInfraLocation: 'rbInfraLocation',
    CreateCompeteRequests: 'Create Compete requests in Unified Action Tracker',
    ManageCompeteRequests: 'Compete requests in Unified Action Tracker',
    CreateActionForField: 'CreateActionForField',
    CompeteConfirmationDialogTitle: 'Is this a Copilot Compete escalation?',
    CompeteConfirmationDialogSubText: 'AI Business Solutions Copilot compete issues include:',
    CompeteConfirmationBullet1: 'Address roadmap (including competitor roadmap)',
    CompeteConfirmationBullet2: 'Security/compliance/risk blockers/concerns',
    CompeteConfirmationBullet3: 'Quality and performance issues',
    CompeteConfirmationBullet4: 'Product feature gaps',
    CompeteConfirmationBullet5: 'Engineering executive engagement',
    CompeteSalesPlay: 'Copilot and Agents at Work',
    CompeteConversations: 'AI in the flow of human ambition',
    CompeteConfirmationYesButton: 'Yes',
    CompeteConfirmationNoButton: 'No',
    MCAPSHelp: 'MCAPSHelp',
    mcapshelp: 'mcapshelp',
    GetHelp: 'GetHelp',
    gethelp: 'gethelp',
    NodefinedSource: 'No defined source',
    CopilotCompeteTag: 'Copilot Compete;',
    eciffundtype: 'eciffundtype',
    ecifprogramName: 'ecifprogramName',
    ecifEntityType: 'ecifEntityType',
    ecifDeliveryMethod: 'ecifDeliveryMethod',
    yesOrNo: 'yesOrNo',
    ecifTypeofActivity: 'ecifTypeofActivity',
    ecifProgramNameLocalFund: 'ecifProgramNameLocalFund',
    ecifRequestReason: 'ecifRequestReason',
    ecifDealStructure: 'ecifDealStructure',
    ecifWorkType: 'ecifWorkType',
    ecifSupplierType: 'ecifSupplierType',
    ECIF_Approved_Rejected: 'ECIF_Approved_Rejected',
    ecifFiscalyear: 'ecifFiscalyear',
    ecifCrossFiscalYear: 'ecifCrossFiscalYear',
    MaaSRequestType: 'MaaS_RequestType',
    MaaSRole: 'MaaS_Role',
    MaaSMarketerRole: 'MaaS_Marketer_Role',
    MaaSSellerRole: 'MaaS_Seller_Role',
    MaaSArea: 'MaaS_Area',
    MaaSIndustry: 'MaaS_Industry',
    MaaSSegment: 'MaaS_Segment',
    MaaSSubSegment: 'MaaS_SubSegment',
    MaaSEventType: 'MaaS_EventType',
    MaaSMarketingPlay: 'MaaS_MarketingPlay',
    MaaSLeadSourceType: 'MaaS_LeadSourceType',
    MaaSOU: 'MaaS_OU',
    MaaSMCEMStage: 'MaaS_MCEM_Stage',
    MaaSTechTargetexistingAccount: 'MaaS_TechTargetexistingAccount',
    MaaSExistingAccountValues: 'MaaS_ExistingAccountValues',
    MaccReasonforException: 'maccReasonforException',
    AOAIModel: 'aoaiModel',
    GPSRequestType: 'GPS_RequestType',
    GPSDiscussionType: 'GPS_DiscussionType',
    GPSBusiness: 'GPS_Business',
    GPSProduct: 'GPS_Product',
    GPSPartnerType: 'GPS_PartnerType',
    GPSInvestmentType: 'GPS_InvestmentType',
    GPSInvestmentComplexity: 'GPS_InvestmentComplexity',
    GPSPIRDecision: 'GPS_PIRDecision',
    GPSStage: 'GPS_Stage',
    GPSFundingSource: 'GPS_FundingSource',
    GPSFundingType: 'GPS_FundingType',
    GpS_Area: 'gpS_Area',
    GPSAskType: 'GPS_AskType',
    GPSCustomerSegment: 'GPS_CustomerSegment',
    Operating_Unit: 'operatingUnit',
    AMMScopeOfWork: 'AMM_ScopeOfWork',
    AmmOfferType: 'AMM_OfferType',
    AmmActionTaken: 'AMM_ActionTaken',
    AmmOffer: 'AMM_Offer',
    AmmOfferEnginestatus: 'AMM_OfferEnginestatus',
    AmmCancellationreason: 'AMM_Cancellationreason',
    AmmRejectionreason: 'AMM_Rejectionreason',
    AmmDisqualificationreason: 'AMM_DisqualificationReason',
    AmmAaofertype: 'AMM_AaoferType',
    AmmPendingactionfrom: 'AMM_PendingActionFrom',
    AmmNextbestaction: 'AMM_NextBestAction',
    AmmTypeoffundingneeded: 'AMM_TypeOfFundingNeeded',
    AmmMicrosoftdefenderforcloud: 'AMM_Microsoftdefenderforcloud',
    AmmPrimaryazurevirtualmachinevmseries: 'AMM_PrimaryAzureVirtualMachineVmSeries',
    AmmCommunicationpreference: 'AMM_CommunicationPreference',
    AmmDealstructure: 'AMM_DealStructure',
    AmmSupportRegion: 'AMM_SupportRegion',
    AmmProgram: 'AMM_Program',
    AmmSupportedScenario: 'AMM_SupportedScenario',
    AmmMigrationpartnerattached: 'AMM_MigrationPartnerAttached',
    AmmWillThisRequestRequireAdditionalAttendees: 'AMM_WillThisRequestRequireAdditionalAttendees',
    AmmRequestType: 'AMM_RequestType',
    AmmEsi: 'AMM_Esi',
    AmmFta: 'AMM_Fta',
    AMMFutureGrowthECIF: 'AMM_ECIFFutureGrowthOffer',
    AMMDoYouWantToRequestAcoInAdditionToEcif: 'AMM_DoYouWantToRequestAcoInAdditionToEcif',
    AMMFinancialYear: 'AMM_FinancialYear',
    AMMHelpType: 'AMM_HelpType',
    Ammprimaryazurevirtualmachinevmseries: 'Amm_primaryazurevirtualmachinevmseries',
    AmmPreOrPost: 'AMM_PreOrPost',
    AmmCustomerCommitment: 'AMM_CustomerCommitment',
    AMMRequestReason: 'AMM_RequestReason',
    AMMAzureFutureGrowthOffer: 'AMM_AzureFutureGrowthOffer',
    AMMECIFFutureGrowthOffer: 'AMM_ECIFFutureGrowthOffer',
    AMMPrimarySolutionPlaySupporting: 'AMM_PrimarySolutionPlaySupporting',
    AMMSupplierType: 'AMM_SupplierType',
    AMMIsSupplierSameEndCustomer: 'AMM_IsSupplierSameEndCustomer',
    AMMFutureGrowthOffer: 'AMM_FutureGrowthOffer',
    AMMServiceLocation: 'AMM_ServiceLocation',
    AMMAvsOfferRequestCorrespond: 'AMM_AvsOfferRequestCorrespond',
    AMMSapOffer: 'AMM_SapOffer',
    AMMIsThisRequestRelated: 'AMM_YesOrNo',
    AMMIsThisAVMCToAVSMigration: 'AMM_YesOrNo',
    AMMIsThereExistingACO: 'AMM_YesOrNo',
    AMMDoesThisIncludeHighRiskDeal: 'AMM_YesOrNo',
    AMMIsThereACompetitiveBid: 'AMM_YesOrNo',
    AMMOfferDeskRegion: 'AMM_OfferDeskRegion',
    AMMAVSOfferEnginestatus: 'AMM_AVSOfferEnginestatus',
    AMMOfferEngineType: 'AMM_OfferEngineType',
    AMMAVSOfferEngineStatus: 'AMM_AVSOfferEngineStatus',
    AMMCMFOfferEngineStatus: 'AMM_CMFOfferEngineStatus',
    AMMECIFOfferEngineStatus: 'AMM_ECIFOfferEngineStatus',
    AMMAVSOfferEngineType: 'AMM_AVSOfferEngineType',
    AMMCMFOfferEngineType: 'AMM_CMFOfferEngineType',
    AMMECIFOfferEngineType: 'AMM_ECIFOfferEngineType',
    MCAPSHelpCompete: 'MCAPSHelp-COMPETE',
    GetHelpCompete: 'GetHelp-COMPETE',
    UATPORTALFieldCompete: 'UATPORTAL-FIELD-COMPETE',
  };

  public static Telemetry = {
    CreateAction: 'Create Action',
    CreateCorpAction: 'Create Corp Action',
    CreateAHPMActionPageVisit: 'AH-PM create action page visit',
    CreateAHPMActionSaveClick: 'AH-PM create action Save click',
    CreateAHPMActionError: 'AH-PM create action error',
    CreateSupportAction: 'Create Support Action',
    CreateFieldAction: 'Create Field Action',
    CreateApexAction: 'Create APEX Action',
    CreateMWHubAction: 'Create MWHub Customer Action',
    CreateMWHubActionPageVisit: 'MWHub Customer create action page visit',
    CreateMWHubActionSaveClick: 'MWHub Customer create action Save click',
    CreateMWHubActionError: 'MWHub Customer create action error',
    GetAction: 'Get Action',
    ManageAction: 'Manage Action Load',
    ManageActionFailure: 'User specific manage action failure',
    UpdateActionFailure: 'User specific Update action failure',
    UATActionReopened: 'Action Reopened Event Log',
    DashboardAPIFailure: 'User Specific Dashboard Failure',
    WaveDetailsFailure: 'Get Wave details Failure',
    GetContactDetailsFailure: 'Get Contact details Failure',
    CompeteRequestInitiatedBy: 'Compete Request Initiated By',
    DirectLandingCreateOpened: 'Direct Landing Create Opened',
    DashboardAPI: 'Dashboard API',
    ActionCountAPIFailure: 'User Specific Dashboard Action Count Failure',
    Dashboard: 'Dashboard',
    ExportActionFailure: 'User Specific Dashboard Failure in export actions',
    CustomViewFailure: 'User Specific Dashboard Failure in fetching custom views',
    UpdateUserMetrics: 'User Metrics Update',
    FeedbackCommentsLoad: 'Feedback Comments Load',
    SharedViewLoadFailure: 'User Specific Shared Link fetch failure.',
    TemporaryViewCreationFailure: 'Failed to create Temporary view.',
    TokenRenewal: 'Token Renewal Triggered.',
    TokenRenewalAPI: 'Token Renewal for API Successful.',
    TokenRenewalCORE: 'Token Renewal for Core Successful.',
    TokenRenewalGraph: 'Token Renewal for Graph Successful.',
    TokenRenewalDevops: 'Token Renewal for Devops Successful.',
    CoreTokenRenewalFailed: 'Unable to refresh Core token.',
    APITokenRenewalFailed: 'Unable to refresh API token.',
    GraphTokenRenewalFailed: 'Unable to refresh Graph token.',
    NoAccess: 'Unable to edit',
    DevopsTokenRenewalFailed: 'Unable to refresh Devops token.',
    ResumeManagePage: 'Manage action page resumed after freezing',
    ResumeDashboard: 'Dashboard page resumed after freezing',
    FailedDeleteFollowsInfo: 'Failed to get delete User Action Following Info',
    FailedAddFollowsInfo: 'Failed to  add User Action Following Info',
    FailedGetFollowsInfo: 'Failed to get User Action Following info',
    FailedLoadAction: 'Failed to load the action',
    FailedFetchPersona: 'Failed to fetch persona',
    FailedFetchAlias: 'Failed to fetch alias',
    FailedFetchPhoto: 'Failed to fetch photo',
    FailedFetchMSXData: 'Failed to fetch MSX data',
    TechnicalFeedbackCheckboxClick: 'Dashboard TechnicalFeeback Checkbox Click',
    ManageActionTabClick: 'Manage Action Tab Click',
    FailedSaveColumnOptions: 'Failed to save ColumnWidth',
    SuccessSaveColumnOptions: 'Column Options are updated successfully!',
    ConflictInSavingAction: 'Conflict in saving action',
    CreateECIFActionClick: 'ECIF create action click',
    CreateCoSellActionClick: 'CoSell create action click',
    BulkUploadCosellActions: 'On Bulk upload of CoSell actions',
    SaveCoSellActionClick: 'On manage CoSell action save',
    CreateECIFActionSave: 'ECIF create action save',
    CreateECIFActionError: 'ECIF create action error',
    GetGroupMembersWithAreaEou: 'Failed to fetch Security group Members',
    BulkRelateActionSave: 'Bulk Relate action save',
    BulkEditActionSave: 'Bulk Edit action save',
    ManageActionUpdate: 'Manage Action',
    REWRITE_POSITIVE_FEEDBACK: 'Copilot Rewrite - Positive Feedback',
    REWRITE_NEGATIVE_FEEDBACK: 'Copilot Rewrite - Negative Feedback',
    REWRITE_DISCARD_SUGGESTION: 'Copilot Rewrite - Discard Suggestion',
    REWRITE_REPLACE_SUGGESTION: 'Copilot Rewrite - Replace Text',
    REWRITE_REGENERATE_SUGGESTION: 'Copilot Rewrite - Regenerate Text',
    REWRITE_SUGGESTION: 'Copilot Rewrite - Rewrite Text',
    ManageViewEditCancel: 'Manage Views - View Edit Cancel',
    ROBCreateAction: 'MCAPS ROB Create Action',
    ROBDownload: 'MCAPS ROB Download',
    ROBDownload_PPT_VSU: 'MCAPS ROB PPT Download - VSU',
    ROBDownload_PPT_APR_IAR: 'MCAPS ROB PPT Download - APR_IAR',
    ROBDownload_WORD_VSU: 'MCAPS ROB WORD Download - VSU',
    ROBDownload_WORD_APR_IAR: 'MCAPS ROB WORD Download - APR_IAR',
    ROBEmail: 'MCAPS ROB Email',
    ROBFilter: 'MCAPS ROB Filter Actions',
    ROBColumnOptions: 'MCAPS ROB Column Options',
    ROBColumnOptionsNewView: 'MCAPS ROB Column Options New View',
    ROBTagsAdded: 'MCAPS ROB Tags Added',
    ROBTagsRemoved: 'MCAPS ROB Tags Removed',
    ROBAttachmentComplete: 'MCAPS ROB Attachment Complete',
    ROBAttachmentRemoved: 'MCAPS ROB Attachment Removed',
    ROBHistoryTab: ' MCAPS ROB History Tab',
    ROBManageActionUpdate: 'MCAPS ROB Manage Action',
    ROBManageActionCopyLink: 'MCAPS ROB Manage Action Copy Link',
    ROBManageActionEmailSent: 'MCAPS ROB Manage Action SendMail',
    ROBADDFollow: 'MCAPS ROB Manage Action Add Follow',
    ROBRemoveFollow: 'MCAPS ROB Manage Action Remove Follow',
    ROBNewComment: 'MCAPS ROB Manage Action New Comment',
    ROBCommentEdit: 'MCAPS ROB Manage Action Edit Comment',
    ROBCommentDelete: 'MCAPS ROB Manage Action Delete Comment',
    ROBManageViewAddedRule: 'MCAPS ROB Manage View Rule Added',
    ROBCreatedNewFolderView: 'MCAPS ROB Created New Folder View in Manage Views',
    ROBCreatedNewView: 'MCAPS ROB Created New View in Manage Views',
    ROBCreatedCloneView: 'MCAPS ROB Created Clone View in Manage Views',
    ROBManageViewFavorite: 'MCAPS ROB Manage View Favorite',
    ROBManageViewUnFavorite: 'MCAPS ROB Manage View UnFavorite',
    ROBApplyView: 'MCAPS ROB Apply View',
    ROBSplitMode: 'MCAPS ROB Split Mode',
    ROBGridSearch: 'MCAPS ROB Grid Search',
    ROBGlobalSearch: 'MCAPS ROB Global Search',
    ROBViewSelection: 'MCAPS ROB View Selection',
    ROBDashboard: 'MCAPS ROB Dashboard',
    ROBDashboardMoreOptionClicked: 'MCAPS ROB Dashboard More Option Clicked',
    WorkscopePostAction: 'Create Workscope Link Action',
    UpdateWorkscopePostAction: 'Update Workscope Link Action',
    MultiMilestoneAction: 'Multi Milestone Action',
    ValidationAutomationBtnClicked: 'Validation Automation Button Clicked',
  };

  public static RatingContext = {
    CreateAction: 'CreateAction',
    CreateActionLabel: 'Create',
    CreateFieldAction: 'CreateField',
    CreateFieldActionLabel: 'Create Field Action',
    CreateMWHubNominationAction: 'CreateMWHub',
    CreateMWHubNominationActionLabel: 'Create MWHub Nomination Action',
    ManageAction: 'ManageAction',
    ManageActionLabel: 'Manage Action',
    SplitMode: 'SplitMode',
    SplitModeLabel: 'Split Mode',
    TechnicalFeedback: 'Technical Feedback',
    TechnicalFeedbackLabel: 'TechFeedback',
    CreateActionBulkUpload: 'BulkUpload',
    CreateActionBulkUploadLabel: 'Bulk Upload',
    GlobalSearch: 'GlobalSearch',
    GlobalSearchLabel: 'Global Search',
    ManageViews: 'ManageViews',
    ManageViewsLabel: 'Manage Views',
    AddViews: 'AddViews',
    AddViewsLabel: 'Add Views',
    EditViews: 'EditViews',
    EditViewsLabel: 'Edit Views',
    DashboardSearchActions: 'DashboardSearch',
    DashboardSearchActionsLabel: 'Dashboard Search',
    UATNotification: {
      UATNotificationLabel: 'Notification',
      CreateAction: 'UATNotification-CreateAction',
      UpdateAction: 'UATNotification-UpdateAction',
      WeeklyEmails: 'UATNotification-WeeklyEmails',
      ContributorChange: 'UATNotification-ContributorChange',
      Mentions: 'UATNotification-Mentions',
    },
  };

  public static DisplayMessages = {
    ErrorMessages: {
      NewVersionMessage:
        "You're using an older version of UA-Tracker. A newer version is available. The page will reload automatically.",
      GenericMessage: 'Something went wrong. Please contact Support Team.',
      ErrorMsgMaxLimit:
        'Sorry you can migrate max ' + UATConstants.QueriesMigrationMaxLimit + ' queries at a time',
      isRequiredErrorMessage: 'Field is required.',
      invalidInputErrorMessage: 'Invalid date format.',
      MaxFolderLevelMessage:
        'Maximum Folder Level Reached! Cannot add more than ' +
        (UATConstants.MaxSubFolderLevels + 1) +
        ' levels!',
      InvalidViewMessage:
        "The view you are trying to access is either invalid or has been removed. Defaulting to 'Assigned to Me' view.",
    },
    SucessMessages: {
      ViewCopySuccess: 'View copied successfully!',
      ViewSaveSuccess: 'The view has been successfully saved.',
      ViewSharedSuccess: 'The view has been added to your shared views.',
    },
  };

  // public static RequestedByMeViewID =
  //   process.env.REACT_APP_REQUESTEDBYMEVIEWID || 'cc58295b-219b-4ea3-b7e2-90371b5e22d3';
  // public static AssignedToMeAllStatesViewID =
  //   process.env.REACT_APP_ASSIGNEDTOMEALLSTATESVIEWID || '34a6b629-346d-4367-a0d8-1c772eca08e0';
  // public static FollowedByMeViewID =
  //   process.env.REACT_APP_FOLLOWEDBYMEVIEWID || '1088709f-558d-4aee-b058-54e961c4aec2';

  public static RequestedByMeViewID =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? 'cc58295b-219b-4ea3-b7e2-90371b5e22d3'
      : 'cc58295b-219b-4ea3-b7e2-90371b5e22d3';
  public static AssignedToMeAllStatesViewID =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? '34a6b629-346d-4367-a0d8-1c772eca08e0'
      : 'cf2fa1eb-ea67-42c8-b768-a8d1d6e0280c';
  public static FollowedByMeViewID =
    UATConstants.Project === 'Unified Action Tracker Test'
      ? '1088709f-558d-4aee-b058-54e961c4aec2'
      : 'a83377af-dc5a-431e-95e2-bf6043d8d5cb';

  public static MyAMMTasksViewID = '8105ee8f-ef65-408c-ba7b-698b255debec';
  public static AllAMMTasksViewID = 'c1ddcc1c-6bbb-4188-85d2-2de16423c654';

  public static ShowMultipleRequestor = 'YES'; // Set this value to "NO" for hiding multiple requestorfield
  public static DefaultSorting: Sorting = {
    column: 'System.ChangedDate',
    sortOrder: 'DESC',
  };

  public static DataSanitizeinitialMessages = {
    toShow: false,
    message: 'Intial Bar Message',
    messageType: MessageBarType.info,
    styleClass: '',
    timeout: 0,
  };

  public static DataSanitizeMessages = {
    toShow: true,
    message: `Modified text to ensure security`,
    messageType: MessageBarType.warning,
    styleClass: '',
    timeout: 20000,
  };
  public static DeleteSubscriptiontoWokrItemMessages = {
    toShow: true,
    message: `You are no longer following this Action.`,
    messageType: MessageBarType.success,
    styleClass: '',
    timeout: 20000,
  };

  public static AddSubscriptiontoWokrItemMessages = {
    toShow: true,
    message: `You are now following this Action.`,
    messageType: MessageBarType.success,
    styleClass: '',
    timeout: 20000,
  };

  public static ModernWorkMeetingTypes: string[] = [
    'Azure Sentinel 100',
    'Cisco 100',
    'Compete',
    'Deal Clinic',
    'Google 100',
    'MW Field Scrum',
    'Partner Sharing',
    'Power BI 100',
    'Windows 365 100',
    'D365 Field Scrum',
  ];

  public static DataAndAIMeetingTypes: string[] = [
    'Azure Field Scrum',
    'Data Ranger Review',
    'Partner Sharing',
  ];
  public static SolutionAreas = {
    ModernWork: 'Modern Work',
    DataAndAI: 'Data and AI',
    FinancialServices: 'Financial Services',
    HealthCare: 'Health Care',
    Manufacturing: 'Manufacturing',
    Retail: 'Retail',
    UnifiedSupport: 'Unified Support',
    Sustainability: 'Sustainability',
    NonProfit: 'Non-Profit',
    AIBS: 'AI Business Solutions',
    CAI: 'Cloud and AI Platforms',
    MSUnified: 'Microsoft Unified',
    MSServices: 'Microsoft Services',
  };

  public static DashboardViewMenu = {
    SaveAs: 'saveAs',
    ColumnOptions: 'columnOptions',
    ColumnOptionsIcon: 'ColumnOptions',
    AddView: 'AddView',
    EditView: 'EditView',
    CopyTooltipMsg: 'CopyTooltipMsg',
  };

  public static NoATCSolutionAreas: string[] = [
    UATConstants.SolutionAreas.FinancialServices,
    UATConstants.SolutionAreas.HealthCare,
    UATConstants.SolutionAreas.Manufacturing,
    UATConstants.SolutionAreas.Retail,
    UATConstants.SolutionAreas.UnifiedSupport,
    UATConstants.SolutionAreas.Sustainability,
    UATConstants.SolutionAreas.NonProfit,
    UATConstants.SolutionAreas.MSUnified,
    UATConstants.SolutionAreas.MSServices,
  ];

  public static ManageActionTabs: string[] = [
    UATConstants.PlaceholdersAndTitles.Details,
    UATConstants.PlaceholdersAndTitles.TechnicalFeedback,
    UATConstants.PlaceholdersAndTitles.CSG,
    UATConstants.PlaceholdersAndTitles.Compete,
    UATConstants.PlaceholdersAndTitles.VSUQBC,
    UATConstants.PlaceholdersAndTitles.CSR,
    UATConstants.PlaceholdersAndTitles.Related,
    UATConstants.PlaceholdersAndTitles.Attachments,
    UATConstants.PlaceholdersAndTitles.History,
    UATConstants.PlaceholdersAndTitles.GlobalTeam,
    UATConstants.PlaceholdersAndTitles.CAE,
    UATConstants.PlaceholdersAndTitles.APEX,
    UATConstants.PlaceholdersAndTitles.Nominations,
    UATConstants.PlaceholdersAndTitles.AOAICapacity,
    UATConstants.PlaceholdersAndTitles.RedButton,
    UATConstants.PlaceholdersAndTitles.CAPSL,
    UATConstants.PlaceholdersAndTitles.IAT,
    UATConstants.PlaceholdersAndTitles.MACC,
    UATConstants.PlaceholdersAndTitles.MACCManageTab,
    UATConstants.PlaceholdersAndTitles.GPS,
    UATConstants.PlaceholdersAndTitles.CoSell,
    UATConstants.PlaceholdersAndTitles.PMYT,
    UATConstants.PlaceholdersAndTitles.SMCRedButton,
    UATConstants.PlaceholdersAndTitles.RedButtonTSI,
    UATConstants.PlaceholdersAndTitles.GSI,
  ];

  //Default
  public static DefaultFilter: string[] = [
    UATConstants.PlaceholdersAndTitles.Related,
    UATConstants.PlaceholdersAndTitles.Attachments,
    UATConstants.PlaceholdersAndTitles.History,
    UATConstants.PlaceholdersAndTitles.Details,
  ];

  //Cosell
  public static CosellPerfFilter: string[] = [
    UATConstants.PlaceholdersAndTitles.CoSell,
    UATConstants.PlaceholdersAndTitles.Related,
    UATConstants.PlaceholdersAndTitles.Attachments,
    UATConstants.PlaceholdersAndTitles.History,
    UATConstants.PlaceholdersAndTitles.Details,
  ];

  //GPS
  public static GPSFilter: string[] = [
    UATConstants.PlaceholdersAndTitles.GPS,
    UATConstants.PlaceholdersAndTitles.Related,
    UATConstants.PlaceholdersAndTitles.Attachments,
    UATConstants.PlaceholdersAndTitles.History,
    UATConstants.PlaceholdersAndTitles.Details,
  ];

  //Relevent Quick users w.r.t action
  public static QuickUsersColumns: string[] = [
    UATConstants.UATFields.AssignedTo,
    UATConstants.UATFields.Requestor,
    UATConstants.UATFields.Contributor1,
    UATConstants.UATFields.Contributor2,
  ];

  public static CopilotActionDetailInsights: string[] = [
    'Customer summary for ',
    // "Other Actions for ",
    //  "Blockers For "
  ];

  public static NotificationCategory = {
    Report: 'Report',
    FeatureCopilot: 'FeatureCopilot',
    FeatureCopilotMoreIcon: 'FeatureCopilotMoreIcon',
  };

  public static AnalyticViewText = {
    privateView: "You're the only one who can see this view and use it in Power BI.",
    sharedView: 'All users on your project can see this view and use it in Power BI.',
    addedSuccessMessage: 'Analytics view created successfully.',
    updatedSucessMessage: 'Analytics view updated successfully.',
  };
  //AMM
  public static AMMConstants = {
    StateClosed: '6 - Closed',
    StateCancelled: 'Cancelled by Seller',
    StateRejected: 'Rejection Reason Collected',
    StateDisqualified: 'Disqualified',
    StateInProgress: 'In Progress',
    StateNew: 'New',
    subStateUnderReview: 'Under Review',
    subStateAwaitingAssignment: 'Awaiting Assignment',
    slaSatus_Breached: 'Breached',
    slaSatus_Met: 'Met',
    ECIFFutureGrowthOffer_HPC_PlanningPovBenchmarking: 'HPC: Planning/POV/Benchmarking',
    ECIFFutureGrowthOffer_HPC_MigrationDeploymentServices: 'HPC: Migration/Deployment services',
    SAPOffer: 'SAP RI',
    subStateNextBestActionAccepted: '1 - Next Best Action Accepted',
    subStateNextBestActionRejected: 'Next Best Action Rejected',
    subStateNextBestActionRecommended: 'Next Best Action Recommended',
    subStateRejectionReasonCollected: 'Rejection Reason Collected',
    subStateRejectionReasonNotCollected: 'Rejection Reason Not Collected',
    subStateCancelledbySeller: 'Cancelled by Seller',
    subStateDisqualified: 'Disqualified',
    stateOfferActivated: 'Offer Activated',
    subStateProjectCancelled: 'Project Cancelled',
    stateActivatingOffer: 'Activating Offer',
    subStateRejectedbyApprover: 'Rejected by Approver',
    subStateCollectingRejectionReason: 'Collecting Rejection Reason',
    subStateSubmittedPendingApproval: 'Submitted - Pending Approval',
    subStateFollowUptoOfferEngineSent: 'Follow Up to Offer Engine Sent',
    subStateOfferApproved: 'Offer Approved',
    substateImplementingOffer: 'Implementing Offer',
    substatePendingApproval: 'Submitted - Pending Approval',
    microsoftDefenderforCloud_3rdpartysecurity:
      'Customer already has a 3rd party security solution in place',
    microsoftDefenderforCloud_CustomerHasOtherReason: 'Customer has other reason not listed',
    ecifFutureGrowthOffer_Assessment_POV: `Mainframe & Midrange: Assessment/POV`,
    ecifFutureGrowthOffer_Migration_Deployment_services: `Mainframe & Midrange: Migration/Deployment services`,
    cancellationReason_Other: 'Other',
    sapOffer_SAPNativeToRISEACO: 'SAP Native to RISE ACO',
    AccessRestricted: 'Access Restricted',
    AccessToolTip: 'This field is restricted to view based on your access permissions',
    AMM_SLARemainingTime: 'slaRemainingTime',
  };

  public static AMMActionUpdate = {
    amm_3yearACR: 'amm_3yearACR',
    isThereAnyExistingMACCOrCTCOrMC: 'isThereAnyExistingMACCOrCTCOrMC',
    primarySolutionPlay: 'primarySolutionPlay',
    microsoftDefenderforCloud: 'amm_microsoftDefenderforCloud',
    esi: 'amm_ESI',
    howManyVMsIsTheCustomerDeploying: 'amm_vmDeploymentCount',
    whatAreTheVMsSizes: 'amm_vmSizeDetails',
    avsRIQuantity: 'avsRIQuantity',
    avsRIRegion: 'avsRIRegion',
    isThisAVMCToAVSMigration: 'isThisAVMCToAVSMigration',
    avsOffer: 'avsOffer',
    amm_DealStructure: 'amm_DealStructure',
    amm_RequestReason: 'amm_RequestReason',
    amm_ACOCE: 'amm_ACOCE',
    amm_ACOID: 'amm_ACOID',
    amm_doyouwanttorequestACOinadditiontoECIF: 'amm_doyouwanttorequestACOinadditiontoECIF',
    acoCreditRequestAmount: 'acoCreditRequestAmount',
    amm_ecifOfferEngineType: 'amm_ecifOfferEngineType',
    amm_ecifOfferEngineStatus: 'amm_ecifOfferEngineStatus',
    amm_ecifOfferEngineCaseID: 'amm_ecifOfferEngineCaseID',
    amm_avsOfferEngineType: 'amm_avsOfferEngineType',
    amm_avsOfferEngineStatus: 'amm_avsOfferEngineStatus',
    amm_avsOfferEngineCaseID: 'amm_avsOfferEngineCaseID',
    amm_cmfOfferEngineType: 'amm_cmfOfferEngineType',
    amm_cmfOfferEngineStatus: 'amm_cmfOfferEngineStatus',
    amm_cmfOfferEngineCaseID: 'amm_cmfOfferEngineCaseID',
    thirdPartySecuritySolution: 'thirdPartySecuritySolution',
    securityMigration: 'securityMigration',
    isPartnerInitiated: 'amm_isPartnerInitiated',
    partner: 'amm_partner',
    plannedDateofAssessment: 'amm_plannedDateofAssessment',
    plannedDateofMigration: 'amm_plannedDateofMigration',
    doYouHaveTheRVToolsExport: 'amm_doYouHaveTheRVToolsExport',
    fieldEmpowermentDiscount: 'amm_FieldEmpowermentDiscount',
    alreadyRunningAtleast1AVSClusterInTheTargetRegion:
      'amm_alreadyRunningAtleast1AVSClusterInTheTargetRegion',
    microsoftSellerContactAccountExecutive: 'amm_microsoftSellerContactAccountExecutive',
    customerName: 'amm_customerName',
    customerContact: 'amm_customerContact',
    customerContactEmail: 'amm_CustomerContactEmail',
    customerContactName: 'amm_CustomerContactName',
    expectedBidCloseDate: 'amm_expectedBidCloseDate',
    serviceLocation: 'amm_ServiceLocation',
    isAwareOfApprovedECIFSuppliers: 'amm_isAwareOfApprovedECIFSuppliers',
    willNotStartWithoutApprovedPO: 'amm_willNotStartWithoutApprovedPO',
    isECIFFundingForQualifiedActivities: 'amm_isECIFFundingForQualifiedActivities',
    isSupplierSameAsEndCustomer: 'amm_isSupplierSameAsEndCustomer',
    isThereACompetitiveBid: 'isThereACompetitiveBid',
    sponsoringCountry: 'amm_sponsoringCountry',
    financialYear: 'amm_FinancialYear',
    firstYearACR: 'amm_1stYearACR',
    requestAmount: 'requestAmount',
    fta: 'amm_FTA',
    ammTriageOwner: 'amm_triageOwner',
  };

  public static RTEConfig = {
    wrapperHeight: '200px',
    wrapperHeightByZoom: '435px',
  };
  public static GlobalView = 'Global View';

  public static SortableColumnList: string[] = [
    'raidid',
    'title',
    'areapath',
    'assignedto',
    'state',
    'substate',
    'requesteddate',
    'accountableexec',
    'meetingtype',
    'meetingsubtype',
    'fy',
    'month',
    'revenueimpact',
    'actionownergroup',
    'actionpriorityfield',
    'duedate',
    'closeddate',
    'changeddate',
    'createdby',
    'createddate',
    'id',
    'problemdetails',
    'raidentitysource',
    'tenantid',
    'userChangedByObj',
    'gps_partneroneid',
    'closurestatement',
    'capsl_amname',
    'capsl_suppresschildaccount',
    'capsl_accountname',
    'capsl_firstname',
    'capsl_lastname',
    'capsl_title',
    'capsl_businessjustification',
    'capsl_contactalias',
    'pmyt_sourceofinvestment',
    'pmyt_coinvestmentamount',
    'pmyt_investmentamount',
    'pmyt_estimatedstartdate',
    'pmyt_estimatedpipelinerevenue',
    'pmyt_partnername',
    'pmyt_partneroneid',
    'pmyt_estimatedcosellreferrals',
    'pmyt_role',
    'problemtype',
    'solutionarea',
    'category',
    'financialyear',
    'quarter',
    'area',
    'eou',
    'customerscenarioanddesiredoutcome',
    'CustomerImpact',
    'ltexecdirect',
    'managingowner',
    'agreementid',
    'agreementname',
    'packageid',
    'packagename',
    'mailbox',
    'sender',
    'conversationid',
    'priority',
    'workarounddetails',
    'l1businessprocess',
    'l2businessprocess',
    'l3businessprocess',
    'actualcreateddate',
    'ticketvolumepermonth',
    'supportscenariosimpacted',
    'impacttype',
    'intaketype',
    'strategiccategory',
    'synthesizer',
    'internaltriagedate',
    'rejecteddate',
    'triagecomments',
    'executingteamname',
    'executingteamadolink',
    'onelistfeedback',
    'isproductfeedback',
    'CoSell Activity',
    'CoSell Date Accepted',
    'CoSell Date Expired',
    'CoSell Deal Created Date',
    'CoSell Deal Status',
    'CoSell Deal Value',
    'CoSell Delivery Language Support',
    'CoSell Expected Expiration Date',
    'CoSell MCEM Sales Stage',
    'CoSell Microsoft CoOwner',
    'CoSell Microsoft Owner',
    'CoSell Partner Area',
    'CoSell Partner Contact Name',
    'CoSell Partner Country',
    'CoSell Partner Email',
    'CoSell Partner Engaged',
    'CoSell Partner Region',
    'CoSell Referral ID',
    'CoSell Request Category',
    'CoSell SubCategory Topic',
    'CoSell Assignment Date',
    'CoSell Initiated By',
  ];

  public static AllTenant = {
    'MCAPS Leader RoB': {
      ID: '6f76ed48-1b55-4951-920a-90b995e4d5cc',
      TYPE: '',
      TITLE: 'MCAPS Leader RoB Requests',
      SHORTNAME: 'ROB',
    },
    'Red Button': {
      ID: 'b878ed1d-5d4c-4a64-9716-221f9484f381',
      TYPE: 'RedBtn',
      TITLE: 'Red Button Requests',
      SHORTNAME: 'REDBUTTON',
    },
    'MCAPS Help PM': {
      ID: '1762e166-b734-43de-a37d-63aadd76becc',
      TYPE: 'MCAPSHelpPM',
      TITLE: 'MCAPS Help PM Requests',
      SHORTNAME: 'MCAPSHelpPM',
    },
    'Partner Co Marketing': {
      ID: 'af315f0d-0028-49ed-b197-1aaf897d56c8',
      TYPE: 'PMYT',
      TITLE: 'Partner marketing Yield Tracker Requests',
      SHORTNAME: 'PMYT',
    },
    'MACC Support': {
      ID: '6f76ed48-1b55-4951-920a-90b995e4d5dd',
      TYPE: 'MACCSupport',
      TITLE: 'MACC Unified Purple Empowerment Requests',
      SHORTNAME: 'MACCSupport',
    },
    CAPSL: {
      ID: 'dd373c03-794c-4500-b944-62fe9698de56',
      TYPE: 'CAPSL',
      TITLE: 'CAPSL Requests',
      SHORTNAME: 'CAPSL',
    },
    GSI: {
      ID: 'd5a5c2d1-b8be-4b18-a4c3-6d09e5f0c59c',
      TYPE: 'GSI',
      TITLE: 'GSI ACO PIR Requests',
      SHORTNAME: 'GSI',
    },
    'Microsoft Federal': {
      ID: 'bdbf05d2-ea1a-4d40-952f-41cd5d9bb8d6',
      TYPE: 'MicrosoftFederal',
      TITLE: 'Microsoft Federal Requests',
      SHORTNAME: 'MSFed',
    },
    MaccSupport: {
      ID: '6f76ed48-1b55-4951-920a-90b995e4d5dd',
      TYPE: 'MACCSupport',
      TITLE: 'MACC Unified Purple Empowerment Requests',
      SHORTNAME: 'MACCSupport',
    },
    SGS: {
      ID: '5461fe99-0356-49d5-be95-63cbbe8a187a',
      TYPE: 'SGS',
      TITLE: 'Strategic Growth Shaping (SGS) Requests',
      SHORTNAME: 'SGS',
    },
    USMaaS: {
      ID: 'ca7c5a04-59de-48f1-b6b7-48c3acc5dac5',
      TYPE: 'USMaaS',
      TITLE: 'USMaaS Requests',
      SHORTNAME: 'USMaaS',
    },
    AzureISV: {
      ID: 'af315f0d-0028-49ed-b197-1bbf897d56c8',
      TYPE: 'AzureSponsorshipISV',
      TITLE: 'Azure Sponsorship for ISV Requests',
      SHORTNAME: 'Azure Sponsorship for ISV',
    },
    PartnerInvestments: {
      ID: '4b9ac222-75ab-4566-a4e4-c9553896f290',
      TYPE: 'PartnerInvestments',
      TITLE: 'Partner Investments Requests',
      SHORTNAME: 'PartnerInvestments',
    },
    MCAPSHelpPM: {
      ID: '1762e166-b734-43de-a37d-63aadd76becc',
      TYPE: 'MCAPSHelpPM',
      TITLE: 'MCAPS Help PM Requests',
      SHORTNAME: 'MCAPSHelpPM',
    },
    CoSell: {
      ID: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d',
      TYPE: 'CoSell',
      TITLE: 'CoSell Requests',
      SHORTNAME: 'CoSell',
    },
    ECIF: {
      ID: '17a19c34-f60e-465f-af34-fa82b61cb9b9',
      TYPE: 'ECIF',
      TITLE: 'ECIF Requests',
      SHORTNAME: 'ECIF',
    },
    'AOAI Capacity': {
      ID: '3d6753f0-f836-4e55-8323-049767b02179',
      TYPE: 'AOAICapacity',
      TITLE: 'Azure AI Capacity',
      SHORTNAME: 'AOAICapacity',
    },
    AMM: {
      ID: '628691d2-59ef-4452-9f02-1d837497cc9a',
      TYPE: 'AMM',
      TITLE: 'AMM Offer Desk Requests',
      SHORTNAME: 'AMM',
    },
    '@Azure Triage': {
      ID: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b',
      TYPE: 'TechRoB',
      TITLE: 'Tech RoB Requests',
      SHORTNAME: '@Azure Triage',
    },
    'Tech RoB': {
      ID: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b',
      TYPE: 'TechRoB',
      TITLE: 'Tech RoB Requests',
      SHORTNAME: 'TechRoB',
    },
    'Non AI Capacity': {
      ID: '2b6b4852-8108-4aef-afca-d951b9fade7c',
      TYPE: 'NonAICapacity',
      TITLE: 'Non AI Capacity Requests',
      SHORTNAME: 'NonAICapacity',
    },
    Compete: {
      ID: '46cd8037-9f33-43c8-9262-432efd3317c6',
      TYPE: 'Compete',
      TITLE: 'Compete Requests',
      SHORTNAME: 'Compete',
    },
    PartnerSkilling: {
      ID: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b',
      TYPE: 'RegionalSkilling',
      TITLE: 'Regional Skilling Requests',
      SHORTNAME: 'RegionalSkilling',
    },
    DefenderForCloud: {
      ID: '1e3033d1-ae10-4d59-99b9-b7a6dd6e30d4',
      TYPE: 'DefenderForCloud',
      TITLE: 'MDC Attach Purple Exception Requests',
      SHORTNAME: 'DefenderForCloud',
    },
    MEA: {
      ID: '3f9c2a7e-6b4a-4f8a-9e7d-1c5b6a8e42d9',
      TYPE: 'MEA',
      TITLE: 'MEA Bedrock Requests',
      SHORTNAME: 'MEA',
    },
    'Microsoft Security Research': {
      ID: 'd6d337f6-2ff5-4d52-9a74-5d9dad09164b',
      TYPE: 'MicrosoftSecurityResearch',
      TITLE: 'Microsoft Security Research Requests',
      SHORTNAME: 'MSR',
    },
    'Microsoft Marketplace': {
      ID: 'f9e0f41d-707c-46af-b24b-626dc68601ce',
      TYPE: 'MarketplaceRequest',
      TITLE: 'Microsoft Marketplace Requests',
      SHORTNAME: 'Marketplace',
    },
  };

  public static TenantTitles = {
    CreatePrefix: 'Create New ',
    CreateSuffix: ' Action Request',
    RedButton: 'Red Button Requests',
    McapsHelpPM: 'MCAPS Help PM Requests',
    MACC: 'MACC Unified Purple Empowerment Requests',
    CAPSL: 'CAPSL Requests',
    PMYT: 'Partner Marketing Yield Tracker',
    ROB: 'MCAPS Leader RoB Requests',
    USMaaS: 'MaaS Requests',
    ECIF: 'ECIF Requests',
    GPS: 'Partner Investments Requests',
    GSI: 'GSI ACO PIR Requests',
    COSELL: 'CoSell Requests',
    USMASS: 'MaaS Requests',
    AMM: 'AMM Offer Desk Requests',
    SGCI: 'SGCI Requests',
    SGS: 'Strategic Growth Shaping (SGS) Requests',
    AOAI: 'Azure AI Capacity',
    AzureISV: 'Azure Sponsorship for ISV Requests',
    TechROB: 'Tech RoB Requests',
    Compete: 'Compete Requests',
    MSFed: 'Microsoft Federal Requests',
    AzureAOAI: 'Azure AI Capacity Requests',
    NonAICapacity: 'Non AI Capacity Intake Requests',
    PartnerSkilling: 'Partner Skilling Requests',
    RegionalSkilling: 'Regional Skilling Requests',
    DefenderForCloud: 'MDC Attach Purple Exception Requests',
    MEA: 'MEA Bedrock Requests',
    MicrosoftSecurityResearch: 'Microsoft Security Research Requests',
    Marketplace: 'Microsoft Marketplace Requests',
  };

  public static TenantADOText = {
    CreatePrefix: 'Create New ',
    CreateSuffix: ' Action Request',
    RedButton: 'MCAPS Red Button',
    McapsHelpPM: 'MCAPS Help PM',
    MACC: 'MCAPSHelp MACC Exception',
    CAPSL: 'CAPSL Requests',
    PMYT: 'Partner Marketing Yield Tracker',
    ROB: 'MCAPS Leader RoB Requests',
    ECIF: 'MCAPS ECIF Pre-Approval',
    GPS: 'MCAPS Partner Investments',
    COSELL: 'MCAPS CoSell',
    USMASS: 'MCAPS MaaS',
    AMM: 'AMM',
    SGCI: 'SGCI Requests',
    SGS: 'Strategic Growth Shaping (SGS) Requests',
    AOAI: 'AOAI Capacity',
    AzureISV: 'Azure Sponsorship for ISV Requests',
    Compete: 'Compete Requests',
    TechROB: 'Tech RoB Requests',
    MSFed: 'Microsoft Federal Requests',
    NonAICapacity: 'Non AI Capacity Intake Requests',
    PartnerSkilling: 'Partner Skilling Requests',
    RegionalSkilling: 'Regional Skilling Desk Requests',
    DefenderForCloud: 'MDC Attach Purple Exception',
    MEA: 'MEA Items',
    MicrosoftSecurityResearch: 'Microsoft Security Research Items',
    Marketplace: 'Microsoft Marketplace Requests',
  };

  public static TenantIds = {
    RB: 'b878ed1d-5d4c-4a64-9716-221f9484f381',
    CAPSL: 'dd373c03-794c-4500-b944-62fe9698de56',
    MCAPS_HELP: '1762e166-b734-43de-a37d-63aadd76becc',
    MACC: '6f76ed48-1b55-4951-920a-90b995e4d5dd',
    PMYT: 'af315f0d-0028-49ed-b197-1aaf897d56c8',
    ROB: '6f76ed48-1b55-4951-920a-90b995e4d5cc',
    AOAI: '3d6753f0-f836-4e55-8323-049767b02179',
    GPS: '4b9ac222-75ab-4566-a4e4-c9553896f290',
    COSELL: 'e4f33c50-d2ff-4b1c-8b1b-21b8ed18bc3d',
    ECIF: '17a19c34-f60e-465f-af34-fa82b61cb9b9',
    US_MAAS: 'ca7c5a04-59de-48f1-b6b7-48c3acc5dac5',
    AMM: '628691d2-59ef-4452-9f02-1d837497cc9a',
    SGS: '5461fe99-0356-49d5-be95-63cbbe8a187a',
    GSI: 'd5a5c2d1-b8be-4b18-a4c3-6d09e5f0c59c',
    AZURE_ISV: 'af315f0d-0028-49ed-b197-1bbf897d56c8',
    COMPETE: '46cd8037-9f33-43c8-9262-432efd3317c6',
    TECH_ROB: 'fdd1b0d7-b70a-4e73-b4a3-de6ac7ba3f5b',
    MS_FED: 'bdbf05d2-ea1a-4d40-952f-41cd5d9bb8d6',
    NON_AI_CAPACITY_INTAKE: '2b6b4852-8108-4aef-afca-d951b9fade7c',
    PARTNER_SKILLING: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b',
    PS: '7e4d3c2b-1a9f-4e8d-9c6b-5a4f3e2d1c0b',
    DEFENDER_FOR_CLOUD: '1e3033d1-ae10-4d59-99b9-b7a6dd6e30d4',
    MEA: '3f9c2a7e-6b4a-4f8a-9e7d-1c5b6a8e42d9',
    MSR: 'd6d337f6-2ff5-4d52-9a74-5d9dad09164b',
    MARKETPLACE: 'f9e0f41d-707c-46af-b24b-626dc68601ce',
  };

  public static FlightedItems = [
    this.AllTenant['Partner Co Marketing'].ID,
    this.AllTenant['Tech RoB'].ID,
  ];

  public static CreateActionMenuKey = {
    CreateCAPSLRequest: 'CreateCAPSLRequest',
    CreateUSMaaSRequest: 'CreateUSMaaSRequest',
    CreateMaccSupportRequest: 'CreateMaccSupportRequest',
    CreateMCAPSHelpRequest: 'CreateMCAPSHelpRequest',
    CreatePartnerMarketingYieldTracker: 'CreatePartnerMarketingYieldTracker',
    CreateRedButtonRequest: 'CreateRedButtonRequest',
    CreateROBRequest: 'CreateROBRequest',
    CreateGSIRequest: 'CreateGSIRequest',
    BulkImportActions: 'BulkImportActions',
    DownloadTemplate: 'DownloadTemplate',
    CreateCosellRequest: 'CreateCosellRequest',
    CreateECIFRequest: 'CreateECIFRequest',
    CreateGPSRequest: 'CreateGPSRequest',
    CreateUSMaasRequest: 'CreateUSMaasRequest',
    CreateSGCIRequest: 'CreateSGCIRequest',
    CreateAMMRequest: 'CreateAMMRequest',
    CreateSGSRequest: 'CreateSGSRequest',
    CreatePSRequest: 'CreatePSRequest',
    CreateAzureISVRequest: 'CreateAzureISVRequest',
    CreateAOAIRequest: 'CreateAOAIRequest',
    NonAICapacityRequest: 'NonAICapacityRequest',
    CreateTechROBRequest: 'CreateTechROBRequest',
    CreateCompeteRequest: 'CreateCompeteRequest',
    CreateMSFedRequest: 'MicrosoftFederalRequest',
    CreateRegionalSkillingRequest: 'CreateRegionalSkillingRequest',
    CreateDefenderForCloudRequest: 'CreateDefenderForCloudRequestTitle',
    CreateMEARequest: 'CreateMEARequest',
    CreateMSRRequest: 'CreateMSRRequest',
    CreateMarketplaceRequest: 'CreateMarketplaceRequest',
    CreateLeaderRobRequest: 'CreateLeaderRobRequest',
    RiskIssueDependency: 'RiskIssueDependency',
    Risk: 'Risk',
    Issue: 'Issue',
    Dependency: 'Dependency',
  };

  public static CreateActionMenuTitle = {
    CreateCAPSLRequest: 'CAPSL Request',
    CreateUSMaaSRequest: 'US MaaS Request',
    CreateMaccSupportRequest: 'MACC Unified Purple Empowerment Request',
    CreateMCAPSHelpRequest: 'MCAPS Help PM Request',
    CreatePartnerMarketingYieldTracker: 'Partner Marketing Yield Tracker Request',
    CreateRedButtonRequest: 'Red Button Request',
    CreateROBRequest: 'MCAPS Leader RoB Request',
    BulkImportActions: 'Bulk Import',
    DownloadTemplate: 'Download Template',
    CreateCosellRequest: 'CoSell Request',
    CreateECIFRequest: 'ECIF Request',
    CreateGPSRequest: 'Partner Investments Request',
    CreateGSIRequest: 'GSI ACO PIR Request',
    CreateUSMaasRequest: 'US MaaS Request',
    CreateSGCIRequest: 'SGCI Request',
    CreateAMMRequest: 'AMM Request',
    CreateSGSRequest: 'SGS Request',
    CreatePSRequest: 'Regional Skilling Request',
    CreateAzureISVRequest: 'Azure Sponsorship for ISV Request',
    CreateAOAIRequest: 'Azure AI Capacity',
    CreateTechROBRequest: 'Create Tech RoB Request',
    CreateCompeteRequest: 'Create Compete Request',
    CreateMSFedRequest: 'Microsoft Federal Request',
    CreateAzureAOAIRequest: 'Azure AI Capacity Request',
    CreateAzureNonAICapacityRequest: 'Non AI Capacity Intake Request',
    CreateRegionalSkillingRequest: 'Regional Skilling Request',
    CreateDefenderForCloudRequest: 'Create new MDC Attach Purple Exception Request',
    CreateDefenderForCloudRequestTitle: 'MDC Attach Purple Exception Request',
    CreateMEARequest: 'MEA Bedrock Request',
    CreateMSRRequest: 'Microsoft Security Research Request',
    CreateMarketplaceRequest: 'Microsoft Marketplace Request',
    CreateLeaderRobRequest: 'MCAPS Leader RoB Request',
  };

  public static EntityMetaDataKeys = {
    UATDataDictionary: 'uatDataDictionary',
  };

  public static SMCCareaPathMappings = {
    SMC_Americas: UATConstants.Project + '\\SMC\\Americas',
    SMC_Asia: UATConstants.Project + '\\SMC\\Asia',
    SMC_EMEA: UATConstants.Project + '\\SMC\\EMEA',
    MCAPS_SMC: UATConstants.Project + '\\SMC\\MSCAPS-SMC',
    SMB_Americas: UATConstants.Project + '\\SMB\\Americas',
    SMB_Asia: UATConstants.Project + '\\SMB\\Asia',
    SMB_EMEA: UATConstants.Project + '\\SMB\\EMEA',
  };

  public static TechRoBAreaPaths = {
    WCBAzureTriage: UATConstants.Project + '\\WCB\\@Azure Triage',
    WCBSecurityTriage: UATConstants.Project + '\\WCB\\@Security Triage',
    WCBAppsandInfra: UATConstants.Project + '\\WCB\\Customer Success\\Apps and Infra',
    WCBDataandAI: UATConstants.Project + '\\WCB\\Customer Success\\Data and AI',
    WCBDigitalAppInnovation:
      UATConstants.Project + '\\WCB\\Customer Success\\Digital App Innovation',
    WCBSecurity: UATConstants.Project + '\\WCB\\Customer Success\\Security',
    WCBSecurityCloud: UATConstants.Project + '\\WCB\\Customer Success\\Security\\Cloud',
    WCBSecurityEntra: UATConstants.Project + '\\WCB\\Customer Success\\Security\\Entra',
    WCBSecurityPurview: UATConstants.Project + '\\WCB\\Customer Success\\Security\\Purview',
    WCBSecuritySentinel: UATConstants.Project + '\\WCB\\Customer Success\\Security\\Sentinel',
    WCBSecurityScrum: UATConstants.Project + '\\WCB\\Customer Success\\Security\\Scrum',
    WCBInfrastructure: UATConstants.Project + '\\WCB\\Customer Success\\Infrastructure',
    WCBSecurityTriageSTU: UATConstants.Project + '\\WCB\\@Security Triage\\STU',
    WCBSTUSecurity: UATConstants.Project + '\\WCB\\STU\\Security',
    WCBSTU: UATConstants.Project + '\\WCB\\STU',
    MCAPSCESIAT: UATConstants.Project + '\\MCAPS\\CE-S\\IAT',
    MCAPSGCSPartnerSuccess: UATConstants.Project + '\\MCAPS\\GCS Partner Success',
    WCBSecurityXDR: UATConstants.Project + '\\WCB\\Customer Success\\Security\\XDR',
    WCBABSTriage: UATConstants.Project + '\\WCB\\@ABS Triage',
    WCBCustomerSuccessABSScrum:
      UATConstants.Project + '\\WCB\\Customer Success\\AI Business Solutions',
    WCBMicrosoftSolutionsABSScrum:
      UATConstants.Project + '\\WCB\\Microsoft Solutions\\AI Business Solutions',
  };

  public static DefaultTenantValues = {
    ecifState: 'In Progress',
    ecifSubState: 'Active Action Resolution',
    defaultPSState: 'New',
    defaultPSSubState: 'New',
    NotStarted: 'Not Started',
  };

  //Kindly refer below ammOfferList
  public static AMM_OFFER_NAMES = {
    AMMAzureAccess: 'AMM - Azure Access',
    AzureAccess: 'Azure Access',
    AMMMigrateSecureAssessment: 'AMM - Migrate and Secure WIN/SQL/LIN Solution Assessment',
    BenchmarkTCO: 'Benchmark TCO',
    AMMPreSalesECIF: 'AMM - Pre-Sales ECIF',
    AMMPostSalesECIF: 'AMM - Post-Sales ECIF',
    AMMFieldECIF: 'Field ECIF',
    AzureAccelerateECIF: 'Azure Accelerate ECIF',
    AMMFutureGrowthECIF: 'AMM - Future Growth ECIF',
    AzureIncubationECIF: 'Azure Incubation ECIF',
    AMMSAPRiseECIF: 'AMM - SAP RISE ECIF',
    SAPRiseECIF: 'SAP RISE ECIF',
    AMMMigrateOracleECIF: 'AMM - Migrate Oracle ECIF',
    AMMCSUMigrationFactory: 'AMM - CSU Migration Factory',
    CloudAccelerateFactory: 'Cloud Accelerate Factory',
    AMMLocalAzureCreditOffer: 'AMM - Local Azure Credit Offer',
    LocalAzureCreditOffer: 'Local Azure Credit Offer',
    FY27_LocalACO: 'Local ACO',
    AMMAVSAzureCreditOffer: 'AMM - AVS Azure Credit Offer',
    AVSAzureCreditOffer: 'AVS Azure Credit Offer',
    AMMSAPAzureCreditOffer: 'AMM - SAP Azure Credit Offer',
    SAPAzureCreditOffer: 'SAP Azure Credit Offer',
    AMMFutureGrowthAzureCreditOffer: 'AMM - Future Growth Azure Credit Offer',
    IncubationAzureCreditOffer: 'Incubation Azure Credit Offer',
    AMMMigrateOracleAzureCreditOffer: 'AMM - Migrate Oracle Azure Credit Offer',
    AMMAVSExpressTCO: 'AMM - AVS Express TCO',
    AVSExpressTCO: 'AVS Express TCO',
    AMMVMWareMigration: 'AMM - VMWare Migration',
    VMwareMigration: 'VMware Migration',
    AnalyticsModernizationAccelerator: 'Analytics Modernization Accelerator (AMA)',
    AnalyticsCompeteECIF: 'Analytics Compete ECIF',
    AzureFrontierOfferECIF: 'Azure Frontier Offer ECIF',
    CorpECIF: 'Corp ECIF',
    AnalyticsCompeteACO: 'Analytics Compete ACO',
    ECIFFactoryCoreMigration: 'ECIF + Factory Core Migration',
    ECIFFactory: 'ECIF + Factory',
    GeneralPurposeHackathonECIF: '1:1 General Purpose Hackathon ECIF',
    GitHubCopilotHackathon: '1:1 GitHub Co-pilot Hackathon',
    SMBACO: 'SMB ACO',
    OracleODAAECIF: 'Oracle ODAA ECIF',
    OracleODAAACO: 'Oracle ODAA ACO',
    SynapseHDIToFabricMigration: 'Synapse or HDI Migration to Fabric',
    SpecialtyECIF: 'Specialty ECIF',
    AVSPilotProgram: 'AVS Pilot Program',
    AzureFrontierOfferACO: 'Azure Frontier Offer ACO',
    CorpACO: 'Corp ACO',
    SSCACO: 'Service Specific CACO',
  };

  public static ValidationAutomation = {
    modal: {
      title: 'Validation Summary',
      subtitle:
        'Below is the validation summary for the items. You can add any additional validation items manually if required.',
      detail: 'Validation Details',
    },
    summary: {
      title: 'Validation Summary',
      subtitle: 'Expand this section to view details of validations performed.',
      expand: 'Expand to view',
    },
    form: {
      addtitle: 'Add a new validation item',
      edittitle: 'Edit Validation Item',
      label: {
        name: 'Validation Item Name',
        value: 'Validation Item Value',
        state: 'Validation State',
        feedback: 'Validation Feedback',
        comments: 'Comments',
      },
      placeholder: {
        name: 'Enter validation item name',
        value: 'Enter the value being validated',
        state: 'Select validation state',
        feedback: 'Enter the validation feedback',
        comments: 'Enter if any additional comments',
      },
      button: {
        add: 'Add Item',
        cancel: 'Cancel',
        save: 'Save',
      },
    },
    messages: {
      success: {
        add: 'Validation item added successfully.',
        update: 'Validation item updated successfully.',
        validation: 'Validation checks completed successfully.',
      },
      error: {
        add: 'Error occurred while adding the validation item. Please try again.',
        update: 'Error occurred while updating the validation item. Please try again.',
        server:
          'Error occurred while performing validation checks. Please try again sometime later.',
        validation: 'Error occurred while performing validation checks. Please try again.',
      },
    },
    rtkq: {
      fixedKeys: {
        update: 'update-validation-message',
        get: 'get-validation-messages',
      },
    },
    grid: {
      report: {
        link: import.meta.env.VITE_VALIDATION_AUTOMATION_PBI_REPORT_LINK || '',
        linkText: 'PBI Report link',
        linkToolTip: 'PBI Report link',
      },
    },
    emptyState: {
      headingText: 'No Validation Items Found',
      descriptionText:
        'There are no validation items to display. You can add new validation items by clicking the button below.',
      ctaText: 'Run Validation Check',
    },
    apiState: {
      runningValidating: 'Running validation checks...',
      validatingItems: 'Validating items...',
    },
    additionalValidtionField: {
      PartnerSpecialization: 'Partner Specialization',
      MACCCheck: 'MACC Check',
      ProjectStacking: 'Project Stacking',
    },
  };

  public static NBARTab = {
    headerAndPanelName: {
      ACTION_DETAILS: 'ACTION_DETAILS',
      MSX_SECTION: 'MSX_SECTION',
      NBAR_DETAILS: 'NBAR_DETAILS',
      ADDITIONAL_INFORMATION: 'ADDITIONAL_INFORMATION',
      NEXT_BEST_ACTION_RECOMMENDATION: 'NEXT_BEST_ACTION_RECOMMENDATION',
    },
    accordionHeaderTitle: {
      ActionDetails: 'Action Details',
      MSX: 'MSX',
      NBARDetails: 'NBAR Details',
      NextBestActionRecommendation: 'Next Best Action Recommendation',
      AdditionalInformation: 'Additional Information',
    },
    tooltipText: {
      currentMcemStage: 'Current MCEM Stage',
      area: 'Area',
      nbarScenario: 'NBAR Scenario',
      nbarStatus: 'NBAR Status',
      recommendNextBestAction: 'Recommend Next Best Action',
      actionTakenByNbarOwner: 'Action Taken by NBAR Owner',
      rejectionReason: 'Rejection Reason',
      reasonForDelayBeyond3Months: 'Reason for delay beyond 3 months',
      workLoads: 'Workloads',
      addedWorkloads: 'These workloads will be added to the action',
    },
    messageBarTitle: {
      offersGivenToCustomer: 'Offers already given to the customer in last 1 year',
      lxpRecommendedOffers: 'LXP Recommended Offers',
    },
    fieldLabel: {
      actionOwner: 'Action Owner',
      requestorOnBehalf: 'Requestor On Behalf',
      expectedProjectEndDate: 'Expected Project End Date',
      firstYearACR: '1st Year ACR',
      currentCaseRevenue: 'Current Case Revenue',
      projectNotes: 'Project Notes',
      accountName: 'Account Name',
      opportunityId: 'Opportunity ID',
      originatingCaseId: 'Originating Case ID',
      currentMcemStage: 'Current MCEM Stage',
      area: 'Area',
      msxSellerNotes: 'MSX Seller Notes',
      nbarOwner: 'NBAR Owner',
      nbarScenario: 'NBAR Scenario',
      nbarStatus: 'NBAR Status',
      benchmarkTCOReportLink: 'Benchmark TCO Report Link',
      lxpRecommendedOffers: 'LXP Recommended Offers',
      recommendNextBestAction: 'Recommend Next Best Action',
      commitmentStatus: 'Commitment Status',
      nbarSuggestedRevenue: 'NBAR Suggested Revenue (USD)',
      nbarOwnerNotes: 'NBAR Owner Notes',
      originatingCaseID: 'Originating Case ID',
      actionTaken: 'Action Taken',
      followUp: 'Follow Up',
      followUpTargetDate: 'Follow Up Target Date',
      actionTakenByNbarOwner: 'Action Taken by NBAR Owner',
      actionTakenDetails: 'Action Taken details',
      rejectionReason: 'NBAR Rejection Reason',
      reasonForDelayBeyond3Months: 'Reason for delay beyond 3 months',
      rejectionDetails: 'NBAR Rejection Details',
      workLoads: 'Workloads',
      Workload: 'Workload',
      nextBestActionRecommendedOffer: 'Next Best Action (Recommended Offer)',
      nextBestActionDetails: 'Next Best Action Details',
    },
    arialabel: {
      actionOwner: 'Action Owner',
      requestorOnBehalf: 'Requestor On Behalf',
      nbarOwner: 'NBAR Owner',
    },
    placeholder: {
      searchActionOwner: 'Search Action Owner',
      searchRequestorOnBehalf: 'Search Requestor On Behalf',
      selectDate: 'Select Date',
      enterCurrentCaseRevenue: 'Enter Current Case Revenue',
      enterFirstYearACR: 'Enter 1st Year ACR',
      enterOriginatingCaseId: 'Enter Originating Case ID',
      enterAccountName: 'Enter Account Name',
      enterOpportunityId: 'Enter Opportunity ID',
      placeholderValue: 'Placeholder Value',
      unitedStates: 'Unites States',
      searchNbarOwner: 'Search NBAR Owner',
      selectNbarScenario: 'Select NBAR Scenario',
      selectNbarStatus: 'Select NBAR Status',
      benchmarkTCOReportLink: 'Enter Benchmark TCO Report Link',
      lxpRecommendedOffers: 'Enter LXP Recommended Offers',
      selectNextBestAction: 'Select Next Best Action',
      selectCommitmentStatus: 'Select Commitment Status',
      nbarSuggestedRevenue: 'Enter NBAR Suggested Revenue (USD)',
      selectAction: 'Select Action',
      selectRejectionReason: 'Select NBAR Rejection Reason',
      selectReasonForDelayBeyond3Months: 'Select Reason for delay beyond 3 months',
      enterRejectionDetails: 'Enter NBAR Rejection Details',
      selectActionTaken: 'Select Action Taken',
      selectFollowUp: 'Select Follow Up',
      workloadToAdd: 'Search workloads to add',
      nextBestActionDetails: 'Enter Next Best Action Details',
    },
    buttonName: {
      recommendTheOffer: 'Recommend the offer',
      addWorkloadBtn: 'Add Workload',
      add: 'Add',
      cancel: 'Cancel',
    },
    addWorkloadPanel: {
      title: 'Add a new workload',
      workloadLabel: 'Workload',
      searchPlaceholder: 'Search workloads to add',
      addedWorkloadsLabel: 'Added Workloads',
    },
    paragraph: {
      workloadCountText: 'Selected Workloads',
      workloadRecommendation:
        'Select workloads from the list below for the next best offer recommendation',
      nextBestActionRecommendationList:
        'The below are the list of next best action recommended offers where you can select and continue with the action:',
    },
    title: {
      nextBestActionRecommendation: 'Next Best Action Recommendation',
      nextBestActionRecommendations: 'Next Best Action Recommendations',
      addedWorkloads: 'Added Workloads',
    },
    optionsPlaceholder: {
      mcemStageOptions: 'Select MCEM Stage',
      areaOptions: 'Select Area',
      nbarScenarioOptions: 'Select NBAR Scenario',
      nbarStatusOptions: 'Select NBAR Status',
      recommendNextBestActionOptions: 'Select recommend next best action',
      rejectionReasonOptions: 'Select NBAR Rejection Reason',
      reasonForDelayBeyond3MonthsOptions: 'Select reason for delay beyond 3 months',
      actionTakenByOwnerOptions: 'Select action taken by owner',
      commitmentStatusOptions: 'Select commitment status',
      actionTakenOptions: 'Select action taken',
      followUpOptions: 'Select follow up',
    },
    panel: {
      workloadHeader: 'Add a new workload',
    },
    loading: {
      nbarInitial: 'Loading NBAR details...',
      nbarRecommendation: 'Loading recommendations...',
    },
    emptyState: {
      iconType: 2,
      headingText: 'No Recommendations Available',
      headingLevel: 'h2' as const,
      descriptionText: 'There are no next best action recommendations available at this time.',
    },
    allowedStatesAndSubStatesForNBAR: [
      { state: '4 - Offer Activated', substate: [] },
      { state: '5 - Project Complete', substate: [] },
      {
        state: '6 - Closed',
        substate: [
          '1 - Next Best Action Accepted',
          '2 - Next Best Action Recommended',
          '3 - Next Best Action Rejected',
        ],
      },
    ],
    Others: 'Others',
  };
  public static ACOStructureSSCACO = {
    FormLabels: {
      ServiceSpecificCACOForDatabases: 'Service Specific CACO for Databases',
      ServiceSpecificCACOForFabric: 'Service Specific CACO for Fabric',
      ServiceSpecificCACOForAgents: 'Service Specific CACO for Agents',
      ServiceSpecificCACOForModernization: 'Service Specific CACO for Modernization',
    },
    RowFieldLabels: {
      MilestoneID: 'Milestone ID',
      ConsumptionTargetUSD: 'Consumption Target (USD):',
      TargetStartDate: 'Target Start Date:',
      TargetEndDate: 'Target End Date:',
      CreditValueUSD: 'Credit Value (USD):',
      CreditValidityMonths: 'Credit Validity (Months):',
    },
    RowFieldNames: {
      Milestone: 'milestone',
      ConsumptionTargetUSD: 'consumptionTargetUSD',
      TargetStartDate: 'targetStartDate',
      TargetEndDate: 'targetEndDate',
      CreditValueUSD: 'creditValueUSD',
      CreditValidityMonths: 'creditValidityMonths',
    },
  };
  public static isTechRobAction = (areaPath: string, tagsList: string): boolean => {
    const isTechRobArea = TechRoBValidationNeeded(areaPath);
    const hasCompeteTag = tagsList?.includes('Copilot Compete') || false;

    return isTechRobArea && !hasCompeteTag;
  };

  public static isMSFedAreaPath = (areaPath: string): boolean =>
    areaPath === UATConstants.MicrosoftFederalAreaPath ||
    areaPath === UATConstants.MicrosoftFederalRootAreaPath;

  public static isMSFederalUser = (userContext?: Pick<UserContext, 'authorization'>): boolean =>
    userContext?.authorization?.TenantAuthorizations?.some(
      (tenantAuthorization) =>
        tenantAuthorization.TenantID === UATConstants.TenantIds.MS_FED &&
        tenantAuthorization.RoleScopeMap?.some((roleScope) => roleScope.Role === 'MSFedUser')
    ) ?? false;

  public static canAccessMSFederalAreaPath = (
    areaPath: string,
    userContext?: Pick<UserContext, 'authorization'>
  ): boolean => UATConstants.isMSFedAreaPath(areaPath) && UATConstants.isMSFederalUser(userContext);

  public static normalizeAreaPathForComparison = (areaPath: string): string =>
    areaPath.replace(/\\/g, ' ').replace(/@/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

  public static isSelectedAreaPath = (
    customQuery: AreaPathCustomQuery | null,
    areaPathName: string,
    areaPath: string
  ): boolean => {
    const selectedAreaPaths = new Set(
      customQuery?.queryGroups
        ?.filter((group) => group.clause?.column?.internalName === UATConstants.UATFields.AreaPath)
        .flatMap((group) => group.clause?.values ?? [])
        .map(UATConstants.normalizeAreaPathForComparison) ?? []
    );

    return (
      selectedAreaPaths.has(UATConstants.normalizeAreaPathForComparison(areaPathName)) ||
      selectedAreaPaths.has(UATConstants.normalizeAreaPathForComparison(areaPath))
    );
  };
}

export const isSMCCAreaPath = (areaPath: string) => {
  switch (areaPath) {
    case UATConstants.SMCCareaPathMappings.SMC_Americas:
    case UATConstants.SMCCareaPathMappings.SMC_Asia:
    case UATConstants.SMCCareaPathMappings.SMC_EMEA:
    case UATConstants.SMCCareaPathMappings.MCAPS_SMC:
    case UATConstants.SMCCareaPathMappings.SMB_Americas:
    case UATConstants.SMCCareaPathMappings.SMB_Asia:
    case UATConstants.SMCCareaPathMappings.SMB_EMEA:
      return true;
    default:
      return false;
  }
};

export const qbcValidationNeeded = (areaPath: string) =>
  areaPath === UATConstants.QBCValidationAreaPath;

export const gsmoValidationNeeded = (areaPath: string) =>
  areaPath === UATConstants.GSMOValidationAreaPath;

export const federalValidationNeeded = (areaPath: string) =>
  areaPath === UATConstants.MicrosoftFederalAreaPath;
export const SOValidationNeeded = (areaPath: string) => {
  return areaPath.includes('\\EO\\');
};

export const AHPMValidationNeeded = (areaPath: string) => {
  return areaPath.includes('\\MCAPS Help PM');
};

export const AOAIValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPS\\AOAI Capacity');
};

export const ECIFValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPS\\ECIF Pre-Approval');
};

export const GPSValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPS\\Partner Investments');
};

export const MAASValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPS\\MaaS');
};

export const MACCValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPSHelp\\MACC Exception');
};

export const TechRoBValidationNeeded = (areaPath: string) => {
  const validPaths = new Set([
    UATConstants.TechRoBAreaPaths.WCBAzureTriage,
    UATConstants.TechRoBAreaPaths.WCBSecurityTriage,
    UATConstants.TechRoBAreaPaths.WCBAppsandInfra,
    UATConstants.TechRoBAreaPaths.WCBDataandAI,
    UATConstants.TechRoBAreaPaths.WCBDigitalAppInnovation,
    UATConstants.TechRoBAreaPaths.WCBSecurity,
    UATConstants.TechRoBAreaPaths.WCBSecurityCloud,
    UATConstants.TechRoBAreaPaths.WCBSecurityEntra,
    UATConstants.TechRoBAreaPaths.WCBSecurityPurview,
    UATConstants.TechRoBAreaPaths.WCBSecuritySentinel,
    UATConstants.TechRoBAreaPaths.WCBSecurityScrum,
    UATConstants.TechRoBAreaPaths.WCBInfrastructure,
    UATConstants.TechRoBAreaPaths.WCBSecurityTriageSTU,
    UATConstants.TechRoBAreaPaths.WCBSTUSecurity,
    UATConstants.TechRoBAreaPaths.WCBSTU,
    UATConstants.TechRoBAreaPaths.MCAPSCESIAT,
    UATConstants.TechRoBAreaPaths.MCAPSGCSPartnerSuccess,
    UATConstants.TechRoBAreaPaths.WCBSecurityXDR,
    UATConstants.TechRoBAreaPaths.WCBABSTriage,
    UATConstants.TechRoBAreaPaths.WCBCustomerSuccessABSScrum,
    UATConstants.TechRoBAreaPaths.WCBMicrosoftSolutionsABSScrum,
  ]);
  return validPaths.has(areaPath);
};

export const IsApexRequest = (refID: string) => {
  return refID !== null && refID !== undefined && refID.includes('APEX');
};
export const isMWHubValidationNeeded = (areaPath: string, nominationtype: string) => {
  return (
    areaPath === UATConstants.MWHub_DefaultValue_AreaPath &&
    nominationtype === UATConstants.customerhub
  );
};

export const AHPMValidationNeededForSubstates = (Substate: string) => {
  return Substate.includes('Recommended');
};

export const RedButtonValidationNeeded = (areaPath: string) => {
  return areaPath.includes('Red Button');
};

export const isAMMAreapath = (areaPath: string) => {
  return areaPath.includes('AMM Offer Desk');
};

export const CAPSLValidationNeeded = (areaPath: string) => {
  return areaPath.includes('CAPSL');
};

export const CoSellValidationNeeded = (areaPath: string) => {
  return areaPath.includes('CoSell');
};

export const AMMValidationNeeded = (areaPath: string) => {
  return areaPath.includes('AMM');
};

export const RoBValidationNeeded = (areaPath: string) => {
  return areaPath.includes('MCAPS Leader RoB');
};

export const FeedbackareaPathMappings = {
  'Technical Feedback\\AI Business Solutions': UATConstants.Project + '\\WCB\\@ABS Triage',
  'Technical Feedback\\Security': UATConstants.Project + '\\WCB\\@Security Triage',
  'Technical Feedback\\Azure': UATConstants.Project + '\\WCB\\@Azure Triage',
};

export const OpportunityIDValidationNeeded = (refID: string) => {
  let OpportunityIDValid =
    refID !== null &&
    refID !== undefined &&
    (refID.includes('APEX') ||
      refID.includes('UATPORTAL-FIELD') ||
      refID.includes(UATConstants.UATKeyTerms.UATPORTALFieldCompete) ||
      refID.includes(UATConstants.UATKeyTerms.MCAPSHelpCompete) ||
      refID.includes(UATConstants.UATKeyTerms.GetHelpCompete))
      ? true
      : false;
  //Make opportunity field optional if its a feedback request. (part of Save feedback as an action functionality)
  if (refID !== null && refID !== undefined && refID.includes('FB360PORTAL-FEEDBACK'))
    OpportunityIDValid = false;
  return OpportunityIDValid;
};

export const generateAOAIDWRRequestURL = (requestId: any) => {
  return `${UATConstants.AOAIDWRPORTALURL}${requestId}`;
};

export const generateURL = (id: any) => {
  let cPanel = TrueFalse.true.toString();
  //if session storage is not null set showCopilotWindow to session value
  //if session storage is null but cPanel exists in url then setCPanel to url value
  if (sessionStorage.getItem(UATConstants.showCopilotWindow) !== null) {
    if (sessionStorage.getItem(UATConstants.showCopilotWindow) === TrueFalse.false.toString()) {
      cPanel = TrueFalse.false.toString();
    } else {
      cPanel = TrueFalse.true.toString();
    }
  } else {
    if (window.location.href.includes('cw=')) {
      cPanel = getURLParameter('cw')?.toString() || TrueFalse.false.toString();
    }
  }

  return `${window.location.origin}/action?id=${id}&cw=${cPanel}`;
  // return `${window.location.origin}/manageaction?id=${id}&cw=${cPanel}`;
};

export const addTabNameIntoManageActionURL = (manageActionUrl: string, areaPath: string) => {
  switch (areaPath) {
    case UATConstants.McapsCoSellAreaPath:
      return manageActionUrl + UATConstants.CoSelltabPath;
    case UATConstants.GPSInvestmentsAreaPath:
      return manageActionUrl + UATConstants.GPSInvestmentstabPath;
    case UATConstants.McapsCapslAreaPath:
      return manageActionUrl + UATConstants.CapsltabPath;
    case UATConstants.MCAPSHelpPM_DefaultValue_AreaPath:
      return manageActionUrl + UATConstants.McapsHelpPmTabPath;
    case UATConstants.PMYTAreaPath:
      return manageActionUrl + UATConstants.PMYTTabNodePath;
    default:
      return manageActionUrl;
  }
};

export const generateNewManageActionURL = (id: any) => {
  let cPanel = TrueFalse.true.toString();
  //if session storage is not null set showCopilotWindow to session value
  //if session storage is null but cPanel exists in url then setCPanel to url value
  if (sessionStorage.getItem(UATConstants.showCopilotWindow) !== null) {
    if (sessionStorage.getItem(UATConstants.showCopilotWindow) === TrueFalse.false.toString()) {
      cPanel = TrueFalse.false.toString();
    } else {
      cPanel = TrueFalse.true.toString();
    }
  } else {
    if (window.location.href.includes('cw=')) {
      cPanel = getURLParameter('cw')?.toString() || TrueFalse.false.toString();
    }
  }
  return `${window.location.origin}/manageaction?id=${id}&cw=${cPanel}`;
};

export const generateRoBManageActionURL = (id: any) => {
  return `${window.location.origin}/manageaction?raidid=${id}&tenant=RoB`;
};

export const generateTenantManageActionURL = (
  id: string,
  tenantId: string | undefined,
  partitionKey?: string,
  raidEntityCosmosId?: string
) => {
  const baseUrl = `${window.location.origin}/manageaction?raidid=${id}&tenantId=${tenantId}`;

  const params = new URLSearchParams();

  if (partitionKey) {
    params.set('partitionKey', partitionKey);
  }

  if (raidEntityCosmosId) {
    params.set('raidEntityCosmosId', raidEntityCosmosId);
  }

  const additionalParams = params.toString();

  const transformedUrl: string = additionalParams ? `${baseUrl}&${additionalParams}` : baseUrl;
  return transformedUrl;
};

export const generateAKAURL = (id: any) => {
  return `${UATConstants.WebUiAkaUrl}?id=${id}`;
};

export const generateRaidAKAURL = (id: any, tenantId: any) => {
  return `${UATConstants.WebUiAkaUrl}?raidid=${id}&tenantId=${tenantId}`;
};

export const generateDashboardLinkWithViewID = (viewID: string) => {
  return `${window.location.origin}/?viewID=${viewID}`;
};

export const html2text = (html: any) => {
  if (html === undefined) {
    return '';
  }
  const tag = document.createElement('div');
  tag.innerHTML = html;
  return tag.textContent || tag.innerText || '';
};
// when more statecolor is added here it should be updated in StateColors.style.ts
export const StateColorsNew: any[] = [
  { key: 'In Progress', bgColor: '#2886DE' },
  { key: 'In-Progress', bgColor: '#2886DE' },
  { key: 'Done', bgColor: '#107C10' },
  { key: 'New', bgColor: '#7160E8' },
  { key: 'Removed', bgColor: '#BBBBBB' },
  { key: 'On Hold', bgColor: '#D77F00' },
  { key: 'On-Hold', bgColor: '#D77F00' },
  { key: 'In Technical Feedback', bgColor: '#1AEBFF' },
  { key: 'Rejected', bgColor: '#D13438' },
  { key: 'Cancelled', bgColor: '#6E0811' },
  { key: 'Completed', bgColor: '#00CC6A' },
  { key: 'Open', bgColor: 'rgb(184, 124, 230)' },
  { key: 'Closed', bgColor: '#EEACB2' },
  { key: 'Pending', bgColor: '#605E5C' },
  { key: 'Draft', bgColor: '#EAA301' },
  { key: 'Activating Offer', bgColor: '#0027B4' },
  { key: 'Offer Activated', bgColor: '#01666D' },
  { key: 'Project Complete', bgColor: '#00CC6A' },
  { key: 'Not Started', bgColor: '#605E5C' },
  { key: 'On Track', bgColor: '#2886DE' },
  { key: 'At Risk or Blocked', bgColor: '#D13438' },
  { key: 'Default', bgColor: '#5C5C5C' },
];

//
export const eeaCountriesNames: any[] = [
  { key: 'France' },
  { key: 'Germany & Austria' },
  { key: 'Netherlands' },
  { key: 'North Europe' },
  { key: 'South Europe' },
];

//Added this for navbar scroll to dedicated tab
export const NavBarIds: any[] = [
  { key: '2', value: UATConstants.PlaceholdersAndTitles.APEX },
  // {key: "2", value: UATConstants.PlaceholdersAndTitles.AOAI},
  // {key: "3", value: UATConstants.PlaceholdersAndTitles.AHPM},
  // {key: "4", value: UATConstants.PlaceholdersAndTitles.CAE},
  // {key: "5", value: UATConstants.PlaceholdersAndTitles.Compete},
  { key: '6', value: UATConstants.PlaceholdersAndTitles.CSG },
  { key: '7', value: UATConstants.PlaceholdersAndTitles.ECIF },
  { key: '8', value: UATConstants.PlaceholdersAndTitles.CSR },
  { key: '9', value: UATConstants.PlaceholdersAndTitles.VSUQBC },
  { key: '10', value: UATConstants.PlaceholdersAndTitles.Nominations },
  { key: '11', value: UATConstants.PlaceholdersAndTitles.RedButton },
  { key: '4', value: UATConstants.PlaceholdersAndTitles.CAPSL },
  { key: '12', value: UATConstants.PlaceholdersAndTitles.CoSell },
  { key: '13', value: UATConstants.PlaceholdersAndTitles.IAT },
  { key: '14', value: UATConstants.PlaceholdersAndTitles.McapsMaaS },
  { key: '15', value: UATConstants.PlaceholdersAndTitles.MACC },
  { key: '16', value: UATConstants.PlaceholdersAndTitles.GPS },
];

//TechROB Assigned Team display name changes
export const AssignedTeamDisplayNameReplace: Record<string, string> = {
  'MCAPS Red Button TSI': UATConstants.RedButtonTSIAssignedTeam,
  'MCAPS SMC Red Button': UATConstants.RedButtonSMCAssignedTeam,
  'MCAPS SME&C Red Button': UATConstants.RedButtonSMCAssignedTeam,
  'WCB Microsoft Solutions MW Escalations': 'MCAPS Microsoft Solutions AIW Escalations',
  'WCB BA Triage': 'MCAPS AIBP Triage',
  'WCB Customer Success BizApps GTT': 'MCAPS Customer Success AIBP GTT',
  'WCB Microsoft Solutions AI Business Solutions': 'MCAPS Microsoft Solutions ABS Scrum',
  'WCB ABS Triage': 'MCAPS ABS Triage',
  'WCB Customer Success AI Business Solutions': 'MCAPS Customer Success ABS Scrum',
};

export const getUpdatedAssignedTeamDisplayName = (oldDisplayName: any): any => {
  return AssignedTeamDisplayNameReplace[oldDisplayName] || oldDisplayName;
};

export const CannedResponseOptions: any[] = [
  {
    key: 'Capacity Guidance',
    selected: false,
    text: 'Capacity Guidance',
  },
  {
    key: 'Info Needed',
    selected: false,
    text: 'Info Needed',
  },
  {
    key: 'IoT Guidance',
    selected: false,
    text: 'IoT Guidance',
  },
  {
    key: 'Open AI Capacity',
    selected: false,
    text: 'Open AI Capacity',
  },
  {
    key: 'Support Guidance',
    selected: false,
    text: 'Support Guidance',
  },

  {
    key: 'Uncommitted Milestone',
    selected: false,
    text: 'Uncommitted Milestone',
  },
  {
    key: 'Americas CTO',
    selected: false,
    text: 'Americas CTO',
  },
];
export const updateUATConstants = (language: string) => {
  const searchText = {
    SearchTextInprogress: SearchText.InProgress,
    SearchTextNoActions: SearchText.NoActions,
    // Add other properties as needed
  };
  const constantObjects = [
    UATConstants.PlaceholdersAndTitles,
    UATConstants.RatingContext,
    UATConstants.ErrorMessages,
    UATConstants,
    searchText,
  ];
  let translationFile: any;

  // Load the respective translation file based on the selected language
  switch (language) {
    case 'en-US':
      translationFile = translationENUS;
      break;
    case 'ja':
      translationFile = translationJA;
      break;
    default:
      translationFile = translationENUK;
      break;
  }
  constantObjects.forEach((constant) => {
    for (const propertyName of Object.keys(constant)) {
      if (translationFile.hasOwnProperty(propertyName)) {
        constant[propertyName] = translationFile[propertyName];
      }
    }
  });
};
export const formatDatewithNullParam = (date: Date | undefined | null): string => {
  if (!date) return '';
  return moment(date).format('MMMM DD YYYY');
};

export const CannedResponses: any[] = [
  {
    key: 'Support Guidance',
    value: `<p><span data-contrast="auto">Hi @_____,</span>\n<span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p><ol><li data-leveltext="%1." data-font="Segoe UI,Times New Roman" data-listid="5" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="1" data-aria-level="1"><p><span data-contrast="auto">Please open a support case and provide that support case ID here for reference.</span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559738&quot;:120,&quot;335559739&quot;:120,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n<li data-leveltext="%1." data-font="Segoe UI,Times New Roman" data-listid="5" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="2" data-aria-level="1"><p><span data-contrast="auto">If there is a CSAM assigned to this account, if further escalation is required after opening a support case, please work with your CSAM to follow the CSS "Escalate NOW" process as described here &gt; </span><a href="http://aka.ms/reactiveescalation"><span data-contrast="none">http://aka.ms/reactiveescalation</span></a><span data-contrast="auto"> </span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559738&quot;:60,&quot;335559739&quot;:120,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n<li data-leveltext="%1." data-font="Segoe UI,Times New Roman" data-listid="5" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="3" data-aria-level="1"><p><span data-contrast="auto">If all CSS escalation options from </span><a href="https://dev.azure.com/unifiedactiontracker/e58ef066-647b-43b2-84de-6ec1f1f31714/_workitems/edit/2"><span data-contrast="none">#2</span></a><span data-contrast="auto"> have been exhausted (or if there is not a CSAM assigned to this account) and engineering escalation is required, please file a </span><a href="https://aka.ms/gethelp"><span data-contrast="none">GetHelp</span></a><span data-contrast="auto"  case and provide that GetHelp case ID here for reference.</span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559739&quot;:0,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n<li data-leveltext="%1." data-font="Segoe UI,Times New Roman" data-listid="5" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="4" data-aria-level="1"><p><span data-contrast="auto">If further technical assistance is needed with design review, analysis of workarounds or engineering feedback to unblock this engagement, please update this Action 360 request with those details.</span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559738&quot;:60,&quot;335559739&quot;:120,&quot;335559740&quot;:240}">&nbsp;</span></p></li></ol>`,
  },
  {
    key: 'Capacity Guidance',
    value: `<p><span data-contrast="auto"><p>Hi @_____ ,</p>\n</span><span data-contrast="auto">For Azure Capacity issues, the process for field is to follow the steps and guidance on </span><a href="https://aka.ms/AzureCapacity"><span data-contrast="none">https://aka.ms/AzureCapacity</span></a><span data-contrast="auto">  . There is a 9-page scenario-based guidance when you scroll down.</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><span data-contrast="auto">Here are the summary of steps:</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<ol><li data-leveltext="%1." data-font="Segoe UI" data-listid="4" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="1" data-aria-level="1"><p><span data-contrast="auto">CSS Support: If it&rsquo;s about capacity restriction, please ask customer to open Quota CSS request &ldquo;Service and subscription limits (quota)&rdquo; from their Azure Portal </span><a href="https://aka.ms/GetAzureSupport"><span data-contrast="none">https://aka.ms/GetAzureSupport</span></a><span data-contrast="auto">  and have the CSS support ticket ID.</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n<li data-leveltext="%1." data-font="Segoe UI" data-listid="4" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="2" data-aria-level="1"><p><span data-contrast="auto">MSX: If it&rsquo;s related to MSX, please set MSX Milestones Status as "At Risk&rdquo; or &ldquo;Blocked", Status Reason as &ldquo;Capacity/Service Availability&rdquo;, Help Needed as "Azure Capacity Availability" (capacity) or &ldquo;Azure Service/Product Availability&rdquo; (service availability in specific region). Also, for Success Engagements, please select the right &ldquo;Preferred Azure Region&rdquo; and &ldquo;Azure Capacity Type&rdquo;. Azure Capacity team cannot review the requests if any of them is not available.</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n<li data-leveltext="%1." data-font="Segoe UI" data-listid="4" data-list-defn-props="{&quot;335552541&quot;:0,&quot;335559684&quot;:-1,&quot;335559685&quot;:720,&quot;335559991&quot;:360,&quot;469769242&quot;:[65533,0],&quot;469777803&quot;:&quot;left&quot;,&quot;469777804&quot;:&quot;%1.&quot;,&quot;469777815&quot;:&quot;multilevel&quot;}" aria-setsize="-1" data-aria-posinset="3" data-aria-level="1"><p><span data-contrast="auto">GetHelp: Open GetHelp ticket (with CSS ticket ID if applicable) and provide details why the capacity/availability should be prioritized.</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p></li>\n</ol><p><span data-contrast="auto">If related to MSX: Use OneAsk </span><a href="https://oneask.microsoft.com/"><span data-contrast="none">https://oneask.microsoft.com/</span></a><span data-contrast="auto">  to link MSX ID to the GetHelp process</span>&nbsp;<br><span data-contrast="auto">If no MSX ID: Use GetHelp </span><a href="https://aka.ms/GetHelp"><span data-contrast="none">https://aka.ms/GetHelp</span></a><span data-contrast="auto"> </span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><span data-contrast="auto">For an update on ND A100 capacity, please check item </span><a href="https://dev.azure.com/unifiedactiontracker/e58ef066-647b-43b2-84de-6ec1f1f31714/_workitems/edit/8"><span data-contrast="none">#8</span></a><span data-contrast="auto"> under 'TOP Frequently Asked Questions on the Capacity Portal (</span><a href="https://aka.ms/azurecapacity"><span data-contrast="none">https://aka.ms/azurecapacity</span></a><span data-contrast="auto"> ) for both customer ready guidance as well as additional internal only context.</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:0,&quot;335559740&quot;:240}">&nbsp;</span></p>`,
  },
  {
    key: 'Open AI Capacity',
    value: `<p><span data-contrast="auto">Hi @_____,</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><span data-contrast="auto">Azure Open AI is a unique commercial service that runs on separate GPU clusters from customers provisioned to Azure Machine Learning. At present, capacity is challenged with Azure Open AI Service. We are currently in a pause in providing rate limits quota increases and also in bringing customers onto provisioned throughput. While we have resumed quota increases week by week basis, Engineering Guidance is to guide customers to use Azure OpenAI Service: </span><a href="https://customervoice.microsoft.com/Pages/ResponsePage.aspx?id=v4j5cvGGr0GRqy180BHbR4xPXO648sJKt4GoXAed-0pURVJWRU4yRTMxRkszU0NXRFFTTEhaT1g1NyQlQCN0PWcu"><span data-contrast="none">Quota Request form</span></a><span data-contrast="auto">  to provide projected usage information.</span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><span data-contrast="auto">"Engineering has published a PBI report that the field can use to lookup status of each request after your customer has been submitted &gt;</span><span data-ccp-props="{&quot;201341983&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><a href="https://msit.powerbi.com/groups/me/apps/385063ea-4346-4bff-955d-3088e5caec2a/reports/0d917cda-c95e-4290-ad20-501f4abbd646/ReportSection5384b1f6c017bd18ab81?ctid=72f988bf-86f1-41af-91ab-2d7cd011db47&amp;experience=power-bi"><span data-contrast="none">Gating - Power BI</span></a><span data-contrast="auto"> </span><span data-ccp-props="{}">&nbsp;</span></p>`,
  },
  {
    key: 'Uncommitted Milestone',
    value: `<p><span data-contrast="auto">Hi @_____,</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span> <span data-contrast="auto">
    We are assigning this UAT to Commercial Solution Areas team to further assist.</span></p>`,
  },
  {
    key: 'IoT Guidance',
    value: `<span data-contrast="auto"><p>As a company, we have changed our investment strategy around IoT solutions to focus on industry solutions that can be supported by either ISV partners who are building industry solutions that leverage our IoT platform or SI partners that can support design and implementation of custom industry solutions that leverage our IoT platform. As a result, our Corp CSU and STU teams no longer have resources with expertise in IoT.</p>
    <p>Instead, please ask your CSAM to submit a request for a local CSA(E) resource and/or connect with your regional MTC for assistance. If additional assistance is required beyond what your CSA(E) team and regional MTC are able to provide, please consider bringing in a partner to help support this opportunity.</p>  
    <p>As you move forward with this customer opportunity, if you encounter any technical product gaps putting this opportunity at-risk, please submit those feature requests via UAT so that we can feed them into our technical feedback program with engineering for review, prioritization and tracking.</p></span>`,
  },
  {
    key: 'Info Needed',
    value: `<p><span data-contrast="auto">Hi @_____,</span><span data-ccp-props="{&quot;134233117&quot;:false,&quot;134233118&quot;:false,&quot;201341983&quot;:0,&quot;335559738&quot;:0,&quot;335559739&quot;:240,&quot;335559740&quot;:240}">&nbsp;</span></p>\n<p><span data-contrast="auto">
    <ol>\n<li>Opportunity and Milestone ID</li>&nbsp;<li>Workarounds evaluated</li>&nbsp;<li>Azure Regions</li>&nbsp;<li>Support Case &amp; GetHelp Number</li>&nbsp;<li>Product Group Contacts you've worked with</li></ol>
    </p>`,
  },
  {
    key: 'Americas CTO',
    value: `<p><span data-contrast="auto">Hi @_____,thank you for submitting your request. We will respond to you by the end of the next business day.</span><br>In the meantime, here&apos;s what we&apos;ll need from you:</p>\n<p><span data-contrast="auto">
       <p>
      <ol>\n<li>Confirm that your Area lead has approved this escalation.</li>&nbsp;<li>Please ensure that you have included the TPID and the Opportunity ID in your request.</li>&nbsp;<li>Provide the History of the escalation and the current issue.</li>&nbsp;<li>Discuss what success looks like for this situation.</li>&nbsp;<li>Provide the assigned technical team (CSA, TSP, ATS, etc). We will partner with the technical team assigned to the customer.</li>
        &nbsp;<li>Provide any relevant design documents for our review.</li></ol>
        <span>We look forward to diving in and seeing how we can assist.<br><br>
  
  Thank you,<br>
  Americas OCTO Team
  
  
  </span>
        </p>`,
  },
];

export const languages = [{ code: 'en-US', name: 'US English', flag: UATUSLogo }];
export const formatDate = (date: Date | undefined): string => {
  if (!date) return '';
  return moment(date).format('MMMM DD YYYY');
};

export const formatingDate = (date: Date | undefined): string => {
  if (!date) return '';
  return moment(date).format('MMMM DD YYYY');
};

export const formatedDate = (date: Date | undefined): string => {
  if (!date) return '';
  return moment(date).format('MMM DD, YYYY');
};

export const suggestionProps = {
  suggestionsHeaderText: 'Suggested People',
  noResultsFoundText: 'No results found',
  loadingText: 'Loading',
};

export const offerEngineTypeMapping: { [key: string]: string } = {
  'AMM - CSU Migration Factory': 'Migration Factory',
  'AMM - Post-Sales ECIF': 'ECIF',
  'AMM - Future Growth ECIF': 'ECIF',
  'AMM - Migrate and Secure WIN/SQL/LIN Solution Assessment': 'Solution Assessments',
  'AMM - Pre-Sales ECIF': 'ECIF',
  'AMM - Azure Access': 'Azure Access',
  'AMM - SAP RISE ECIF': 'ECIF',
  'AMM - Migrate Oracle ECIF': 'ECIF',
  'AMM - Local Azure Credit Offer': 'ACO',
  'AMM - AVS Azure Credit Offer': 'ACO',
  'AMM - Future Growth Azure Credit Offer': 'ACO',
  'AMM - SAP Azure Credit Offer': 'ACO',
  'AMM - Migrate Oracle Azure Credit Offer': 'ACO',
};

export enum AMMStates {
  Draft = '0 - Draft',
  New = '1 - New',
  InProgress = '2 - In Progress',
  ActivatingOffer = '3 - Activating Offer',
  OfferActivated = '4 - Offer Activated',
  ProjectComplete = '5 - Project Complete',
  Closed = '6 - Closed',
  Closed_Help = '3 - Closed',
}

export enum AMMSubStates {
  PendingReview = '0 - Pending Review', //FY-27 New substate
  DraftInProgress = '1 - Draft in Progress',
  AwaitingFactoryReview = '0 - Awaiting Factory review',
  AwaitingAssignment = '1 - Awaiting Assignment',
  UnderReview = '1 - Under Review',
  AwaitingSellerResponse = '2 - Awaiting Seller Response',
  AwaitingOfferEngineResponse = '3 - Awaiting Offer Engine Response',
  PendingAMMOfferAgentResponse = '4 - Pending AMM Offer Agent Response',
  MeetingScheduled = '5 - Meeting Scheduled',
  PendingScheduledMeeting = '6 - Pending Scheduled Meeting',
  MeetingComplete = '7 - Meeting Complete',
  CollectingRejectionReason = '8 - Collecting Rejection Reason',
  NominationonHold = '5 - Nomination on Hold', // AMM8.0 | Renamed
  SubmittedPendingApproval = '1 - Submitted - Pending Approval',
  FollowUpToOfferEngineSent = '2 - Follow Up to Offer Engine Sent',
  Competitivebid = '3 - Competitive bid',
  ImplementingOffer = '4 - Implementing Offer',
  RejectedByApprover = '5 - Rejected by Approver',
  CancelledBySeller = '6 - Cancelled by Seller',
  ProjectStarted = '1 - Project Started',
  ProjectStartPending = '2 - Approved - Project Start Pending',
  ProjectDelayed = '3 - Project Delayed',
  ProjectComplete = '4 - Project Complete',
  ProjectCancelled = '5 - Project Cancelled',
  NextBestActionReviewInProgress = '1 - Next Best Action Review in Progress',
  NextBestActionRecommended = '2 - Next Best Action Recommended',
  NextBestActionAccepted = '1 - Next Best Action Accepted',
  NextBestActionRejected = '3 - Next Best Action Rejected',
  CancelledBySeller_closed = '4 - Cancelled by Seller',
  Disqualified = '5 - Disqualified',
  RejectionReasonCollected = '6 - Rejection Reason Collected',
  RejectionReasonNotCollected = '7 - Rejection Reason Not Collected',
  NoResponse = '8 - No Response', // AMM8.0
  PendingScheduledMeeting_Help = '3 - Pending Scheduled Meeting',
  MeetingScheduled_Help = '4 - Meeting Scheduled',
  MeetingComplete_Help = '5 - Meeting Complete',
  OfferRecommended = '6 - Offer Recommended',
  ConfirmOfferUsage = '7 - Confirm Offer Usage',
  AwaitingOfferSubmission_Help = '8 - Awaiting Offer Submission',
  OfferSubmitted_Help = '1 - Offer Submitted',
  NoResponse_Help = '2 - No Response',
  CancelledBySeller_Help = '3 - Cancelled by Seller',
  PendingSellerResponse = '6 - Pending Seller Response', // AMM8.0 | Renamed
  PendingExceptionApproval = '6 - Pending Exception Approval', // AMM8.0 | Renamed
  //New Sub State : AMM8.0 Start
  ActivationNominationonHold = '7 - Nomination on Hold',
  OfferActivatedNominationonHold = '6 - Nomination on Hold',
  OfferActivatedNextActionAccepted = '7 - Next Best Action Accepted',
  OfferActivatedNextActionRejected = '8 - Next Best Action Rejected',
  OfferActivatedPendingSellerResponse = '9 - Pending Seller Response',
  ProjectCompleteNextActionAccepted = '3 - Next Best Action Accepted',
  ProjectCompleteNextActionRejected = '4 - Next Best Action Rejected',
  ClosedDuplicate = '9 - Duplicate',
  //New Sub State : AMM8.0 End
}

export enum ApiStatusCode {
  OK = '200',
  Error = 'Error',
  NotFound = '404',
  InternalServerError = '500',
  Unauthorized = '401',
  Forbidden = '403',
  BadRequest = '400',
  ServiceUnavailable = '503',
  Created = '201',
  None = '0',
}

export const AllowedExtensions = [
  'doc',
  'docx',
  'dot',
  'docm',
  'Xlsx',
  'Xls',
  'Xlsb',
  'pptx',
  'PPT',
  'PDF',
  'JPEG',
  'jpg',
  'txt',
  'png',
];

export const tenantFeaturePermissions = {
  tenantFeature: ['Copilot', 'Analytics', 'Global Search', 'Export'],
  tenantActions: ['Action-history', 'Action-Related', 'Action-attachments', 'Action-tags'],
};

export const tenantSpecificDownloadTemplate = {
  'MCAPS Leader RoB': 'MCAPS Leader RoB',
  'Azure AI Capacity': 'Azure AI Capacity',
};

export const ROBDefaultViewId = 'd4e5f6a7-0123-4567-89de-fabc12345678';

export const NoPolicyFooter = ['PMYT', 'CAPSL', 'MCAPSHelpPM'];

export const ArtemisTenantName = ['PMYT', 'CAPSL', 'MCAPSHelpPM', 'SGS'];

//Nodename as a action name
export const newSplitScreenMode = [
  UATConstants.PlaceholdersAndTitles.GSI,
  UATConstants.PlaceholdersAndTitles.CoSell,
  UATConstants.PlaceholdersAndTitles.GPS,
  UATConstants.PlaceholdersAndTitles.PMYT,
  UATConstants.PlaceholdersAndTitles.CAPSL,
  UATConstants.PlaceholdersAndTitles.RedButtonTSI,
  UATConstants.PlaceholdersAndTitles.SMCRedButton,
  UATConstants.PlaceholdersAndTitles.AOAICapacity,
  UATConstants.PlaceholdersAndTitles.MCAPSHelpPM,
];

//Newly launched manage action tabs
export const newManageAction = [
  UATConstants.PlaceholdersAndTitles.GSI,
  UATConstants.PlaceholdersAndTitles.AOAI,
  UATConstants.PlaceholdersAndTitles.CAPSL,
  UATConstants.PlaceholdersAndTitles.CoSell,
  UATConstants.PlaceholdersAndTitles.GPS,
  UATConstants.PlaceholdersAndTitles.MCAPSHelpPM,
  UATConstants.PlaceholdersAndTitles.RedButton,
  UATConstants.PlaceholdersAndTitles.PMYT,
];

export const convertArrayFormat = (array: any[]) => {
  const newArray: any[] = [];
  array?.forEach((element) => {
    newArray.push(element.key);
  });
  return newArray;
};
export const convertAssignedTeamFormat = (array: any[]) => {
  const newArray: any[] = [];
  array?.forEach((element) => {
    newArray.push(element.text);
  });
  return newArray;
};

export const checkFilterStoreData = (filterData: any[]) => {
  const selectedFilterValueKeys = filterData ? Object.keys(filterData) : [];
  const storeData = selectedFilterValueKeys?.some((key) => {
    const value = filterData[key];
    return Array.isArray(value)
      ? value?.length > 0
      : value !== null && value !== undefined && value !== '';
  });
  return storeData;
};

export enum IElementType {
  Link = 'LINK',
  Copy = 'COPY',
  Button = 'BUTTON',
  DownloadTemplate = 'DOWNLOAD_TEMPLATE',
  Display = 'DISPLAY',
  Icon = 'ICON',
}

//We are converting Artemis data into Work Item structure for Gps & CoSell Tenant .
//In future , if any other tenent requires the same we can extent the below function.
//Below function we can pass either areaPath or tenantId to check if the tenant is in ADO or not.
export const isArtemisAllowedInAdo = (payload: { areaPath?: string; tenantId?: string }) => {
  const { areaPath, tenantId } = payload;
  if (areaPath) {
    return GPSValidationNeeded(areaPath) || CoSellValidationNeeded(areaPath) ? true : false;
  } else if (tenantId) {
    return (
      UATConstants.EXcludedTenants.CoSell === tenantId ||
      UATConstants.EXcludedTenants.PartnerInvestment === tenantId
    );
  } else {
    return false;
  }
};

//Below function is used to convert the search filter query technicalfeedback value string to boolean format.
export const convertQueryToBoolean = (query: string): string => {
  return query.replace(/'true'/g, 'true').replace(/'false'/g, 'false');
};

export const ExcludedFromArtemisFlow: string[] = [
  // UATConstants.PlaceholdersAndTitles.ECIFPreApproval_Category, //Rollback to ADO
  UATConstants.PlaceholdersAndTitles.AOAICapacity,
  UATConstants.PlaceholdersAndTitles.TechROB,
  UATConstants.PlaceholdersAndTitles.MSFed,
];

//Below function is used to find duplicate elements in the layout data to prevent duplicate register in dynamic form.
export const findDuplicateElements = (data: ILayoutData[]): string[] => {
  const elementSet = new Set<string>();
  const duplicates = new Set<string>();

  data.forEach((section) => {
    section.layout.forEach((layoutItem) => {
      layoutItem.row.forEach((rowItem) => {
        if (elementSet.has(rowItem.element)) {
          duplicates.add(rowItem.element);
        } else {
          elementSet.add(rowItem.element);
        }
      });
    });
  });

  return Array.from(duplicates);
};

//Below function is used to get the manager email from user org hierarchy.
export const getManagerEmailFromHierarchy = (orgHierarchy: string): string => {
  const segments = orgHierarchy.split('/');
  const managerAlias = segments.length >= 2 ? segments[segments.length - 2] : undefined;
  return managerAlias ? `${managerAlias}@microsoft.com` : '';
};

export const getPickerKey = (arr: any) => (Array.isArray(arr) && arr[0]?.key ? arr[0].key : '');

export const subTitleRedButton = [
  "We've received your request. Our screening team will review.",
  'If approved, a Red Button CSAM will be contacting the customer with you included in the correspondence.',
  'If rejected, we will notify you and offer further options of escalation.',
];

export const TagsTooltipData = {
  'EC1':
    'EC1 has been auto-added by Copilot as Customer Team Contact info is missing or incomplete in MSX Partner Engagement > People Tab',
  'AG': 'The AG tag has been auto added by Copilot as the Partner referral has been identified as Assisted Growth',
  'PSDS':
    'PSDS tag has been auto-added by Copilot as #PSDS is present on the Name or Referral campaign on MSX Partner Referral',
  '1P': '1P tag has been auto-added by Copilot as the Partner Referral Solution Type is 1P on MSX',
  'BAU':
    'BAU tag has been auto-added by Copilot as the Partner Name is listed on the COS Special Partners List',
  'SP': 'SP tag has been auto-added by Copilot as the Partner Name is listed on the COS Special Partners List',
  'GISV27':
    'The GISV27 tag has been auto added by Copilot as the Partner referral has been identified as Global Independent Software Vendor (GISV)',
  'GeoX':
    'The  GeoX  tag has been auto added by Copilot as the Partner referral has been identified as GeoX',
};

export const TagsDetails: Record<string, string> = {
  GISV27:
    'No prequalification; No Opportunity creation; Assign to seller. Add relevant tags in UAT; Change the sub-status to "Requirement OOS" and Final status as "CANCELLED" and close & submit in UAT.',
  AG: 'No prequalification needed; create Opportunity, add Sales Program and assign to seller; Add special comments in MSX Opty summary page. No handover email required in UAT; Add relevant tags in UAT; Change the sub-status : Done and final status Handover Completed.',
  GeoX: 'No prequalification needed; create Opportunity, add Sales Program and assign to seller; Add special comments in MSX Opty summary page. No handover email required in UAT; Add relevant tags in UAT; Change the sub-status : Done and final status Handover Completed.',
};

export const defaultToastTimer = 8000;

export function normalizeAreaNames(arr: string[]): string[] {
  // Update country names containing '&' by replacing it with 'and' to ensure compatibility with ADO.
  // Implemented in the Additional Area field for General requests.

  if (!arr || !Array.isArray(arr)) return [];

  const result: string[] = [];
  let i = 0;

  while (i < arr.length) {
    let current = arr[i] ?? '';
    let next = arr[i + 1] ?? '';

    // Replace any &amp with &
    current = current.replace(/&amp;?/gi, '&');

    // Detect if current ends with & (like "Germany &" or "SME&")
    const endsWithAmp = /&\s*$/i.test(current);

    if (endsWithAmp && next) {
      // Remove trailing & (or spaces after it) from current and append next
      const merged = current.replace(/&\s*$/i, '&') + next; // ← no space between & and next token
      result.push(merged);
      i += 2;
    } else {
      result.push(current);
      i += 1;
    }
  }

  return result;
}

export const WorkScopeValidURL = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

export const caseTrackingPropertyLabelMapping = {
  requestor: 'Requestor',
  agent: 'Agent',
  date: 'Date',
  state: 'State',
  subState: 'Sub State',
  mdaCaseId: 'MDA Case ID',
  mdaStatus: 'MDA Status',
  oneAskCaseId: 'OneAsk Case ID',
  oneAskStatus: 'OneAsk Status',
  migrationStatus: 'Migration Status',
  linkWorkScope: 'WorkScope Link',
  sponsorshipStatus: 'Sponsorship Status',
  approvedOnDate: 'ApprovedOn Date​',
  transacted: 'Transacted ( Yes / No )',
  transactedAmount: 'Transacted Amount',
  transactedDate: 'Transacted Date',
  ioNumber: 'IO Number',
  ioApprovalStatus: 'IO Approval Status',
  //Below are not part of UI
  title: 'Title',
  stageStatus: 'Stage Status',
  source: 'Source',
};

// Generated by GitHub Copilot | Updated on: 2026-01-16 | 8 lines modified | Added DefenderForCloud tenant constants including TenantIds, AreaPath, TenantTitles, TenantADOText, CreateActionMenuKey, CreateActionMenuTitle, and PlaceholdersAndTitles
// Generated by GitHub Copilot | Updated on: 2026-01-16 | 6 lines modified | Added DefenderForCloud entry to AllTenant object

export const isMdaEnabledForECIF = (configData: any) => {
  const isMdaEnabled = configData?.isMdaEnabledForECIF ?? true;
  return isMdaEnabled;
};

export const isMdaEnabledForACO = (configData: any) => {
  const isMdaEnabled = configData?.isMdaEnabledForACO ?? true;
  return isMdaEnabled;
};

export const isMdaEnabledForAA = (configData: any) => {
  const isMdaEnabled = configData?.isMdaEnabledForAA ?? true;
  return isMdaEnabled;
};

export const isMdaEnabledForBenchMarkTCO = (configData: any) => {
  const isMdaEnabled = configData?.isMdaEnabledForBenchMarkTCO ?? true;
  return isMdaEnabled;
};

export const isMdaEnabledForACOInECIF = (configData: any) => {
  const isMdaEnabled = configData?.isMdaEnabledForACOInECIF ?? true;
  return isMdaEnabled;
};

//Applicable for MDA not created
export const toggleBtnDisableStatesForMDA = {
  ECIF: ['0 - Draft', '1 - New', '6 - Closed'],
  ECIFWITHACO: ['0 - Draft', '1 - New', '6 - Closed'],
  ACO: ['0 - Draft', '1 - New', '6 - Closed'],
  AA: ['0 - Draft', '1 - New', '6 - Closed'],
  BenchMarkTCO: ['0 - Draft', '1 - New', '6 - Closed'],
};

const microsoftEmailsRegex = /^[\w.+-]+@([a-zA-Z0-9-]+\.)*(microsoft|onmicrosoft)\.com$/;
export const isMicrosoftEmail = (email: string): boolean => {
  return microsoftEmailsRegex.test(email);
};

export const isAMMFileSizeRestrictionNeeded = (offerId: number) => {
  if (
    UATConstants.ECIFOffersKeysList.includes(offerId) ||
    offerId === UATConstants.UATFields.AMM_OFFER_AMMAPAzureAccess ||
    UATConstants.ACOOffersKeysList.includes(offerId)
  ) {
    return true;
  } else {
    return false;
  }
};
export const discussionFieldLevelUserMailInValidErrorMessage = (commentMentions: string[]) => {
  const grammer = commentMentions.length > 1 ? 'are' : 'is';
  const errorMessages = commentMentions.join(' & ') + ` ${grammer} not valid id .Please change.`;
  return errorMessages;
};

export const isNbarVisible = (configData: any, actionData: any) => {
  const isEnabled = configData?.isNbarEnabled ?? true;
  const isHelpType = actionData?.fields?.requestType === 'Help';
  return isEnabled && !isHelpType;
};

export const formAmmAONRequestLink = (raidId: number) => {
  const ammAONBaseUrl = import.meta.env.VITE_AONRequestLink;
  return `${ammAONBaseUrl}${raidId}`;
};

export const AMM_PARTNER_SPLIT = {
  MIN: 10,
  MAX: 90,
};

// Generated by GitHub Copilot | Updated on: 2026-04-06 | 3 lines modified | Added SubTier label, placeholder, and error message constants

// Generated by GitHub Copilot | Updated on: 2026-04-13 | 1 lines modified | Updated PipAdoCreationError message to 'Error creating work Item.'

// Generated by GitHub Copilot | Updated on: 2026-05-06 | 12 lines modified | Added SSCACO offer key, name, ACOOffersKeysList entry, MDAExcludedACOOffers list, and ammOfferList entry

// Generated by GitHub Copilot | Updated on: 2026-05-25 | 2 lines modified | Added pagehide and visibilitychange browser event constants

// Generated by GitHub Copilot | Updated on: 2026-06-29 | 2 lines modified | Added MSServices 'Microsoft Services' solution area and included it in NoATCSolutionAreas
// Generated by GitHub Copilot | Updated on: 2026-06-03 | 4 lines modified | Added AMM_OFFER_FieldECIF constant, ECIFOffersKeysList and OffersBelongsToAMMTriageOwnerMandate entries
// Generated by GitHub Copilot | Updated on: 2026-06-03 | 6 lines modified | Added Corp ECIF offer key, name, ECIFOffersKeysList, OffersBelongsToAMMTriageOwnerMandate, and offer mapping entries

// Generated by GitHub Copilot | Updated on: 2026-06-03 | 5 lines modified | Added Specialty ECIF offer key, name, ECIFOffersKeysList, and offer mapping entry

// Generated by GitHub Copilot | Updated on: 2026-06-03 | 5 lines modified | Added Corp ACO offer key, name, ACOOffersKeysList, and offer mapping entry

// Generated by GitHub Copilot | Updated on: 2026-06-03 | 5 lines modified | Added FY27 Local ACO offer key, name, ACOOffersKeysList, and offer mapping entry

// Generated by GitHub Copilot | Updated on: 2026-06-08 | 1 lines modified | Changed offerId type from string to number with default 0

// Generated by GitHub Copilot | Updated on: 2026-06-25 | 2 lines modified | US 138301: Add LinkedTechRoB constants to RAIDFields and ArtemisFieldPaths
// Generated by GitHub Copilot | Updated on: 2026-07-17 | 27 lines modified | Moved RetiredAMMOfferIds into UATConstants class as a static member above OffersBelongsToAMMTriageOwnerMandate
// Generated by GitHub Copilot | Updated on: 2026-07-22 | 2 lines modified | Added CustomerCommitmentToStartMigration label and mandatory error message
// Generated by GitHub Copilot | Updated on: 2026-07-24 | 2 lines modified | Prefixed Seller to ROI and ECIF+Factory split justification labels
// Generated by GitHub Copilot | Updated on: 2026-07-24 | 5 lines modified | Added Project Scope Distribution allocation/description/tooltip constants
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 12 lines added | Added FormEngineApiUrl (localhost-default, VITE_FORMENGINEAPIURL) so the new FormEngine v1 APIs target the local dev server
// Generated by GitHub Copilot | Updated on: 2026-07-15 | 4 lines modified | FormEngineApiUrl now falls back to CommonServicesWebApiUrl (not localhost) so deployed envs (perf/test/int/prod) target the right host; localhost is opt-in via VITE_FORMENGINEAPIURL in .env.dev
// Generated by GitHub Copilot | Updated on: 2026-07-30 | 1 lines added | Added Telemetry.DirectLandingCreateOpened event name for deep-link tracking
// Generated by GitHub Copilot | Updated on: 2026-08-12 | 11 lines modified | Added majority ACR workload constants
// Generated by GitHub Copilot | Updated on: 2026-09-15 | 7 lines added | Added Red Button TPID validation constants
// Generated by GitHub Copilot | Updated on: 2026-08-12 | 7 lines modified | Removed majority ACR normalization
// Generated by GitHub Copilot | Updated on: 2026-08-12 | 4 lines modified | Moved majority ACR label to placeholders
// Generated by GitHub Copilot | Updated on: 2026-08-13 | 4 lines modified | Added GPS request reason constants
// Generated by GitHub Copilot | Updated on: 2026-08-18 | 1 line removed | Removed Flights.MarketplaceAccess enum entry (retired flight)
// Generated by GitHub Copilot | Updated on: 2026-08-18 | 6 lines modified | Normalize path and add validation message
// Generated by GitHub Copilot | Updated on: 2026-08-25 | 8 lines added | Added Model, AccountType and SkillingId title/placeholder constants for Regional Skilling
// Generated by GitHub Copilot | Updated on: 2026-08-28 | 2 lines added | Added Azure AI Capacity RBAC message
// Generated by GitHub Copilot | Updated on: 2026-08-28 | 1 line modified | Updated Azure AI read-only banner text
// Generated by GitHub Copilot | Updated on: 2026-08-28 | 2 lines added | Added Azure AI create form notice
// Generated by GitHub Copilot | Updated on: 2026-09-01 | 2 lines modified | Updated Survey description label
// Generated by GitHub Copilot | Updated on: 2026-09-01 | 6 lines modified | Resolved Survey PR merge history
// Generated by GitHub Copilot | Updated on: 2026-09-10 | 3 lines modified | Added AON resubmission banner text
// Generated by GitHub Copilot | Updated on: 2026-09-10 | 2 lines modified | Updated financial validation labels
// Generated by GitHub Copilot | Updated on: 2026-09-21 | 6 lines modified | Added eligible PIR decisions for ADO warning
// Generated by GitHub Copilot | Updated on: 2026-09-24 | 1 line modified | Updated Competitor Name placeholder
// Generated by GitHub Copilot | Updated on: 2026-09-24 | 2 lines modified | Restored legacy competitor placeholders
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 3 lines modified | Removed negotiate decision from ADO validation
// Generated by GitHub Copilot | Updated on: 2026-09-29 | 2 lines modified | Added customer priority mandatory validation message
// Generated by GitHub Copilot | Updated on: 2026-09-18 | 2 lines modified | Relabel primary blocker and required message
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 2 lines modified | Allowed negotiate decision for ADO creation
// Generated by GitHub Copilot | Updated on: 2026-10-12 | 1 line added | Added AOAI Contributors ADO field
// Generated by GitHub Copilot | Updated on: 2026-10-21 | 1 line added | Added UAT Subscription ID lookup route
// Generated by GitHub Copilot | Updated on: 2026-10-23 | 1 line modified | Clarified Partner Surveys launch question
// Generated by GitHub Copilot | Updated on: 2026-10-23 | 1 line modified | Clarified MAQ eligibility launch question
// Generated by GitHub Copilot | Updated on: 2026-10-23 | 1 line modified | Clarified partner performance launch question
// Generated by GitHub Copilot | Updated on: 2026-10-23 | 1 line modified | Clarified MCI policy guide launch question
// Generated by GitHub Copilot | Updated on: 2026-10-23 | 2 lines modified | Add Red Button additional-request warning copy
